// build: 2026-04-26d
import { useState, useMemo, useEffect, useRef, useCallback, Component } from "react";
import KommunikationView from './modules/kommunikation/KommunikationView';

/* ─── Error Boundary (fängt Render-Fehler ab) ───────────────────────── */
class MsgErrorBoundary extends Component {
  state = { err: null };
  static getDerivedStateFromError(e) { return { err: e }; }
  componentDidCatch(e, info) { console.error("MessagesView crash:", e, info); }
  render() {
    if (this.state.err) return (
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:24,background:"#fff"}}>
        <div style={{fontSize:28}}>⚠️</div>
        <div style={{fontWeight:700,fontSize:15,color:"#111827"}}>Chat-Fehler</div>
        <div style={{fontSize:12,color:"#dc2626",background:"#fef2f2",padding:"10px 14px",borderRadius:8,maxWidth:300,wordBreak:"break-all",textAlign:"center"}}>
          {this.state.err.message || String(this.state.err)}
        </div>
        <button onClick={()=>this.setState({err:null})} style={{padding:"10px 20px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700}}>
          Erneut versuchen
        </button>
      </div>
    );
    return this.props.children;
  }
}

const API_URL = "https://burn-firsttimer-bot-production.up.railway.app";

/* ── Auth (Phase 4): Token automatisch an alle API-Requests hängen ──────── */
const _origFetch = window.fetch.bind(window);
window.fetch = (url, opts = {}) => {
  try {
    if (typeof url === "string" && url.startsWith(API_URL)) {
      const t = localStorage.getItem("burn_token");
      if (t) opts = { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${t}` } };
    }
  } catch {}
  return _origFetch(url, opts).then(r => {
    if (r.status === 401 && typeof url === "string" && url.startsWith(API_URL) && !url.includes("/auth/login")) {
      /* Token abgelaufen/ungültig → zurück zum Login.
         WICHTIG: Nur neu laden, wenn überhaupt eine Sitzung existierte —
         der Login-Screen selbst bekommt 401s (Module etc.), ein Reload dort
         erzeugte eine Endlos-Schleife (blinkender Login). Zusätzlich
         Reload-Bremse: höchstens alle 5 Sekunden. */
      const hadSession = !!(localStorage.getItem("burn_token") || localStorage.getItem("burn_user"));
      localStorage.removeItem("burn_token");
      localStorage.removeItem("burn_user");
      const last = Number(sessionStorage.getItem("burn_401_reload") || 0);
      if (hadSession && Date.now() - last > 5000) {
        sessionStorage.setItem("burn_401_reload", String(Date.now()));
        window.location.reload();
      }
    }
    return r;
  });
};

/* ─── localStorage cache helpers (stale-while-revalidate) ───────────────── */
const LS_TTL = 5 * 60 * 1000; // 5 min default TTL
function lsGet(key) {
  try {
    const raw = localStorage.getItem("burn_cache_" + key);
    if (!raw) return null;
    const { ts, val } = JSON.parse(raw);
    if (Date.now() - ts > LS_TTL) return null; // expired
    return val;
  } catch { return null; }
}
function lsSet(key, val) {
  try { localStorage.setItem("burn_cache_" + key, JSON.stringify({ ts: Date.now(), val })); } catch {}
}
function lsGetStale(key) { // returns even expired cache
  try { const raw = localStorage.getItem("burn_cache_" + key); return raw ? JSON.parse(raw).val : null; } catch { return null; }
}



const PIPELINE_STAGES = [
  { id:"lead",        label:"Lead",        color:"#6366f1", desc:"Interesse gezeigt" },
  { id:"first_timer", label:"First Timer", color:"#7c3aed", desc:"Erster Besuch" },
  { id:"returning",   label:"Wiederkommer",color:"#0891b2", desc:"2-4 Besuche" },
  { id:"member",      label:"Member",      color:"#059669", desc:"Aktives Mitglied" },
  { id:"vip",         label:"VIP",         color:"#d97706", desc:"25+ Sessions" },
  { id:"inactive",    label:"Inaktiv",     color:"#dc2626", desc:"Nicht mehr aktiv" },
  { id:"duplicate",   label:"Duplikat",    color:"#9ca3af", desc:"Zusammengeführt" },
];

const MB_STATUSES = { Active:"#059669", Inactive:"#dc2626", Non_Member:"#6366f1", Suspended:"#f97316", Expired:"#9ca3af", Declined:"#d97706" };

const isValidMobile = (phone) => {
  if (!phone) return false;
  const cleaned = String(phone).replace(/[\s\-\(\)\.]/g, "");
  return /^(\+49|0049|0)1[5-9]\d{7,10}$/.test(cleaned);
};

const EMAIL_TEMPLATES = [
  { id:1, name:"Email 1 – First Session", subject:"Hey {{name}}, how was your first BURN session?", category:"Journey", opens:78, clicks:45, lastUsed:"2026-03-22" },
  { id:2, name:"Email 2 – Follow Up", subject:"Hey {{name}}, we miss you!", category:"Journey", opens:52, clicks:28, lastUsed:"2026-03-20" },
  { id:3, name:"Email 3 – Free Trial", subject:"Hey {{name}}, free class on us!", category:"Journey", opens:61, clicks:38, lastUsed:"2026-03-18" },
  { id:4, name:"Milestone 5", subject:"Hey {{name}}, 5 sessions done!", category:"Milestone", opens:91, clicks:67, lastUsed:"2026-03-15" },
  { id:5, name:"Milestone 10", subject:"Hey {{name}}, you are on fire!", category:"Milestone", opens:88, clicks:71, lastUsed:"2026-02-28" },
  { id:6, name:"Milestone 25", subject:"Hey {{name}}, legend status!", category:"Milestone", opens:95, clicks:82, lastUsed:"2026-03-01" },
  { id:7, name:"Reaktivierung", subject:"Hey {{name}}, we want you back!", category:"Retention", opens:44, clicks:19, lastUsed:"2026-03-10" },
  { id:8, name:"Membership-Verlängerung", subject:"Hey {{name}}, dein Paket läuft ab", category:"Retention", opens:82, clicks:60, lastUsed:"2026-03-05" },
];
const SMS_TEMPLATES = [
  { id:1, name:"First Session", message:"Hey {{name}}! BURN hier. Wie war deine erste Session? Wir freuen uns dich wieder zu sehen! 🥊" },
  { id:2, name:"Free Trial", message:"Hey {{name}}, Julian von BURN. Wir schenken dir eine kostenlose Klasse. Einfach antworten!" },
  { id:3, name:"Reaktivierung", message:"Hey {{name}}! Lange nicht gesehen bei BURN. Komm zurück – wir vermissen dich! 💪" },
  { id:4, name:"Credit-Erinnerung", message:"Hey {{name}}, du hast noch Credits bei BURN! Buche jetzt deine nächste Klasse." },
];

const AUTOMATIONS = [
  { id:1, name:"First Timer Journey", trigger:"Erster Besuch", steps:["Sofort: Email 1","Tag 5: Email 2","Tag 10: Email 3"], active:true, triggered:47 },
  { id:2, name:"Milestone Emails", trigger:"Sessions Meilenstein", steps:["5 Sessions: Milestone Email","10 Sessions: Milestone Email","25 Sessions: Milestone Email"], active:true, triggered:23 },
  { id:3, name:"Inaktivität", trigger:"30 Tage kein Besuch", steps:["Reaktivierungs-Email","Tag 7: SMS"], active:true, triggered:12 },
  { id:4, name:"Vertragsende", trigger:"7 Tage vor Ablauf", steps:["Verlängerungs-Email","Tag 3: Follow-up"], active:true, triggered:8 },
  { id:5, name:"Geburtstag", trigger:"Geburtstag", steps:["Geburtstags-Email mit Rabatt"], active:false, triggered:0 },
];
function decodeHTML(str) {
  if (!str || typeof str !== "string") return str;
  return str.replace(/&#(\d+);/g, (m, code) => String.fromCharCode(code))
            .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"').replace(/&apos;/g, "'");
}

function sanitize(c) {
  return {
    id:              String(c.id || ""),
    name:            decodeHTML(String(c.name || "Unbekannt")),
    email:           String(c.email || ""),
    phone:           String(c.phone || ""),
    avatar:          decodeHTML(String(c.avatar || "?")),
    mbStatus:        String(c.mbStatus || "Non_Member"),
    source:          String(c.source || "Direct"),
    stage:           String(c.stage || "lead"),
    trainer:         decodeHTML(String(c.trainer || "")),
    lastVisit:       String(c.lastVisit || ""),
    joinDate:        String(c.joinDate || ""),
    birthDate:       String(c.birthDate || ""),
    paymentMethod:   String(c.paymentMethod || "—"),
    classPackage:    String(c.classPackage || "—"),
    notes:           String(c.notes || ""),
    membership:      c.membership ? String(c.membership) : null,
    contractEnd:     c.contractEnd ? String(c.contractEnd) : null,
    lastPayment:     c.lastPayment ? String(c.lastPayment) : null,
    visits:          Number(c.visits) || 0,
    ltv:             Number(c.ltv) || 0,
    leadScore:       Number(c.leadScore) || 0,
    noShows:         Number(c.noShows) || 0,
    remainingCredits:c.remainingCredits != null ? Number(c.remainingCredits) : 0,
    openBalance:     Number(c.openBalance) || 0,
    smsCapable:      Boolean(c.smsCapable),
    returned:        Boolean(c.returned),
    tags:            Array.isArray(c.tags) ? c.tags.map(t => String(t)) : [],
    emails:          Array.isArray(c.emails) ? c.emails.map(e => ({
                       type: String(e.type || ""),
                       sent: String(e.sent || ""),
                       opened: Boolean(e.opened),
                       openedAt: e.openedAt ? String(e.openedAt) : null,
                       clicked: Boolean(e.clicked),
                     })) : [],
    classHistory:    Array.isArray(c.classHistory) ? c.classHistory : [],
    purchases:       Array.isArray(c.purchases) ? c.purchases : [],
    tasks:           Array.isArray(c.tasks) ? c.tasks : [],
  };
}
const Avatar = ({ initials, size=32, color="#7c3aed" }) => (
  <div style={{ width:size, height:size, borderRadius:"50%", background:`${color}18`, border:`1.5px solid ${color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.35, fontWeight:700, color, flexShrink:0 }}>{initials}</div>
);

const BurnLoader = ({ text="" }) => (
  <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, padding:40 }}>
    <img src="https://cdn.prod.website-files.com/6507f8c501706168d0387816/650c3d06688d8062fd53aad4_webclip_burn.png"
      className="burn-fallback-spin"
      style={{ width:56, height:56, objectFit:"contain" }} alt="BURN" />
    {text && <div style={{ fontSize:13, color:"#9ca3af", fontWeight:500 }}>{text}</div>}
  </div>
);

const Tag = ({ label, color="#6366f1" }) => (
  <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:4, background:`${color}12`, color, border:`1px solid ${color}20`, whiteSpace:"nowrap" }}>{String(label || "")}</span>
);

const Btn = ({ children, onClick, variant="ghost", color="#7c3aed", size="md" }) => {
  const pad = size==="sm" ? "5px 10px" : "8px 16px";
  const styles = {
    primary: { background:color, color:"#fff", border:"none" },
    ghost:   { background:"transparent", color:"#374151", border:"1px solid #e5e7eb" },
    outline: { background:"transparent", color, border:`1px solid ${color}50` },
  };
  return <button onClick={onClick} style={{ ...styles[variant], borderRadius:6, padding:pad, cursor:"pointer", fontSize:13, fontWeight:500, fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:6 }}>{children}</button>;
};

const Score = ({ value }) => {
  const v = Number(value) || 0;
  const color = v>=80?"#059669":v>=50?"#d97706":"#dc2626";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ width:40, height:4, background:"#f3f4f6", borderRadius:2 }}>
        <div style={{ height:"100%", width:`${v}%`, background:color, borderRadius:2 }} />
      </div>
      <span style={{ fontSize:12, fontWeight:600, color }}>{v}</span>
    </div>
  );
};
/* ─── CRM Sidebar Heroicons Filled ───────────────────────────────────── */
function CrmNavIcon({ id, color="#9ca3af" }) {
  const s = { display:"block", flexShrink:0 };
  const icons = {
    pipeline:    <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M3.792 2.938A49.069 49.069 0 0 1 12 2.25c2.797 0 5.54.236 8.209.688a1.857 1.857 0 0 1 1.541 1.836v1.044a3 3 0 0 1-.879 2.121l-6.182 6.182a1.5 1.5 0 0 0-.439 1.061v2.927a3 3 0 0 1-1.658 2.684l-1.757.878A.75.75 0 0 1 9.75 21v-5.818a1.5 1.5 0 0 0-.44-1.06L3.13 7.938a3 3 0 0 1-.879-2.121V4.774c0-.9.63-1.683 1.542-1.836z" clipRule="evenodd"/></svg>,
    customers:   <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z"/></svg>,
    emails:      <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z"/><path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z"/></svg>,
    sms:         <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223z" clipRule="evenodd"/></svg>,
    automations: <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M14.615 1.595a.75.75 0 0 1 .359.852L12.982 9.75h7.268a.75.75 0 0 1 .548 1.262l-10.5 11.25a.75.75 0 0 1-1.272-.71l1.992-7.302H3.818a.75.75 0 0 1-.548-1.262l10.5-11.25a.75.75 0 0 1 .845-.143z" clipRule="evenodd"/></svg>,
    analytics:   <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 0 1-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 0 1 3 19.875v-6.75z"/></svg>,
    segments:    <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M3.792 2.938A49.069 49.069 0 0 1 12 2.25c2.797 0 5.54.236 8.209.688a1.857 1.857 0 0 1 1.541 1.836v1.044a3 3 0 0 1-.879 2.121l-6.182 6.182a1.5 1.5 0 0 0-.439 1.061v2.927a3 3 0 0 1-1.658 2.684l-1.757.878A.75.75 0 0 1 9.75 21v-5.818a1.5 1.5 0 0 0-.44-1.06L3.13 7.938a3 3 0 0 1-.879-2.121V4.774c0-.9.63-1.683 1.542-1.836z" clipRule="evenodd"/></svg>,
    leads:       <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd"/></svg>,
    cash:        <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M12 7.5a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z"/><path fillRule="evenodd" d="M1.5 4.875C1.5 3.839 2.34 3 3.375 3h17.25c1.035 0 1.875.84 1.875 1.875v9.75c0 1.036-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 14.625v-9.75ZM8.25 9.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM18.75 9a.75.75 0 0 0-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 0 0 .75-.75V9.75a.75.75 0 0 0-.75-.75h-.008ZM4.5 9.75A.75.75 0 0 1 5.25 9h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H5.25a.75.75 0 0 1-.75-.75V9.75Z" clipRule="evenodd"/><path d="M2.25 18a.75.75 0 0 0 0 1.5c5.4 0 10.63.722 15.6 2.075 1.19.324 2.4-.558 2.4-1.82V18.75a.75.75 0 0 0-.75-.75H2.25Z"/></svg>,
    weekly:      <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M15.22 6.268a.75.75 0 0 1 .968-.431l5.942 2.28a.75.75 0 0 1 .431.97l-2.28 5.941a.75.75 0 1 1-1.4-.537l1.63-4.251-1.086.484a17.252 17.252 0 0 0-3.126 2.023 9.573 9.573 0 0 1-6.201 2.512c-2.716 0-5.048-1.19-6.76-3.062a.75.75 0 0 1 1.12-.998c1.42 1.596 3.35 2.56 5.64 2.56 1.807 0 3.507-.68 4.81-1.917a15.741 15.741 0 0 1 2.849-1.845l1.087-.484-4.254-1.63a.75.75 0 0 1-.43-.968Z" clipRule="evenodd"/></svg>,
    staff:       <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z"/></svg>,
    bestand:     <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918z" clipRule="evenodd"/></svg>,
    duplicates:  <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" clipRule="evenodd"/></svg>,
    journeys:    <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M8.161 2.58a1.875 1.875 0 0 1 1.678 0l4.993 2.498c.106.052.23.052.336 0l3.869-1.935A1.875 1.875 0 0 1 21.75 4.82v12.485c0 .71-.401 1.36-1.037 1.677l-4.875 2.437a1.875 1.875 0 0 1-1.676 0l-4.994-2.497a.375.375 0 0 0-.336 0l-3.868 1.935A1.875 1.875 0 0 1 2.25 19.18V6.695c0-.71.401-1.36 1.036-1.677l4.875-2.437ZM9 6a.75.75 0 0 1 .75.75v9.5a.75.75 0 0 1-1.5 0v-9.5A.75.75 0 0 1 9 6zm6.75 2.75a.75.75 0 0 0-1.5 0v9.5a.75.75 0 0 0 1.5 0v-9.5z" clipRule="evenodd"/></svg>,
    journeyEditor: <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712zm-2.218 5.93-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4z"/></svg>,
    templates:   <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625zM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15zm.75-6.75a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25z" clipRule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963z"/></svg>,
    messages:    <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223z" clipRule="evenodd"/></svg>,
    leaderboard: <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 0 0-.584.859 6.753 6.753 0 0 0 6.138 5.6 6.73 6.73 0 0 0 2.743 1.346A6.707 6.707 0 0 1 9.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a2.25 2.25 0 0 0-2.25 2.25c0 .414.336.75.75.75h15a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-2.25-2.25h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.706 6.706 0 0 1-1.112-3.173 6.73 6.73 0 0 0 2.743-1.347 6.753 6.753 0 0 0 6.139-5.6.75.75 0 0 0-.585-.858 47.077 47.077 0 0 0-3.07-.543V2.62a.75.75 0 0 0-.658-.744 49.798 49.798 0 0 0-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 0 0-.657.744zm0 2.629c0 1.196.312 2.32.857 3.294A5.266 5.266 0 0 1 3.16 5.337a45.6 45.6 0 0 1 2.006-.343v.256zm13.5 0v-.256c.674.1 1.343.214 2.006.343a5.265 5.265 0 0 1-2.863 3.207 6.72 6.72 0 0 0 .857-3.294z" clipRule="evenodd"/></svg>,
    permissions: <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M15.75 1.5a6.75 6.75 0 0 0-6.651 7.906c.067.39-.032.717-.221.906l-6.5 6.499a.75.75 0 0 0-.187.395l-.5 2.25a.75.75 0 0 0 .218.677l1.5 1.5a.75.75 0 0 0 .677.218l1-.223 1-.224a.75.75 0 0 0 .418-.242l.498-.598a.75.75 0 0 0 .189-.384l.125-.5a.75.75 0 0 0-.15-.624l-.25-.3A.75.75 0 0 1 7 18.376l.25-.5a.75.75 0 0 1 .655-.5h.25l1.5-.5a.75.75 0 0 0 .395-.187c.19-.19.517-.288.906-.221A6.75 6.75 0 1 0 15.75 1.5zm0 3a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 13.5 8.25a.75.75 0 0 1-1.5 0 3.75 3.75 0 0 1 3.75-3.75z" clipRule="evenodd"/></svg>,
  };
  return icons[id] || <svg style={s} width="18" height="18" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z" clipRule="evenodd"/></svg>;
}

const NAV = [
  { id:"pipeline",         label:"Pipeline",        module:null,          hidden:true },
  { id:"customers",        label:"Kundendaten",     module:null           },
  { id:"emails",           label:"E-Mails",         module:null           },
  { id:"sms",              label:"SMS",             module:null,  hidden:true },
  { id:"automations",      label:"Automationen",    module:"journeys",    hidden:true },
  { id:"analytics",        label:"Analytics",       module:null           },
  { id:"segments",         label:"Segmente",        module:null, hidden:true },
  { id:"leads",            label:"Leads",           module:null, hidden:true },
  { id:"cash",             label:"Cash",            module:"cash"         },
  { id:"profitability",    label:"Profitabilität",  module:"profitability"},
  { id:"modules",          label:"Module 🧩",       module:null,          hidden:true },
  { id:"weekly",           label:"Weekly",          module:"payroll"      },
  { id:"staff",            label:"Staff",           module:"payroll"      },
  { id:"bestand",          label:"Bestand",         module:"payroll"      },
  { id:"duplicates",       label:"Duplikate",       module:null,          hidden:true },
  { id:"journeys",         label:"Journeys",        module:"journeys",    hidden:true },
  { id:"journeyEditor",    label:"E-Mail Editor",   module:"email_editor",hidden:true },
  { id:"emailBlockEditor", label:"📝 Templates",    module:"email_editor",hidden:true },
  { id:"templates",        label:"Templates",       module:null,          hidden:true },
  { id:"messages",         label:"Nachrichten",     module:null           },
  { id:"leaderboard",      label:"Rangliste",       module:null           },
  { id:"kommunikation",   label:"📬 Kommunikation", module:"kommunikation"  },
  { id:"permissions",      label:"Berechtigungen",  module:null           },
];

const Sidebar = ({ active, setActive, customers, loading, hasModule }) => {
  const isMobile = useMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const smsCount = customers.filter(c=>c.smsCapable&&isValidMobile(c.phone)).length;
  const taskCount = customers.flatMap(c=>c.tasks.filter(t=>!t.done)).length;

  const navInner = (onPick) => (
    <>
      <div style={{ padding:"18px 20px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <img src={BURN_LOGO_WHITE} style={{ height:28, width:"auto", filter:"brightness(0)" }} alt="BURN" />
          <div style={{ fontSize:11, color:"#9ca3af" }}>CRM &amp; Marketing</div>
        </div>
        {isMobile && <button onClick={onPick} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280", padding:4 }}>✕</button>}
      </div>
      <nav style={{ padding:"10px 10px", flex:1, overflowY:"auto" }}>
        {NAV.filter(n => !n.hidden && (!n.module || !hasModule || hasModule(n.module))).map(n => (
          <button key={n.id} onClick={()=>{ setActive(n.id); onPick(); }} style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"10px 12px", borderRadius:8, border:"none", cursor:"pointer", fontSize:13, fontWeight:active===n.id?600:400, background:active===n.id?"#f5f3ff":"transparent", color:active===n.id?"#7c3aed":"#6b7280", fontFamily:"inherit", marginBottom:2, textAlign:"left" }}>
            <CrmNavIcon id={n.id} color={active===n.id?"#7c3aed":"#9ca3af"} />
            <span style={{ flex:1 }}>{n.label}</span>
            {n.id==="sms"&&smsCount>0&&<span style={{ background:"#0891b2", color:"#fff", fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10 }}>{smsCount}</span>}
          </button>
        ))}
      </nav>
      {taskCount>0&&(
        <div style={{ padding:"10px 14px", borderTop:"1px solid #f3f4f6", background:"#fefce8" }}>
          <div style={{ fontSize:11, fontWeight:600, color:"#92400e" }}>⚠ {taskCount} offene Aufgaben</div>
        </div>
      )}
      <div style={{ padding:"12px 16px", borderTop:"1px solid #f3f4f6" }}>
        <div style={{ fontSize:11, color:"#9ca3af" }}>{loading?"Lädt...":"Mindbody Sync"}</div>
        <div style={{ fontSize:11, color:"#374151", fontWeight:500 }}>{customers.length} Kunden</div>
      </div>
    </>
  );

  if (isMobile) return (
    <>
      {/* Mobile top bar hamburger button — rendered in main layout */}
      {drawerOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex" }}>
          <div style={{ width:280, background:"#fff", display:"flex", flexDirection:"column", height:"100%", boxShadow:"4px 0 24px #00000030", overflowY:"auto" }}>
            {navInner(() => setDrawerOpen(false))}
          </div>
          <div style={{ flex:1, background:"rgba(0,0,0,0.5)" }} onClick={() => setDrawerOpen(false)} />
        </div>
      )}
      {/* Expose open fn via ref pattern — simpler: pass via prop */}
      <div data-mobile-menu-trigger="true" style={{ display:"none" }} onClick={() => setDrawerOpen(true)} />
    </>
  );

  return (
    <div style={{ width:220, background:"#fff", borderRight:"1px solid #e5e7eb", display:"flex", flexDirection:"column", flexShrink:0 }}>
      {navInner(() => {})}
    </div>
  );
};
const PipelineCard = ({ c, onClick, selected, onDragStart }) => {
  const stage = PIPELINE_STAGES.find(s=>s.id===c.stage);
  const openRate = c.emails.length ? Math.round(c.emails.filter(e=>e.opened).length/c.emails.length*100) : null;
  return (
    <div draggable onDragStart={e=>onDragStart(e,c)} onClick={()=>onClick(c)}
      style={{ background:"#fff", border:`1.5px solid ${selected?"#7c3aed":"#e5e7eb"}`, borderRadius:10, padding:"12px 14px", cursor:"pointer", marginBottom:8, boxShadow:selected?"0 0 0 3px #7c3aed20":"0 1px 3px #0000000a", transition:"all 0.15s" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
        <Avatar initials={c.avatar} size={30} color={stage?.color||"#777"} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:600, color:"#111827", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.name}</div>
          <div style={{ fontSize:10, color:"#9ca3af" }}>{c.trainer||c.source}</div>
        </div>
        <div style={{ display:"flex", gap:4 }}>
          {c.smsCapable&&isValidMobile(c.phone)&&<span title="SMS">💬</span>}
          {c.remainingCredits>0&&<span title={`${c.remainingCredits} Credits`} style={{fontSize:10,background:"#eff6ff",color:"#0891b2",borderRadius:99,padding:"1px 5px"}}>{c.remainingCredits}</span>}
        </div>
      </div>
      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:6 }}>
        {c.source==="CLASSPASS"&&<Tag label="ClassPass" color="#d97706"/>}
        {c.membership&&<Tag label={c.membership} color="#059669"/>}
        {c.visits>0&&<Tag label={`${c.visits}x`} color="#6b7280"/>}
        {openRate!==null&&<Tag label={`${openRate}% offen`} color={openRate>50?"#059669":"#d97706"}/>}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <Score value={c.leadScore} />
        <span style={{ fontSize:11, color:c.ltv>0?"#059669":"#9ca3af" }}>{c.ltv>0?`${c.ltv}€ LTV`:"—"}</span>
      </div>
    </div>
  );
};

const CustomerPanel = ({ c, onClose, setCustomers }) => {
  const [tab, setTab] = useState("overview");
  const [note, setNote] = useState(c.notes);
  const [taskText, setTaskText] = useState("");
  const stage = PIPELINE_STAGES.find(s=>s.id===c.stage);
  const openRate = c.emails.length ? Math.round(c.emails.filter(e=>e.opened).length/c.emails.length*100) : 0;

  const addTask = () => {
    if (!taskText.trim()) return;
    setCustomers(prev=>prev.map(x=>x.id===c.id?{...x,tasks:[...x.tasks,{id:Date.now(),text:taskText,done:false}]}:x));
    setTaskText("");
  };

  const TABS = [["overview","Übersicht"],["emails",`Emails (${c.emails.length})`],["classes",`Klassen (${c.classHistory.length})`],["purchases","Käufe"],["tasks","Aufgaben"],["actions","Aktionen"]];

  return (
    <div style={{ width:380, background:"#fff", borderLeft:"1px solid #e5e7eb", display:"flex", flexDirection:"column", flexShrink:0, height:"100%" }}>
      <div style={{ padding:"16px 18px 12px", borderBottom:"1px solid #f3f4f6" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <Avatar initials={c.avatar} size={44} color={stage?.color||"#777"} />
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>{c.name}</div>
              <div style={{ fontSize:11, color:"#9ca3af" }}>{c.email}</div>
              <div style={{ fontSize:11, color:"#9ca3af" }}>{c.phone||"Kein Telefon"}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"#f3f4f6", border:"none", cursor:"pointer", borderRadius:6, width:28, height:28, color:"#6b7280", fontSize:14 }}>✕</button>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          <Tag label={stage?.label||c.stage} color={stage?.color||"#777"} />
          <Tag label={c.mbStatus} color={MB_STATUSES[c.mbStatus]||"#777"} />
          {c.smsCapable&&isValidMobile(c.phone)&&<Tag label="💬 SMS" color="#0891b2"/>}
          {c.source==="CLASSPASS"&&<Tag label="ClassPass" color="#d97706"/>}
          {c.tags.map((t,i)=><Tag key={i} label={t} color="#6b7280"/>)}
        </div>
      </div>

      <div style={{ display:"flex", borderBottom:"1px solid #f3f4f6", overflowX:"auto", flexShrink:0 }}>
        {TABS.map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ background:"none", border:"none", cursor:"pointer", padding:"9px 12px", fontSize:11, fontWeight:600, color:tab===id?"#7c3aed":"#6b7280", borderBottom:tab===id?"2px solid #7c3aed":"2px solid transparent", whiteSpace:"nowrap" }}>{label}</button>
        ))}
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:16 }}>
        {tab==="overview"&&(
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
              {[["Besuche",c.visits,"#7c3aed"],["LTV",`${c.ltv}€`,"#059669"],["Lead Score",c.leadScore,"#d97706"],["Credits",c.remainingCredits,"#0891b2"]].map(([l,v,col])=>(
                <div key={l} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5, marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:16, fontWeight:700, color:col }}>{String(v)}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:8 }}>Mindbody Details</div>
            {[["Mitgliedschaft",c.membership||"Keine"],["Paket",c.classPackage],["Zahlung",c.paymentMethod],["Vertragsende",c.contractEnd||"—"],["Letzter Besuch",c.lastVisit||"—"],["Letzte Zahlung",c.lastPayment||"—"],["Geburtstag",c.birthDate||"—"],["Quelle",c.source],["Mitglied seit",c.joinDate||"—"],["Trainer",c.trainer||"—"]].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #f9fafb" }}>
                <span style={{ fontSize:12, color:"#9ca3af" }}>{k}</span>
                <span style={{ fontSize:12, color:"#374151", fontWeight:500 }}>{String(v||"—")}</span>
              </div>
            ))}
            <div style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"14px 0 8px" }}>Notizen</div>
            <textarea value={note} onChange={e=>setNote(e.target.value)} style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:8, padding:"9px 12px", fontSize:12, resize:"none", height:80, outline:"none", fontFamily:"inherit", boxSizing:"border-box", color:"#374151" }} />
            <Btn variant="primary" size="sm">Notiz speichern</Btn>
          </>
        )}

        {tab==="emails"&&(
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(80px,1fr))", gap:8, marginBottom:14, textAlign:"center" }}>
              {[["Gesendet",c.emails.length,"#7c3aed"],["Geöffnet",c.emails.filter(e=>e.opened).length,"#059669"],["Geklickt",c.emails.filter(e=>e.clicked).length,"#6366f1"],["Rate",`${openRate}%`,"#d97706"]].map(([l,v,col])=>(
                <div key={l} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 6px", border:"1px solid #e5e7eb" }}>
                  <div style={{ fontSize:16, fontWeight:800, color:col }}>{String(v)}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>{l}</div>
                </div>
              ))}
            </div>
            {c.emails.length===0
              ? <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:20 }}>Noch keine E-Mails</div>
              : c.emails.map((e,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"#f9fafb", borderRadius:8, marginBottom:6, border:"1px solid #f3f4f6" }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:e.opened?"#059669":"#d1d5db", flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{e.type}</div>
                    <div style={{ fontSize:10, color:"#9ca3af" }}>Gesendet: {e.sent}</div>
                  </div>
                  <div style={{ display:"flex", gap:4 }}>
                    {e.opened&&<Tag label={`Offen ${e.openedAt||""}`} color="#059669"/>}
                    {e.clicked&&<Tag label="Klick" color="#6366f1"/>}
                    {!e.opened&&<Tag label="Nicht geöffnet" color="#9ca3af"/>}
                  </div>
                </div>
              ))
            }
          </>
        )}

        {tab==="classes"&&(
          c.classHistory.length===0
            ? <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:20 }}>Keine Klassen</div>
            : c.classHistory.map((cl,i)=>(
              <div key={i} style={{ padding:"10px 12px", background:"#f9fafb", borderRadius:8, marginBottom:6, border:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:cl.noShow?"#dc2626":"#059669", flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{String(cl.class||cl.name||"")}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>{String(cl.date||"")} · {String(cl.trainer||"")}</div>
                </div>
                {cl.noShow?<Tag label="No-Show ⚠" color="#dc2626"/>:<Tag label="Anwesend" color="#059669"/>}
              </div>
            ))
        )}

        {tab==="purchases"&&(
          <>
            {c.purchases.length===0
              ? <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:20 }}>Keine Käufe</div>
              : c.purchases.map((p,i)=>(
                <div key={i} style={{ padding:"10px 12px", background:"#f9fafb", borderRadius:8, marginBottom:6, border:"1px solid #f3f4f6", display:"flex", alignItems:"center" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{String(p.item||"")}</div>
                    <div style={{ fontSize:10, color:"#9ca3af" }}>{String(p.date||"")} · {String(p.method||"")}</div>
                  </div>
                  <span style={{ fontSize:14, fontWeight:700, color:"#059669" }}>{p.amount}€</span>
                </div>
              ))
            }
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px", marginTop:10 }}>
              <div style={{ fontSize:11, color:"#166534" }}>Gesamt LTV</div>
              <div style={{ fontSize:22, fontWeight:800, color:"#059669" }}>{c.ltv}€</div>
            </div>
            {}
            <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:14, marginTop:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#7c3aed", marginBottom:10 }}>🗺️ Journey starten</div>

              {}
              <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, textTransform:"uppercase", letterSpacing:0.8, marginBottom:6 }}>Fall 1 — Event</div>
              <div style={{ display:"flex", gap:6, marginBottom:12 }}>
                {[
                  { label:"Vor Event (Step 1)", step:1, desc:"Bestätigung" },
                  { label:"Nach Event (Step 3)", step:3, desc:"Free Class" },
                  { label:"Angebot (Step 6)", step:6, desc:"20% Rabatt" },
                ].map(({label, step}) => (
                  <button key={step} onClick={async () => {
                    const r = await fetch(`${API_URL}/journeys/start`, {
                      method:"POST", headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({ client_id:c.id, first_name:c.name.split(" ")[0], email:c.email, phone:c.phone||"", journey:"fall1", step })
                    });
                    const d = await r.json();
                    if(r.ok) notify(`✅ Fall 1 Step ${step} gestartet für ${c.name.split(" ")[0]}!`);
                    else notify(`❌ Fehler: ${d.detail}`);
                  }} style={{ flex:1, padding:"6px 4px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", lineHeight:1.3, textAlign:"center" }}>
                    {label}
                  </button>
                ))}
              </div>

              {}
              <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, textTransform:"uppercase", letterSpacing:0.8, marginBottom:6 }}>Fall 2 — Mindbody</div>
              <div style={{ display:"flex", gap:6 }}>
                {[
                  { label:"Willkommen (Step 1)", step:1, desc:"Neu registriert" },
                  { label:"Vorbereitung (Step 2)", step:2, desc:"Klasse gebucht" },
                  { label:"ROOTS Angebot (Step 4)", step:4, desc:"Nach 1. Klasse" },
                ].map(({label, step}) => (
                  <button key={step} onClick={async () => {
                    const r = await fetch(`${API_URL}/journeys/start`, {
                      method:"POST", headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({ client_id:c.id, first_name:c.name.split(" ")[0], email:c.email, phone:c.phone||"", journey:"fall2", step })
                    });
                    const d = await r.json();
                    if(r.ok) notify(`✅ Fall 2 Step ${step} gestartet für ${c.name.split(" ")[0]}!`);
                    else notify(`❌ Fehler: ${d.detail}`);
                  }} style={{ flex:1, padding:"6px 4px", background:"#9900cc", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", lineHeight:1.3, textAlign:"center" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {tab==="tasks"&&(
          <>
            <div style={{ display:"flex", gap:8, marginBottom:14 }}>
              <input value={taskText} onChange={e=>setTaskText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTask()} placeholder="Neue Aufgabe..." style={{ flex:1, border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:13, outline:"none", color:"#374151" }} />
              <Btn variant="primary" size="sm" onClick={addTask}>+</Btn>
            </div>
            {c.tasks.length===0
              ? <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:20 }}>Keine Aufgaben</div>
              : c.tasks.map(t=>(
                <div key={t.id} onClick={()=>setCustomers(prev=>prev.map(x=>x.id===c.id?{...x,tasks:x.tasks.map(tk=>tk.id===t.id?{...tk,done:!tk.done}:tk)}:x))}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background:"#f9fafb", borderRadius:8, marginBottom:6, cursor:"pointer", border:"1px solid #f3f4f6" }}>
                  <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${t.done?"#059669":"#d1d5db"}`, background:t.done?"#059669":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {t.done&&<span style={{ color:"#fff", fontSize:10 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:13, color:t.done?"#9ca3af":"#374151", textDecoration:t.done?"line-through":"none" }}>{String(t.text||"")}</span>
                </div>
              ))
            }
          </>
        )}

        {tab==="actions"&&(
          <>
            <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:8 }}>E-Mail senden</div>
            {["Email 1 – First Session","Email 2 – Follow Up","Email 3 – Free Trial","Milestone 5","Milestone 10","Milestone 25","Reaktivierung"].map(a=>(
              <button key={a} style={{ display:"block", width:"100%", background:"#f9fafb", border:"1px solid #e5e7eb", color:"#374151", borderRadius:8, padding:"9px 14px", cursor:"pointer", fontSize:12, textAlign:"left", marginBottom:6, fontFamily:"inherit" }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="#7c3aed50";e.currentTarget.style.color="#7c3aed";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.color="#374151";}}>
                ✉ {a}
              </button>
            ))}
            {isValidMobile(c.phone)&&(
              <>
                <div style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"14px 0 8px" }}>SMS senden</div>
                {SMS_TEMPLATES.map(t=>(
                  <button key={t.id} style={{ display:"block", width:"100%", background:"#f0f9ff", border:"1px solid #bae6fd", color:"#0891b2", borderRadius:8, padding:"9px 14px", cursor:"pointer", fontSize:12, textAlign:"left", marginBottom:6, fontFamily:"inherit" }}>
                    💬 {t.name}
                  </button>
                ))}
              </>
            )}
            <div style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"14px 0 8px" }}>Pipeline verschieben</div>
            {PIPELINE_STAGES.map(s=>(
              <button key={s.id} style={{ display:"block", width:"100%", background:"transparent", border:`1px solid ${s.color}30`, color:s.color, borderRadius:8, padding:"8px 14px", cursor:"pointer", fontSize:12, textAlign:"left", marginBottom:6, fontFamily:"inherit", fontWeight:600 }}>
                → Zu {s.label}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const PipelineView = ({ customers, setCustomers, notify }) => {
  const [dragging, setDragging] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const filtered = useMemo(()=>customers.filter(c=>
    c.name.toLowerCase().includes(search.toLowerCase())||
    c.email.toLowerCase().includes(search.toLowerCase())
  ),[customers,search]);

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      <div style={{ padding:"12px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", gap:10, alignItems:"center" }}>
        <input placeholder="Pipeline durchsuchen..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 14px", fontSize:13, outline:"none", width:220, color:"#374151" }} />
        <span style={{ fontSize:12, color:"#9ca3af" }}>{filtered.length} Kunden</span>
      </div>
      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
        <div style={{ flex:1, padding:"16px", display:"flex", gap:12, overflow:"auto" }}>
          {PIPELINE_STAGES.map(stage=>{
            const cols = filtered.filter(c=>c.stage===stage.id);
            return (
              <div key={stage.id}
                onDrop={e=>{
                  e.preventDefault();
                  if(!dragging||dragging.stage===stage.id) return;
                  const prevStage = dragging.stage;
                  setCustomers(p=>p.map(c=>c.id===dragging.id?{...c,stage:stage.id}:c));
                  notify(`${dragging.name} → ${stage.label}`);
                  fetch(`${API_URL}/pipeline/update`, {
                    method:"POST",
                    headers:{"Content-Type":"application/json"},
                    body: JSON.stringify({ client_id: dragging.id, stage: stage.id, note: "" })
                  }).catch(() => {
                    setCustomers(p=>p.map(c=>c.id===dragging.id?{...c,stage:prevStage}:c));
                    notify("❌ Speichern fehlgeschlagen");
                  });
                  setDragging(null);
                }}
                onDragOver={e=>e.preventDefault()}
                style={{ flex:1, minWidth:200, background:"#f9fafb", borderRadius:12, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                <div style={{ padding:"12px 14px 10px", background:"#fff", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:stage.color }} />
                  <span style={{ fontSize:11, fontWeight:700, color:"#374151", textTransform:"uppercase", letterSpacing:0.8, flex:1 }}>{stage.label}</span>
                  <span style={{ background:"#f3f4f6", color:"#6b7280", fontSize:11, fontWeight:600, padding:"1px 8px", borderRadius:20 }}>{cols.length}</span>
                </div>
                <div style={{ padding:"8px", maxHeight:"calc(100vh - 250px)", overflowY:"auto" }}>
                  {cols.length===0
                    ? <div style={{ textAlign:"center", color:"#d1d5db", fontSize:12, padding:"20px 0" }}>Leer</div>
                    : cols.map(c=><PipelineCard key={c.id} c={c} onDragStart={(e,c)=>setDragging(c)} onClick={c=>setSelected(selected?.id===c.id?null:c)} selected={selected?.id===c.id}/>)
                  }
                </div>
              </div>
            );
          })}
        </div>
        {selected&&<CustomerPanel c={selected} onClose={()=>setSelected(null)} setCustomers={setCustomers}/>}
      </div>
    </div>
  );
};
const CustomerDB = ({ customers, setCustomers, sourceFilter, onClearSourceFilter }) => {
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [smsFilter, setSmsFilter] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sortBy, setSortBy] = useState("name");

  const filtered = useMemo(()=>{
    let r = customers;
    if (search) r = r.filter(c=>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.includes(search)
    );
    if (stageFilter==="inactive_mb") r = r.filter(c=>c.mbStatus==="Inactive"||c.mbStatus==="Expired");
    else if (stageFilter==="inactive_60") { const now=new Date(); r = r.filter(c=>c.visits>0&&c.lastVisit&&Math.floor((now-new Date(c.lastVisit))/(1000*60*60*24))>=60); }
    else if (stageFilter!=="all") r = r.filter(c=>c.stage===stageFilter);
    if (smsFilter) r = r.filter(c=>isValidMobile(c.phone));
    if (sourceFilter) r = r.filter(c=>c.source===sourceFilter);
    if (sortBy==="ltv")      r = [...r].sort((a,b)=>b.ltv-a.ltv);
    if (sortBy==="visits")   r = [...r].sort((a,b)=>b.visits-a.visits);
    if (sortBy==="score")    r = [...r].sort((a,b)=>b.leadScore-a.leadScore);
    if (sortBy==="lastVisit") r = [...r].sort((a,b)=>new Date(b.lastVisit||0)-new Date(a.lastVisit||0));
    if (sortBy==="name")     r = [...r].sort((a,b)=>a.name.localeCompare(b.name));
    return r;
  },[customers,search,stageFilter,smsFilter,sortBy,sourceFilter]);

  return (
    <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
          <input placeholder="Kunden suchen..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 14px", fontSize:13, outline:"none", width:200, color:"#374151" }} />
          {sourceFilter && (
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"#f3e8ff", border:"1px solid #9900cc", borderRadius:8, padding:"6px 12px" }}>
              <span style={{ fontSize:12, color:"#9900cc", fontWeight:700 }}>Quelle: {sourceFilter}</span>
              <button onClick={onClearSourceFilter} style={{ background:"none", border:"none", cursor:"pointer", color:"#9900cc", fontSize:14, fontWeight:700, lineHeight:1 }}>×</button>
            </div>
          )}
          <select value={stageFilter} onChange={e=>setStageFilter(e.target.value)} style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 12px", fontSize:13, color:"#374151", outline:"none" }}>
            <option value="all">Alle Stufen</option>
            <option value="lead">Lead</option>
            <option value="first_timer">First Timer</option>
            <option value="returning">Wiederkommer</option>
            <option value="member">Member</option>
            <option value="vip">VIP</option>
            <option value="inactive_mb" disabled style={{color:"#9ca3af"}}>── Inaktiv ──</option>
            <option value="inactive_mb">Inaktiv (MB Status)</option>
            <option value="inactive_60">Inaktiv (60+ Tage)</option>
            <option value="duplicate">Duplikat</option>
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 12px", fontSize:13, color:"#374151", outline:"none" }}>
            <option value="name">Name A-Z</option>
            <option value="visits">Besuche ↓</option>
            <option value="ltv">LTV ↓</option>
            <option value="score">Lead Score ↓</option>
            <option value="lastVisit">Letzter Besuch ↓</option>
          </select>
          <select onChange={e=>{ if(e.target.value) {  } }}
            style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 12px", fontSize:13, color:"#374151", outline:"none" }}
            value={sourceFilter||"all"}
            onChange={e => { const v=e.target.value; v==="all" ? onClearSourceFilter() : window.dispatchEvent(new CustomEvent("setSourceFilter",{detail:v})); }}>
            <option value="all">Alle Quellen</option>
            <option value="Direct">Direct</option>
            <option value="CLASSPASS">ClassPass</option>
            <option value="EGYM">EGYM / Wellpass</option>
            <option value="WELLHUB">Wellhub</option>
          </select>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#374151", cursor:"pointer" }}>
            <input type="checkbox" checked={smsFilter} onChange={e=>setSmsFilter(e.target.checked)} /> Nur SMS-fähig
          </label>
          <span style={{ marginLeft:"auto", fontSize:12, color:"#9ca3af" }}>{filtered.length} Kunden</span>
        </div>
        <div style={{ flex:1, overflowY:"auto", overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
            <thead>
              <tr style={{ background:"#f9fafb", borderBottom:"1px solid #e5e7eb" }}>
                {["Name","E-Mail","Telefon","Stage","MB Status","Quelle","Paket","Besuche","LTV","Score","Credits","Letzter Besuch"].map(h=>(
                  <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c,i)=>{
                const stage = PIPELINE_STAGES.find(s=>s.id===c.stage);
                const openRate = c.emails.length ? Math.round(c.emails.filter(e=>e.opened).length/c.emails.length*100) : null;
                return (
                  <tr key={c.id} onClick={()=>setSelected(selected?.id===c.id?null:c)}
                    style={{ borderBottom:"1px solid #f3f4f6", background:selected?.id===c.id?"#f5f3ff":i%2===0?"#fff":"#fafafa", cursor:"pointer" }}>
                    <td style={{ padding:"9px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <Avatar initials={c.avatar} size={26} color={stage?.color||"#777"} />
                        <span style={{ fontSize:13, fontWeight:500, color:"#111827" }}>{c.name}</span>
                      </div>
                    </td>
                    <td style={{ padding:"9px 12px", fontSize:12, color:"#6b7280" }}>{c.email||"—"}</td>
                    <td style={{ padding:"9px 12px", fontSize:12, color:"#6b7280" }}>{c.phone||"—"}</td>
                    <td style={{ padding:"9px 12px" }}><Tag label={stage?.label||"—"} color={stage?.color||"#777"}/></td>
                    <td style={{ padding:"9px 12px" }}><Tag label={c.mbStatus} color={MB_STATUSES[c.mbStatus]||"#777"}/></td>
                    <td style={{ padding:"9px 12px", fontSize:12, color:"#6b7280" }}>{c.source}</td>
                    <td style={{ padding:"9px 12px", fontSize:12, color:"#6b7280" }}>{c.classPackage}</td>
                    <td style={{ padding:"9px 12px", fontSize:13, fontWeight:600, color:"#374151" }}>{c.visits}</td>
                    <td style={{ padding:"9px 12px", fontSize:13, fontWeight:700, color:"#059669" }}>{c.ltv}€</td>
                    <td style={{ padding:"9px 12px" }}><Score value={c.leadScore}/></td>
                    <td style={{ padding:"9px 12px" }}>{c.smsCapable&&c.phone?<Tag label="✓ SMS" color="#0891b2"/>:<span style={{ color:"#d1d5db" }}>—</span>}</td>
                    <td style={{ padding:"9px 12px", fontSize:12, color:"#6b7280" }}>{c.lastVisit||"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {selected&&<CustomerPanel c={selected} onClose={()=>setSelected(null)} setCustomers={setCustomers}/>}
    </div>
  );
};
const EmailEditor = ({ onClose }) => {
  const [prompt, setPrompt] = useState(() => sessionStorage.getItem("email_draft_prompt") || "");
  const [subject, setSubject] = useState(() => sessionStorage.getItem("email_draft_subject") || "");
  const [body, setBody] = useState(() => sessionStorage.getItem("email_draft_body") || "");
  const [loading, setLoading] = useState(false);
  const [abTest, setAbTest] = useState(false);
  const [subjectB, setSubjectB] = useState("");
  const [preview, setPreview] = useState("desktop");
  const [useDesign, setUseDesign] = useState(true);
  useEffect(() => { sessionStorage.setItem("email_draft_prompt", prompt); }, [prompt]);
  useEffect(() => { sessionStorage.setItem("email_draft_subject", subject); }, [subject]);
  useEffect(() => { sessionStorage.setItem("email_draft_body", body); }, [body]); // BURN Design oder Plain Text
  const [testEmail, setTestEmail] = useState("sertac@burnbxng.com");
  const [testSending, setTestSending] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const sendTestEmail = async () => {
    if (!subject || !body || !testEmail) return;
    setTestSending(true);
    setTestSent(false);
    try {
      const r = await fetch(`${API_URL}/debug/test-email`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({ to: testEmail, subject: `[TEST] ${subject}`, body: useDesign
          ? `<div style="background:#ffffff;font-family:'Helvetica Neue',Arial,sans-serif;padding:32px 36px;color:#111111;font-size:15px;line-height:1.8;">${body}</div>`
          : body })
      });
      setTestSent(r.ok);
    } catch {}
    setTestSending(false);
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/generate-email`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ prompt })
      });
      const parsed = await res.json();
      setSubject(parsed.subject||"");
      setBody(parsed.body||"");
    } catch(e) { setSubject("Fehler"); setBody("Bitte erneut versuchen."); }
    setLoading(false);
  };

  const saveAsTemplate = async ({ triggerType="", triggerValue="", operator=">=" } = {}) => {
    if (!subject || !body) return;
    sessionStorage.removeItem("email_draft_prompt");
    sessionStorage.removeItem("email_draft_subject");
    sessionStorage.removeItem("email_draft_body");
    const finalBody = useDesign
      ? `<div style="background:#ffffff;font-family:'Helvetica Neue',Arial,sans-serif;padding:32px 36px;color:#111111;font-size:15px;line-height:1.8;">${body}</div>`
      : body;
    const res = await fetch(`${API_URL}/templates`, {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ subject, body: finalBody, trigger_type: triggerType, trigger_value: triggerValue, operator, active: true })
    });
    const data = await res.json();
    if (data.id) { alert(`✅ Template gespeichert! ID: ${data.id}`); onClose(); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000050", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:16, width:720, maxWidth:"calc(100vw - 32px)", maxHeight:"92vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px #00000030", overflow:"hidden" }}>
        <div style={{ padding:"18px 24px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>E-Mail erstellen</div>
          <button onClick={onClose} style={{ background:"#f3f4f6", border:"none", cursor:"pointer", borderRadius:6, width:30, height:30, color:"#6b7280", fontSize:14 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:24 }}>
          <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:12, padding:18, marginBottom:20 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#7c3aed", marginBottom:4 }}>✨ Claude AI Generator</div>
            <textarea value={prompt} onChange={e=>setPrompt(e.target.value)} placeholder="z.B. 'Follow-up für Kunden die nach erstem Besuch nicht zurückgekommen sind...'"
              style={{ width:"100%", border:"1px solid #ddd6fe", borderRadius:8, padding:"10px 12px", fontSize:13, resize:"none", height:70, outline:"none", fontFamily:"inherit", boxSizing:"border-box", background:"#fff" }} />
            <div style={{ marginTop:10 }}><Btn variant="primary" onClick={generate}>{loading?"Generiere...":"✨ Mit Claude generieren"}</Btn></div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#374151", cursor:"pointer" }}>
              <input type="checkbox" checked={abTest} onChange={e=>setAbTest(e.target.checked)} /> A/B Test
            </label>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:12, color:"#6b7280" }}>Format:</span>
              {[["design","🎨 BURN Design"],["plain","📄 Plain Text"]].map(([val, label]) => (
                <button key={val} onClick={() => setUseDesign(val === "design")}
                  style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:600, cursor:"pointer", border:"none",
                    background: (val === "design") === useDesign ? "#9900cc" : "#f3f4f6",
                    color: (val === "design") === useDesign ? "#fff" : "#374151" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>Betreff {abTest?"(A)":""}</label>
            <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Betreff..."
              style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:8, padding:"9px 12px", fontSize:13, outline:"none", boxSizing:"border-box", color:"#374151" }} />
          </div>
          {abTest&&(
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>Betreff (B)</label>
              <input value={subjectB} onChange={e=>setSubjectB(e.target.value)} placeholder="Alternative Betreffzeile..."
                style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:8, padding:"9px 12px", fontSize:13, outline:"none", boxSizing:"border-box", color:"#374151" }} />
            </div>
          )}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>E-Mail Text</label>
            <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="E-Mail Text..."
              style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 12px", fontSize:13, resize:"none", height:200, outline:"none", fontFamily:"inherit", boxSizing:"border-box", lineHeight:1.6, color:"#374151" }} />
          </div>
          {body&&(
            <div>
              <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center" }}>
                <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Vorschau</span>
                {["desktop","mobile"].map(p=>(
                  <button key={p} onClick={()=>setPreview(p)} style={{ padding:"4px 10px", border:`1px solid ${preview===p?"#7c3aed":"#e5e7eb"}`, borderRadius:6, background:preview===p?"#f5f3ff":"transparent", color:preview===p?"#7c3aed":"#6b7280", fontSize:12, cursor:"pointer" }}>
                    {p==="desktop"?"🖥 Desktop":"📱 Mobile"}
                  </button>
                ))}
              </div>
              <div style={{ border:"1px solid #e5e7eb", borderRadius:10, overflow:"hidden", maxWidth:preview==="mobile"?360:"100%" }}>
                {useDesign ? (
                  <iframe
                    srcDoc={`<html><head><meta charset="UTF-8"/><meta name="color-scheme" content="light"/></head><body style="margin:0;padding:20px;background:#fff;font-family:Helvetica Neue,Arial,sans-serif;font-size:13px;color:#111;line-height:1.7;">${body.replace("{{name}}","Sarah")}</body></html>`}
                    style={{ width:"100%", minHeight:200, border:"none" }}
                    title="Preview"
                  />
                ) : (
                  <div style={{ padding:16, background:"#fafafa" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:6 }}>Betreff: {subject}</div>
                    <div style={{ fontSize:13, color:"#374151", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{body.replace("{{name}}","Sarah")}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {body && (
          <div style={{ padding:"12px 24px", borderTop:"1px solid #e5e7eb", background:"#f0fdf4" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#15803d", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Test senden</div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="test@email.de"
                style={{ flex:1, padding:"6px 10px", border:"1px solid #86efac", borderRadius:8, fontSize:12, outline:"none" }} />
              <button onClick={sendTestEmail} disabled={testSending}
                style={{ padding:"7px 16px", background:testSent?"#059669":"#16a34a", color:"#fff", border:"none",
                  borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                {testSending ? "Sende..." : testSent ? "✅ Gesendet!" : "📨 Test senden"}
              </button>
            </div>
          </div>
        )}
        {body && (
          <div style={{ padding:"12px 24px", borderTop:"1px solid #f3f4f6", background:"#f9fafb" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Als Template speichern</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <select id="tt" style={{ padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, color:"#374151" }}>
                <option value="">Kein Trigger</option>
                <option value="visit_count">Anzahl Besuche</option>
                <option value="source">Quelle</option>
                <option value="stage">Pipeline Stage</option>
              </select>
              <select id="top" style={{ padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, color:"#374151" }}>
                <option value=">=">≥ (ab)</option>
                <option value="==">= (genau)</option>
                <option value=">"> > (über)</option>
              </select>
              <input id="tv" placeholder="Wert (z.B. 50)" style={{ width:120, padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12 }} />
              <button onClick={() => saveAsTemplate({
                triggerType: document.getElementById("tt").value,
                operator:    document.getElementById("top").value,
                triggerValue: document.getElementById("tv").value,
              })} style={{ padding:"7px 16px", background:"#9900cc", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                💾 Speichern
              </button>
            </div>
          </div>
        )}
        <div style={{ padding:"14px 24px", borderTop:"1px solid #e5e7eb", display:"flex", gap:10, justifyContent:"flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Schließen</Btn>
        </div>
      </div>
    </div>
  );
};

const EmailView = ({ customers }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [emailStats, setEmailStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/email/stats`)
      .then(r => r.json())
      .then(setEmailStats)
      .catch(() => {});
  }, []);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/templates`)
      .then(r => r.json())
      .then(data => { setCustomTemplates(data.templates || []); setLoadingTemplates(false); })
      .catch(() => setLoadingTemplates(false));
  }, [showEditor]); // Reload after editor closes

  const deleteTemplate = async (id) => {
    if (!confirm("Template wirklich löschen?")) return;
    await fetch(`${API_URL}/templates/${id}`, { method:"DELETE" });
    setCustomTemplates(p => p.filter(t => t.id !== id));
  };

  const sendBroadcast = async (id) => {
    if (!confirm("Template an ALLE Kunden senden? (Duplicate Prevention aktiv)")) return;
    setSending(id);
    const res = await fetch(`${API_URL}/templates/${id}/send`, {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ template_id: id })
    });
    const data = await res.json();
    setSending(null);
    alert(`✅ Gesendet: ${data.sent} · Übersprungen: ${data.skipped}`);
  };

  const triggerLabel = (tmpl) => {
    if (!tmpl.trigger_type) return "Kein Trigger (manuell)";
    const ops = { ">=":"≥", "==":"=", ">":">", "<":"<", "<=":"≤" };
    return `${tmpl.trigger_type} ${ops[tmpl.operator]||tmpl.operator} ${tmpl.trigger_value}`;
  };
  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {showEditor&&<EmailEditor onClose={()=>setShowEditor(false)}/>}
      <div style={{ padding:"16px 24px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>E-Mail Templates</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>{customTemplates.length} Templates</div>
        </div>
        <div style={{ flex:1 }} />
        <Btn variant="primary" onClick={()=>setShowEditor(true)}>+ Neues Template</Btn>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:24 }}>
        <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:12, padding:20, marginBottom:24 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#7c3aed", marginBottom:10 }}>✨ Schnell eine E-Mail mit Claude erstellen</div>
          <div style={{ display:"flex", gap:10 }}>
            <input placeholder="Beschreibe die E-Mail..." style={{ flex:1, border:"1px solid #ddd6fe", borderRadius:8, padding:"9px 14px", fontSize:13, outline:"none", color:"#374151" }} />
            <Btn variant="primary" onClick={()=>setShowEditor(true)}>Generieren</Btn>
          </div>
        </div>
        {emailStats ? (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
            {[
              ["Emails gesendet", emailStats.total, "#7c3aed"],
              ["Geöffnet", `${emailStats.open_rate}%`, "#059669"],
              ["Geklickt", `${emailStats.click_rate}%`, "#0891b2"],
              ["Custom Templates", customTemplates.length, "#9900cc"],
            ].map(([l,v,c]) => (
              <div key={l} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:22, fontWeight:800, color:c }}>{String(v)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
            {["Emails gesendet","Geöffnet","Geklickt","Custom Templates"].map(l => (
              <div key={l} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:22, fontWeight:800, color:"#e5e7eb" }}>—</div>
              </div>
            ))}
          </div>
        )}

        {}
        {emailStats?.by_type && Object.keys(emailStats.by_type).length > 0 && (
          <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:12, padding:16, marginBottom:24 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", marginBottom:12, textTransform:"uppercase", letterSpacing:0.8 }}>
              📊 Tracking per E-Mail Typ
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {Object.entries(emailStats.by_type).map(([type, data]) => (
                <div key={type} style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:140, fontSize:12, fontWeight:600, color:"#374151" }}>{type}</div>
                  <div style={{ flex:1, background:"#e5e7eb", borderRadius:99, height:8 }}>
                    <div style={{ height:8, borderRadius:99, background:"#059669", width:`${data.sent > 0 ? Math.round(data.opened/data.sent*100) : 0}%` }} />
                  </div>
                  <div style={{ fontSize:11, color:"#6b7280", width:80, textAlign:"right" }}>
                    {data.opened}/{data.sent} geöffnet ({data.sent > 0 ? Math.round(data.opened/data.sent*100) : 0}%)
                  </div>
                  <div style={{ fontSize:11, color:"#0891b2", width:70, textAlign:"right" }}>
                    {data.clicked} klicks
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {}
        {customTemplates.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#9900cc", textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>
              🎯 Custom Templates (Layer 2) — {customTemplates.length} Templates
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {customTemplates.map(t => (
                <div key={t.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16,
                  borderLeft: `4px solid ${t.active ? "#9900cc" : "#e5e7eb"}` }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:2 }}>{t.subject}</div>
                      <div style={{ fontSize:11, color:"#6b7280", marginBottom:8 }}>
                        <span style={{ background:"#f3f4f6", padding:"2px 8px", borderRadius:99, marginRight:6 }}>ID: {t.id}</span>
                        <span style={{ background: t.active ? "#dcfce7" : "#fee2e2", color: t.active ? "#15803d" : "#dc2626",
                          padding:"2px 8px", borderRadius:99, marginRight:6 }}>
                          {t.active ? "Aktiv" : "Inaktiv"}
                        </span>
                        {t.trigger_type && (
                          <span style={{ background:"#f5f3ff", color:"#7c3aed", padding:"2px 8px", borderRadius:99 }}>
                            ⚡ {triggerLabel(t)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:12, color:"#9ca3af" }}>Erstellt: {t.created_at?.slice(0,10)}</div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button onClick={() => sendBroadcast(t.id)} disabled={sending === t.id}
                        style={{ padding:"5px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                          border:"1px solid #9900cc", background:"#fff", color:"#9900cc", cursor:"pointer" }}>
                        {sending === t.id ? "..." : "📨 Senden"}
                      </button>
                      <button onClick={() => deleteTemplate(t.id)}
                        style={{ padding:"5px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                          border:"1px solid #fca5a5", background:"#fef2f2", color:"#dc2626", cursor:"pointer" }}>
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {loadingTemplates && <div style={{ fontSize:12, color:"#9ca3af", marginBottom:16 }}>Lade Custom Templates...</div>}

        {}
        <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>
          🔒 System Templates (Layer 1 — unveränderlich)
        </div>
        {}
        <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>
          🔒 System Templates (Layer 1)
        </div>
        {[
          { name:"Email 1 — First Session", subject:"Hey {{name}}, how was your first BURN session?", category:"Journey", color:"#9900cc",
            count: (customers||[]).filter(c=>c.emails?.some(e=>e.type==="Email 1")).length },
          { name:"Email 2 — Follow Up", subject:"Hey {{name}}, we miss you!", category:"Journey", color:"#7c3aed",
            count: (customers||[]).filter(c=>c.emails?.some(e=>e.type==="Email 2")).length },
          { name:"Email 3 — Last Chance", subject:"Hey {{name}}, free class on us!", category:"Journey", color:"#6366f1",
            count: (customers||[]).filter(c=>c.emails?.some(e=>e.type==="Email 3")).length },
          { name:"Milestone 5 Sessions", subject:"Great start!", category:"Milestone", color:"#d97706", count: null },
          { name:"Milestone 10 Sessions", subject:"You're on fire!", category:"Milestone", color:"#d97706", count: null },
          { name:"Milestone 25 Sessions", subject:"Legend status!", category:"Milestone", color:"#d97706", count: null },
          { name:"Milestone 50 Sessions", subject:"Elite level!", category:"Milestone", color:"#d97706", count: null },
          { name:"Milestone 100 Sessions", subject:"BURN Royalty!", category:"Milestone", color:"#d97706", count: null },
        ].map(t => (
          <div key={t.name} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginBottom:10, borderLeft:`4px solid ${t.color}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"#111827", marginBottom:2 }}>{t.name}</div>
                <div style={{ fontSize:12, color:"#9ca3af", fontStyle:"italic" }}>{t.subject}</div>
              </div>
              {t.count !== null && (
                <div style={{ textAlign:"center", background:"#f5f3ff", borderRadius:8, padding:"6px 12px" }}>
                  <div style={{ fontSize:16, fontWeight:800, color:t.color }}>{t.count}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>gesendet</div>
                </div>
              )}
              <span style={{ fontSize:11, background:"#f3f4f6", color:"#6b7280", padding:"2px 8px", borderRadius:99 }}>{t.category}</span>
            </div>
          </div>
        ))}

      </div>
    </div>
  );
};
const SMSView = ({ customers }) => {
  const [selected, setSelected] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [search, setSearch] = useState("");
  const [mobilePane, setMobilePane] = useState("list"); // "list" | "compose"
  const isMobile = useMobile();

  const sendSMS = async () => {
    if (!selected.length || !msg) return;
    setSending(true);
    setSent(false);
    let count = 0;
    for (const id of selected) {
      const c = safeC.find(x => x.id === id);
      if (!c || !isValidMobile(c.phone)) continue;
      try {
        const r = await fetch(`${API_URL}/sms/send`, {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            to: c.phone,
            message: msg.replace("{{name}}", c.name.split(" ")[0]),
            client_id: c.id,
          })
        });
        if (r.ok) count++;
      } catch {}
    }
    setSentCount(count);
    setSent(true);
    setSending(false);
  };

  const safeC = (customers || []).map(c => ({
    ...c,
    name: decodeHTML(typeof c.name === "string" && c.name ? c.name : "Unbekannt"),
    phone: typeof c.phone === "string" ? c.phone : "",
    email: typeof c.email === "string" ? c.email : "",
    avatar: typeof c.avatar === "string" ? c.avatar : "?",
    stage: typeof c.stage === "string" ? c.stage : "lead",
  }));

  const smsC = safeC
    .filter(c => c.smsCapable && isValidMobile(c.phone))
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  const toggle = id => setSelected(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const selectStage = stage => setSelected(smsC.filter(c=>c.stage===stage).map(c=>c.id));

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {/* Mobile Tab-Switcher */}
      {isMobile && (
        <div style={{ display:"flex", borderBottom:"2px solid #e5e7eb", background:"#fff", flexShrink:0 }}>
          {[["list","👥 Empfänger"],["compose","✏️ Verfassen"]].map(([id,label])=>(
            <button key={id} onClick={()=>setMobilePane(id)}
              style={{ flex:1, padding:"12px 8px", border:"none", borderBottom:`3px solid ${mobilePane===id?"#0891b2":"transparent"}`,
                background:"transparent", fontSize:13, fontWeight:mobilePane===id?700:400,
                color:mobilePane===id?"#0891b2":"#6b7280", cursor:"pointer" }}>
              {label}{id==="list"?` (${selected.length} ausgewählt)`:""}
            </button>
          ))}
        </div>
      )}
      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
      {/* Left: recipients list — hidden on mobile when compose pane is active */}
      <div style={{ width: isMobile ? "100%" : 340, flexShrink:0, borderRight: isMobile ? "none" : "1px solid #e5e7eb",
        display: isMobile && mobilePane !== "list" ? "none" : "flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e5e7eb" }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:2 }}>SMS-Empfänger</div>
          <div style={{ fontSize:12, color:"#9ca3af", marginBottom:10 }}>{smsC.length} Kunden mit Telefonnummer</div>
          <input placeholder="Suchen..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:8, padding:"7px 12px", fontSize:12, outline:"none", color:"#374151", boxSizing:"border-box", marginBottom:10 }} />
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            <Btn variant="ghost" size="sm" onClick={()=>setSelected(smsC.map(c=>c.id))}>Alle</Btn>
            <Btn variant="ghost" size="sm" onClick={()=>setSelected([])}>Keine</Btn>
            {["first_timer","returning","member","vip"].map(sid=>{
              const st = PIPELINE_STAGES.find(s=>s.id===sid);
              const cnt = smsC.filter(c=>c.stage===sid).length;
              return <Btn key={sid} variant="outline" size="sm" color={st?.color||"#777"} onClick={()=>selectStage(sid)}>{st?.label||sid} ({cnt})</Btn>;
            })}
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"10px" }}>
          {smsC.map(c=>{
            const stage = PIPELINE_STAGES.find(s=>s.id===c.stage);
            const isSelected = selected.includes(c.id);
            return (
              <div key={c.id} onClick={()=>toggle(c.id)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, cursor:"pointer", background:isSelected?"#f0fdf4":"#fff", border:`1px solid ${isSelected?"#059669":"#e5e7eb"}`, marginBottom:6 }}>
                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${isSelected?"#059669":"#d1d5db"}`, background:isSelected?"#059669":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {isSelected&&<span style={{ color:"#fff", fontSize:10 }}>✓</span>}
                </div>
                <Avatar initials={c.avatar} size={28} color={stage?.color||"#777"}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</div>
                  <div style={{ fontSize:11, color:"#9ca3af" }}>{c.phone}</div>
                </div>
                <Tag label={stage?.label||c.stage} color={stage?.color||"#777"}/>
              </div>
            );
          })}
        </div>
        <div style={{ padding:"10px 14px", borderTop:"1px solid #e5e7eb", background:"#f9fafb", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{selected.length} ausgewählt</div>
          {isMobile && (
            <button onClick={()=>setMobilePane("compose")}
              style={{ padding:"8px 16px", background:"#0891b2", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              Weiter ✉️
            </button>
          )}
        </div>
      </div>

      {/* Right: compose panel — hidden on mobile when list pane is active */}
      <div style={{ flex:1, padding:24, display: isMobile && mobilePane !== "compose" ? "none" : "flex", flexDirection:"column", overflowY:"auto" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:4 }}>SMS verfassen</div>
        <div style={{ fontSize:13, color:"#9ca3af", marginBottom:20 }}>{selected.length} Empfänger ausgewählt</div>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:8 }}>Template wählen</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {SMS_TEMPLATES.map(t=>(
              <button key={t.id} onClick={()=>{setTemplateId(t.id);setMsg(t.message);}}
                style={{ padding:"8px 14px", borderRadius:8, border:`1.5px solid ${templateId===t.id?"#0891b2":"#e5e7eb"}`, background:templateId===t.id?"#f0f9ff":"#fff", color:templateId===t.id?"#0891b2":"#374151", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:6 }}>Nachricht</div>
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="SMS schreiben..."
            style={{ width:"100%", height:130, border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 14px", fontSize:13, resize:"none", outline:"none", fontFamily:"inherit", boxSizing:"border-box", lineHeight:1.6, color:"#374151" }} />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
            <span style={{ fontSize:11, color:msg.length>160?"#dc2626":"#9ca3af" }}>{msg.length} / 160</span>
            <span style={{ fontSize:11, color:"#9ca3af" }}>~{(selected.length*0.08).toFixed(2)}€ geschätzt</span>
          </div>
        </div>
        {msg&&(
          <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:"#0891b2", marginBottom:6 }}>📱 Vorschau</div>
            <div style={{ background:"#0891b2", color:"#fff", borderRadius:"16px 16px 16px 4px", padding:"10px 14px", fontSize:13, lineHeight:1.6, display:"inline-block", maxWidth:"80%" }}>
              {msg.replace("{{name}}","Sarah")}
            </div>
          </div>
        )}
        {sent&&<div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:14, marginBottom:16, fontSize:13, color:"#166534", fontWeight:500 }}>✓ SMS an {sentCount} von {selected.length} Kunden gesendet!</div>}
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>{setSent(false);setSelected([]);setMsg("");}}>Zurücksetzen</Btn>
          <Btn variant="primary" color="#0891b2" onClick={sendSMS} disabled={sending||!selected.length||!msg}>
            {sending ? "Sende..." : `💬 SMS an ${selected.length} Kunden senden`}
          </Btn>
        </div>
      </div>
      </div>{/* end inner flex row */}
    </div>
  );
};
const AutomationsView = ({ customers }) => {
  const [stats, setStats] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [newTmpl, setNewTmpl] = useState({ subject:"", body:"", trigger_type:"days_since_last_visit", trigger_value:"30", operator:">=", active:true });
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    fetch(`${API_URL}/automations/stats`).then(r=>r.json()).then(setStats).catch(()=>{});
    fetch(`${API_URL}/journeys`).then(r=>r.json()).then(d=>setJourneys(d.journeys||[])).catch(()=>{});
    fetch(`${API_URL}/templates`).then(r=>r.json()).then(d=>setCustomTemplates(d.templates||[])).catch(()=>{});
  };

  useEffect(() => { loadData(); }, []);

  const toggleTemplate = async (id, active) => {
    await fetch(`${API_URL}/templates/${id}`, {
      method: "PATCH",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify({ active: !active })
    });
    setCustomTemplates(p => p.map(t => t.id === id ? {...t, active: !active} : t));
  };

  const deleteTemplate = async (id) => {
    if (!confirm("Template wirklich löschen?")) return;
    await fetch(`${API_URL}/templates/${id}`, { method: "DELETE" });
    setCustomTemplates(p => p.filter(t => t.id !== id));
  };

  const saveNewTemplate = async () => {
    if (!newTmpl.subject || !newTmpl.body) return alert("Betreff und Text sind Pflichtfelder!");
    setSaving(true);
    await fetch(`${API_URL}/templates`, {
      method: "POST",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify(newTmpl)
    });
    setSaving(false);
    setShowNewForm(false);
    setNewTmpl({ subject:"", body:"", trigger_type:"days_since_last_visit", trigger_value:"30", operator:">=", active:true });
    loadData();
  };

  const TRIGGER_LABELS = {
    days_since_last_visit: "Tage seit letztem Besuch",
    visit_count: "Anzahl Besuche",
    birthday: "🎂 Geburtstag — Tage vorher",
    contract_expiry: "📋 Vertragsende — Tage vorher",
    source: "Quelle",
    stage: "Pipeline Stage",
  };

  const OP_LABELS = { ">=":"≥", "==":"=", ">":">", "<":"<", "<=":"≤" };
  const REAL_AUTOMATIONS = [
    {
      id: "phase1",
      name: "First Timer Email",
      status: "live",
      color: "#9900cc",
      trigger: "Erster Besuch erkannt via Mindbody",
      steps: ["Sofort: Email 1 + Audio (ElevenLabs)", "Fall 2 Journey startet in 3 Tagen"],
      count: customers.filter(c => c.emails?.length > 0).length,
      description: "Phase 1 — läuft täglich 03:00 Uhr"
    },
    {
      id: "phase2",
      name: "Follow-Up Emails",
      status: "live",
      color: "#7c3aed",
      trigger: "5 / 10 Tage nach erster Email — kein zweiter Besuch",
      steps: ["Tag 5: Email 2 — We miss you", "Tag 10: Email 3 — Last chance + Free class"],
      count: customers.filter(c => c.emails?.length > 1).length,
      description: "Phase 2 — läuft täglich 03:00 Uhr"
    },
    {
      id: "milestone",
      name: "Milestone Emails",
      status: "live",
      color: "#d97706",
      trigger: "5 / 10 / 25 / 50 / 100 Besuche",
      steps: ["5 Besuche: Great start!", "10 Besuche: On fire!", "25 Besuche: Legend!", "50 Besuche: Elite!", "100 Besuche: Royalty!"],
      count: null,
      description: "Phase 2 — läuft täglich 03:00 Uhr"
    },
    {
      id: "journey_fall2",
      name: "Journey — Fall 2 (Mindbody)",
      status: "live",
      color: "#059669",
      trigger: "Nach Email 1 → startet automatisch",
      steps: ["Step 4: ROOTS Angebot (Tag 3)", "Step 5: 20% Rabatt (Tag 2)", "Step 6: Einwand-Mail Nico (Tag 2)", "Step 7: 10+2 Bundle (Tag 3)", "Step 8: Social Proof (Tag 4)", "Step 9: SMS Reminder (Tag 2)", "Step 10: Post-Kauf (nach Kauf)"],
      count: journeys.filter(j => j.journey === "fall2" && j.status === "active").length,
      description: "Phase 6 — läuft täglich 03:00 Uhr"
    },
    {
      id: "journey_fall1",
      name: "Journey — Fall 1 (Event)",
      status: "manual",
      color: "#0891b2",
      trigger: "Manuell starten für Event-Teilnehmer",
      steps: ["Step 1: Event Bestätigung", "Step 2: Reminder Tag vorher", "Step 3: Nach Event + Free Class", "Step 4: Code läuft ab", "Step 5: Einwand Nico", "Step 6: 20% Rabatt", "Step 7: 10+2 Bundle", "Step 8: SMS", "Step 9: Nach Rückkehr"],
      count: journeys.filter(j => j.journey === "fall1" && j.status === "active").length,
      description: "Phase 6 — läuft täglich 03:00 Uhr"
    },
    {
      id: "layer2",
      name: "Custom Templates (Layer 2)",
      status: "live",
      color: "#9900cc",
      trigger: "Trigger: visit_count, source, stage, days_since_last_visit",
      steps: ["Phase 7 — läuft nach Journey Engine", "Duplicate Prevention aktiv", "Neue Templates jederzeit im Dashboard hinzufügbar"],
      count: customTemplates.filter(t => t.active).length,
      description: "Phase 7 — läuft täglich 03:00 Uhr"
    },
    {
      id: "sms",
      name: "SMS via Bird",
      status: "live",
      color: "#16a34a",
      trigger: "Journey Step 8 (Fall 1) / Step 9 (Fall 2)",
      steps: ["Automatisch bei Journey SMS Step", "Manuell über SMS Tab", "Webhook: Delivery Status tracking"],
      count: null,
      description: "Integriert in Phase 6"
    },
    {
      id: "inaktiv",
      name: "Inaktivitäts-Email",
      status: customTemplates.some(t => t.trigger_type === "days_since_last_visit") ? "live" : "planned",
      color: "#6b7280",
      trigger: "days_since_last_visit — via Custom Template (Layer 2)",
      steps: customTemplates.filter(t => t.trigger_type === "days_since_last_visit").length > 0
        ? customTemplates.filter(t => t.trigger_type === "days_since_last_visit").map(t => `${t.subject} (${OP_LABELS[t.operator]||t.operator}${t.trigger_value} Tage)`)
        : ["Noch kein Template — jetzt erstellen!"],
      count: customers.filter(c => c.stage === "inactive").length,
      description: "Über Custom Templates (Layer 2) steuerbar"
    },
  ];

  const liveCount   = REAL_AUTOMATIONS.filter(a => a.status === "live").length;
  const manualCount = REAL_AUTOMATIONS.filter(a => a.status === "manual").length;
  const plannedCount= REAL_AUTOMATIONS.filter(a => a.status === "planned").length;
  const activeJourneys = journeys.filter(j => j.status === "active").length;

  const statusConfig = {
    live:    { label: "Live ✅", color: "#059669", bg: "#dcfce7" },
    manual:  { label: "Manuell", color: "#d97706", bg: "#fef3c7" },
    planned: { label: "Geplant", color: "#6b7280", bg: "#f3f4f6" },
  };

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>Automationen</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Echte System-Automationen — läuft täglich 03:00 Uhr</div>
        </div>
        <div style={{ fontSize:11, background:"#f0fdf4", color:"#15803d", padding:"6px 12px", borderRadius:99, fontWeight:600 }}>
          🤖 Bot läuft täglich 03:00 Uhr
        </div>
      </div>

      {}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
        {[
          ["Live", liveCount, "#059669"],
          ["Manuell", manualCount, "#d97706"],
          ["Geplant", plannedCount, "#6b7280"],
          ["Aktive Journeys", activeJourneys, "#9900cc"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
            <div style={{ fontSize:11, color:"#9ca3af", marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:22, fontWeight:800, color:c }}>{String(v)}</div>
          </div>
        ))}
      </div>

      {}
      <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>
        🔒 System Automationen (Layer 1 — unveränderlich)
      </div>
      {REAL_AUTOMATIONS.map(a => {
        const sc = statusConfig[a.status];
        return (
          <div key={a.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20, marginBottom:12, borderLeft:`4px solid ${a.color}` }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{a.name}</span>
                  <span style={{ fontSize:11, background:sc.bg, color:sc.color, padding:"2px 8px", borderRadius:99, fontWeight:600 }}>{sc.label}</span>
                  {a.count !== null && (
                    <span style={{ fontSize:11, background:"#f3f4f6", color:"#374151", padding:"2px 8px", borderRadius:99, marginLeft:"auto" }}>
                      {a.count} aktiv
                    </span>
                  )}
                </div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:8 }}>⚡ {a.trigger}</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                  {a.steps.map((step, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
                      {i > 0 && <span style={{ color:"#d1d5db", fontSize:10 }}>→</span>}
                      <span style={{ background:"#f9fafb", border:"1px solid #e5e7eb", color:"#374151", fontSize:11, padding:"3px 8px", borderRadius:6 }}>{step}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>📅 {a.description}</div>
              </div>
            </div>
          </div>
        );
      })}

      {}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:24, marginBottom:12 }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#9900cc", textTransform:"uppercase", letterSpacing:0.8 }}>
          🎯 Custom Automationen (Layer 2 — steuerbar)
        </div>
        <button onClick={() => setShowNewForm(!showNewForm)}
          style={{ padding:"6px 16px", background:"#9900cc", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          + Neue Automation
        </button>
      </div>

      {}
      {showNewForm && (
        <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:12, padding:20, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#7c3aed", marginBottom:16 }}>Neue Automation erstellen</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Trigger-Typ</label>
              <select value={newTmpl.trigger_type} onChange={e => setNewTmpl(p => ({...p, trigger_type: e.target.value}))}
                style={{ width:"100%", padding:"7px 10px", border:"1px solid #ddd6fe", borderRadius:8, fontSize:12, color:"#374151" }}>
                <option value="days_since_last_visit">📅 Tage seit letztem Besuch</option>
                <option value="visit_count">🔢 Anzahl Besuche</option>
                <option value="birthday">🎂 Geburtstag (Tage vorher)</option>
                <option value="contract_expiry">📋 Vertragsende (Tage vorher)</option>
                <option value="source">📍 Quelle (EGYM, CLASSPASS...)</option>
                <option value="stage">🏷️ Pipeline Stage</option>
              </select>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Operator</label>
                <select value={newTmpl.operator} onChange={e => setNewTmpl(p => ({...p, operator: e.target.value}))}
                  style={{ width:"100%", padding:"7px 10px", border:"1px solid #ddd6fe", borderRadius:8, fontSize:12, color:"#374151" }}>
                  {[">=","==",">","<","<="].map(op => <option key={op} value={op}>{OP_LABELS[op]} {op}</option>)}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Wert</label>
                <input value={newTmpl.trigger_value} onChange={e => setNewTmpl(p => ({...p, trigger_value: e.target.value}))}
                  placeholder="z.B. 30"
                  style={{ width:"100%", padding:"7px 10px", border:"1px solid #ddd6fe", borderRadius:8, fontSize:12, boxSizing:"border-box" }} />
              </div>
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Betreff</label>
            <input value={newTmpl.subject} onChange={e => setNewTmpl(p => ({...p, subject: e.target.value}))}
              placeholder="z.B. Wir vermissen dich bei BURN BXNG!"
              style={{ width:"100%", padding:"8px 12px", border:"1px solid #ddd6fe", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>E-Mail Text (HTML erlaubt)</label>
            <textarea value={newTmpl.body} onChange={e => setNewTmpl(p => ({...p, body: e.target.value}))}
              placeholder="Hey {{name}}, wir haben dich vermisst..."
              style={{ width:"100%", height:120, padding:"8px 12px", border:"1px solid #ddd6fe", borderRadius:8, fontSize:13, resize:"none", fontFamily:"inherit", boxSizing:"border-box" }} />
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => setShowNewForm(false)}
              style={{ padding:"8px 16px", background:"#f3f4f6", border:"none", borderRadius:8, fontSize:12, cursor:"pointer" }}>
              Abbrechen
            </button>
            <button onClick={saveNewTemplate} disabled={saving}
              style={{ padding:"8px 20px", background:"#9900cc", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              {saving ? "Speichert..." : "✅ Speichern &amp; Aktivieren"}
            </button>
          </div>
        </div>
      )}

      {}
      {customTemplates.length === 0 ? (
        <div style={{ background:"#f9fafb", border:"1px dashed #e5e7eb", borderRadius:12, padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>
          Noch keine Custom Automationen — klick "+ Neue Automation" um eine zu erstellen
        </div>
      ) : customTemplates.map(t => (
        <div key={t.id} style={{ background:"#fff", border:`1px solid ${t.active ? "#9900cc40" : "#e5e7eb"}`, borderRadius:12, padding:16, marginBottom:10,
          borderLeft:`4px solid ${t.active ? "#9900cc" : "#e5e7eb"}`, opacity: t.active ? 1 : 0.6 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {}
            <div onClick={() => toggleTemplate(t.id, t.active)}
              style={{ width:44, height:24, borderRadius:12, background:t.active?"#9900cc":"#e5e7eb", cursor:"pointer", position:"relative", flexShrink:0 }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:t.active?22:2, transition:"all 0.2s", boxShadow:"0 1px 3px #00000030" }} />
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{t.subject}</span>
                <span style={{ fontSize:11, background:t.active?"#f5f3ff":"#f3f4f6", color:t.active?"#7c3aed":"#6b7280",
                  padding:"2px 8px", borderRadius:99, fontWeight:600 }}>
                  {t.active ? "Aktiv" : "Inaktiv"}
                </span>
              </div>
              {t.trigger_type && (
                <div style={{ fontSize:11, color:"#6b7280" }}>
                  ⚡ {TRIGGER_LABELS[t.trigger_type] || t.trigger_type} {OP_LABELS[t.operator]||t.operator} {t.trigger_value}
                </div>
              )}
            </div>
            <button onClick={() => deleteTemplate(t.id)}
              style={{ padding:"5px 12px", borderRadius:8, fontSize:11, border:"1px solid #fca5a5", background:"#fef2f2", color:"#dc2626", cursor:"pointer" }}>
              Löschen
            </button>
          </div>
        </div>
      ))}

      {}
      {stats && (
        <div style={{ marginTop:24, padding:16, background:"#f9fafb", borderRadius:10, fontSize:11, color:"#9ca3af" }}>
          <div>🤖 Bot letzter Lauf Phase 1: <strong>{stats.last_run_phase1 ? new Date(stats.last_run_phase1).toLocaleString("de-DE") : "—"}</strong></div>
          <div>🎯 Bot letzter Lauf Template Engine: <strong>{stats.last_run_template_engine ? new Date(stats.last_run_template_engine).toLocaleString("de-DE") : "—"}</strong></div>
        </div>
      )}
    </div>
  );
};
const AnalyticsView = ({ customers }) => {
  const totalEmails = customers.flatMap(c=>c.emails).length;
  const avgLtv = customers.length ? Math.round(customers.reduce((s,c)=>s+c.ltv,0)/customers.length) : 0;
  const churnRisk = customers.filter(c=>c.mbStatus==="Inactive"||c.mbStatus==="Expired").length;

  const total      = customers.length || 1;
  const leads      = customers.filter(c=>c.stage==="lead").length;
  const firstTimer = customers.filter(c=>c.stage==="first_timer").length;
  const returning  = customers.filter(c=>c.stage==="returning").length;
  const members    = customers.filter(c=>c.stage==="member").length;
  const vip        = customers.filter(c=>c.stage==="vip").length;
  const inactive   = customers.filter(c=>c.stage==="inactive").length;
  const convPct = (a,b) => b>0 ? Math.round(a/b*100) : 0;

  const funnelSteps = [
    { label:"Leads",        value:leads,       color:"#6366f1", icon:"◈" },
    { label:"First Timer",  value:firstTimer,  color:"#7c3aed", icon:"★" },
    { label:"Wiederkommer", value:returning,   color:"#0891b2", icon:"↩" },
    { label:"Member",       value:members,     color:"#059669", icon:"♦" },
    { label:"VIP",          value:vip,         color:"#d97706", icon:"👑" },
  ];

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:14, marginBottom:24 }}>
        {[["Kunden gesamt",customers.length,"#7c3aed"],["Ø LTV",`${avgLtv}€`,"#059669"],["Members",customers.filter(c=>c.stage==="member").length,"#0891b2"],["Churn-Risiko",churnRisk,"#dc2626"]].map(([l,v,c])=>(
          <div key={l} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
            <div style={{ fontSize:12, color:"#9ca3af", marginBottom:6 }}>{l}</div>
            <div style={{ fontSize:28, fontWeight:800, color:c }}>{String(v)}</div>
          </div>
        ))}
      </div>

      {}
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:24, marginBottom:24 }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:20 }}>Conversion Funnel</div>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        <div style={{ display:"flex", alignItems:"flex-end", gap:4, minWidth:320 }}>
          {funnelSteps.map((step,i) => {
            const maxVal = funnelSteps[0].value || 1;
            const barH = Math.max(40, Math.round((step.value/maxVal)*180));
            const conv = i>0 ? convPct(step.value, funnelSteps[i-1].value) : 100;
            return (
              <div key={step.label} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center" }}>
                {i>0 ? (
                  <div style={{ fontSize:11, marginBottom:4, textAlign:"center", fontWeight:700, color:conv>30?"#059669":conv>10?"#d97706":"#dc2626" }}>
                    {conv}% →
                  </div>
                ) : <div style={{ marginBottom:20 }}/>}
                <div style={{ width:"85%", height:barH, background:`${step.color}15`, border:`2px solid ${step.color}`, borderRadius:"8px 8px 0 0", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column" }}>
                  <div style={{ fontSize:18, fontWeight:800, color:step.color }}>{step.value}</div>
                  <div style={{ fontSize:10, color:step.color }}>{convPct(step.value,total)}%</div>
                </div>
                <div style={{ fontSize:10, color:"#6b7280", marginTop:6, textAlign:"center", fontWeight:600 }}>{step.icon} {step.label}</div>
              </div>
            );
          })}
        </div>
        </div>{/* end overflowX scroll wrapper */}
        <div style={{ marginTop:16, display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 140px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:12, color:"#dc2626", fontWeight:600 }}>⚠ Inaktiv</span>
            <span style={{ fontSize:18, fontWeight:800, color:"#dc2626" }}>{inactive} ({convPct(inactive,total)}%)</span>
          </div>
          <div style={{ flex:"1 1 140px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:12, color:"#059669", fontWeight:600 }}>✓ Gesamt Conversion</span>
            <span style={{ fontSize:18, fontWeight:800, color:"#059669" }}>{convPct(members+vip, total)}%</span>
          </div>
          <div style={{ flex:"1 1 140px", background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:12, color:"#7c3aed", fontWeight:600 }}>First Timer → Member</span>
            <span style={{ fontSize:18, fontWeight:800, color:"#7c3aed" }}>{convPct(members+vip, firstTimer+returning+members+vip)}%</span>
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:16 }}>Pipeline</div>
          {PIPELINE_STAGES.map(stage=>{
            const count = customers.filter(c=>c.stage===stage.id).length;
            const pct = customers.length ? Math.round(count/customers.length*100) : 0;
            return (
              <div key={stage.id} style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12, color:"#374151" }}>{stage.label}</span>
                  <span style={{ fontSize:12, color:"#9ca3af" }}>{count} ({pct}%)</span>
                </div>
                <div style={{ height:5, background:"#f3f4f6", borderRadius:3 }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:stage.color, borderRadius:3 }} />
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:16 }}>E-Mail Performance</div>
          {[["Gesendet",totalEmails,"#7c3aed"],["Email 1",customers.filter(c=>c.emails?.some(e=>e.type==="Email 1")).length,"#9900cc"],["Email 2",customers.filter(c=>c.emails?.some(e=>e.type==="Email 2")).length,"#7c3aed"],["Email 3",customers.filter(c=>c.emails?.some(e=>e.type==="Email 3")).length,"#6366f1"]].map(([l,v,c])=>(
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f9fafb" }}>
              <span style={{ fontSize:12, color:"#6b7280" }}>{l}</span>
              <span style={{ fontSize:13, fontWeight:700, color:c }}>{String(v)}</span>
            </div>
          ))}
        </div>
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:16 }}>SMS Reichweite</div>
          <div style={{ fontSize:36, fontWeight:800, color:"#0891b2", marginBottom:4 }}>{customers.filter(c=>c.smsCapable&&isValidMobile(c.phone)).length}</div>
          <div style={{ fontSize:13, color:"#9ca3af", marginBottom:16 }}>Kunden mit Telefonnummer</div>
          <div style={{ height:6, background:"#f3f4f6", borderRadius:3, marginBottom:8 }}>
            <div style={{ height:"100%", width:`${customers.length?customers.filter(c=>c.smsCapable&&isValidMobile(c.phone)).length/customers.length*100:0}%`, background:"#0891b2", borderRadius:3 }} />
          </div>
        </div>
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:16 }}>Top Kunden (LTV)</div>
          {[...customers].sort((a,b)=>b.ltv-a.ltv).slice(0,5).map((c,i)=>(
            <div key={c.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #f9fafb" }}>
              <span style={{ fontSize:11, color:"#9ca3af", minWidth:16 }}>#{i+1}</span>
              <Avatar initials={c.avatar} size={24} color={PIPELINE_STAGES.find(s=>s.id===c.stage)?.color||"#777"}/>
              <span style={{ flex:1, fontSize:12, color:"#374151" }}>{c.name}</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#059669" }}>{c.ltv}€</span>
            </div>
          ))}
        </div>
        {}
        {["EGYM", "CLASSPASS", "WELLHUB", "WELLPASS"].map(source => {
          const agg = customers.filter(c => (c.source||"Direct").toUpperCase() === source);
          if (agg.length === 0) return null;
          const PRICE_PER_CLASS = parseInt(localStorage.getItem("burn_price_per_class") || "10");
          const totalVisits = agg.reduce((s, c) => s + (c.visits || 0), 0);
          const estimatedRevenue = totalVisits * PRICE_PER_CLASS;
          const avgVisits = agg.length ? Math.round(totalVisits / agg.length) : 0;
          const colors = { EGYM:"#059669", CLASSPASS:"#d97706", WELLHUB:"#dc2626", WELLPASS:"#0891b2" };
          const color = colors[source] || "#6b7280";
          return (
            <div key={source} style={{ background:"#fff", border:`1px solid #e5e7eb`, borderRadius:12, padding:20, borderTop:`3px solid ${color}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:color }} />
                <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{source}</div>
                <span style={{ marginLeft:"auto", fontSize:11, background:color+"15", color, padding:"2px 8px", borderRadius:99, fontWeight:600 }}>{agg.length} Kunden</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:16 }}>
                {[
                  ["Klassen", totalVisits, "#374151"],
                  ["Ø/Kunde", avgVisits, "#374151"],
                  ["Est. €", estimatedRevenue >= 1000 ? `${(estimatedRevenue/1000).toFixed(1)}k` : `${estimatedRevenue}`, color],
                ].map(([label, value, col]) => (
                  <div key={label} style={{ background:"#f9fafb", borderRadius:8, padding:"8px 6px", textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:700, color:col }}>{value}</div>
                    <div style={{ fontSize:9, color:"#9ca3af", marginTop:1 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:11, color:"#9ca3af", marginBottom:8 }}>Top Besucher</div>
              {[...agg].sort((a,b)=>b.visits-a.visits).slice(0,5).map((c,i) => (
                <div key={c.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #f9fafb" }}>
                  <span style={{ fontSize:11, color:"#9ca3af", minWidth:16 }}>#{i+1}</span>
                  <Avatar initials={c.avatar} size={24} color={color}/>
                  <span style={{ flex:1, fontSize:12, color:"#374151" }}>{c.name}</span>
                  <span style={{ fontSize:11, color:"#6b7280" }}>{c.visits} Klassen</span>
                  <span style={{ fontSize:12, fontWeight:700, color }}>{c.visits * PRICE_PER_CLASS}€</span>
                </div>
              ))}
              <div style={{ marginTop:10, fontSize:10, color:"#9ca3af", textAlign:"right" }}>
                Berechnung: {PRICE_PER_CLASS}€/Klasse — 
                <button onClick={() => { const p = prompt("Preis pro Klasse (€):", PRICE_PER_CLASS); if(p && !isNaN(p)) { localStorage.setItem("burn_price_per_class", p); window.location.reload(); }}}
                  style={{background:"none",border:"none",color:"#9900cc",cursor:"pointer",fontSize:11,fontWeight:600,textDecoration:"underline",padding:0}}>
                  anpassen
                </button>
              </div>
            </div>
          );
        })}
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:16 }}>Quellen</div>
          {(() => {
            const sourceColors = { "Direct":"#7c3aed", "CLASSPASS":"#d97706", "EGYM":"#059669", "WELLPASS":"#0891b2", "WELLHUB":"#dc2626" };
            const sourceCounts = {};
            customers.forEach(c => { const src = c.source||"Direct"; sourceCounts[src]=(sourceCounts[src]||0)+1; });
            return Object.entries(sourceCounts).sort((a,b)=>b[1]-a[1]).map(([source,count])=>(
              <div key={source} style={{ marginBottom:12, cursor:"pointer" }}
                onClick={()=>window.dispatchEvent(new CustomEvent("filterBySource",{detail:source}))}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{source}</span>
                  <span style={{ fontSize:12, color:"#9ca3af" }}>{count}</span>
                </div>
                <div style={{ height:6, background:"#f3f4f6", borderRadius:3 }}>
                  <div style={{ height:"100%", width:`${customers.length?count/customers.length*100:0}%`, background:sourceColors[source]||"#6b7280", borderRadius:3 }} />
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
};
const CACHE_TTL = 10 * 60 * 1000; // 10 Minuten

const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(`burn_cache_${key}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
};

const cacheSet = (key, data) => {
  try {
    localStorage.setItem(`burn_cache_${key}`, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
};

const cacheClear = (key) => {
  try { localStorage.removeItem(`burn_cache_${key}`); } catch {}
};

const cachedFetch = async (key, url, opts={}) => {
  const cached = cacheGet(key);
  if (cached !== null) return cached;
  const r = await fetch(url, opts);
  const data = await r.json();
  cacheSet(key, data);
  return data;
};
const WeeklyView = ({ customers, notify }) => {
  const [sales, setSales] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      cachedFetch("sales_summary", `${API_URL}/sales/summary`).catch(()=>null),
      cachedFetch("attendance_summary", `${API_URL}/attendance/summary`).catch(()=>null),
    ]).then(([s, a]) => {
      setSales(s);
      setAttendance(a);
      setLoading(false);
    });
  }, []);

  const fmt = (n) => new Intl.NumberFormat("de-DE", {style:"currency", currency:"EUR"}).format(n||0);
  const pct = (n) => `${(n||0).toFixed(1)}%`;
  const now = new Date();
  const firstTimers = (customers||[]).filter(c => c.stage === "first_timer").length;
  const members     = (customers||[]).filter(c => c.stage === "member").length;
  const leads       = (customers||[]).filter(c => c.stage === "lead").length;
  const activeJourneys = 0; // from journeys
  const sourceMap = {};
  (customers||[]).forEach(c => {
    const s = c.source || "Direct";
    sourceMap[s] = (sourceMap[s] || 0) + (c.ltv || 0);
  });
  const totalLTV = Object.values(sourceMap).reduce((a,b)=>a+b, 0);

  const KPICard = ({label, value, sub, color="#111827", alert=false}) => (
    <div style={{background:"#fff", borderRadius:10, padding:"14px 16px", border:`1px solid ${alert?"#fca5a5":"#e5e7eb"}`,
      background:alert?"#fef2f2":"#fff"}}>
      <div style={{fontSize:10, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:4}}>{label}</div>
      <div style={{fontSize:20, fontWeight:800, color}}>{value}</div>
      {sub && <div style={{fontSize:11, color:"#9ca3af", marginTop:2}}>{sub}</div>}
    </div>
  );

  if (loading) return <BurnLoader text="Lade Daten..." />;

  return (
    <div style={{flex:1, overflowY:"auto", padding:20}}>
      {}
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
        <div>
          <div style={{fontSize:16, fontWeight:700, color:"#111827"}}>📈 Weekly Steering</div>
          <div style={{fontSize:12, color:"#9ca3af"}}>
            Stand: {new Date().toLocaleDateString("de-DE", {weekday:"long", day:"2-digit", month:"long", year:"numeric"})}
          </div>
        </div>
      </div>

      {}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10}}>
          💶 Umsatz & Revenue
        </div>
        {sales ? (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10}}>
            <KPICard label="Umsatz MTD" value={fmt(sales.mtd)} color="#059669" />
            <KPICard label="Gesamt (Periode)" value={fmt(sales.total)} />
            {Object.entries(sales.by_category||{}).slice(0,2).map(([k,v]) => (
              <KPICard key={k} label={k} value={fmt(v)} sub={`${totalLTV > 0 ? ((v/sales.total)*100).toFixed(1) : 0}%`} />
            ))}
          </div>
        ) : (
          <div style={{background:"#f5f3ff", borderRadius:10, padding:16, fontSize:13, color:"#7c3aed"}}>
            ⚠️ Noch keine Sales-Daten — Bot muss einmal mit Phase 8 laufen (nach heutigem Commit)
          </div>
        )}
      </div>

      {}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10}}>
          🥊 Kursauslastung
        </div>
        {attendance && attendance.total_classes > 0 ? (
          <>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:12}}>
              <KPICard label="Ø Auslastung" value={pct(attendance.avg_utilization)}
                color={attendance.avg_utilization >= 60 ? "#059669" : attendance.avg_utilization >= 40 ? "#d97706" : "#dc2626"}
                alert={attendance.avg_utilization < 40} />
              <KPICard label="Kurse gesamt" value={attendance.total_classes} />
              <KPICard label="Slots unter 40%" value={attendance.slots_under_40}
                alert={attendance.slots_under_40 > 3} color={attendance.slots_under_40 > 3 ? "#dc2626" : "#374151"} />
              <KPICard label="No-Show Rate" value={pct(attendance.avg_no_show_rate)}
                alert={attendance.avg_no_show_rate > 15} />
            </div>

            {}
            {attendance.trainer_perf && attendance.trainer_perf.length > 0 && (
              <div style={{background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", overflowX:"auto"}}>
                <div style={{padding:"10px 14px", borderBottom:"1px solid #e5e7eb", fontSize:12, fontWeight:600, color:"#374151"}}>
                  Trainer Performance
                </div>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:360}}>
                  <thead>
                    <tr style={{background:"#f9fafb"}}>
                      {["Trainer","Kurse","Ø Auslastung","Gebucht gesamt"].map(h=>(
                        <th key={h} style={{padding:"8px 12px", textAlign:"left", fontWeight:600, color:"#6b7280", fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.trainer_perf.map((t,i)=>(
                      <tr key={t.trainer} style={{borderTop:"1px solid #f3f4f6", background:i%2===0?"#fff":"#f9fafb"}}>
                        <td style={{padding:"8px 12px", fontWeight:600, color:"#111827"}}>{t.trainer}</td>
                        <td style={{padding:"8px 12px", color:"#374151"}}>{t.classes}</td>
                        <td style={{padding:"8px 12px"}}>
                          <span style={{fontWeight:700, color:t.avg_utilization>=60?"#059669":t.avg_utilization>=40?"#d97706":"#dc2626"}}>
                            {pct(t.avg_utilization)}
                          </span>
                        </td>
                        <td style={{padding:"8px 12px", color:"#374151"}}>{t.total_booked}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div style={{background:"#f5f3ff", borderRadius:10, padding:16, fontSize:13, color:"#7c3aed"}}>
            ⚠️ Noch keine Attendance-Daten — Bot muss einmal mit Phase 8 laufen
          </div>
        )}
      </div>

      {}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10}}>
          🎯 CRM Funnel
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10}}>
          <KPICard label="Leads" value={leads} color="#6366f1" />
          <KPICard label="First Timer" value={firstTimers} color="#7c3aed" />
          <KPICard label="Members" value={members} color="#059669" />
          <KPICard label="Kunden gesamt" value={(customers||[]).length} />
        </div>
      </div>

      {}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10}}>
          📊 Kundenmix nach Quelle (LTV-Basis)
        </div>
        <div style={{background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:16}}>
          {Object.entries(sourceMap).sort((a,b)=>b[1]-a[1]).map(([source, ltv]) => {
            const pctVal = totalLTV > 0 ? ltv / totalLTV * 100 : 0;
            return (
              <div key={source} style={{marginBottom:10}}>
                <div style={{display:"flex", justifyContent:"space-between", marginBottom:3}}>
                  <span style={{fontSize:12, color:"#374151", fontWeight:500}}>{source}</span>
                  <span style={{fontSize:12, color:"#6b7280"}}>{fmt(ltv)} ({pctVal.toFixed(1)}%)</span>
                </div>
                <div style={{height:8, background:"#f3f4f6", borderRadius:4}}>
                  <div style={{height:8, background:"#9900cc", borderRadius:4, width:`${pctVal}%`, transition:"width 0.5s"}} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {}
      <div style={{background:"#f5f3ff", borderRadius:10, padding:14, fontSize:12, color:"#7c3aed", marginTop:8}}>
        <b>Hinweis:</b> Sales und Attendance Daten werden ab sofort täglich um 03:00 Uhr automatisch aktualisiert.
        Nach dem ersten Bot-Lauf mit Phase 8 sind alle KPIs befüllt.
      </div>
    </div>
  );
};
const BestandView = ({ notify }) => {
  const [tab, setTab]           = useState("overview");
  const [stats, setStats]       = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading]   = useState(true);
  const [editTmpl, setEditTmpl] = useState(null);
  const [editKey, setEditKey]     = useState(null);
  const [triggerFilter, setTriggerFilter] = useState("all");

  const TRIGGER_LABELS = {
    no_show:          "No-show Recovery",
    early_retention:  "Early Retention (14d)",
    reactivation:     "Reactivation (30d+)",
    power_user:       "Power User Upgrade",
    repeated_package: "Package → Membership",
    milestone:        "Milestone",
    birthday:         "Birthday",
  };

  const TRIGGER_COLORS = {
    no_show:          "#dc2626",
    early_retention:  "#d97706",
    reactivation:     "#7c3aed",
    power_user:       "#059669",
    repeated_package: "#0891b2",
    milestone:        "#9900cc",
    birthday:         "#f59e0b",
  };

  const loadAll = async () => {
    setLoading(true);
    const [s, j, t] = await Promise.all([
      cachedFetch("bestand_stats",     `${API_URL}/bestand/stats?days=7`).catch(()=>null),
      cachedFetch("bestand_journeys",  `${API_URL}/bestand/journeys?active=true`).catch(()=>({journeys:[]})),
      cachedFetch("bestand_templates", `${API_URL}/bestand/templates`).catch(()=>({templates:{}})),
    ]);
    setStats(s);
    setJourneys(j?.journeys || []);
    setTemplates(t?.templates || {});
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE",{day:"2-digit",month:"short",year:"2-digit"}) : "—";

  const filtered = journeys.filter(j =>
    triggerFilter === "all" || j.trigger_type === triggerFilter
  );
  const allTemplates = Object.values(templates).flat();

  if (loading) return <BurnLoader text="Lade Bestand..." />;

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {}
      <div style={{display:"flex",borderBottom:"1px solid #e5e7eb",padding:"0 16px",background:"#fff"}}>
        {[["overview","Übersicht"],["journeys","Aktive Journeys"],["templates","Templates"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:"10px 16px",border:"none",borderBottom:`2px solid ${tab===id?"#9900cc":"transparent"}`,
              background:"transparent",fontSize:12,fontWeight:tab===id?700:400,
              color:tab===id?"#9900cc":"#6b7280",cursor:"pointer"}}>
            {label}
          </button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={()=>{
          ["bestand_stats","bestand_journeys","bestand_templates"].forEach(k=>cacheClear(k));
          loadAll();
        }} style={{padding:"6px 10px",border:"none",background:"transparent",fontSize:12,color:"#9ca3af",cursor:"pointer"}}>↻</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:16}}>

        {}
        {tab==="overview" && (
          <div>
            {}
            <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#92400e"}}>
              <strong>DRY RUN aktiv</strong> — Bot sendet noch keine echten Nachrichten.
              Um zu aktivieren: Railway → Worker → Variables → <code>BESTAND_DRY_RUN=false</code>
            </div>

            {}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
              {[
                ["Aktive Journeys",    stats?.active_journeys||0,     "#9900cc"],
                ["Emails (7 Tage)",    stats?.emails_sent_period||0,  "#0891b2"],
                ["Reaktivierungen",    stats?.reactivations_period||0,"#059669"],
                ["Upgrades erkannt",   stats?.upgrades_period||0,     "#d97706"],
              ].map(([label,value,color])=>(
                <div key={label} style={{background:"#fff",borderRadius:10,padding:"12px 14px",border:"1px solid #e5e7eb"}}>
                  <div style={{fontSize:10,color:"#6b7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:22,fontWeight:800,color}}>{value}</div>
                </div>
              ))}
            </div>

            {}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",padding:16,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:12}}>Aktive Journeys nach Trigger</div>
              {Object.entries(stats?.by_trigger||{}).length === 0 ? (
                <div style={{fontSize:12,color:"#9ca3af"}}>Noch keine aktiven Journeys — Bot läuft um 03:00 Uhr</div>
              ) : Object.entries(stats?.by_trigger||{}).sort((a,b)=>b[1]-a[1]).map(([trigger,count])=>(
                <div key={trigger} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:TRIGGER_COLORS[trigger]||"#9ca3af",flexShrink:0}}/>
                  <div style={{flex:1,fontSize:12,color:"#374151"}}>{TRIGGER_LABELS[trigger]||trigger}</div>
                  <div style={{fontSize:13,fontWeight:700,color:TRIGGER_COLORS[trigger]||"#374151"}}>{count}</div>
                  <div style={{width:80,height:6,background:"#f3f4f6",borderRadius:3}}>
                    <div style={{height:6,borderRadius:3,background:TRIGGER_COLORS[trigger]||"#9ca3af",
                      width:`${Math.min(100, count / (stats?.active_journeys||1) * 100)}%`}}/>
                  </div>
                </div>
              ))}
            </div>

            {}
            <div style={{background:"#f5f3ff",borderRadius:10,padding:14}}>
              <div style={{fontSize:12,fontWeight:600,color:"#7c3aed",marginBottom:10}}>Trigger Priorität</div>
              {Object.entries(TRIGGER_LABELS).map(([id,label],i)=>(
                <div key={id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontSize:12}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:TRIGGER_COLORS[id],
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>{i+1}</div>
                  <span style={{color:"#374151"}}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        {tab==="journeys" && (
          <div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <select value={triggerFilter} onChange={e=>setTriggerFilter(e.target.value)}
                style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12}}>
                <option value="all">Alle Trigger</option>
                {Object.entries(TRIGGER_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select>
              <div style={{flex:1}}/>
              <span style={{fontSize:12,color:"#6b7280",alignSelf:"center"}}>{filtered.length} aktiv</span>
            </div>

            {filtered.length === 0 ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:32,marginBottom:8}}>🔄</div>
                <div style={{fontSize:14,fontWeight:600,color:"#374151"}}>Keine aktiven Journeys</div>
                <div style={{fontSize:12,marginTop:4}}>Bot startet Journeys täglich um 03:00 Uhr</div>
              </div>
            ) : (
              <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden",overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:480}}>
                  <thead>
                    <tr style={{background:"#f9fafb"}}>
                      {["Name","Trigger","Gestartet","Step","Nächster Send","Aktion"].map(h=>(
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"#6b7280",fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((j,i)=>(
                      <tr key={j.customer_id} style={{borderTop:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                        <td style={{padding:"8px 12px",fontWeight:600,color:"#111827"}}>{j.name}</td>
                        <td style={{padding:"8px 12px"}}>
                          <span style={{fontSize:10,padding:"3px 8px",borderRadius:99,
                            background:(TRIGGER_COLORS[j.trigger_type]||"#9ca3af")+"22",
                            color:TRIGGER_COLORS[j.trigger_type]||"#6b7280",fontWeight:600}}>
                            {TRIGGER_LABELS[j.trigger_type]||j.trigger_type}
                          </span>
                        </td>
                        <td style={{padding:"8px 12px",color:"#6b7280"}}>{fmtDate(j.started_at)}</td>
                        <td style={{padding:"8px 12px",color:"#374151",textAlign:"center"}}>{j.step}</td>
                        <td style={{padding:"8px 12px",color:"#374151"}}>{fmtDate(j.next_send_at)}</td>
                        <td style={{padding:"8px 12px"}}>
                          <button onClick={async()=>{
                            if(!confirm(`Journey für ${j.name} stoppen?`)) return;
                            await fetch(`${API_URL}/bestand/journeys/exit`,{
                              method:"POST",headers:{"Content-Type":"application/json"},
                              body:JSON.stringify({customer_id:j.customer_id,exit_reason:"manual_stop"})
                            });
                            cacheClear("bestand_journeys");
                            notify("✅ Journey gestoppt");
                            loadAll();
                          }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontSize:10,cursor:"pointer"}}>
                            Stoppen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {}
        {tab==="templates" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:12,color:"#6b7280"}}>Alle Templates sind im Dashboard editierbar — kein Code-Deploy nötig.</div>
              <button onClick={async()=>{
                await fetch(`${API_URL}/bestand/templates/seed`,{method:"POST"});
                cacheClear("bestand_templates");
                notify("✅ Standard-Templates geladen!");
                loadAll();
              }} style={{padding:"7px 14px",background:"#f5f3ff",color:"#7c3aed",border:"1px solid #ddd6fe",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                Standard Templates laden
              </button>
            </div>

            {allTemplates.length === 0 ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:12}}>Keine Templates — klick "Standard Templates laden"</div>
              </div>
            ) : Object.entries(
              allTemplates.reduce((acc,t)=>{
                if (!acc[t.trigger]) acc[t.trigger]=[];
                acc[t.trigger].push(t);
                return acc;
              },{})
            ).map(([trigger, tpls])=>(
              <div key={trigger} style={{marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:TRIGGER_COLORS[trigger]||"#9ca3af"}}/>
                  <div style={{fontSize:12,fontWeight:700,color:"#374151"}}>{TRIGGER_LABELS[trigger]||trigger}</div>
                </div>
                {tpls.sort((a,b)=>a.step-b.step).map((t,i)=>(
                  <div key={i} style={{background:"#fff",borderRadius:10,border:`1px solid ${t.active?"#e5e7eb":"#fca5a5"}`,
                    padding:14,marginBottom:8,opacity:t.active?1:0.6}}>
                    {editKey === `${t.trigger}_${t.step}` ? (
                      <div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                          <div>
                            <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Betreff</label>
                            <input value={editTmpl.subject} onChange={e=>setEditTmpl(p=>({...p,subject:e.target.value}))}
                              style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                          </div>
                          <div>
                            <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Delay (Stunden)</label>
                            <input type="number" value={editTmpl.delay_hours} onChange={e=>setEditTmpl(p=>({...p,delay_hours:parseInt(e.target.value)||0}))}
                              style={{width:"100%",padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                          </div>
                        </div>
                        <div style={{marginBottom:8}}>
                          <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Body (HTML)</label>
                          <textarea value={editTmpl.body} onChange={e=>setEditTmpl(p=>({...p,body:e.target.value}))}
                            style={{width:"100%",height:120,padding:"8px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:11,resize:"vertical",boxSizing:"border-box"}}/>
                        </div>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,cursor:"pointer"}}>
                            <input type="checkbox" checked={editTmpl.active} onChange={e=>setEditTmpl(p=>({...p,active:e.target.checked}))}/>
                            Aktiv
                          </label>
                          <div style={{flex:1}}/>
                          <button onClick={()=>{ setEditTmpl(null); setEditKey(null); }} style={{padding:"6px 12px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                          <button onClick={async()=>{
                            await fetch(`${API_URL}/bestand/templates`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(editTmpl)});
                            cacheClear("bestand_templates");
                            notify("✅ Template gespeichert!"); setEditTmpl(null); setEditKey(null); loadAll();
                          }} style={{padding:"6px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",gap:6,marginBottom:4}}>
                            <span style={{fontSize:10,background:"#f3f4f6",color:"#6b7280",padding:"2px 6px",borderRadius:99}}>Step {t.step}</span>
                            <span style={{fontSize:10,background:"#e0f2fe",color:"#0c4a6e",padding:"2px 6px",borderRadius:99}}>{t.channel}</span>
                            <span style={{fontSize:10,padding:"2px 6px",borderRadius:99,
                              background:t.active?"#dcfce7":"#fef2f2",color:t.active?"#166534":"#dc2626"}}>
                              {t.active?"aktiv":"inaktiv"}
                            </span>
                            {t.delay_hours > 0 && <span style={{fontSize:10,background:"#fef3c7",color:"#92400e",padding:"2px 6px",borderRadius:99}}>+{t.delay_hours}h</span>}
                          </div>
                          <div style={{fontSize:12,fontWeight:600,color:"#111827",marginBottom:2}}>{t.subject}</div>
                          <div style={{fontSize:11,color:"#9ca3af",maxHeight:40,overflow:"hidden"}}
                            dangerouslySetInnerHTML={{__html:t.body?.replace(/<[^>]*>/g," ").slice(0,120)+"..."}}/>
                        </div>
                        <button onClick={()=>{ setEditTmpl({...t}); setEditKey(`${t.trigger}_${t.step}`); }}
                          style={{padding:"5px 12px",borderRadius:7,border:"1px solid #ddd6fe",background:"#f5f3ff",color:"#7c3aed",fontSize:11,cursor:"pointer",flexShrink:0}}>
                          Bearbeiten
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
const StaffView = ({ notify }) => {
  const [tab, setTab]             = useState("payroll");
  const [staff, setStaff]         = useState([]);
  const [payRules, setPayRules]   = useState([]);
  const [payroll, setPayroll]     = useState([]);
  const [mbTrainers, setMbTrainers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [selMonth, setSelMonth]   = useState(new Date().toISOString().slice(0,7));
  const [selStaff, setSelStaff]   = useState("all");
  const [editRule, setEditRule]   = useState(null);
  const [staffForm, setStaffForm] = useState({ name:"", mb_id:"", role:"trainer", email:"" });

  const loadAll = async () => {
    // Stale-while-revalidate: show cache instantly, fetch fresh in background
    const cacheKey = `admin_all_${selMonth}`;
    const cached = lsGetStale(cacheKey);
    if (cached) {
      setStaff(cached.staff || []);
      setPayRules((cached.payRules || []).filter(r => r.name !== '_default' && r.trainer !== '_default'));
      setPayroll(cached.payroll || []);
      setMbTrainers(cached.mbTrainers || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    // Always fetch fresh in background
    try {
      const [s, r, p, att] = await Promise.all([
        fetch(`${API_URL}/staff`).then(x=>x.json()).catch(()=>({staff:[]})),
        fetch(`${API_URL}/staff/pay-rules`).then(x=>x.json()).catch(()=>({rules:[]})),
        fetch(`${API_URL}/staff/payroll?month=${selMonth}`).then(x=>x.json()).catch(()=>({entries:[]})),
        fetch(`${API_URL}/attendance/summary`).then(x=>x.json()).catch(()=>({trainer_perf:[]})),
      ]);
      const newStaff = s.staff || [];
      const newRules = (r.rules || []).filter(r => r.name !== '_default' && r.trainer !== '_default');
      const newPayroll = p.entries || [];
      const newTrainers = (att.trainer_perf || []).map(t => t.trainer).filter(Boolean);
      setStaff(newStaff);
      setPayRules(newRules);
      setPayroll(newPayroll);
      setMbTrainers(newTrainers);
      setLoading(false);
      lsSet(cacheKey, { staff: newStaff, payRules: newRules, payroll: newPayroll, mbTrainers: newTrainers });
    } catch { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [selMonth]);

  const fmt = (n) => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR"}).format(n||0);
  const trainerSummary = {};
  payroll.forEach(e => {
    const _tName = normalizeTrainer(e.name);
    if (!trainerSummary[_tName]) trainerSummary[_tName] = { classes:0, total:0, billable:0, excluded:0 };
    trainerSummary[_tName].classes++;
    trainerSummary[_tName].total    += e.total_pay || 0;
    trainerSummary[_tName].billable += e.billable_participants || 0;
    trainerSummary[_tName].excluded += e.staff_excluded || 0;
  });

  const filtered = payroll.filter(e =>
    (selStaff === "all" || normalizeTrainer(e.name) === normalizeTrainer(selStaff))
  ).sort((a,b) => b.class_date?.localeCompare(a.class_date));

  const MODELS = [
    { value:"base_only",             label:"Nur Grundvergütung" },
    { value:"base_plus_retroactive", label:"Grundverg. + alle TN × Rate (rückwirkend ab X)" },
    { value:"base_plus_overhang",    label:"Grundverg. + Überhang × Rate (ab TN X+1)" },
    { value:"flat_rate",             label:"Flat Rate fix" },
  ];

  // Modell A: Nahomi/Sheila/Ayman/Thommy — ab TN 6 → ALLE TN × 3€
  // Modell B: alle anderen              — ab TN 6 → nur (TN-5) × 2€
  const PRESETS = [
    { label:"A: 35€ + alle×3€ ab TN 6", base_fee:35, per_participant:3, min_participants:6, model:"base_plus_retroactive" },
    { label:"B: 35€ + Überhang×2€ ab TN 6", base_fee:35, per_participant:2, min_participants:6, model:"base_plus_overhang" },
  ];

  if (loading) return <BurnLoader text="Lade Staff..." />;

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {}
      <div style={{display:"flex",borderBottom:"1px solid #e5e7eb",background:"#fff",overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {[["payroll","Abrechnung"],["savings","Einsparungen"],["rules","Vergütungsregeln"],["team","Team"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:"12px 16px",border:"none",borderBottom:`2px solid ${tab===id?"#9900cc":"transparent"}`,
              background:"transparent",fontSize:13,fontWeight:tab===id?700:400,
              color:tab===id?"#9900cc":"#6b7280",cursor:"pointer",
              whiteSpace:"nowrap",flexShrink:0,minHeight:44}}>
            {label}
          </button>
        ))}
        <button onClick={()=>loadAll()} style={{padding:"12px 10px",border:"none",background:"transparent",fontSize:13,color:"#9ca3af",cursor:"pointer",flexShrink:0,marginLeft:"auto"}}>↻</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:16}}>

        {}
        {tab==="payroll" && (
          <div>
            {}
            <div style={{display:"flex",gap:10,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
              <input type="month" value={selMonth} onChange={e=>setSelMonth(e.target.value)}
                style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13}}/>
              <select value={selStaff} onChange={e=>setSelStaff(e.target.value)}
                style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12}}>
                <option value="all">Alle Trainer</option>
                {Object.keys(trainerSummary).map(n=><option key={n}>{n}</option>)}
              </select>
              <button onClick={async()=>{
                await fetch(`${API_URL}/staff/payroll/refresh?month=${selMonth}`,{method:"POST"}).catch(()=>{});
                loadAll();
              }} style={{padding:"7px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,background:"#f9fafb",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                🔄 Aktualisieren
              </button>
              <div style={{flex:1}}/>
              <div style={{fontSize:12,color:"#6b7280"}}>
                Gesamt: <strong style={{color:"#9900cc"}}>{fmt(Object.values(trainerSummary).reduce((s,t)=>s+t.total,0))}</strong>
              </div>
            </div>

            {}
            {Object.keys(trainerSummary).length > 0 && (
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
                {Object.entries(trainerSummary).sort((a,b)=>b[1].total-a[1].total).map(([name,s])=>(
                  <div key={name} style={{background:"#fff",borderRadius:10,padding:"12px 14px",border:"1px solid #e5e7eb",cursor:"pointer",
                    outline:selStaff===name?"2px solid #9900cc":"none"}}
                    onClick={()=>setSelStaff(selStaff===name?"all":name)}>
                    <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:4}}>{name}</div>
                    <div style={{fontSize:18,fontWeight:800,color:"#9900cc"}}>{fmt(s.total)}</div>
                    <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                      {s.classes} Kurse · {s.billable} Teilnehmer
                      {s.excluded > 0 && <span style={{color:"#d97706"}}> · {s.excluded} Staff ausgefiltert</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}


                {payroll.length === 0 ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:32,marginBottom:8}}>📊</div>
                <div style={{fontSize:14,fontWeight:600,color:"#374151"}}>Keine Daten für {selMonth}</div>
                <div style={{fontSize:12,marginTop:4}}>Payroll wird täglich um 03:00 Uhr berechnet</div>
              </div>
            ) : (
              <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"#f9fafb"}}>
                      {["Datum","Kurs","Trainer","Angemeldet / Eingecheckt","Staff out","Billable","Payout","Details"].map(h=>(
                        <th key={h} style={{padding:"8px 10px",textAlign:h==="Payout"?"right":"left",fontWeight:600,color:"#6b7280",fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e,i)=>(
                      <tr key={e.id} style={{borderTop:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                        <td style={{padding:"7px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>
                          {e.class_date ? new Date(e.class_date).toLocaleDateString("de-DE",{day:"2-digit",month:"short"}) : "—"}
                        </td>
                        <td style={{padding:"7px 10px",color:"#374151",maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.class_name}</td>
                        <td style={{padding:"7px 10px",fontWeight:600,color:"#111827"}}>{e.name}</td>
                        <td style={{padding:"7px 10px",color:"#374151",textAlign:"center"}}>
                          <span>{e.total_booked > 0 ? e.total_booked : e.total_participants}</span>
                          <span style={{color:"#6b7280"}}> / {e.total_participants}</span>
                          {e.no_shows > 0 && (
                            <span style={{marginLeft:4,background:"#fee2e2",color:"#dc2626",borderRadius:4,padding:"1px 5px",fontSize:10,fontWeight:600}}>
                              {e.no_shows} NS
                            </span>
                          )}
                        </td>
                        <td style={{padding:"7px 10px",textAlign:"center"}}>
                          {e.staff_excluded > 0
                            ? <span style={{color:"#d97706",fontWeight:600}}>{e.staff_excluded}</span>
                            : <span style={{color:"#9ca3af"}}>0</span>}
                        </td>
                        <td style={{padding:"7px 10px",color:"#059669",fontWeight:600,textAlign:"center"}}>{e.billable_participants}</td>
                        <td style={{padding:"7px 10px",textAlign:"right",fontWeight:700,color:"#9900cc"}}>{fmt(e.total_pay)}</td>
                        <td style={{padding:"7px 10px"}}>
                          <button onClick={()=>{
                              const info = e.breakdown && e.breakdown.trim()
                                ? e.breakdown
                                : `Kurs: ${e.class_name||'–'}\nDatum: ${e.class_date||'–'}\nModell: ${e.pay_model||'–'}\nBillable TN: ${e.billable_participants||0}\nGesamt TN: ${e.total_participants||0}\nStaff abgezogen: ${e.staff_excluded||0}\nAuszahlung: ${fmt(e.total_pay)}`;
                              alert(info);
                            }}
                            style={{padding:"3px 8px",borderRadius:6,border:"1px solid #e5e7eb",background:"#f9fafb",fontSize:10,cursor:"pointer",color:"#6b7280"}}>
                            Detail
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

              {}
              {tab==="savings" && (() => {
                const payRulesMap = {};
                payRules.forEach(r => { payRulesMap[r.name?.toLowerCase()] = r; });
                const savingsData = payroll.map(e => {
                  const rule = payRulesMap[e.name?.toLowerCase()] || { base_fee:35, per_participant:0 };
                  const perP = rule.per_participant || 0;
                  const excluded = e.staff_excluded || 0;
                  const saving = excluded * perP;
                  const totalOld = rule.base_fee + ((e.total_participants||0) * perP);
                  const totalNew = e.total_pay || rule.base_fee;
                  return { ...e, saving, totalOld, totalNew, perP };
                });

                const totalOld     = savingsData.reduce((s,e)=>s+e.totalOld,0);
                const totalNew     = savingsData.reduce((s,e)=>s+e.totalNew,0);
                const totalSaving  = savingsData.reduce((s,e)=>s+e.saving,0);
                const totalExcluded= savingsData.reduce((s,e)=>s+(e.staff_excluded||0),0);
                const byTrainer = {};
                savingsData.forEach(e => {
                  if (!byTrainer[e.name]) byTrainer[e.name] = { name:e.name, saving:0, excluded:0, classes:0 };
                  byTrainer[e.name].saving   += e.saving;
                  byTrainer[e.name].excluded += e.staff_excluded||0;
                  byTrainer[e.name].classes  += 1;
                });

                const noPerParticipant = payRules.every(r => !r.per_participant || r.per_participant === 0);

                return (
                  <div>
                    {}
                    {(noPerParticipant || payRules.length === 0) && (
                      <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:10,
                        padding:"12px 16px",marginBottom:16,fontSize:12,color:"#92400e"}}>
                        <strong>Hinweis:</strong> Einsparungen entstehen erst wenn "Pro Teilnehmer" Vergütung eingetragen ist.
                        Gehe zu <strong>Vergütungsregeln</strong> und trage für jeden Trainer einen Wert ein.
                        Die Berechnung startet dann automatisch beim nächsten Bot-Lauf (03:00 Uhr).
                      </div>
                    )}

                    {}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
                      {[
                        ["Incentives ohne Exklusion", fmt(totalOld), "#dc2626"],
                        ["Incentives mit Exklusion",  fmt(totalNew), "#9900cc"],
                        ["Einsparung gesamt",         fmt(totalSaving), "#059669"],
                        ["Hochrechnung Jahr",         fmt(totalSaving * 12), "#059669"],
                      ].map(([label,value,color])=>(
                        <div key={label} style={{background:"#fff",borderRadius:10,padding:"12px 14px",
                          border:`1px solid ${label.includes("Einsparung")?"#bbf7d0":"#e5e7eb"}`,
                          background:label.includes("Einsparung")?"#f0fdf4":"#fff"}}>
                          <div style={{fontSize:10,color:"#6b7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{label}</div>
                          <div style={{fontSize:18,fontWeight:800,color}}>{value}</div>
                          {label.includes("Hochrechnung") && <div style={{fontSize:10,color:"#9ca3af"}}>basierend auf diesem Monat</div>}
                        </div>
                      ))}
                    </div>

                    {}
                    <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",padding:14,marginBottom:16}}>
                      <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:10}}>Zusammenfassung</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
                        {[
                          ["Staff-Teilnahmen ausgeschlossen", totalExcluded, "Personen"],
                          ["Ø Einsparung pro Kurs", fmt(savingsData.length > 0 ? totalSaving/savingsData.length : 0), ""],
                          ["Ø Einsparung pro Woche", fmt(totalSaving / 4.3), ""],
                        ].map(([label,value,unit])=>(
                          <div key={label} style={{background:"#f9fafb",borderRadius:8,padding:"10px 12px"}}>
                            <div style={{fontSize:10,color:"#6b7280",marginBottom:4}}>{label}</div>
                            <div style={{fontSize:16,fontWeight:700,color:"#374151"}}>{value} <span style={{fontSize:11,color:"#9ca3af"}}>{unit}</span></div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {}
                    <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"10px 14px",borderBottom:"1px solid #e5e7eb",fontSize:12,fontWeight:600,color:"#374151"}}>
                        Einsparung nach Trainer
                      </div>
                      {Object.values(byTrainer).length === 0 ? (
                        <div style={{padding:20,color:"#9ca3af",fontSize:12,textAlign:"center"}}>
                          Keine Payroll-Daten für {selMonth} — Bot läuft um 03:00 Uhr
                        </div>
                      ) : Object.values(byTrainer).sort((a,b)=>b.saving-a.saving).map((t,i)=>(
                        <div key={t.name} style={{display:"flex",alignItems:"center",padding:"10px 14px",
                          borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                          <div style={{width:32,height:32,borderRadius:"50%",background:"#f5f3ff",
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0}}>
                            {t.name?.charAt(0)}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{t.name}</div>
                            <div style={{fontSize:11,color:"#9ca3af"}}>{t.classes} Kurse · {t.excluded} Staff ausgeschlossen</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:14,fontWeight:700,color:"#059669"}}>{fmt(t.saving)} gespart</div>
                            <div style={{fontSize:11,color:"#9ca3af"}}>Ø {fmt(t.classes > 0 ? t.saving/t.classes : 0)}/Kurs</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {}
                    {savingsData.filter(e=>e.saving>0).length > 0 && (
                      <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
                        <div style={{padding:"10px 14px",borderBottom:"1px solid #e5e7eb",fontSize:12,fontWeight:600,color:"#374151"}}>
                          Kurse mit höchster Einsparung
                        </div>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                          <thead>
                            <tr style={{background:"#f9fafb"}}>
                              {["Datum","Kurs","Trainer","Staff out","Einsparung"].map(h=>(
                                <th key={h} style={{padding:"8px 12px",textAlign:h==="Einsparung"?"right":"left",fontWeight:600,color:"#6b7280",fontSize:11}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {savingsData.filter(e=>e.saving>0).sort((a,b)=>b.saving-a.saving).slice(0,20).map((e,i)=>(
                              <tr key={e.id||i} style={{borderTop:"1px solid #f3f4f6"}}>
                                <td style={{padding:"7px 12px",color:"#6b7280"}}>
                                  {e.class_date ? new Date(e.class_date).toLocaleDateString("de-DE",{day:"2-digit",month:"short"}) : "—"}
                                </td>
                                <td style={{padding:"7px 12px",color:"#374151",maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.class_name}</td>
                                <td style={{padding:"7px 12px",fontWeight:600,color:"#111827"}}>{e.name}</td>
                                <td style={{padding:"7px 12px",textAlign:"center",color:"#d97706",fontWeight:600}}>{e.staff_excluded}</td>
                                <td style={{padding:"7px 12px",textAlign:"right",fontWeight:700,color:"#059669"}}>{fmt(e.saving)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}

        {}
        {tab==="rules" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:12,color:"#6b7280"}}>Standard: 35€ Grundvergütung pro Kurs. Individuelle Regeln überschreiben den Standard.</div>
              <button onClick={()=>setEditRule({staff_id:"",name:"",base_fee:35,per_participant:0,model:"base_only",pay_day:15,active:true})}
                style={{padding:"7px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                + Regel hinzufügen
              </button>
            </div>
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {payRules.length === 0 ? (
                <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>
                  Keine Regeln — Standard 35€/Kurs wird für alle verwendet
                </div>
              ) : payRules.map((r,i)=>(
                <div key={r.staff_id} style={{display:"flex",alignItems:"center",padding:"12px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{r.name}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>
                      {r.model==="base_only" ? `${r.base_fee}€ fix` :
                       r.model==="flat_rate" ? `${r.base_fee}€ Flat Rate` :
                       r.model==="base_plus_retroactive" ? `${r.base_fee}€ + alle TN × ${r.per_participant}€ (ab TN ${r.min_participants||6}, rückwirkend)` :
                       r.model==="base_plus_overhang" ? `${r.base_fee}€ + Überhang × ${r.per_participant}€ (ab TN ${r.min_participants||6})` :
                       `${r.base_fee}€ + ${r.per_participant}€/TN`}
                      {" · Zahlung am "}{r.pay_day}{". des Folgemonats"}
                      {r.exclude_staff && " · Staff gefiltert"}
                    </div>
                  </div>
                  <button onClick={()=>setEditRule(r)}
                    style={{padding:"5px 12px",borderRadius:7,border:"1px solid #ddd6fe",background:"#f5f3ff",color:"#7c3aed",fontSize:11,cursor:"pointer"}}>
                    Bearbeiten
                  </button>
                </div>
              ))}
            </div>

            {}
            {editRule && (
              <div style={{marginTop:14,background:"#f5f3ff",borderRadius:10,padding:14}}>
                <div style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:10}}>
                  {editRule.staff_id ? `Regel: ${editRule.name}` : "Neue Vergütungsregel"}
                </div>
                {}
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,color:"#6b7280",alignSelf:"center"}}>Schnellauswahl:</span>
                  {PRESETS.map(p=>(
                    <button key={p.label} onClick={()=>setEditRule(prev=>({...prev,...p}))}
                      style={{fontSize:10,padding:"3px 10px",border:"1px solid #ddd6fe",borderRadius:6,background:"#f5f3ff",color:"#7c3aed",cursor:"pointer",fontWeight:600}}>
                      {p.label}
                    </button>
                  ))}
                </div>

                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:8,marginBottom:10}}>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Trainer *</label>
                    <select value={editRule.name||""} onChange={e=>setEditRule(p=>({...p,name:e.target.value,staff_id:p.staff_id||e.target.value.toLowerCase().replace(/\s+/g,"_")}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box",background:"#fff"}}>
                      <option value="">— Trainer wählen —</option>
                      {[...new Set([
                          ...STAFF_USERS.filter(u=>u.role==="Trainer").map(u=>u.name),
                          ...mbTrainers.map(normalizeTrainer),
                          ...staff.filter(s=>s.role==="trainer"||s.role==="Trainer").map(s=>normalizeTrainer(s.name)),
                          ...payroll.map(e=>normalizeTrainer(e.name)),
                        ].filter(Boolean))].sort().map(n=>(
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Modell</label>
                    <select value={editRule.model||"base_only"} onChange={e=>setEditRule(p=>({...p,model:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12}}>
                      {MODELS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Grundverg. (€)</label>
                    <input type="number" value={editRule.base_fee||35} onChange={e=>setEditRule(p=>({...p,base_fee:parseFloat(e.target.value)||35}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Pro Teilnehmer (€)</label>
                    <input type="number" value={editRule.per_participant||0} onChange={e=>setEditRule(p=>({...p,per_participant:parseFloat(e.target.value)||0}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Ab Teilnehmer #</label>
                    <input type="number" min={1}
                      value={editRule.min_participants ?? 6}
                      onChange={e=>setEditRule(p=>({...p,min_participants:parseInt(e.target.value)||1}))}
                      disabled={editRule.model==="base_only"||editRule.model==="flat_rate"}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box",
                        background:editRule.model==="base_only"||editRule.model==="flat_rate"?"#f3f4f6":"#fff",
                        color:editRule.model==="base_only"||editRule.model==="flat_rate"?"#9ca3af":"#111827"}}/>
                  </div>
                </div>

                {(editRule.model==="base_plus_retroactive"||editRule.model==="base_plus_overhang") && editRule.per_participant > 0 && (()=>{
                  const base  = editRule.base_fee||35;
                  const pp    = editRule.per_participant||0;
                  const min   = editRule.min_participants||6;
                  const retro = editRule.model==="base_plus_retroactive";
                  const calc  = (tn) => tn < min ? base : retro ? base + tn*pp : base + (tn-(min-1))*pp;
                  return (
                    <div style={{fontSize:11,color:"#7c3aed",marginBottom:8,background:"#ede9fe",borderRadius:6,padding:"6px 10px",display:"flex",gap:16,flexWrap:"wrap"}}>
                      <span>💡 {retro ? "Rückwirkend" : "Überhang"}</span>
                      <span>5 TN → <strong>{calc(5)}€</strong></span>
                      <span>{min} TN → <strong>{calc(min)}€</strong></span>
                      <span>8 TN → <strong>{calc(8)}€</strong></span>
                      <span>12 TN → <strong>{calc(12)}€</strong></span>
                    </div>
                  );
                })()}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setEditRule(null)} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={async()=>{
                    if (!editRule.name?.trim()) { notify("❌ Trainername fehlt!"); return; }
                    const payload = {
                      staff_id: editRule.staff_id || editRule.name.trim().toLowerCase().replace(/\s+/g,"_"),
                      name: editRule.name.trim(),
                      base_fee: editRule.base_fee || 35,
                      per_participant: editRule.per_participant || 0,
                      min_participants: editRule.min_participants ?? 6,
                      model: editRule.model || "base_only",
                      pay_day: 15,
                      active: true,
                    };
                    const r = await fetch(`${API_URL}/staff/pay-rules`,{method:"POST",
                      headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
                    if (r.ok) { notify("✅ Gespeichert!"); setEditRule(null); loadAll(); }
                    else { notify("❌ Fehler beim Speichern"); }
                  }} style={{padding:"6px 16px",background:"#9900cc",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}
          </div>
        )}

        {}
        {tab==="team" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>Team-Mitglieder</div>
              <button onClick={()=>setShowForm(!showForm)}
                style={{padding:"7px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                + Mitarbeiter
              </button>
            </div>
            {showForm && (
              <div style={{background:"#f5f3ff",borderRadius:10,padding:14,marginBottom:12}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
                  {[["Name","name",""],["Mindbody ID","mb_id",""],["Email","email",""]].map(([l,k,ph])=>(
                    <div key={k}>
                      <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>{l}</label>
                      <input value={staffForm[k]} onChange={e=>setStaffForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                        style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Rolle</label>
                    <select value={staffForm.role} onChange={e=>setStaffForm(p=>({...p,role:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12}}>
                      {["trainer","staff","owner"].map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowForm(false)} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={async()=>{
                    await fetch(`${API_URL}/staff`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(staffForm)});
                    notify("✅ Mitarbeiter gespeichert!"); setShowForm(false); loadAll();
                  }} style={{padding:"6px 16px",background:"#9900cc",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {staff.length===0 ? (
                <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>
                  Noch keine Mitarbeiter angelegt — Trainer werden automatisch aus Mindbody erkannt
                </div>
              ) : staff.map((s,i)=>(
                <div key={s.staff_id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:"#f5f3ff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                    {s.name.charAt(0)}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{s.name}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{s.role} · {s.email||"keine Email"}</div>
                  </div>
                  <span style={{fontSize:10,padding:"3px 8px",borderRadius:99,
                    background:s.role==="trainer"?"#f5f3ff":s.role==="owner"?"#fef3c7":"#f3f4f6",
                    color:s.role==="trainer"?"#7c3aed":s.role==="owner"?"#92400e":"#6b7280"}}>
                    {s.role}
                  </span>
                  <button onClick={async()=>{
                    if(!confirm(`${s.name} löschen?`)) return;
                    await fetch(`${API_URL}/staff/${s.staff_id}`,{method:"DELETE"});
                    loadAll();
                  }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// MODULES ADMIN VIEW — White Label Feature Flags
// ════════════════════════════════════════════════════════════════════════════════
const ModulesAdminView = ({ notify, onModulesChanged }) => {
  const [modules, setModules] = useState([]);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [brandForm, setBrandForm] = useState({name:"",primary_color:"",logo_url:"",contact_email:"",domain:""});

  const load = () => {
    fetch(`${API_URL}/modules`).then(r=>r.json()).then(d=>{
      setModules(d.modules||[]);
      setBranding(d.branding||{});
      setBrandForm(f=>({...f,...(d.branding||{})}));
      setLoading(false);
    });
  };
  useEffect(()=>{ load(); },[]);

  const toggle = async(mod) => {
    await fetch(`${API_URL}/modules/${mod}/toggle`,{method:"POST"});
    notify(`Modul ${mod} umgeschaltet`);
    load();
    if(onModulesChanged) onModulesChanged(); // update nav immediately
  };

  const saveBranding = async() => {
    await fetch(`${API_URL}/branding`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(brandForm)});
    notify("✅ Branding gespeichert!"); load();
  };

  if(loading) return <BurnLoader text="Lade Module..."/>;

  const moduleColors = {crm:"#3b82f6",cash:"#10b981",journeys:"#8b5cf6",email_editor:"#ec4899",payroll:"#f59e0b",profitability:"#ef4444"};

  return (
    <div style={{flex:1,overflowY:"auto",height:"100%"}}>
    <div style={{maxWidth:900,margin:"0 auto",padding:24}}>
      <div style={{fontSize:22,fontWeight:800,color:"#111",marginBottom:4}}>🧩 Module & White Label</div>
      <div style={{fontSize:13,color:"#6b7280",marginBottom:24}}>Aktiviere/deaktiviere Features pro Studio. Jedes Modul kann separat verkauft werden.</div>

      {/* Module Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16,marginBottom:32}}>
        {modules.map(m=>(
          <div key={m.module} style={{background:"#fff",borderRadius:14,border:`2px solid ${m.enabled?(moduleColors[m.module]||"#6366f1"):"#e5e7eb"}`,padding:20,transition:"all 0.2s"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div>
                <div style={{fontSize:24,marginBottom:4}}>{m.icon}</div>
                <div style={{fontSize:15,fontWeight:700,color:"#111"}}>{m.label}</div>
                <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{m.description}</div>
              </div>
              <div>
                {m.price_monthly > 0 && (
                  <div style={{fontSize:11,fontWeight:700,color:"#059669",background:"#d1fae5",padding:"2px 8px",borderRadius:20,marginBottom:6,textAlign:"center"}}>
                    +{m.price_monthly}€/mo
                  </div>
                )}
                {m.price_monthly === 0 && (
                  <div style={{fontSize:11,fontWeight:700,color:"#6b7280",background:"#f3f4f6",padding:"2px 8px",borderRadius:20,marginBottom:6,textAlign:"center"}}>
                    Basis
                  </div>
                )}
              </div>
            </div>
            <button onClick={()=>toggle(m.module)} style={{width:"100%",padding:"8px 0",borderRadius:8,border:"none",fontWeight:700,fontSize:13,cursor:"pointer",
              background:m.enabled?(moduleColors[m.module]||"#6366f1"):"#f3f4f6",
              color:m.enabled?"#fff":"#374151",transition:"all 0.2s"}}>
              {m.enabled?"✓ Aktiv — Deaktivieren":"Aktivieren"}
            </button>
          </div>
        ))}
      </div>

      {/* White Label Branding */}
      <div style={{background:"#fff",borderRadius:14,border:"1px solid #e5e7eb",padding:24}}>
        <div style={{fontSize:16,fontWeight:700,color:"#111",marginBottom:4}}>🎨 White Label Branding</div>
        <div style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>Passe Name, Logo und Farben für dein Studio an.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
          {[["Studio Name","name","BURN"],["Primärfarbe","primary_color","#FF4F00"],["Logo URL","logo_url","https://..."],["Domain","domain","crm.meinestudio.de"],["Kontakt E-Mail","contact_email","info@studio.de"]].map(([l,k,ph])=>(
            <div key={k}>
              <label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:4}}>{l}</label>
              {k==="primary_color"?(
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input type="color" value={brandForm[k]||"#FF4F00"} onChange={e=>setBrandForm(p=>({...p,[k]:e.target.value}))}
                    style={{width:40,height:36,border:"1px solid #e5e7eb",borderRadius:6,cursor:"pointer",padding:2}}/>
                  <input type="text" value={brandForm[k]||""} onChange={e=>setBrandForm(p=>({...p,[k]:e.target.value}))}
                    style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12}}/>
                </div>
              ):(
                <input type="text" value={brandForm[k]||""} placeholder={ph} onChange={e=>setBrandForm(p=>({...p,[k]:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12}}/>
              )}
            </div>
          ))}
        </div>
        <button onClick={saveBranding} style={{padding:"10px 24px",background:"#111",color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
          Branding speichern
        </button>
      </div>
    </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// PROFITABILITY MODULE
// ════════════════════════════════════════════════════════════════════════════════
const ProfitabilityModule = ({ notify }) => {
  const [tab, setTab] = useState("overview");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0,7));
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState({aggregator_inputs:[],fixed_costs:[],variable_costs:[]});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [aggForm, setAggForm] = useState({month:new Date().toISOString().slice(0,7),channel:"ClassPass",total_revenue:"",total_checkins:""});
  const [fcForm, setFcForm] = useState({label:"",category:"Miete",amount:"",allocation:"per_checkin"});
  const [showAggForm, setShowAggForm] = useState(false);
  const [showFcForm, setShowFcForm] = useState(false);

  // Simulator state
  const [sim, setSim] = useState({price_per_checkin:25,checkins_per_class:12,classes_per_month:80,trainer_cost:45,service_cost:15,fixed_monthly:15000,var_per_checkin:1});

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/profitability/overview?month=${month}`).then(r=>r.json()).catch(()=>null),
      fetch(`${API_URL}/profitability/settings`).then(r=>r.json()).catch(()=>({aggregator_inputs:[],fixed_costs:[],variable_costs:[]})),
      fetch(`${API_URL}/profitability/courses?month=${month}`).then(r=>r.json()).catch(()=>({courses:[]})),
      fetch(`${API_URL}/profitability/trainers?month=${month}`).then(r=>r.json()).catch(()=>({trainers:[]})),
    ]).then(([ov,st,co,tr])=>{
      setOverview(ov);
      setSettings(st);
      setCourses(co?.courses || []);
      setTrainers(tr?.trainers || []);
      setLoading(false);
    });
  };

  useEffect(()=>{ loadAll(); },[month]);

  const fmt = n => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR"}).format(n||0);
  const fmtPct = n => `${(n||0).toFixed(1)}%`;

  const saveAgg = async() => {
    await fetch(`${API_URL}/profitability/settings/aggregator`,{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({...aggForm,total_revenue:parseFloat(aggForm.total_revenue)||0,total_checkins:parseInt(aggForm.total_checkins)||0})});
    notify("✅ Aggregator-Umsatz gespeichert!"); setShowAggForm(false); loadAll();
  };

  const saveFixedCost = async() => {
    await fetch(`${API_URL}/profitability/settings/fixed-cost`,{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({...fcForm,amount:parseFloat(fcForm.amount)||0})});
    notify("✅ Fixkosten gespeichert!"); setShowFcForm(false); loadAll();
  };

  const calculateFromMindbody = async () => {
    setCalculating(true);
    setCalcResult(null);
    try {
      const r = await fetch(`${API_URL}/profitability/calculate?month=${month}`, {method:"POST"});
      const data = await r.json();
      setCalcResult(data);
      if(data.status === "ok") {
        notify(`✅ Berechnung abgeschlossen: ${data.processed} Kurse verarbeitet`);
        loadAll();
      } else {
        notify(`⚠️ ${data.status}: ${data.processed || 0} Kurse`);
      }
    } catch(e) {
      notify("❌ Berechnungsfehler: " + e.message);
    } finally {
      setCalculating(false);
    }
  };

  // Simulator calculation
  const simCalc = () => {
    const revenue = sim.price_per_checkin * sim.checkins_per_class * sim.classes_per_month;
    const trainerCost = sim.trainer_cost * sim.classes_per_month;
    const serviceCost = sim.service_cost * sim.classes_per_month;
    const varCost = sim.var_per_checkin * sim.checkins_per_class * sim.classes_per_month;
    const totalCost = trainerCost + serviceCost + varCost + sim.fixed_monthly;
    const cm = revenue - totalCost;
    const breakeven = totalCost / (sim.price_per_checkin - sim.var_per_checkin - (sim.trainer_cost + sim.service_cost) / sim.checkins_per_class);
    return {revenue, trainerCost, serviceCost, varCost, totalCost, cm, marginPct: revenue>0?cm/revenue*100:0, breakeven:Math.ceil(breakeven)};
  };

  const CHANNELS = ["ClassPass","EGYM/Wellpass","Wellhub","Mindbody direkt","Sonstiges"];
  const FC_CATS = ["Miete","Nebenkosten","Personal","Marketing","Versicherung","Sonstiges"];
  const ALLOC = [{v:"per_checkin",l:"Pro Check-in"},{v:"per_class",l:"Pro Kurs"},{v:"monthly",l:"Monatlich fix"}];

  const tabs = [{id:"overview",l:"📊 Übersicht"},{id:"courses",l:"📋 Kurse"},{id:"channels",l:"📡 Kanäle"},{id:"costs",l:"💸 Kosten"},{id:"simulator",l:"🎛️ Simulator"}];

  if(loading) return <BurnLoader text="Lade Profitabilität..."/>;

  const ov = overview || {};
  const rev = ov.revenue || {};
  const costs = ov.costs || {};

  return (
    <div style={{flex:1,overflowY:"auto",height:"100%"}}>
    <div style={{maxWidth:1100,margin:"0 auto",padding:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:"#111"}}>📈 Profitabilitäts-Dashboard</div>
          <div style={{fontSize:13,color:"#6b7280"}}>Kurs-, Trainer- & Studio-Profitabilität</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="month" value={month} onChange={e=>{setMonth(e.target.value);}}
            style={{padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13}}/>
          <button onClick={()=>loadAll()} style={{padding:"8px 14px",background:"#f3f4f6",border:"none",borderRadius:8,fontSize:13,cursor:"pointer"}}>↻</button>
          <button onClick={calculateFromMindbody} disabled={calculating}
            style={{padding:"8px 16px",background:calculating?"#9ca3af":"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:calculating?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
            {calculating ? "⏳ Berechne..." : "⚡ Mindbody-Daten laden"}
          </button>
        </div>
      </div>
      {calcResult && (
        <div style={{background:calcResult.status==="ok"?"#d1fae5":"#fee2e2",border:`1px solid ${calcResult.status==="ok"?"#6ee7b7":"#fca5a5"}`,borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13}}>
          {calcResult.status==="ok"
            ? `✅ ${calcResult.processed} Kurse berechnet · ${calcResult.skipped||0} übersprungen · Ø DB: ${calcResult.summary?.total_cm ? (calcResult.summary.total_cm / calcResult.processed).toFixed(0) + "€/Kurs" : "–"}`
            : `⚠️ ${calcResult.status}`}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:24}}>
        {[
          ["Umsatz",fmt(rev.total),"#059669","#d1fae5"],
          ["Gesamtkosten",fmt(costs.total),"#dc2626","#fee2e2"],
          ["Deckungsbeitrag",fmt(ov.contribution_margin),"#7c3aed","#ede9fe"],
          ["Marge",fmtPct(ov.margin_pct),ov.margin_pct>0?"#059669":"#dc2626",ov.margin_pct>0?"#d1fae5":"#fee2e2"],
          ["Check-ins",ov.total_checkins||0,"#0284c7","#e0f2fe"],
          ["Ø pro Check-in",fmt(ov.revenue_per_checkin),"#d97706","#fef3c7"],
        ].map(([l,v,color,bg])=>(
          <div key={l} style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:16}}>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
            <div style={{fontSize:20,fontWeight:800,color}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,background:"#f3f4f6",padding:4,borderRadius:10,width:"fit-content"}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"8px 16px",borderRadius:7,border:"none",fontWeight:tab===t.id?700:400,
            background:tab===t.id?"#fff":"transparent",color:tab===t.id?"#111":"#6b7280",fontSize:13,cursor:"pointer",
            boxShadow:tab===t.id?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {tab==="overview" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:20}}>
            <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:16}}>💹 Umsatz nach Kanal</div>
            {(rev.by_channel||[]).length===0?(
              <div style={{color:"#9ca3af",fontSize:13,textAlign:"center",padding:20}}>Noch keine Aggregator-Daten für {month}.<br/>Gehe zu "Kosten" → Aggregator-Umsatz eintragen.</div>
            ):(rev.by_channel||[]).map(ch=>(
              <div key={ch.channel} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>{ch.channel}</div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>{ch.checkins} Check-ins · {fmt(ch.revenue_per_checkin)}/Check-in</div>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:"#059669"}}>{fmt(ch.revenue)}</div>
              </div>
            ))}
          </div>
          <div style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:20}}>
            <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:16}}>💸 Kostenaufstellung</div>
            {[["Fixkosten",costs.fixed,"#6366f1"],["Variabel",costs.variable,"#f59e0b"],["Trainer",costs.trainer,"#ec4899"],["Gesamt",costs.total,"#dc2626"]].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
                <span style={{fontSize:13,color:"#374151"}}>{l}</span>
                <span style={{fontSize:13,fontWeight:700,color:c}}>{fmt(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COURSES TAB */}
      {tab==="courses" && (
        <div>
          {courses.length === 0 ? (
            <div style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:40,textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:12}}>📋</div>
              <div style={{fontSize:15,fontWeight:600,color:"#374151",marginBottom:8}}>Noch keine Kursdaten für {month}</div>
              <div style={{fontSize:13,color:"#9ca3af",marginBottom:20}}>Klicke "⚡ Mindbody-Daten laden" um echte Kursprofitabilität zu berechnen.</div>
              <button onClick={calculateFromMindbody} disabled={calculating}
                style={{padding:"10px 20px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer"}}>
                {calculating ? "⏳ Berechne..." : "⚡ Jetzt berechnen"}
              </button>
            </div>
          ) : (
            <div>
              {/* Trainer summary */}
              {trainers.length > 0 && (
                <div style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:20,marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:12}}>👥 Trainer-Übersicht</div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:"#f9fafb"}}>
                          {["Trainer","Kurse","Check-ins","Umsatz","Trainerkosten","Deckungsbeitrag","Ø DB/Kurs"].map(h=>(
                            <th key={h} style={{padding:"8px 10px",textAlign:h==="Trainer"?"left":"right",color:"#6b7280",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {trainers.map((t,i)=>(
                          <tr key={i} style={{borderTop:"1px solid #f3f4f6"}}>
                            <td style={{padding:"8px 10px",fontWeight:600}}>{t.trainer}</td>
                            <td style={{padding:"8px 10px",textAlign:"right"}}>{t.classes}</td>
                            <td style={{padding:"8px 10px",textAlign:"right"}}>{t.total_checkins}</td>
                            <td style={{padding:"8px 10px",textAlign:"right",color:"#059669"}}>{fmt(t.total_revenue)}</td>
                            <td style={{padding:"8px 10px",textAlign:"right",color:"#dc2626"}}>{fmt(t.total_trainer_cost)}</td>
                            <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:t.total_cm>=0?"#059669":"#dc2626"}}>{fmt(t.total_cm)}</td>
                            <td style={{padding:"8px 10px",textAlign:"right",color:"#6b7280"}}>{fmt(t.avg_cm_per_class)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Course table */}
              <div style={{background:"#fff",borderRadius:12,border:"1px solid #e5e7eb",padding:20}}>
                <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:12}}>📋 Alle Kurse — {courses.length} Einträge</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"#f9fafb"}}>
                        {["Datum","Kurs","Trainer","Check-ins","Kanal","Umsatz","Kosten","DB","Auslastung"].map(h=>(
                          <th key={h} style={{padding:"8px 10px",textAlign:h==="Datum"||h==="Kurs"||h==="Trainer"?"left":"right",color:"#6b7280",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((c,i)=>{
                        const totalCost = (c.trainer_cost||0)+(c.fixed_cost||c.fixed_cost_alloc||0)+(c.variable_cost||0);
                        return (
                          <tr key={i} style={{borderTop:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                            <td style={{padding:"6px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>{c.class_date?.slice(0,10)||"–"}</td>
                            <td style={{padding:"6px 10px",fontWeight:600,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.class_name}</td>
                            <td style={{padding:"6px 10px",color:"#374151",whiteSpace:"nowrap"}}>{c.trainer||"–"}</td>
                            <td style={{padding:"6px 10px",textAlign:"right"}}>{c.checkins}</td>
                            <td style={{padding:"6px 10px",textAlign:"right"}}><span style={{background:"#e0f2fe",color:"#0284c7",padding:"2px 6px",borderRadius:4,fontSize:10,fontWeight:600}}>{c.channel||"Mindbody"}</span></td>
                            <td style={{padding:"6px 10px",textAlign:"right",color:"#059669"}}>{fmt(c.revenue)}</td>
                            <td style={{padding:"6px 10px",textAlign:"right",color:"#dc2626"}}>{fmt(totalCost)}</td>
                            <td style={{padding:"6px 10px",textAlign:"right",fontWeight:700,color:c.contribution_margin>=0?"#059669":"#dc2626"}}>{fmt(c.contribution_margin)}</td>
                            <td style={{padding:"6px 10px",textAlign:"right",color:"#6b7280"}}>{c.occupancy_pct||0}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CHANNELS TAB */}
      {tab==="channels" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700}}>Aggregator-Umsätze für {month}</div>
            <button onClick={()=>setShowAggForm(!showAggForm)} style={{padding:"8px 16px",background:"#6366f1",color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
              + Umsatz eintragen
            </button>
          </div>
          {showAggForm && (
            <div style={{background:"#eef2ff",borderRadius:10,padding:16,marginBottom:16}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:10}}>
                <div><label style={{fontSize:11,color:"#4338ca",display:"block",marginBottom:3}}>Monat</label>
                  <input type="month" value={aggForm.month} onChange={e=>setAggForm(p=>({...p,month:e.target.value}))}
                    style={{width:"100%",padding:"7px 9px",border:"1px solid #c7d2fe",borderRadius:6,fontSize:12}}/></div>
                <div><label style={{fontSize:11,color:"#4338ca",display:"block",marginBottom:3}}>Kanal</label>
                  <select value={aggForm.channel} onChange={e=>setAggForm(p=>({...p,channel:e.target.value}))}
                    style={{width:"100%",padding:"7px 9px",border:"1px solid #c7d2fe",borderRadius:6,fontSize:12}}>
                    {CHANNELS.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div><label style={{fontSize:11,color:"#4338ca",display:"block",marginBottom:3}}>Umsatz (€)</label>
                  <input type="number" value={aggForm.total_revenue} onChange={e=>setAggForm(p=>({...p,total_revenue:e.target.value}))} placeholder="z.B. 4200"
                    style={{width:"100%",padding:"7px 9px",border:"1px solid #c7d2fe",borderRadius:6,fontSize:12}}/></div>
                <div><label style={{fontSize:11,color:"#4338ca",display:"block",marginBottom:3}}>Check-ins</label>
                  <input type="number" value={aggForm.total_checkins} onChange={e=>setAggForm(p=>({...p,total_checkins:e.target.value}))} placeholder="z.B. 180"
                    style={{width:"100%",padding:"7px 9px",border:"1px solid #c7d2fe",borderRadius:6,fontSize:12}}/></div>
              </div>
              {aggForm.total_checkins>0&&aggForm.total_revenue>0&&(
                <div style={{fontSize:12,color:"#4338ca",marginBottom:8,fontWeight:600}}>
                  → Ø {(parseFloat(aggForm.total_revenue)/parseInt(aggForm.total_checkins)).toFixed(2)}€ pro Check-in
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setShowAggForm(false)} style={{padding:"7px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                <button onClick={saveAgg} style={{padding:"7px 16px",background:"#6366f1",color:"#fff",border:"none",borderRadius:7,fontWeight:700,fontSize:12,cursor:"pointer"}}>Speichern</button>
              </div>
            </div>
          )}
          <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
            {settings.aggregator_inputs.length===0?(
              <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:13}}>Noch keine Einträge</div>
            ):settings.aggregator_inputs.map((a,i)=>(
              <div key={a.id} style={{display:"flex",alignItems:"center",padding:"12px 16px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600}}>{a.channel} · {a.month}</div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>{a.total_checkins} Check-ins · {fmt(a.total_revenue/a.total_checkins)}/Check-in</div>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:"#059669"}}>{fmt(a.total_revenue)}</div>
                <button onClick={async()=>{
                  await fetch(`${API_URL}/profitability/settings/aggregator/${a.month}/${a.channel}`,{method:"DELETE"});
                  notify("Gelöscht"); loadAll();
                }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #e5e7eb",background:"#fee2e2",color:"#dc2626",fontSize:11,cursor:"pointer"}}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COSTS TAB */}
      {tab==="costs" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {/* Fixed Costs */}
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:14,fontWeight:700}}>Fixkosten / Monat</div>
              <button onClick={()=>setShowFcForm(!showFcForm)} style={{padding:"6px 12px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>+ Fixkosten</button>
            </div>
            {showFcForm && (
              <div style={{background:"#fef2f2",borderRadius:10,padding:14,marginBottom:12}}>
                <div style={{display:"grid",gap:8,marginBottom:8}}>
                  {[["Bezeichnung","label","z.B. Miete"],["Betrag (€)","amount",""]].map(([l,k,ph])=>(
                    <div key={k}><label style={{fontSize:11,color:"#991b1b",display:"block",marginBottom:3}}>{l}</label>
                      <input type={k==="amount"?"number":"text"} value={fcForm[k]} placeholder={ph}
                        onChange={e=>setFcForm(p=>({...p,[k]:e.target.value}))}
                        style={{width:"100%",padding:"7px 9px",border:"1px solid #fca5a5",borderRadius:6,fontSize:12}}/></div>
                  ))}
                  <div><label style={{fontSize:11,color:"#991b1b",display:"block",marginBottom:3}}>Kategorie</label>
                    <select value={fcForm.category} onChange={e=>setFcForm(p=>({...p,category:e.target.value}))}
                      style={{width:"100%",padding:"7px 9px",border:"1px solid #fca5a5",borderRadius:6,fontSize:12}}>
                      {FC_CATS.map(c=><option key={c}>{c}</option>)}
                    </select></div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowFcForm(false)} style={{padding:"6px 12px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={saveFixedCost} style={{padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:7,fontWeight:700,fontSize:12,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {settings.fixed_costs.length===0?<div style={{padding:20,textAlign:"center",color:"#9ca3af",fontSize:13}}>Noch keine Fixkosten</div>:
              settings.fixed_costs.map((fc,i)=>(
                <div key={fc.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600}}>{fc.label}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{fc.category}</div>
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:"#dc2626"}}>{fmt(fc.amount)}</div>
                  <button onClick={async()=>{await fetch(`${API_URL}/profitability/settings/fixed-cost/${fc.id}`,{method:"DELETE"});notify("Gelöscht");loadAll();}}
                    style={{padding:"3px 7px",borderRadius:5,border:"1px solid #fca5a5",background:"#fee2e2",color:"#dc2626",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ))}
            </div>
          </div>
          {/* Variable Costs */}
          <div>
            <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>Variable Kosten / Check-in</div>
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {settings.variable_costs.map((vc,i)=>(
                <div key={vc.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600}}>{vc.label}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{vc.category}</div>
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:"#f59e0b"}}>{vc.cost_per_checkin.toFixed(2)}€</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SIMULATOR TAB */}
      {tab==="simulator" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div style={{background:"#fff",borderRadius:14,border:"1px solid #e5e7eb",padding:24}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:20}}>🎛️ Parameter anpassen</div>
            {[
              ["Ø Preis / Check-in (€)","price_per_checkin",1,100,1],
              ["Ø Check-ins / Kurs","checkins_per_class",1,30,1],
              ["Kurse / Monat","classes_per_month",10,200,1],
              ["Trainerkosten / Kurs (€)","trainer_cost",0,200,5],
              ["Servicekosten / Kurs (€)","service_cost",0,100,5],
              ["Fixkosten / Monat (€)","fixed_monthly",0,50000,500],
              ["Variable Kosten / Check-in (€)","var_per_checkin",0,5,0.1],
            ].map(([label,key,min,max,step])=>(
              <div key={key} style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <label style={{fontSize:12,color:"#374151"}}>{label}</label>
                  <span style={{fontSize:13,fontWeight:700,color:"#111"}}>{sim[key]}</span>
                </div>
                <input type="range" min={min} max={max} step={step} value={sim[key]}
                  onChange={e=>setSim(p=>({...p,[key]:parseFloat(e.target.value)}))}
                  style={{width:"100%",accentColor:"#6366f1"}}/>
              </div>
            ))}
          </div>
          <div style={{background:"#fff",borderRadius:14,border:"1px solid #e5e7eb",padding:24}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:20}}>📊 Ergebnis</div>
            {(()=>{const r=simCalc();return(
              <div>
                {[
                  ["Umsatz/Monat",r.revenue,"#059669","💹"],
                  ["Trainerkosten",r.trainerCost,"#dc2626","👤"],
                  ["Servicekosten",r.serviceCost,"#dc2626","🔧"],
                  ["Variable Kosten",r.varCost,"#f59e0b","📦"],
                  ["Fixkosten",sim.fixed_monthly,"#6366f1","🏢"],
                  ["Gesamtkosten",r.totalCost,"#dc2626","💸"],
                ].map(([l,v,c,ic])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
                    <span style={{fontSize:13,color:"#374151"}}>{ic} {l}</span>
                    <span style={{fontSize:13,fontWeight:600,color:c}}>{fmt(v)}</span>
                  </div>
                ))}
                <div style={{marginTop:16,padding:16,borderRadius:10,background:r.cm>=0?"#d1fae5":"#fee2e2",border:`1px solid ${r.cm>=0?"#a7f3d0":"#fca5a5"}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:14,fontWeight:700}}>Deckungsbeitrag</span>
                    <span style={{fontSize:18,fontWeight:800,color:r.cm>=0?"#059669":"#dc2626"}}>{fmt(r.cm)}</span>
                  </div>
                  <div style={{fontSize:12,color:"#6b7280"}}>Marge: {r.marginPct.toFixed(1)}%</div>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:4}}>Break-even: {r.breakeven} Check-ins/Monat</div>
                </div>
              </div>
            )})()}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

const CashView = ({ notify }) => {
  const [tab, setTab] = useState("dashboard");
  const [forecast, setForecast] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [outflows, setOutflows] = useState([]);
  const [inflows, setInflows] = useState([]);
  const [payables, setPayables] = useState([]);
  const [settings, setSettings] = useState({});
  const [trainerRules, setTrainerRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadMsg, setUploadMsg] = useState("");
  const [showOutForm, setShowOutForm] = useState(false);
  const [showInForm, setShowInForm] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [outForm, setOutForm] = useState({ category:"Miete", label:"", amount:"", due_day:"1", recurrence:"monthly", confidence:"sicher" });
  const [inForm, setInForm] = useState({ source:"Mindbody", label:"", expected_amount:"", expected_date:"", confidence:"wahrscheinlich" });
  const [payForm, setPayForm] = useState({ supplier:"", invoice_no:"", amount:"", invoice_date:"", due_date:"", category:"Sonstiges", notes:"" });
  const [editBal, setEditBal] = useState(false);
  const [newBal, setNewBal] = useState("");
  const [invoiceText, setInvoiceText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [txSearch, setTxSearch] = useState("");
  const [txFilter, setTxFilter] = useState("all");
  const [txYear, setTxYear]     = useState("all");
  const [txMonth, setTxMonth]   = useState("all");

  const loadAll = (force=false) => {
    setLoading(true);
    if (force) {
      ["cash_forecast","cash_tx","cash_out","cash_in","cash_pay","cash_settings","cash_trainer"].forEach(k=>cacheClear(k));
    }
    Promise.all([
      cachedFetch("cash_forecast",  `${API_URL}/cash/forecast?days=60`).catch(()=>null),
      cachedFetch("cash_tx",        `${API_URL}/cash/transactions`).catch(()=>null),
      cachedFetch("cash_out",       `${API_URL}/cash/outflows`).catch(()=>null),
      cachedFetch("cash_in",        `${API_URL}/cash/inflows`).catch(()=>null),
      cachedFetch("cash_pay",       `${API_URL}/cash/payables`).catch(()=>null),
      cachedFetch("cash_settings",  `${API_URL}/cash/settings`).catch(()=>null),
      cachedFetch("cash_trainer",   `${API_URL}/cash/trainer-rules`).catch(()=>null),
    ]).then(([f,t,o,i,p,s,tr]) => {
      setForecast(f);
      setTransactions((t?.transactions || []).sort((a,b)=>b.date?.localeCompare(a.date)));
      setOutflows(o?.outflows || []);
      setInflows(i?.inflows || []);
      setPayables(p?.payables || []);
      setSettings(s || {});
      setTrainerRules(tr?.rules || []);
      setLoading(false);
    });
  };

  useEffect(() => { loadAll(); }, []);

  const fmt = (n) => new Intl.NumberFormat("de-DE", {style:"currency", currency:"EUR"}).format(n||0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE",{day:"2-digit",month:"short"}) : "—";
  const detectRecurring = (txns) => {
    if (!txns || txns.length === 0) return [];
    const outflows = txns.filter(t => t.amount < 0);
    const normalize = (name) => (name || "")
      .replace(/GmbH & Co\.?\s*KG/gi, "")
      .replace(/\b(GmbH|AG|SE|KG|UG|Ltd|Limited|Corp|Inc)\b/gi, "")
      .replace(/\b\d{5,}\b/g, "")           // Postleitzahlen/Nummern entfernen
      .replace(/\b(Str\.|Strasse|Straße|Weg|Platz|Allee|Ring|Gasse)\b.*/gi, "") // Adressen
      .replace(/[^a-zA-ZäöüÄÖÜß\s]/g, " ")   // Sonderzeichen
      .replace(/\s+/g, " ").trim()
      .toLowerCase()
      .split(" ").slice(0, 3).join(" ");        // Nur erste 3 Wörter = robuster Match
    const groups = {};
    outflows.forEach(t => {
      const key = normalize(t.counterpart);
      if (!key || key.length < 3) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });

    const suggestions = [];
    
    Object.entries(groups).forEach(([key, entries]) => {
      if (entries.length < 2) return;
      const sorted = [...entries].sort((a,b) => a.date?.localeCompare(b.date));
      const gaps = [];
      for (let i = 1; i < sorted.length; i++) {
        const d1 = new Date(sorted[i-1].date);
        const d2 = new Date(sorted[i].date);
        const diff = Math.round((d2-d1) / (1000*60*60*24));
        if (diff > 0) gaps.push(diff);
      }
      if (gaps.length === 0) return;
      
      const avgGap = gaps.reduce((a,b)=>a+b,0) / gaps.length;
      const isMonthly  = avgGap >= 20 && avgGap <= 45;   // ~monatlich (flexibel)
      const isYearly   = avgGap >= 300 && avgGap <= 430;  // ~jährlich
      const isWeekly   = avgGap >= 4  && avgGap <= 12;    // ~wöchentlich
      const isQuarter  = avgGap >= 80 && avgGap <= 100;   // ~quartalsweise

      if (!isMonthly && !isYearly && !isWeekly && !isQuarter) return;
      const avgAmt = Math.abs(entries.reduce((s,t)=>s+t.amount,0) / entries.length);
      const maxAmt = Math.max(...entries.map(t=>Math.abs(t.amount)));
      const minAmt = Math.min(...entries.map(t=>Math.abs(t.amount)));
      const variance = maxAmt > 0 ? (maxAmt - minAmt) / maxAmt : 0;
      const days = entries.map(t => new Date(t.date).getDate());
      const dayCount = {};
      days.forEach(d => dayCount[d] = (dayCount[d]||0)+1);
      const dueDay = parseInt(Object.entries(dayCount).sort((a,b)=>b[1]-a[1])[0][0]);
      const recurrence = isMonthly ? "monthly" : isYearly ? "yearly" : isWeekly ? "weekly" : "quarterly";
      let confidence = "niedrig";
      if (entries.length >= 12 && variance < 0.1) confidence = "hoch";
      else if (entries.length >= 6)               confidence = "hoch";
      else if (entries.length >= 3)               confidence = "mittel";

      suggestions.push({
        id:          `rec_${key.replace(/\s+/g,"_")}`,
        label:       entries[0].counterpart?.split(/\s{2,}/)[0]?.trim() || key,
        amount:      Math.round(avgAmt * 100) / 100,
        amount_min:  Math.round(minAmt * 100) / 100,
        amount_max:  Math.round(maxAmt * 100) / 100,
        recurrence,
        due_day:     dueDay,
        category:    entries[0].category || "Sonstiges",
        occurrences: entries.length,
        last_date:   sorted[sorted.length-1].date,
        confidence,
        variance:    Math.round(variance * 100),
      });
    });
    return suggestions
      .sort((a,b) => b.occurrences - a.occurrences)
      .slice(0, 50); // Zeige bis zu 50 Vorschläge
  };
  const parseCamt052XML = (xmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "application/xml");
    if (doc.querySelector("parsererror")) throw new Error("Ungültiges XML Format.");

    const ns = "urn:iso:std:iso:20022:tech:xsd:camt.052.001.02";
    const get = (el, tag) => el?.getElementsByTagNameNS(ns, tag)?.[0]?.textContent?.trim() || "";
    let accountBalance = null;
    const balances = doc.getElementsByTagNameNS(ns, "Bal");
    for (let b of balances) {
      const code = get(b, "Cd");
      if (code === "CLBD") {
        const amt = parseFloat(get(b, "Amt")) || 0;
        const ind = get(b, "CdtDbtInd");
        accountBalance = ind === "DBIT" ? -amt : amt;
        break;
      }
    }

    const txns = [];
    const entries = doc.getElementsByTagNameNS(ns, "Ntry");
    for (let e of entries) {
      const amtRaw  = parseFloat(get(e, "Amt")) || 0;
      const ind     = get(e, "CdtDbtInd");
      const amount  = ind === "DBIT" ? -amtRaw : amtRaw;
      const date    = get(e, "Dt") || get(e, "BookgDt");
      const addInfo = get(e, "AddtlNtryInf");
      const details = e.getElementsByTagNameNS(ns, "TxDtls")?.[0];
      let counterpart = "";
      if (ind === "CRDT") {
        counterpart = details?.getElementsByTagNameNS(ns, "Dbtr")?.[0]
          ? get(details.getElementsByTagNameNS(ns, "Dbtr")[0], "Nm") : "";
      } else {
        counterpart = details?.getElementsByTagNameNS(ns, "Cdtr")?.[0]
          ? get(details.getElementsByTagNameNS(ns, "Cdtr")[0], "Nm") : "";
      }
      counterpart = counterpart.replace(/\s+/g, " ").trim();

      const reference = get(details || e, "Ustrd") || addInfo;

      if (!date || amtRaw === 0) continue;

      const combined = (reference + " " + counterpart).toLowerCase();
      let category = "Sonstiges";
      if (/miete|nebenkosten|immobilien/.test(combined)) category = "Miete";
      else if (/gehalt|lohn|trainer|honorar|risolo|personal/.test(combined)) category = "Personal";
      else if (/steuer|finanzamt/.test(combined)) category = "Steuern";
      else if (/egym|wellpass|wellhub|classpass/.test(combined)) category = "Aggregatoren";
      else if (/versicherung|insurance|aok|krankenkasse/.test(combined)) category = "Versicherung";
      else if (/mindbody|software|saas/.test(combined)) category = "Software";
      else if (/strom|gas|wasser|energie|stadtwerke/.test(combined)) category = "Energie";
      else if (/marketing|werbung/.test(combined)) category = "Marketing";
      else if (/bank|gebühr|sparkasse/.test(combined)) category = "Bankgebühren";
      else if (/rundfunk|ard|zdf|gez/.test(combined)) category = "Sonstiges";
      else if (/steuerberatung|steuerber/.test(combined)) category = "Steuern";
      else if (/bundesanzeiger/.test(combined)) category = "Sonstiges";

      const id = `${date}_${amount}_${counterpart.slice(0,10)}`.replace(/[^a-zA-Z0-9_-]/g,"_");
      txns.push({ id, date, amount, counterpart, reference, category,
        type: amount >= 0 ? "inflow" : "outflow", source: "sparkasse_xml" });
    }
    return { transactions: txns, balance: accountBalance };
  };

  const parseSparkasseCSV = (text) => {
    const lines = text.split("\n").map(l=>l.trim()).filter(l=>l);
    let headerIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes("Buchungstag")) { headerIdx = i; break; }
    }
    if (headerIdx < 0) throw new Error("Kein Sparkasse-Format erkannt.");
    let accountBalance = null;
    for (let i = 0; i < headerIdx; i++) {
      const m = lines[i].match(/([+-]?[\d.]+,\d{2})\s*EUR/);
      if (m && (lines[i].includes("Kontostand") || lines[i].includes("Saldo"))) {
        accountBalance = parseFloat(m[1].replace(/\./g,"").replace(",","."));
      }
    }
    const headers = lines[headerIdx].split(";").map(h=>h.replace(/"/g,"").trim());
    const iDate   = headers.findIndex(h=>h==="Buchungstag");
    const iText   = headers.findIndex(h=>h.includes("Buchungstext"));
    const iName   = headers.findIndex(h=>h.includes("Auftraggeber")||h.includes("Beguenstigter"));
    const iAmt    = headers.findIndex(h=>h==="Betrag");
    const txns = [];
    for (let i = headerIdx+1; i < lines.length; i++) {
      const line = lines[i];
      if (!line || line.startsWith('"Kontostand')) continue;
      const cols = []; let cur="", inQ=false;
      for (let c=0; c<line.length; c++) {
        if (line[c]==='"') inQ=!inQ;
        else if (line[c]===";" && !inQ) { cols.push(cur.trim()); cur=""; }
        else cur+=line[c];
      }
      cols.push(cur.trim());
      const dateStr = cols[iDate]||"";
      if (!dateStr) continue;
      const dp = dateStr.split(".");
      if (dp.length < 3) continue;
      const date = `${dp[2].length===2?"20"+dp[2]:dp[2]}-${dp[1].padStart(2,"0")}-${dp[0].padStart(2,"0")}`;
      const amount = parseFloat((cols[iAmt]||"0").replace(/\./g,"").replace(",",".")) || 0;
      const counterpart = iName>=0 ? (cols[iName]||"") : "";
      const reference   = iText>=0 ? (cols[iText]||"") : "";
      const combined = (reference+" "+counterpart).toLowerCase();
      let category = "Sonstiges";
      if (/miete|nebenkosten|immobilien/.test(combined)) category="Miete";
      else if (/gehalt|lohn|trainer|honorar/.test(combined)) category="Personal";
      else if (/steuer|finanzamt/.test(combined)) category="Steuern";
      else if (/classpass|wellhub|wellpass|egym/.test(combined)) category="Aggregatoren";
      else if (/versicherung/.test(combined)) category="Versicherung";
      else if (/mindbody|software|saas/.test(combined)) category="Software";
      else if (/strom|gas|wasser|energie|stadtwerke/.test(combined)) category="Energie";
      else if (/marketing|werbung|instagram/.test(combined)) category="Marketing";
      else if (/bank|gebühr|kontoführung|kontofuehrung/.test(combined)) category="Bankgebühren";
      else if (/mitglied|membership|burn/.test(combined)) category="Mitgliedschaften";
      txns.push({ id:`${date}_${amount}_${counterpart.slice(0,8)}`.replace(/[^a-zA-Z0-9_-]/g,"_"),
        date, amount, counterpart, reference, category, type:amount>=0?"inflow":"outflow", source:"sparkasse" });
    }
    return { transactions:txns, balance:accountBalance };
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    let totalImported = 0, totalSkipped = 0, lastBalance = null;
    setUploadMsg(`Verarbeite ${files.length} Datei(en)...`);
    for (let file of files) {
      try {
        const text = await file.text();
        let result;
        if (file.name.endsWith(".xml") || text.trim().startsWith("<?xml")) {
          result = parseCamt052XML(text);
        } else {
          try { if (!text.includes("Buchungstag")) throw new Error(); result = parseSparkasseCSV(text); }
          catch { result = parseSparkasseCSV(new TextDecoder("iso-8859-1").decode(await file.arrayBuffer())); }
        }
        const { transactions:txns, balance:bal } = result;
        if (txns.length === 0) continue;
        const r = await fetch(`${API_URL}/cash/transactions/import`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ transactions:txns })
        });
        const d = await r.json();
        totalImported += d.imported || 0;
        totalSkipped  += d.skipped  || 0;
        if (bal !== null) lastBalance = bal;
      } catch(err) { console.error(file.name, err); }
    }
    if (lastBalance !== null) {
      await fetch(`${API_URL}/cash/settings`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({key:"bank_balance",value:lastBalance})});
      await fetch(`${API_URL}/cash/settings`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({key:"balance_date",value:new Date().toISOString().slice(0,10)})});
    }
    notify(`✅ ${totalImported} neue Buchungen importiert, ${totalSkipped} bereits vorhanden`);
    setUploadMsg(""); loadAll(true);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadMsg("Lese Datei...");
    try {
      let text;
      try { text = await file.text(); if (!text.includes("Buchungstag")) throw new Error(); }
      catch { text = new TextDecoder("iso-8859-1").decode(await file.arrayBuffer()); }
      const { transactions:txns, balance:bal } = parseSparkasseCSV(text);
      if (!txns.length) throw new Error("Keine Buchungen gefunden.");
      setUploadMsg(`Importiere ${txns.length} Buchungen...`);
      const r = await fetch(`${API_URL}/cash/transactions/import`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ transactions:txns })
      });
      const d = await r.json();
      if (bal !== null) {
        await fetch(`${API_URL}/cash/settings`, { method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ key:"bank_balance", value:bal }) });
        await fetch(`${API_URL}/cash/settings`, { method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ key:"balance_date", value:new Date().toISOString().slice(0,10) }) });
      }
      notify(`✅ ${d.imported} neue Buchungen importiert, ${d.skipped} bereits vorhanden`);
      setUploadMsg(""); loadAll(true);
    } catch(err) { setUploadMsg(""); notify(`❌ ${err.message}`); }
    e.target.value="";
  };

  const handleExtractInvoice = async () => {
    if (!invoiceText.trim()) return;
    setExtracting(true);
    try {
      const r = await fetch(`${API_URL}/cash/extract-invoice`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ text:invoiceText })
      });
      const d = await r.json();
      if (r.ok && d.data) {
        setPayForm({ supplier:d.data.supplier||"", invoice_no:d.data.invoice_no||"",
          amount:d.data.amount||"", invoice_date:d.data.invoice_date||"",
          due_date:d.data.due_date||"", category:d.data.category||"Sonstiges", notes:"" });
        setShowPayForm(true);
        notify("✅ Rechnungsdaten extrahiert — bitte prüfen!");
      }
    } catch(err) { notify(`❌ ${err.message}`); }
    setExtracting(false);
  };
  const statusColor = forecast?.status === "red" ? "#dc2626" : forecast?.status === "yellow" ? "#d97706" : "#059669";
  const statusBg    = forecast?.status === "red" ? "#fef2f2" : forecast?.status === "yellow" ? "#fef3c7" : "#dcfce7";
  const ForecastChart = () => {
    if (!forecast?.curve || forecast.curve.length === 0) return null;
    const curve = forecast.curve.slice(0, 60);
    const maxB = Math.max(...curve.map(d=>d.balance), forecast.min_puffer * 1.5);
    const minB = Math.min(...curve.map(d=>d.balance), 0);
    const range = maxB - minB || 1;
    const W=580, H=120, PAD=10;
    const toY = (v) => PAD + (1 - (v - minB) / range) * (H - 2*PAD);
    const toX = (i) => PAD + (i / (curve.length-1)) * (W - 2*PAD);
    const pts = curve.map((d,i)=>`${toX(i)},${toY(d.balance)}`).join(" ");
    const pufferY = toY(forecast.min_puffer);
    const zeroY = toY(0);
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:"block",marginBottom:8}}>
        {}
        {zeroY >= PAD && zeroY <= H-PAD && <line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3"/>}
        {}
        <line x1={PAD} y1={pufferY} x2={W-PAD} y2={pufferY} stroke="#d97706" strokeWidth="1" strokeDasharray="4 3"/>
        <text x={W-PAD+2} y={pufferY+4} fontSize="9" fill="#d97706">Puffer</text>
        {}
        <polyline fill="none" stroke={statusColor} strokeWidth="2" points={pts}/>
        {}
        {forecast.critical_date && curve.map((d,i) => d.date === forecast.critical_date ? (
          <circle key={i} cx={toX(i)} cy={toY(d.balance)} r="4" fill="#dc2626"/>
        ) : null)}
      </svg>
    );
  };

  const TABS = [
    { id:"dashboard", label:"Dashboard" },
    { id:"transactions", label:"Buchungen" },
    { id:"outflows", label:"Ausgaben" },
    { id:"inflows", label:"Einnahmen" },
    { id:"payables", label:"Rechnungen" },
    { id:"settings", label:"Einstellungen" },
  ];

  if (loading) return <BurnLoader text="Lade Cash..." />;

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {}
      <div style={{display:"flex",gap:0,borderBottom:"1px solid #e5e7eb",padding:"0 16px",background:"#fff",overflowX:"auto"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"10px 16px",border:"none",borderBottom:`2px solid ${tab===t.id?"#9900cc":"transparent"}`,
              background:"transparent",fontSize:12,fontWeight:tab===t.id?700:400,
              color:tab===t.id?"#9900cc":"#6b7280",cursor:"pointer",whiteSpace:"nowrap"}}>
            {t.label}
          </button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={()=>loadAll(true)} style={{padding:"6px 10px",border:"none",background:"transparent",fontSize:12,color:"#9ca3af",cursor:"pointer"}} title="Daten neu laden">↻</button>
        <label style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",cursor:"pointer",fontSize:12,color:"#9900cc",fontWeight:600}}>
          {uploadMsg || "📁 XML / CSV importieren"}
          <input type="file" accept=".xml,.csv" multiple onChange={e=>handleFileUpload(Array.from(e.target.files))} style={{display:"none"}}/>
        </label>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:16}}>

        {}
        {tab==="dashboard" && (
          <div>
            {}
            <div style={{background:statusBg,border:`1px solid ${statusColor}`,borderRadius:10,
              padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:12,height:12,borderRadius:"50%",background:statusColor,flexShrink:0}}/>
              <div style={{flex:1}}>
                {forecast?.critical_date ? (
                  <div>
                    <span style={{fontWeight:700,color:statusColor}}>Unterdeckung ab {fmtDate(forecast.critical_date)}</span>
                    <span style={{color:statusColor,marginLeft:8}}>Fehlbetrag: {fmt(forecast.critical_gap)}</span>
                  </div>
                ) : (
                  <span style={{fontWeight:700,color:statusColor}}>Liquidität stabil — kein kritisches Datum in den nächsten 60 Tagen</span>
                )}
              </div>
              <span style={{fontSize:11,color:"#9ca3af"}}>Mindestpuffer: {fmt(settings.min_puffer||5000)}</span>
            </div>

            {}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:16}}>
              {[
                ["Kontostand heute", fmt(forecast?.bank_balance||0), settings.balance_date ? `Stand ${fmtDate(settings.balance_date)}` : "Kein CSV importiert", "#111827"],
                ["Tiefster Punkt", fmt(forecast?.lowest_balance||0), "nächste 60 Tage", forecast?.lowest_balance < (settings.min_puffer||5000) ? "#dc2626" : "#059669"],
                ["Erstes kritisches Datum", forecast?.critical_date ? fmtDate(forecast.critical_date) : "—", forecast?.critical_date ? `Fehlbetrag: ${fmt(forecast.critical_gap)}` : "Alles im grünen Bereich", forecast?.critical_date ? "#dc2626" : "#059669"],
                ["Offene Rechnungen", fmt(payables.filter(p=>p.status!=="bezahlt").reduce((s,p)=>s+p.amount,0)), `${payables.filter(p=>p.status!=="bezahlt").length} offen`, "#d97706"],
              ].map(([label,value,sub,color])=>(
                <div key={label} style={{background:"#fff",borderRadius:10,padding:"12px 14px",border:"1px solid #e5e7eb"}}>
                  <div style={{fontSize:10,color:"#6b7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:18,fontWeight:800,color}}>{value}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{sub}</div>
                </div>
              ))}
            </div>

            {}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",padding:14,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:8}}>Liquiditätskurve — 60 Tage Forecast</div>
              <ForecastChart/>
              <div style={{display:"flex",gap:16,fontSize:11,color:"#9ca3af"}}>
                <span>— Kontostand</span>
                <span style={{color:"#d97706"}}>- - Mindestpuffer</span>
                {forecast?.critical_date && <span style={{color:"#dc2626"}}>● Kritischer Punkt</span>}
              </div>
            </div>

            {}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              <div style={{padding:"10px 14px",borderBottom:"1px solid #e5e7eb",fontSize:12,fontWeight:600,color:"#374151"}}>
                Nächste 14 Tage
              </div>
              {(forecast?.upcoming||[]).length === 0 ? (
                <div style={{padding:20,color:"#9ca3af",fontSize:12,textAlign:"center"}}>Keine Einträge in den nächsten 14 Tagen</div>
              ) : (forecast?.upcoming||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",padding:"8px 14px",borderBottom:"1px solid #f3f4f6",gap:12}}>
                  <div style={{width:36,fontSize:11,color:"#6b7280",flexShrink:0}}>{fmtDate(item.date)}</div>
                  <div style={{flex:1,fontSize:12,color:"#374151"}}>{item.label}</div>
                  <div style={{fontSize:11,background:item.confidence==="sicher"?"#dcfce7":item.confidence==="sehr wahrscheinlich"?"#e0f2fe":"#fef3c7",
                    color:item.confidence==="sicher"?"#166534":item.confidence==="sehr wahrscheinlich"?"#0c4a6e":"#92400e",
                    padding:"2px 8px",borderRadius:99,flexShrink:0}}>{item.confidence}</div>
                  <div style={{fontSize:13,fontWeight:700,color:item.amount>=0?"#059669":"#dc2626",flexShrink:0,minWidth:90,textAlign:"right"}}>
                    {item.amount>=0?"+":""}{fmt(item.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        {tab==="transactions" && (
          <div>
            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              <input placeholder="Suchen..." value={txSearch} onChange={e=>setTxSearch(e.target.value)}
                style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"7px 10px",fontSize:12,outline:"none",flex:1,minWidth:150}}/>
              <select value={txFilter} onChange={e=>setTxFilter(e.target.value)}
                style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"7px 10px",fontSize:12}}>
                <option value="all">Alle</option>
                <option value="inflow">Einnahmen</option>
                <option value="outflow">Ausgaben</option>
              </select>
              <select value={txYear} onChange={e=>setTxYear(e.target.value)}
                style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"7px 10px",fontSize:12}}>
                <option value="all">Alle Jahre</option>
                {[...new Set(transactions.map(t=>t.date?.slice(0,4)).filter(Boolean))].sort().reverse().map(y=>(
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select value={txMonth} onChange={e=>setTxMonth(e.target.value)}
                style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"7px 10px",fontSize:12}}>
                <option value="all">Alle Monate</option>
                {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m=>(
                  <option key={m} value={m}>{new Date(2000,parseInt(m)-1).toLocaleString("de-DE",{month:"long"})}</option>
                ))}
              </select>
              <span style={{fontSize:11,color:"#9ca3af",alignSelf:"center"}}>{transactions.filter(t=>(txYear==="all"||t.date?.startsWith(txYear))&&(txMonth==="all"||t.date?.slice(5,7)===txMonth)&&(txFilter==="all"||t.type===txFilter)&&(!txSearch||t.counterpart?.toLowerCase().includes(txSearch.toLowerCase())||t.reference?.toLowerCase().includes(txSearch.toLowerCase()))).length} Einträge</span>
            </div>
            {transactions.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:32,marginBottom:8}}>🏦</div>
                <div style={{fontSize:14,fontWeight:600,color:"#374151"}}>Sparkasse CSV importieren</div>
                <div style={{fontSize:12,marginTop:4}}>Online-Banking → Kontoauszüge → Export → oben importieren</div>
              </div>
            ) : (
              <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden",overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:480}}>
                  <thead>
                    <tr style={{background:"#f9fafb"}}>
                      {["Datum","Auftraggeber","Buchungstext","Kategorie","Betrag"].map(h=>(
                        <th key={h} style={{padding:"8px 12px",textAlign:h==="Betrag"?"right":"left",fontWeight:600,color:"#6b7280",fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.filter(t=>
                      (txFilter==="all"||t.type===txFilter) &&
                      (!txSearch||t.counterpart?.toLowerCase().includes(txSearch.toLowerCase())||t.reference?.toLowerCase().includes(txSearch.toLowerCase())||t.category?.toLowerCase().includes(txSearch.toLowerCase()))
                    ).slice(0,300).map((t,i)=>(
                      <tr key={i} style={{borderTop:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                        <td style={{padding:"7px 12px",color:"#6b7280",whiteSpace:"nowrap"}}>{fmtDate(t.date)}</td>
                        <td style={{padding:"7px 12px",color:"#374151",maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.counterpart||"—"}</td>
                        <td style={{padding:"7px 12px",color:"#9ca3af",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.reference}</td>
                        <td style={{padding:"7px 12px"}}>
                          <span style={{fontSize:10,background:"#f3f4f6",color:"#6b7280",padding:"2px 6px",borderRadius:99}}>{t.category}</span>
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,color:t.amount>=0?"#059669":"#dc2626",whiteSpace:"nowrap"}}>
                          {t.amount>=0?"+":""}{fmt(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {}
        {tab==="outflows" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>Fixkosten & wiederkehrende Ausgaben</div>
              <button onClick={()=>setShowOutForm(!showOutForm)}
                style={{padding:"7px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                + Ausgabe
              </button>
            </div>
            {showOutForm && (
              <div style={{background:"#f5f3ff",borderRadius:10,padding:14,marginBottom:12}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8,marginBottom:8}}>
                  {[["Bezeichnung","label","z.B. Monatsmiete"],["Betrag (€)","amount","z.B. 2800"],["Fällig am (Tag)","due_day","z.B. 5"]].map(([l,k,ph])=>(
                    <div key={k}>
                      <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>{l}</label>
                      <input value={outForm[k]} onChange={e=>setOutForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                        style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Kategorie</label>
                    <select value={outForm.category} onChange={e=>setOutForm(p=>({...p,category:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12}}>
                      {["Miete","Personal","Steuern","Software","Versicherung","Energie","Marketing","Reinigung","Bankgebühren","Sonstiges"].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Sicherheit</label>
                    <select value={outForm.confidence} onChange={e=>setOutForm(p=>({...p,confidence:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12}}>
                      {["sicher","sehr wahrscheinlich","wahrscheinlich"].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowOutForm(false)} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={async()=>{
                    await fetch(`${API_URL}/cash/outflows`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...outForm,amount:parseFloat(outForm.amount)||0,due_day:parseInt(outForm.due_day)||1})});
                    notify("✅ Ausgabe gespeichert!"); setShowOutForm(false); loadAll(true);
                  }} style={{padding:"6px 16px",background:"#9900cc",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {outflows.length===0 ? (
                <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>Noch keine Ausgaben — klick "+ Ausgabe" um zu beginnen</div>
              ) : outflows.map((o,i)=>(
                <div key={o.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{o.label}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>Jeden Monat am {o.due_day}. · {o.category} · {o.confidence}</div>
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:"#dc2626"}}>-{fmt(o.amount)}</div>
                  <button onClick={async()=>{
                    if(!confirm("Löschen?")) return;
                    await fetch(`${API_URL}/cash/outflows/${o.id}`,{method:"DELETE"});
                    notify("✅ Gelöscht"); loadAll(true);
                  }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ))}
            </div>
            <div style={{marginTop:10,padding:10,background:"#f9fafb",borderRadius:8,fontSize:12,color:"#6b7280"}}>
              Gesamt monatlich: <strong style={{color:"#dc2626"}}>{fmt(outflows.reduce((s,o)=>s+o.amount,0))}</strong>
            </div>

            {}
            {transactions.length > 0 && (() => {
              const suggestions = detectRecurring(transactions).filter(s =>
                !outflows.some(o => o.label?.toLowerCase().includes(s.label?.toLowerCase().slice(0,10)))
              );
              if (suggestions.length === 0) return null;
              return (
                <div style={{marginTop:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:8}}>
                    ✨ Erkannte wiederkehrende Ausgaben — übernehmen?
                  </div>
                  <div style={{background:"#fff",borderRadius:10,border:"1px solid #fde68a",overflow:"hidden"}}>
                    {suggestions.map((s,i)=>(
                      <div key={s.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",
                        borderTop:i>0?"1px solid #f3f4f6":"none",gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{s.label}</div>
                          <div style={{fontSize:11,color:"#9ca3af"}}>
                            {s.recurrence==="monthly"?"Monatlich":s.recurrence==="quarterly"?"Quartalsweise":s.recurrence==="yearly"?"Jährlich":"Wöchentlich"}
                            {" · "}{s.occurrences}x gefunden
                            {" · "}<span style={{color:s.confidence==="hoch"?"#059669":s.confidence==="mittel"?"#d97706":"#9ca3af",fontWeight:600}}>
                              {s.confidence}
                            </span>
                            {s.variance > 10 && <span style={{color:"#9ca3af"}}> · variiert {s.variance}%</span>}
                            {" · "}{s.category}
                          </div>
                        </div>
                        <div style={{fontSize:14,fontWeight:700,color:"#dc2626",whiteSpace:"nowrap"}}>
                          -{fmt(s.amount)}
                        </div>
                        <button onClick={async()=>{
                          await fetch(`${API_URL}/cash/outflows`,{
                            method:"POST",headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({
                              label:      s.label,
                              category:   s.category,
                              amount:     s.amount,
                              due_day:    s.due_day,
                              recurrence: s.recurrence,
                              confidence: "sicher",
                            })
                          });
                          notify(`✅ "${s.label}" als Fixkosten übernommen!`);
                          loadAll(true);
                        }} style={{padding:"6px 14px",borderRadius:8,border:"none",
                          background:"#9900cc",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                          Übernehmen
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {}
        {tab==="inflows" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>Erwartete Einnahmen</div>
              <button onClick={()=>setShowInForm(!showInForm)}
                style={{padding:"7px 14px",background:"#059669",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                + Einnahme
              </button>
            </div>
            {showInForm && (
              <div style={{background:"#dcfce7",borderRadius:10,padding:14,marginBottom:12}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8,marginBottom:8}}>
                  {[["Bezeichnung","label","z.B. ClassPass April"],["Betrag (€)","expected_amount",""],["Erwartet am","expected_date",""]].map(([l,k,ph])=>(
                    <div key={k}>
                      <label style={{fontSize:10,color:"#166534",display:"block",marginBottom:3}}>{l}</label>
                      <input type={k==="expected_date"?"date":"text"} value={inForm[k]} onChange={e=>setInForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                        style={{width:"100%",padding:"6px 8px",border:"1px solid #bbf7d0",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:10,color:"#166534",display:"block",marginBottom:3}}>Quelle</label>
                    <select value={inForm.source} onChange={e=>setInForm(p=>({...p,source:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #bbf7d0",borderRadius:7,fontSize:12}}>
                      {["Mindbody","ClassPass","Wellhub","Wellpass","EGYM","Sonstiges"].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:"#166534",display:"block",marginBottom:3}}>Sicherheit</label>
                    <select value={inForm.confidence} onChange={e=>setInForm(p=>({...p,confidence:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #bbf7d0",borderRadius:7,fontSize:12}}>
                      {["sicher","sehr wahrscheinlich","wahrscheinlich","manuell erwartet"].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowInForm(false)} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={async()=>{
                    await fetch(`${API_URL}/cash/inflows`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...inForm,expected_amount:parseFloat(inForm.expected_amount)||0})});
                    notify("✅ Einnahme gespeichert!"); setShowInForm(false); loadAll(true);
                  }} style={{padding:"6px 16px",background:"#059669",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {inflows.length===0 ? (
                <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>Noch keine Einnahmen eingetragen</div>
              ) : inflows.map((inf,i)=>(
                <div key={inf.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{inf.label}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{inf.source} · erwartet {fmtDate(inf.expected_date)} · {inf.confidence}</div>
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:"#059669"}}>+{fmt(inf.expected_amount)}</div>
                  <button onClick={async()=>{
                    if(!confirm("Löschen?")) return;
                    await fetch(`${API_URL}/cash/inflows/${inf.id}`,{method:"DELETE"});
                    notify("✅ Gelöscht"); loadAll(true);
                  }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #bbf7d0",background:"#dcfce7",color:"#166534",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        {tab==="payables" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>Offene Rechnungen</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setShowPayForm(!showPayForm)}
                  style={{padding:"7px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  + Rechnung
                </button>
              </div>
            </div>

            {}
            <div style={{background:"#f5f3ff",borderRadius:10,padding:14,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:600,color:"#7c3aed",marginBottom:8}}>KI Rechnungs-Extraktion</div>
              <textarea value={invoiceText} onChange={e=>setInvoiceText(e.target.value)}
                placeholder="Rechnungstext hier einfügen (aus PDF kopieren oder abtippen)..."
                style={{width:"100%",height:80,padding:"8px",border:"1px solid #ddd6fe",borderRadius:8,fontSize:12,resize:"vertical",boxSizing:"border-box"}}/>
              <button onClick={handleExtractInvoice} disabled={extracting||!invoiceText.trim()}
                style={{marginTop:8,padding:"7px 14px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {extracting?"Extrahiere...":"✨ Felder automatisch auslesen"}
              </button>
            </div>

            {showPayForm && (
              <div style={{background:"#fef3c7",borderRadius:10,padding:14,marginBottom:12}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8,marginBottom:8}}>
                  {[["Lieferant","supplier",""],["Rechnungs-Nr.","invoice_no",""],["Betrag (€)","amount",""],
                    ["Rechnungsdatum","invoice_date",""],["Fällig am","due_date",""],["Notiz","notes",""]].map(([l,k])=>(
                    <div key={k}>
                      <label style={{fontSize:10,color:"#92400e",display:"block",marginBottom:3}}>{l}</label>
                      <input type={k.includes("date")?"date":"text"} value={payForm[k]} onChange={e=>setPayForm(p=>({...p,[k]:e.target.value}))}
                        style={{width:"100%",padding:"6px 8px",border:"1px solid #fde68a",borderRadius:7,fontSize:12,boxSizing:"border-box"}}/>
                    </div>
                  ))}
                  <div>
                    <label style={{fontSize:10,color:"#92400e",display:"block",marginBottom:3}}>Kategorie</label>
                    <select value={payForm.category} onChange={e=>setPayForm(p=>({...p,category:e.target.value}))}
                      style={{width:"100%",padding:"6px 8px",border:"1px solid #fde68a",borderRadius:7,fontSize:12}}>
                      {["Miete","Personal","Steuern","Software","Versicherung","Energie","Marketing","Reinigung","Sonstiges"].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setShowPayForm(false);setInvoiceText("");}} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
                  <button onClick={async()=>{
                    await fetch(`${API_URL}/cash/payables`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...payForm,amount:parseFloat(payForm.amount)||0})});
                    notify("✅ Rechnung gespeichert!"); setShowPayForm(false); setInvoiceText(""); loadAll(true);
                  }} style={{padding:"6px 16px",background:"#d97706",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Speichern</button>
                </div>
              </div>
            )}

            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
              {payables.length===0 ? (
                <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>Keine offenen Rechnungen</div>
              ) : payables.map((p,i)=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",padding:"10px 14px",borderTop:i>0?"1px solid #f3f4f6":"none",gap:12,
                  background:p.due_date && new Date(p.due_date) < new Date() && p.status!=="bezahlt" ? "#fef2f2":"#fff"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{p.supplier} {p.invoice_no&&`(${p.invoice_no})`}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{p.category} · fällig {fmtDate(p.due_date)} · {p.status}</div>
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:"#d97706"}}>{fmt(p.amount)}</div>
                  <button onClick={async()=>{
                    await fetch(`${API_URL}/cash/payables`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...p,status:"bezahlt"})});
                    notify("✅ Als bezahlt markiert!"); loadAll(true);
                  }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #bbf7d0",background:"#dcfce7",color:"#166534",fontSize:10,cursor:"pointer"}}>✓ Bezahlt</button>
                  <button onClick={async()=>{
                    if(!confirm("Löschen?")) return;
                    await fetch(`${API_URL}/cash/payables/${p.id}`,{method:"DELETE"});
                    loadAll(true);
                  }} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        {tab==="settings" && (
          <div style={{maxWidth:500}}>
            <div style={{fontSize:13,fontWeight:600,color:"#374151",marginBottom:12}}>Cash Einstellungen</div>
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #e5e7eb",padding:16}}>
              {[
                ["Aktueller Kontostand (€)", "bank_balance", "z.B. 18450"],
                ["Kontostand Datum", "balance_date", "YYYY-MM-DD"],
                ["Mindestpuffer (€)", "min_puffer", "z.B. 5000"],
              ].map(([label, key, ph]) => (
                <div key={key} style={{marginBottom:14}}>
                  <label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:4}}>{label}</label>
                  <div style={{display:"flex",gap:8}}>
                    <input defaultValue={settings[key]||""} id={`setting-${key}`} placeholder={ph}
                      style={{flex:1,padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13}}/>
                    <button onClick={async()=>{
                      const val = document.getElementById(`setting-${key}`).value;
                      await fetch(`${API_URL}/cash/settings`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({key,value:val})});
                      notify("✅ Gespeichert!"); loadAll(true);
                    }} style={{padding:"7px 14px",background:"#9900cc",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      Speichern
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{marginTop:16,background:"#f5f3ff",borderRadius:10,padding:14}}>
              <div style={{fontSize:12,fontWeight:600,color:"#7c3aed",marginBottom:10}}>Trainer Vergütungsregeln</div>
              {trainerRules.length===0 ? (
                <div style={{fontSize:12,color:"#9ca3af"}}>Keine Trainer-Regeln — werden automatisch aus Phase 8 Attendance Daten befüllt</div>
              ) : trainerRules.map((r,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,fontSize:12}}>
                  <span style={{flex:1,fontWeight:600}}>{r.trainer}</span>
                  <span style={{color:"#6b7280"}}>Basis: {fmt(r.base_pay)}</span>
                  <span style={{color:"#6b7280"}}>+ {fmt(r.per_participant)}/Pers.</span>
                  <span style={{color:"#9ca3af"}}>→ 15. des Folgemonats</span>
                </div>
              ))}
            </div>

            <div style={{marginTop:12,padding:10,background:"#fef3c7",borderRadius:8,fontSize:11,color:"#92400e"}}>
              💡 Tipp: Kontostand und Datum nach jedem CSV-Import automatisch aktualisiert.
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
const LeadsView = ({ customers, notify }) => {
  const [manualLeads, setManualLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [form, setForm] = useState({ first_name:"", last_name:"", email:"", phone:"", source:"Direct", note:"" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [markedIds, setMarkedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("burn_marked_leads") || "[]")); } catch { return new Set(); }
  });
  const [showOnlyMarked, setShowOnlyMarked] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/leads`)
      .then(r => r.json())
      .then(d => setManualLeads(Array.isArray(d.leads) ? d.leads : []))
      .catch(() => setManualLeads([]));
  }, [showForm]);

  const toggleMark = (id, e) => {
    if (e) e.stopPropagation();
    setMarkedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem("burn_marked_leads", JSON.stringify([...next]));
      return next;
    });
  };

  const toggleBulk = (id, e) => {
    if (e) e.stopPropagation();
    setBulkSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const mbLeads = (customers || []).filter(c => c.stage === "lead").map(c => {
    const rawDate = c.joinDate || c.lastVisit || c.birthDate || "";
    const validDate = rawDate && !isNaN(new Date(rawDate).getTime()) ? rawDate : "";
    return {
      id: c.id, name: c.name, email: c.email, phone: c.phone,
      source: c.source, note: "", stage: "lead", isManual: false,
      visits: c.visits, created_at: validDate,
      joinDate: c.joinDate, lastVisit: c.lastVisit,
    };
  });

  const safeManualLeads = Array.isArray(manualLeads) ? manualLeads : [];
  const allLeads = [
    ...safeManualLeads.map(l => ({
      id: l.id, name: `${l.first_name} ${l.last_name}`.trim(),
      email: l.email, phone: l.phone, source: l.source,
      note: l.note, stage: l.stage, isManual: true, created_at: l.created_at,
    })),
    ...mbLeads,
  ].filter(l =>
    (!search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.email?.toLowerCase().includes(search.toLowerCase())) &&
    (!showOnlyMarked || markedIds.has(l.id))
  ).sort((a, b) => {
    const dA = new Date(a.created_at || 0), dB = new Date(b.created_at || 0);
    if (sortBy === "newest") return dB - dA;
    if (sortBy === "oldest") return dA - dB;
    if (sortBy === "name")   return (a.name||"").localeCompare(b.name||"");
    if (sortBy === "marked") return (markedIds.has(b.id)?1:0) - (markedIds.has(a.id)?1:0);
    return 0;
  });
  const now = new Date();
  const msDay = 86400000;
  const groups = [
    { key:"24h",   label:"Letzte 24 Stunden", color:"#059669", bg:"#dcfce7", max:1*msDay },
    { key:"7d",    label:"Letzte 7 Tage",     color:"#0891b2", bg:"#e0f2fe", max:7*msDay },
    { key:"30d",   label:"Letzter Monat",     color:"#7c3aed", bg:"#f5f3ff", max:30*msDay },
    { key:"90d",   label:"Letzte 3 Monate",   color:"#d97706", bg:"#fef3c7", max:90*msDay },
    { key:"older", label:"Älter / Unbekannt", color:"#9ca3af", bg:"#f3f4f6", max:Infinity },
  ];

  const getGroup = (lead) => {
    if (!lead.created_at) return "older";
    const d = new Date(lead.created_at);
    if (isNaN(d.getTime())) return "older";
    const diff = now - d;
    for (const g of groups) {
      if (diff <= g.max) return g.key;
    }
    return "older";
  };

  const grouped = groups.map(g => ({
    ...g,
    leads: allLeads.filter(l => getGroup(l) === g.key)
  })).filter(g => g.leads.length > 0);

  const saveLead = async () => {
    if (!form.first_name.trim()) return alert("Vorname ist Pflicht");
    setSaving(true);
    const url = editLead ? `${API_URL}/leads/${editLead.id}` : `${API_URL}/leads`;
    const r = await fetch(url, {
      method: editLead ? "PATCH" : "POST",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify(form)
    });
    const d = await r.json();
    setSaving(false);
    if (r.ok) {
      notify(editLead ? "✅ Lead aktualisiert!" : `✅ Lead angelegt! ID: ${d.id}`);
      setShowForm(false); setEditLead(null);
      setForm({ first_name:"", last_name:"", email:"", phone:"", source:"Direct", note:"" });
    }
  };

  const deleteLead = async (id, e) => {
    if (e) e.stopPropagation();
    if (!confirm("Lead wirklich löschen?")) return;
    await fetch(`${API_URL}/leads/${id}`, { method:"DELETE" });
    setManualLeads(p => p.filter(l => l.id !== id));
    if (selectedLead?.id === id) setSelectedLead(null);
    notify("✅ Gelöscht");
  };

  const startJourney = async (l, step, e) => {
    if (e) e.stopPropagation();
    const r = await fetch(`${API_URL}/journeys/start`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ client_id:l.id, first_name:(l.name||"").split(" ")[0], email:l.email||"", phone:l.phone||"", journey:"fall2", step })
    });
    if (r.ok) notify(`✅ Journey Step ${step} für ${(l.name||"").split(" ")[0]}!`);
  };

  const SOURCE_OPTIONS = ["Direct","Instagram","Website","Empfehlung","Event","CLASSPASS","EGYM","WELLHUB","Sonstiges"];

  const LeadCard = ({ l }) => {
    const isSelected = bulkSelected.has(l.id);
    const isMarked = markedIds.has(l.id);
    return (
      <div onClick={() => setSelectedLead(l)}
        style={{ background:isSelected?"#f5f3ff":"#fff", border:`1px solid ${isSelected?"#9900cc":"#e5e7eb"}`,
          borderRadius:10, padding:"12px 14px", cursor:"pointer", transition:"all 0.15s",
          display:"flex", alignItems:"center", gap:10 }}>
        {}
        <div onClick={e=>toggleBulk(l.id,e)}
          style={{ width:16, height:16, borderRadius:4, border:`2px solid ${isSelected?"#9900cc":"#d1d5db"}`,
            background:isSelected?"#9900cc":"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {isSelected && <span style={{color:"#fff",fontSize:10}}>✓</span>}
        </div>
        {}
        <div style={{ width:36, height:36, borderRadius:"50%", background:l.isManual?"#f5f3ff":"#eff6ff",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700,
          color:l.isManual?"#9900cc":"#4f46e5", flexShrink:0 }}>
          {(l.name||"?")[0].toUpperCase()}
        </div>
        {}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:13, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {l.name || "—"}
            </span>
            {isMarked && <span style={{fontSize:12}}>⭐</span>}
            <span style={{ fontSize:10, background:l.isManual?"#f5f3ff":"#eff6ff", color:l.isManual?"#7c3aed":"#4f46e5",
              padding:"1px 6px", borderRadius:99, fontWeight:600, flexShrink:0 }}>
              {l.isManual?"Manuell":"MB"}
            </span>
            {l.source && l.source !== "Direct" && (
              <span style={{ fontSize:10, background:"#f3f4f6", color:"#6b7280", padding:"1px 6px", borderRadius:99, flexShrink:0 }}>
                {l.source}
              </span>
            )}
          </div>
          <div style={{ fontSize:11, color:"#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {l.email || "—"}
            {l.phone && ` · ${l.phone}`}
            {l.created_at && ` · ${new Date(l.created_at).toLocaleDateString("de-DE",{day:"2-digit",month:"short",year:"numeric"})}`}
          </div>
        </div>
        {}
        <div style={{ display:"flex", gap:4, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
          <button onClick={e=>toggleMark(l.id,e)}
            style={{ padding:"4px 6px", borderRadius:6, border:`1px solid ${isMarked?"#f59e0b":"#e5e7eb"}`,
              background:isMarked?"#fef3c7":"#fff", fontSize:12, cursor:"pointer" }}>
            {isMarked?"⭐":"☆"}
          </button>
          <button onClick={e=>startJourney(l,4,e)}
            style={{ padding:"4px 8px", borderRadius:6, border:"1px solid #9900cc", background:"#fff", color:"#9900cc", fontSize:11, fontWeight:600, cursor:"pointer" }}>
            🗺️
          </button>
          {l.isManual && (
            <button onClick={e=>deleteLead(l.id,e)}
              style={{ padding:"4px 6px", borderRadius:6, border:"1px solid #fca5a5", background:"#fef2f2", color:"#dc2626", fontSize:11, cursor:"pointer" }}>
              🗑️
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
      {}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", borderRight: selectedLead ? "1px solid #e5e7eb" : "none" }}>
        {}
        <div style={{ padding:"12px 16px", borderBottom:"1px solid #e5e7eb", background:"#fff" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>🎯 Leads</div>
            <span style={{ fontSize:11, background:"#f3f4f6", color:"#6b7280", padding:"2px 8px", borderRadius:99 }}>
              {allLeads.length} gesamt
            </span>
            {bulkSelected.size > 0 && (
              <span style={{ fontSize:11, background:"#f5f3ff", color:"#9900cc", padding:"2px 8px", borderRadius:99, fontWeight:700 }}>
                {bulkSelected.size} ausgewählt
              </span>
            )}
            <div style={{ flex:1 }} />
            <button onClick={() => { setEditLead(null); setForm({ first_name:"", last_name:"", email:"", phone:"", source:"Direct", note:"" }); setShowForm(true); }}
              style={{ padding:"6px 14px", background:"#9900cc", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              + Neuer Lead
            </button>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <input placeholder="Suchen..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"6px 10px", fontSize:12, outline:"none", flex:1, minWidth:120 }} />
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"6px 10px", fontSize:12, color:"#374151" }}>
              <option value="newest">Neueste zuerst</option>
              <option value="oldest">Älteste zuerst</option>
              <option value="name">Name A-Z</option>
              <option value="marked">⭐ Markierte zuerst</option>
            </select>
            <button onClick={()=>setShowOnlyMarked(!showOnlyMarked)}
              style={{ padding:"6px 10px", borderRadius:8, fontSize:12, cursor:"pointer", border:"1px solid #e5e7eb",
                background:showOnlyMarked?"#fef3c7":"#fff", color:showOnlyMarked?"#d97706":"#374151", fontWeight:showOnlyMarked?700:400 }}>
              ⭐ {showOnlyMarked?"Alle":"Nur Markierte"}
            </button>
            {bulkSelected.size > 0 && (
              <button onClick={async () => {
                for (const id of bulkSelected) {
                  const l = allLeads.find(x=>x.id===id);
                  if (l) await fetch(`${API_URL}/journeys/start`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({client_id:l.id,first_name:(l.name||"").split(" ")[0],email:l.email||"",phone:l.phone||"",journey:"fall2",step:4}) });
                }
                notify(`✅ ${bulkSelected.size} Journeys gestartet!`);
                setBulkSelected(new Set());
              }} style={{ padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:700, background:"#9900cc", color:"#fff", border:"none", cursor:"pointer" }}>
                🗺️ Journey für {bulkSelected.size}
              </button>
            )}
          </div>
        </div>

        {}
        {showForm && (
          <div style={{ padding:16, background:"#f5f3ff", borderBottom:"1px solid #ddd6fe" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#7c3aed", marginBottom:12 }}>{editLead?"Lead bearbeiten":"Neuer Lead"}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
              {[["Vorname *","first_name","Max"],["Nachname","last_name","Mustermann"],["E-Mail","email","max@email.de"],
                ["Telefon","phone","+49 171 ..."],["Notiz","note","Kurze Notiz..."]].map(([label,key,ph]) => (
                <div key={key}>
                  <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>{label}</label>
                  <input value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} placeholder={ph}
                    style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12,boxSizing:"border-box"}} />
                </div>
              ))}
              <div>
                <label style={{fontSize:10,color:"#6b7280",display:"block",marginBottom:3}}>Quelle</label>
                <select value={form.source} onChange={e=>setForm(p=>({...p,source:e.target.value}))}
                  style={{width:"100%",padding:"6px 8px",border:"1px solid #ddd6fe",borderRadius:7,fontSize:12}}>
                  {SOURCE_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setShowForm(false);setEditLead(null);}} style={{padding:"6px 14px",background:"#f3f4f6",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
              <button onClick={saveLead} disabled={saving} style={{padding:"6px 16px",background:"#9900cc",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {saving?"Speichert...":"✅ Speichern"}
              </button>
            </div>
          </div>
        )}

        {}
        <div style={{ flex:1, overflowY:"auto", padding:12 }}>
          {allLeads.length === 0 ? (
            <div style={{textAlign:"center",color:"#9ca3af",padding:40,fontSize:13}}>Keine Leads gefunden</div>
          ) : grouped.map(group => (
            <div key={group.key} style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{height:2,flex:1,background:group.color,opacity:0.3,borderRadius:2}} />
                <span style={{fontSize:10,fontWeight:700,color:group.color,textTransform:"uppercase",letterSpacing:0.8,
                  background:group.bg,padding:"2px 10px",borderRadius:99,whiteSpace:"nowrap"}}>
                  {group.label} · {group.leads.length}
                  {group.key === "older" && group.leads.some(l => !l.created_at) && (
                    <span style={{opacity:0.6}}> (kein Datum)</span>
                  )}
                </span>
                <div style={{height:2,flex:1,background:group.color,opacity:0.3,borderRadius:2}} />
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {group.leads.map(l => <LeadCard key={l.id} l={l} />)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {}
      {selectedLead && (
        <div style={{width:320,display:"flex",flexDirection:"column",overflow:"hidden",background:"#fff"}}>
          <div style={{padding:"14px 16px",borderBottom:"1px solid #e5e7eb",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>Lead Details</div>
            <button onClick={()=>setSelectedLead(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#9ca3af"}}>✕</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:16}}>
            {}
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{width:60,height:60,borderRadius:"50%",background:selectedLead.isManual?"#f5f3ff":"#eff6ff",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:700,
                color:selectedLead.isManual?"#9900cc":"#4f46e5",margin:"0 auto 8px"}}>
                {(selectedLead.name||"?")[0].toUpperCase()}
              </div>
              <div style={{fontSize:16,fontWeight:700,color:"#111827"}}>{selectedLead.name||"—"}</div>
              <div style={{fontSize:11,color:selectedLead.isManual?"#7c3aed":"#4f46e5",marginTop:2}}>
                {selectedLead.isManual?"Manuell angelegt":"Mindbody Lead"}
              </div>
            </div>

            {}
            {[["📧 E-Mail",selectedLead.email],["📱 Telefon",selectedLead.phone],["📍 Quelle",selectedLead.source],
              ["📅 Erstellt",selectedLead.created_at?new Date(selectedLead.created_at).toLocaleDateString("de-DE",{day:"2-digit",month:"long",year:"numeric"}):"—"],
              ["📝 Notiz",selectedLead.note],["🥊 Besuche",selectedLead.visits]].filter(([,v])=>v).map(([k,v])=>(
              <div key={k} style={{display:"flex",gap:8,marginBottom:10,alignItems:"flex-start"}}>
                <span style={{fontSize:12,color:"#6b7280",minWidth:90,flexShrink:0}}>{k}</span>
                <span style={{fontSize:12,color:"#374151",fontWeight:500}}>{String(v)}</span>
              </div>
            ))}

            {}
            <div style={{marginTop:16,marginBottom:8}}>
              <button onClick={e=>toggleMark(selectedLead.id,e)}
                style={{width:"100%",padding:"8px",borderRadius:8,border:`1px solid ${markedIds.has(selectedLead.id)?"#f59e0b":"#e5e7eb"}`,
                  background:markedIds.has(selectedLead.id)?"#fef3c7":"#f9fafb",fontSize:13,fontWeight:600,cursor:"pointer",color:"#374151"}}>
                {markedIds.has(selectedLead.id)?"⭐ Markierung entfernen":"☆ Lead markieren"}
              </button>
            </div>

            {}
            <div style={{background:"#f5f3ff",borderRadius:10,padding:12,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:8,textTransform:"uppercase",letterSpacing:0.8}}>Journey starten</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {[["Fall 2 — Willkommen",1],["Fall 2 — Vorbereitung",2],["Fall 2 — ROOTS Angebot",4]].map(([label,step])=>(
                  <button key={step} onClick={e=>startJourney(selectedLead,step,e)}
                    style={{padding:"8px",borderRadius:8,border:"none",background:"#9900cc",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
                    🗺️ {label} (Step {step})
                  </button>
                ))}
              </div>
            </div>

            {}
            {selectedLead.isManual && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{
                  const parts = selectedLead.name?.split(" ")||[];
                  setEditLead(selectedLead);
                  setForm({first_name:parts[0]||"",last_name:parts.slice(1).join(" ")||"",email:selectedLead.email||"",phone:selectedLead.phone||"",source:selectedLead.source||"Direct",note:selectedLead.note||""});
                  setShowForm(true);
                  setSelectedLead(null);
                }} style={{flex:1,padding:"8px",borderRadius:8,border:"1px solid #e5e7eb",background:"#f9fafb",fontSize:12,fontWeight:600,cursor:"pointer",color:"#374151"}}>
                  ✏️ Bearbeiten
                </button>
                <button onClick={e=>deleteLead(selectedLead.id,e)}
                  style={{flex:1,padding:"8px",borderRadius:8,border:"1px solid #fca5a5",background:"#fef2f2",fontSize:12,fontWeight:600,cursor:"pointer",color:"#dc2626"}}>
                  🗑️ Löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const DuplicatesView = ({ customers, setCustomers, notify }) => {
  const [dupes, setDupes] = useState(() => {
    try { const s = localStorage.getItem("burn_dupes"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (dupes.length > 0) { setLoading(false); return; }
    fetch(`${API_URL}/duplicates`)
      .then(r => r.json())
      .then(data => {
        const d = data.duplicates || [];
        setDupes(d);
        try { localStorage.setItem("burn_dupes", JSON.stringify(d)); } catch {}
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleMerge = (groupId, primaryId, secondaryId) => {
    fetch(`${API_URL}/duplicates/merge`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ group_id: groupId, primary_id: primaryId, secondary_id: secondaryId }),
    })
      .then(r => {
        if (!r.ok) throw new Error(`API Fehler: ${r.status}`);
        return r.json();
      })
      .then(() => {
        const updated = dupes.filter(g => g.group_id !== groupId);
        setDupes(updated);
        try { localStorage.setItem("burn_dupes", JSON.stringify(updated)); } catch {}
        notify("✅ Duplikat zusammengeführt!");
      })
      .catch(err => {
        notify(`❌ Fehler: ${err.message} — bitte erneut versuchen`);
      });
  };

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Lade Duplikate von Mindbody...</div>;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:"#111827" }}>⚠️ Duplikate</div>
        <div style={{ fontSize:13, color:"#9ca3af", marginTop:4 }}>
          {dupes.length} mögliche Duplikate gefunden. Wähle den Haupt-Account — der andere wird als "Duplikat" markiert.
        </div>
      </div>

      {dupes.length === 0 ? (
        <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:12, padding:32, textAlign:"center" }}>
          <div style={{ fontSize:32 }}>✅</div>
          <div style={{ fontSize:16, fontWeight:700, color:"#15803d", marginTop:8 }}>Keine Duplikate gefunden!</div>
        </div>
      ) : dupes.map(group => (
        <div key={group.group_id} style={{ background:"#fff", border:"1px solid #fde68a", borderRadius:12, padding:20, marginBottom:16, boxShadow:"0 1px 4px #00000008" }}>
          <div style={{ fontSize:12, color:"#9ca3af", marginBottom:12 }}>Grund: <strong style={{color:"#d97706"}}>{group.reason}</strong></div>
          <div style={{ display:"flex", gap:12, marginBottom:16 }}>
            {group.clients.map(c => (
              <div key={c.id}
                onClick={() => setSelected(s => ({ ...s, [group.group_id]: c.id }))}
                style={{
                  flex:1, padding:16, borderRadius:8, cursor:"pointer", transition:"all 0.15s",
                  border: selected[group.group_id] === c.id ? "2px solid #7c3aed" : "2px solid #e5e7eb",
                  background: selected[group.group_id] === c.id ? "#f5f3ff" : "#f9fafb",
                }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#111827", marginBottom:4 }}>{c.name}</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:2 }}>{c.email}</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:6 }}>{c.phone}</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11, background:c.mbStatus==="Active"?"#dcfce7":"#f3f4f6", color:c.mbStatus==="Active"?"#15803d":"#6b7280", padding:"2px 8px", borderRadius:99 }}>{c.mbStatus}</span>
                  {c.joinDate && <span style={{ fontSize:11, background:"#e0e7ff", color:"#4338ca", padding:"2px 8px", borderRadius:99 }}>Seit {c.joinDate}</span>}
                  {c.lastVisit && <span style={{ fontSize:11, background:"#fef3c7", color:"#92400e", padding:"2px 8px", borderRadius:99 }}>Letzter Besuch: {c.lastVisit}</span>}
                  {c.visits > 0 && <span style={{ fontSize:11, background:"#f3f4f6", color:"#374151", padding:"2px 8px", borderRadius:99 }}>{c.visits} Besuche</span>}
                </div>
                {selected[group.group_id] === c.id && (
                  <div style={{ marginTop:8, fontSize:12, fontWeight:700, color:"#7c3aed" }}>✓ Haupt-Account</div>
                )}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button
              disabled={!selected[group.group_id]}
              onClick={() => {
                const primaryId = selected[group.group_id];
                const secondaryId = group.clients.find(c => c.id !== primaryId)?.id;
                if (secondaryId) handleMerge(group.group_id, primaryId, secondaryId);
              }}
              style={{
                background: selected[group.group_id] ? "#7c3aed" : "#e5e7eb",
                color: selected[group.group_id] ? "#fff" : "#9ca3af",
                border:"none", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:700,
                cursor: selected[group.group_id] ? "pointer" : "default",
              }}>
              Zusammenführen
            </button>
            <button
              onClick={() => setDupes(d => d.filter(g => g.group_id !== group.group_id))}
              style={{ background:"#f3f4f6", color:"#6b7280", border:"none", borderRadius:8, padding:"8px 20px", fontSize:13, cursor:"pointer" }}>
              Überspringen
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const JOURNEY_TEMPLATES = {
  fall1: {
    label: "Fall 1 — Event / Landing Page",
    color: "#7c3aed",
    steps: [
      { step: 1, name: "Event Bestätigung",       sender: "Julian", subject: "See you at BURN — here's everything you need to know" },
      { step: 2, name: "Event Reminder",           sender: "Julian", subject: "Tomorrow is your day — last things before BURN" },
      { step: 3, name: "Nach Event + Free Class",  sender: "Julian", subject: "You crushed it yesterday — here's your free class" },
      { step: 4, name: "Code läuft ab",            sender: "Julian", subject: "Last Chance — Free Class Expires Tonight" },
      { step: 5, name: "Einwand-Mail",             sender: "Nico",   subject: "A quick question" },
      { step: 6, name: "20% Rabatt Angebot",       sender: "Julian", subject: "20% Off — Your BURN Offer" },
      { step: 7, name: "10+2 Bundle",              sender: "Julian", subject: "10 Classes + 2 FREE" },
      { step: 8, name: "SMS Reminder",             sender: "SMS",    subject: "— wird über Bird gesendet —", sms: true },
      { step: 9, name: "Nach Rückkehr",            sender: "Julian", subject: "Welcome Back" },
    ]
  },
  fall2: {
    label: "Fall 2 — Mindbody App / Web",
    color: "#9900cc",
    steps: [
      { step: 1,  name: "Willkommen",              sender: "Julian", subject: "Welcome to BURN — let's get you started" },
      { step: 2,  name: "Vorbereitung",            sender: "Julian", subject: "You're all set — here's what to know" },
      { step: 3,  name: "Email 1 (Phase 1)",       sender: "Julian", subject: "Hey {name}, how was your first BURN session?", phase1: true },
      { step: 4,  name: "ROOTS Angebot",           sender: "Julian", subject: "5 Free Classes — Your ROOTS Offer" },
      { step: 5,  name: "20% Rabatt Alternative",  sender: "Julian", subject: "20% Off Any Package — 72 Hours Only" },
      { step: 6,  name: "Einwand-Mail",            sender: "Nico",   subject: "What would it take?" },
      { step: 7,  name: "10+2 Bundle",             sender: "Julian", subject: "10 Classes + 2 FREE" },
      { step: 8,  name: "Social Proof",            sender: "Julian", subject: "What BURN members say" },
      { step: 9,  name: "SMS Reminder",            sender: "SMS",    subject: "— wird über Bird gesendet —", sms: true },
      { step: 10, name: "Nach Kauf / Rückkehr",   sender: "Julian", subject: "Welcome to the Family" },
    ]
  }
};

const TemplatesView = () => {
  const [sending, setSending] = useState(null);
  const [testEmail, setTestEmail] = useState("sertac@burnbxng.com");
  const [sent, setSent] = useState({});

  const sendTest = (journey, step) => {
    const key = `${journey}_${step}`;
    setSending(key);
    fetch(`${API_URL}/debug/test-journey-email?journey=${journey}&step=${step}&to=${testEmail}`)
      .then(r => r.json())
      .then(data => {
        setSending(null);
        setSent(p => ({...p, [key]: data.ok ? "✅" : "❌"}));
      })
      .catch(() => { setSending(null); setSent(p => ({...p, [key]: "❌"})); });
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      <div style={{ padding:"16px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", gap:12, alignItems:"center" }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#111827" }}>📧 Journey Templates</div>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, color:"#6b7280" }}>Test-Mail an:</span>
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
            style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:"5px 10px", fontSize:12, outline:"none", width:220 }} />
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:20 }}>
        {Object.entries(JOURNEY_TEMPLATES).map(([journeyId, journey]) => (
          <div key={journeyId} style={{ marginBottom:32 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <div style={{ width:4, height:20, background:journey.color, borderRadius:2 }} />
              <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>{journey.label}</div>
              <span style={{ fontSize:12, color:"#6b7280" }}>{journey.steps.length} Steps</span>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {journey.steps.map(s => {
                const key = `${journeyId}_${s.step}`;
                const isSending = sending === key;
                const sentStatus = sent[key];
                return (
                  <div key={s.step} style={{
                    background: s.sms ? "#f9fafb" : s.phase1 ? "#fef9c3" : "#fff",
                    border: `1px solid ${s.sms ? "#e5e7eb" : s.phase1 ? "#fde68a" : "#e5e7eb"}`,
                    borderRadius:10, padding:"12px 16px",
                    display:"flex", alignItems:"center", gap:12,
                    opacity: s.sms ? 0.6 : 1
                  }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", background: journey.color + "20",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:11, fontWeight:700, color:journey.color, flexShrink:0 }}>
                      {s.step}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827", marginBottom:2 }}>{s.name}</div>
                      <div style={{ fontSize:12, color:"#6b7280" }}>{s.subject}</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:99,
                        background: s.sender === "Nico" ? "#fef3c7" : s.sender === "SMS" ? "#f3f4f6" : "#f5f3ff",
                        color: s.sender === "Nico" ? "#92400e" : s.sender === "SMS" ? "#6b7280" : "#6d28d9",
                        fontWeight:600 }}>{s.sender}</span>
                      {sentStatus && <span style={{ fontSize:13 }}>{sentStatus}</span>}
                      {!s.sms && !s.phase1 && (
                        <button onClick={() => sendTest(journeyId, s.step)} disabled={isSending}
                          style={{ padding:"5px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                            border:"1px solid #9900cc", background: isSending ? "#f5f3ff" : "#fff",
                            color:"#9900cc", cursor: isSending ? "wait" : "pointer" }}>
                          {isSending ? "..." : "Test senden"}
                        </button>
                      )}
                      {s.phase1 && <span style={{ fontSize:11, color:"#92400e", background:"#fef3c7", padding:"3px 8px", borderRadius:99 }}>Phase 1</span>}
                      {s.sms && <span style={{ fontSize:11, color:"#6b7280" }}>Bird (ausstehend)</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Abmeldungen (Unsubscribe List) ────────────────────────────────────────────
const UnsubscribeListView = () => {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/unsubscribe/list`)
      .then(r => r.json())
      .then(d => { setList(d.unsubscribed || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <BurnLoader text="Lade Abmeldungen..." />;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ maxWidth:700, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:"#111827" }}>🚫 Abgemeldete E-Mails</div>
            <div style={{ fontSize:13, color:"#6b7280", marginTop:4 }}>
              Personen die auf "Abmelden" geklickt haben — erhalten keine weiteren E-Mails.
            </div>
          </div>
          <div style={{ background:"#fee2e2", color:"#dc2626", fontWeight:700, fontSize:15, borderRadius:10, padding:"8px 18px" }}>
            {list.length} abgemeldet
          </div>
        </div>

        {list.length === 0 ? (
          <div style={{ textAlign:"center", padding:48, color:"#9ca3af", fontSize:14 }}>
            Noch niemand abgemeldet ✅
          </div>
        ) : (
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 180px", background:"#f9fafb", borderBottom:"1px solid #e5e7eb", padding:"10px 16px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:1 }}>E-Mail</div>
              <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:1 }}>Datum</div>
            </div>
            {list.map((item, i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 180px", padding:"12px 16px", borderBottom: i < list.length-1 ? "1px solid #f3f4f6" : "none", background: i%2===0?"#fff":"#fafafa", alignItems:"center" }}>
                <div style={{ fontSize:13, color:"#111827", fontWeight:500 }}>{item.email}</div>
                <div style={{ fontSize:12, color:"#6b7280" }}>
                  {item.date ? new Date(item.date).toLocaleDateString("de-DE", {day:"2-digit",month:"2-digit",year:"numeric"}) : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const JourneyTemplateEditorView = () => {
  const API = "https://burn-firsttimer-bot-production.up.railway.app";
  const FALL1_STEPS = [
    { step:1,  name:"Event Confirmation",          defaultSubject:"See you at BURN — here's everything you need to know", sender:"julian" },
    { step:2,  name:"Event Reminder (Tag vorher)", defaultSubject:"Tomorrow is your day — last things before BURN 💪",   sender:"julian" },
    { step:3,  name:"Post Event Thanks",           defaultSubject:"You crushed it yesterday — here's your free class 🎁",sender:"julian" },
    { step:4,  name:"Code Expiry Reminder",        defaultSubject:"Your free class expires soon — book now ⏰",          sender:"julian" },
    { step:5,  name:"Einwand / Rückfrage",         defaultSubject:"Quick question from us",                              sender:"nico"   },
    { step:6,  name:"20% Rabatt Angebot",          defaultSubject:"20% off — your next step at BURN 🔥",                sender:"julian" },
    { step:7,  name:"Bundle Angebot",              defaultSubject:"10 classes + 2 FREE — our best deal 🎯",             sender:"julian" },
    { step:8,  name:"SMS Reminder",                defaultSubject:"SMS",                                                 sender:"sms"   },
    { step:9,  name:"Post Return (Willkommen zurück)", defaultSubject:"Welcome back — here's your personal offer 🙌",   sender:"julian" },
  ];
  const FALL2_STEPS = [
    { step:1,  name:"Welcome Registration",        defaultSubject:"Welcome to BURN — let's get you started",             sender:"julian" },
    { step:2,  name:"Pre-Class Prep",              defaultSubject:"Your first BURN class is coming up — here's what to know 💪", sender:"julian" },
    { step:3,  name:"Post First Visit",            defaultSubject:"How was your first BURN? 🔥",                         sender:"julian" },
    { step:4,  name:"ROOTS Angebot",               defaultSubject:"Your special offer — 5 free classes with ROOTS 🎁",   sender:"julian" },
    { step:5,  name:"20% Rabatt Alternative",      defaultSubject:"20% off any class package — 72 hours only ⏰",        sender:"julian" },
    { step:6,  name:"Einwand / Was würde es brauchen?", defaultSubject:"What would it take?",                           sender:"nico"   },
    { step:7,  name:"Bundle Angebot",              defaultSubject:"10 classes + 2 FREE — still here for you 🎯",         sender:"julian" },
    { step:8,  name:"Social Proof",                defaultSubject:"What BURN members say — and one last offer 💬",       sender:"julian" },
    { step:9,  name:"SMS Reminder",                defaultSubject:"SMS",                                                 sender:"sms"   },
    { step:10, name:"Post Purchase",               defaultSubject:"You're part of the family now 🙌",                    sender:"julian" },
  ];

  const [selectedJourney, setSelectedJourney] = useState("fall1");
  const [selectedStep, setSelectedStep] = useState(null);
  const [subject, setSubject] = useState("");
  const [sender, setSender] = useState("julian");
  const [customTemplates, setCustomTemplates] = useState({});
  const [unlayerReady, setUnlayerReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const editorRef = useRef(null);
  const editorInitialized = useRef(false);

  useEffect(() => {
    fetch(`${API}/journey-templates`).then(r=>r.json()).then(d=>{
      const map = {};
      (d.templates||[]).forEach(t => { map[`${t.journey_type}_${t.step}`] = t; });
      setCustomTemplates(map);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (window.unlayer) { setUnlayerReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://editor.unlayer.com/embed.js';
    s.async = true;
    s.onload = () => setUnlayerReady(true);
    document.head.appendChild(s);
  }, []);

  const loadStepContent = (journey, step, templates) => {
    const key = `${journey}_${step.step}`;
    const existing = templates[key];
    if (existing && existing.design_json && existing.design_json !== '{}') {
      try { window.unlayer.loadDesign(JSON.parse(existing.design_json)); return; } catch(e) {}
    }
    if (existing && existing.html_body) {
      window.unlayer.loadHtml(existing.html_body);
    }
    // No saved template → editor stays blank for new creation
  };

  const showPreview = () => {
    if (!selectedStep) return;
    setPreviewLoading(true);
    fetch(`${API}/journey-templates/${selectedJourney}/${selectedStep.step}/default`)
      .then(r => r.json())
      .then(d => { if (d.html_body) setPreviewHtml(d.html_body); })
      .catch(() => {})
      .finally(() => setPreviewLoading(false));
  };

  useEffect(() => {
    if (!unlayerReady || !selectedStep || !editorRef.current) return;
    const journey = selectedJourney;
    const step = selectedStep;
    const templates = customTemplates;

    if (!editorInitialized.current) {
      editorInitialized.current = true;
      window.unlayer.init({
        id: 'unlayer-editor',
        displayMode: 'email',
        appearance: { theme: 'modern_light' },
        features: { textEditor: { spellChecker: true } },
      });
      setTimeout(() => loadStepContent(journey, step, templates), 1500);
    } else {
      loadStepContent(journey, step, templates);
    }
  }, [unlayerReady, selectedStep, selectedJourney]);

  const selectStep = (step) => {
    if (step.sender === "sms") return;
    setSelectedStep(step);
    const key = `${selectedJourney}_${step.step}`;
    const existing = customTemplates[key];
    setSubject(existing ? existing.subject : step.defaultSubject);
    setSender(existing ? (existing.sender || step.sender) : step.sender);
    setSaveMsg("");
  };

  const handleSave = () => {
    if (!selectedStep || !window.unlayer || !editorInitialized.current) return;
    setSaving(true);
    window.unlayer.exportHtml((data) => {
      const { design, html } = data;
      fetch(`${API}/journey-templates`, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          journey_type: selectedJourney,
          step: selectedStep.step,
          subject, sender,
          design_json: JSON.stringify(design),
          html_body: html,
        })
      }).then(r=>r.json()).then(()=>{
        setCustomTemplates(prev => ({
          ...prev,
          [`${selectedJourney}_${selectedStep.step}`]: {
            journey_type: selectedJourney, step: selectedStep.step,
            subject, sender, design_json: JSON.stringify(design), html_body: html,
          }
        }));
        setSaveMsg("✓ Gespeichert");
        setTimeout(()=>setSaveMsg(""), 3000);
      }).catch(()=>setSaveMsg("✗ Fehler")).finally(()=>setSaving(false));
    });
  };

  const handleReset = () => {
    if (!selectedStep) return;
    if (!window.confirm(`Step ${selectedStep.step} zurücksetzen auf Standard-Text?`)) return;
    fetch(`${API}/journey-templates/${selectedJourney}/${selectedStep.step}`, {method:'DELETE'})
      .then(()=>{
        setCustomTemplates(prev => { const n={...prev}; delete n[`${selectedJourney}_${selectedStep.step}`]; return n; });
        setSubject(selectedStep.defaultSubject);
        setSaveMsg("Zurückgesetzt — Bot verwendet wieder Standard-Text");
        // Reload the original hardcoded email into editor
        setLoadingDefault(true);
        fetch(`${API}/journey-templates/${selectedJourney}/${selectedStep.step}/default`)
          .then(r => r.json())
          .then(d => {
            if (d.html_body && window.unlayer && editorInitialized.current) window.unlayer.loadHtml(d.html_body);
          })
          .catch(() => {})
          .finally(() => setLoadingDefault(false));
      });
  };

  const steps = selectedJourney === "fall1" ? FALL1_STEPS : FALL2_STEPS;

  return (
    <div style={{display:"flex", height:"calc(100vh - 60px)", overflow:"hidden", fontFamily:"system-ui,sans-serif"}}>
      {/* Sidebar */}
      <div style={{width:270, borderRight:"1px solid #e5e7eb", overflowY:"auto", background:"#f9fafb", flexShrink:0}}>
        <div style={{padding:16}}>
          <div style={{fontSize:16, fontWeight:700, color:"#111", marginBottom:12}}>Journey E-Mail Editor</div>
          <div style={{display:"flex", gap:6, marginBottom:14}}>
            {["fall1","fall2"].map(j=>(
              <button key={j} onClick={()=>{setSelectedJourney(j);setSelectedStep(null);setSaveMsg("");}}
                style={{flex:1, padding:"7px 0", borderRadius:8, border:"none",
                  background:selectedJourney===j?"#9900cc":"#e5e7eb",
                  color:selectedJourney===j?"#fff":"#374151",
                  fontWeight:600, fontSize:13, cursor:"pointer"}}>
                {j==="fall1" ? "Fall 1" : "Fall 2"}
              </button>
            ))}
          </div>
          <div style={{fontSize:11, color:"#6b7280", marginBottom:10}}>
            {selectedJourney==="fall1" ? "First-Timer mit gebuchtem Kurs" : "First-Timer ohne Buchung"}
          </div>
          {steps.map(s=>{
            const key = `${selectedJourney}_${s.step}`;
            const isCustom = !!customTemplates[key];
            const isSelected = selectedStep?.step === s.step;
            const isSms = s.sender === "sms";
            return (
              <div key={s.step} onClick={()=>selectStep(s)}
                style={{padding:"9px 12px", borderRadius:8, marginBottom:4,
                  background:isSelected?"#f3e8ff":"#fff",
                  border:isSelected?"1.5px solid #9900cc":"1px solid #e5e7eb",
                  cursor:isSms?"not-allowed":"pointer", opacity:isSms?0.5:1,
                  transition:"all 0.1s"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <span style={{fontWeight:600, fontSize:13}}>Step {s.step}</span>
                  {isCustom && <span style={{fontSize:10, background:"#d1fae5", color:"#065f46", padding:"2px 7px", borderRadius:4, fontWeight:700}}>Aktiv</span>}
                  {!isCustom && !isSms && <span style={{fontSize:10, background:"#fef3c7", color:"#92400e", padding:"2px 7px", borderRadius:4}}>Standard</span>}
                  {isSms && <span style={{fontSize:10, background:"#f3f4f6", color:"#6b7280", padding:"2px 7px", borderRadius:4}}>SMS</span>}
                </div>
                <div style={{fontSize:11, color:"#6b7280", marginTop:2}}>{s.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main */}
      {!selectedStep ? (
        <div style={{flex:1, display:"flex", alignItems:"center", justifyContent:"center"}}>
          <div style={{textAlign:"center", color:"#9ca3af"}}>
            <div style={{fontSize:48, marginBottom:12}}>✉️</div>
            <div style={{fontSize:16, fontWeight:600, color:"#374151"}}>E-Mail Step auswählen</div>
            <div style={{fontSize:13, marginTop:6}}>Wähle links einen Step aus um ihn zu bearbeiten</div>
          </div>
        </div>
      ) : (
        <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>
          {/* Toolbar */}
          <div style={{padding:"10px 16px", borderBottom:"1px solid #e5e7eb", background:"#fff", display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap"}}>
            <div style={{flex:1, minWidth:200}}>
              <div style={{fontSize:11, color:"#6b7280", marginBottom:3}}>Betreff</div>
              <input value={subject} onChange={e=>setSubject(e.target.value)}
                placeholder="E-Mail Betreff..."
                style={{width:"100%", padding:"7px 11px", borderRadius:7, border:"1px solid #d1d5db", fontSize:14, boxSizing:"border-box"}} />
            </div>
            <div>
              <div style={{fontSize:11, color:"#6b7280", marginBottom:3}}>Absender</div>
              <select value={sender} onChange={e=>setSender(e.target.value)}
                style={{padding:"7px 11px", borderRadius:7, border:"1px solid #d1d5db", fontSize:14}}>
                <option value="julian">Julian (julian@burnbxng.com)</option>
                <option value="nico">Nico (nico@burnbxng.com)</option>
              </select>
            </div>
            <div style={{display:"flex", gap:8, alignItems:"center"}}>
              {saveMsg && <span style={{fontSize:12, color: saveMsg.startsWith("✓") ? "#16a34a" : "#dc2626"}}>{saveMsg}</span>}
              <button onClick={async () => {
                if (!selectedStep) return;
                setAiGenLoading(true);
                try {
                  const r = await fetch(`${API}/staff/ai/generate-email`, {
                    method:"POST", headers:{"Content-Type":"application/json"},
                    body: JSON.stringify({
                      step_name: selectedStep.name,
                      step_number: selectedStep.step,
                      journey_type: selectedJourney,
                      subject: subject || selectedStep.defaultSubject,
                      tone: "motivierend, herzlich, direkt",
                      language: "English",
                    })
                  });
                  const d = await r.json();
                  if (d.html_body && window.unlayer) {
                    window.unlayer.loadHtml(d.html_body);
                    if (d.subject) setSubject(d.subject);
                    setSaveMsg("🤖 AI-Text geladen");
                  }
                } catch(e) { setSaveMsg("AI-Fehler: " + e.message); }
                setAiGenLoading(false);
              }} disabled={aiGenLoading}
                style={{padding:"7px 13px", borderRadius:7, border:"1px solid #d8b4fe", background:aiGenLoading?"#f3f4f6":"#faf5ff", fontSize:13, cursor:"pointer", color:"#7c3aed", fontWeight:600}}>
                {aiGenLoading ? "⏳..." : "🤖 AI Generieren"}
              </button>
              <button onClick={showPreview} disabled={previewLoading}
                style={{padding:"7px 13px", borderRadius:7, border:"1px solid #d1d5db", background:"#fff", fontSize:13, cursor:"pointer", color:"#374151"}}>
                {previewLoading ? "…" : "👁 Aktuelle E-Mail"}
              </button>
              {customTemplates[`${selectedJourney}_${selectedStep.step}`] && (
                <button onClick={handleReset}
                  style={{padding:"7px 13px", borderRadius:7, border:"1px solid #fca5a5", background:"#fff", fontSize:13, cursor:"pointer", color:"#dc2626"}}>
                  Zurücksetzen
                </button>
              )}
              <button onClick={handleSave} disabled={saving}
                style={{padding:"7px 18px", borderRadius:7, border:"none", background:saving?"#d8b4fe":"#9900cc",
                  color:"#fff", fontWeight:600, fontSize:13, cursor:saving?"wait":"pointer"}}>
                {saving ? "Speichert…" : "Speichern & Aktivieren"}
              </button>
            </div>
          </div>
          {/* Platzhalter-Hinweis */}
          <div style={{padding:"6px 16px", background:"#eff6ff", borderBottom:"1px solid #bfdbfe", fontSize:11, color:"#1d4ed8"}}>
            Platzhalter: <code style={{background:"#dbeafe", padding:"1px 5px", borderRadius:3, fontSize:11}}>{"{first_name}"}</code>
            &nbsp;&nbsp;Werden beim Versand automatisch ersetzt.
          </div>
          {/* Unlayer editor */}
          <div id="unlayer-editor" ref={editorRef} style={{flex:1, minHeight:0}} />
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center"}}
          onClick={()=>setPreviewHtml(null)}>
          <div style={{background:"#fff", borderRadius:12, width:"680px", maxHeight:"90vh",
            display:"flex", flexDirection:"column", overflow:"hidden"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:"12px 16px", borderBottom:"1px solid #e5e7eb", display:"flex",
              justifyContent:"space-between", alignItems:"center"}}>
              <span style={{fontWeight:700, fontSize:14}}>
                Aktuelle E-Mail — {selectedStep?.name} (Step {selectedStep?.step})
              </span>
              <button onClick={()=>setPreviewHtml(null)}
                style={{border:"none", background:"none", fontSize:20, cursor:"pointer", color:"#6b7280"}}>✕</button>
            </div>
            <iframe srcDoc={previewHtml} style={{flex:1, border:"none", minHeight:"500px"}}
              title="E-Mail Vorschau" />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Email Block CMS ─────────────────────────────────────────────
// MVP: Fall 1 Step 1 + Step 2. Modular — weitere Steps über Schema erweiterbar.
const BLOCK_STEPS = [
  // ── Automatische System-Mails (read-only) ────────────────
  { key: "email1",       label: "📧 Email 1",       sublabel: "Post-First-Visit + Trainer",     readonly: true, previewKey: "email1" },
  { key: "email2",       label: "📧 Email 2",       sublabel: "Follow-up (lapsed)",              readonly: true, previewKey: "email2" },
  { key: "email3",       label: "📧 Email 3",       sublabel: "Last Chance (lapsed)",            readonly: true, previewKey: "email3" },
  { key: "milestone_5",  label: "🏅 Milestone 5",  sublabel: "5 Classes",                       readonly: true, previewKey: "milestone_5" },
  { key: "milestone_10", label: "🏅 Milestone 10", sublabel: "10 Classes",                      readonly: true, previewKey: "milestone_10" },
  { key: "milestone_25", label: "🏅 Milestone 25", sublabel: "25 Classes",                      readonly: true, previewKey: "milestone_25" },
  { key: "milestone_50", label: "🏅 Milestone 50", sublabel: "50 Classes",                      readonly: true, previewKey: "milestone_50" },
  { key: "milestone_100",label: "🏅 Milestone 100",sublabel: "100 Classes",                     readonly: true, previewKey: "milestone_100" },
  // ── Fall 1 (editierbar) ───────────────────────────────────
  { key: "fall1_1", label: "Fall 1 — Step 1", sublabel: "Event Confirmation" },
  { key: "fall1_2", label: "Fall 1 — Step 2", sublabel: "Event Reminder" },
  { key: "fall1_3", label: "Fall 1 — Step 3", sublabel: "Post-Event + Free Class" },
  { key: "fall1_4", label: "Fall 1 — Step 4", sublabel: "Code Expiry Reminder" },
  { key: "fall1_5", label: "Fall 1 — Step 5", sublabel: "Nico Objection" },
  { key: "fall1_6", label: "Fall 1 — Step 6", sublabel: "20% Discount Offer" },
  { key: "fall1_7", label: "Fall 1 — Step 7", sublabel: "10+2 Bundle" },
  { key: "fall1_9", label: "Fall 1 — Step 9", sublabel: "Post-Return Offer" },
  // ── Fall 2 (editierbar) ───────────────────────────────────
  { key: "fall2_1", label: "Fall 2 — Step 1", sublabel: "Welcome Registration" },
  { key: "fall2_2", label: "Fall 2 — Step 2", sublabel: "Pre-Class Prep" },
  { key: "fall2_4", label: "Fall 2 — Step 4", sublabel: "ROOTS Offer" },
  { key: "fall2_5", label: "Fall 2 — Step 5", sublabel: "20% Discount" },
  { key: "fall2_6", label: "Fall 2 — Step 6", sublabel: "Nico Objection" },
  { key: "fall2_7", label: "Fall 2 — Step 7", sublabel: "10+2 Bundle" },
  { key: "fall2_8", label: "Fall 2 — Step 8", sublabel: "Social Proof" },
  { key: "fall2_10", label: "Fall 2 — Step 10", sublabel: "Post-Purchase" },
];

const EmailBlockEditorView = () => {
  const API = "https://burn-firsttimer-bot-production.up.railway.app";
  const [activeStep, setActiveStep]   = useState("email1");
  const [schema,     setSchema]       = useState(null);
  const [blocks,     setBlocks]       = useState({});
  const [dirty,      setDirty]        = useState({});   // nur geänderte Felder
  const [saving,     setSaving]       = useState(false);
  const [saveMsg,    setSaveMsg]      = useState("");
  const [customKeys, setCustomKeys]   = useState(new Set()); // welche Felder custom (≠ default) sind
  const iframeRef = useRef(null);
  const debounceRef = useRef(null);

  // Aktueller Step-Eintrag aus BLOCK_STEPS
  const activeStepMeta = BLOCK_STEPS.find(s => s.key === activeStep) || {};
  const isReadonly = !!activeStepMeta.readonly;

  // Lade Schema + aktuelle Werte wenn Step wechselt (nur für editierbare Steps)
  useEffect(() => {
    setSchema(null); setBlocks({}); setDirty({}); setSaveMsg("");
    if (isReadonly) return;  // readonly: kein API-Call nötig
    const [jt, st] = activeStep.split("_");
    fetch(`${API}/email-blocks/${jt}/${st}`)
      .then(r => r.json())
      .then(d => {
        setSchema(d.schema);
        setBlocks(d.blocks || {});
        const custom = new Set();
        (d.schema?.fields || []).forEach(f => {
          if ((d.blocks || {})[f.name] !== f.default) custom.add(f.name);
        });
        setCustomKeys(custom);
      })
      .catch(() => {});
  }, [activeStep]);

  // Live Preview aktualisieren (debounced 500ms, nur für editierbare Steps)
  const refreshPreview = (currentBlocks) => {
    if (isReadonly) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!iframeRef.current) return;
      const [jt, st] = activeStep.split("_");
      const params = new URLSearchParams({ first_name: "Max", event_date: "morgen" });
      iframeRef.current.src = `${API}/email-blocks/${jt}/${st}/preview?${params}&_t=${Date.now()}`;
    }, 500);
  };

  const handleChange = (name, value) => {
    const updated = { ...blocks, [name]: value };
    setBlocks(updated);
    setDirty(prev => ({ ...prev, [name]: value }));
    setSaveMsg("");
    refreshPreview(updated);
  };

  const handleSave = async () => {
    if (!Object.keys(dirty).length) return;
    setSaving(true);
    const [jt, st] = activeStep.split("_");
    try {
      await fetch(`${API}/email-blocks/${jt}/${st}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocks: dirty }),
      });
      // Aktualisiere customKeys
      setCustomKeys(prev => {
        const next = new Set(prev);
        Object.keys(dirty).forEach(k => next.add(k));
        return next;
      });
      setDirty({});
      setSaveMsg("✓ Gespeichert — wird beim nächsten Bot-Run verwendet");
      setTimeout(() => setSaveMsg(""), 4000);
    } catch {
      setSaveMsg("✗ Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Alle Änderungen für diesen Step zurücksetzen?")) return;
    const [jt, st] = activeStep.split("_");
    await fetch(`${API}/email-blocks/${jt}/${st}`, { method: "DELETE" });
    // Defaults neu laden
    const defaultBlocks = {};
    (schema?.fields || []).forEach(f => { defaultBlocks[f.name] = f.default; });
    setBlocks(defaultBlocks);
    setDirty({});
    setCustomKeys(new Set());
    setSaveMsg("Zurückgesetzt auf Standard-Text");
    setTimeout(() => setSaveMsg(""), 3000);
    if (iframeRef.current) {
      const [jt2, st2] = activeStep.split("_");
      iframeRef.current.src = `${API}/email-blocks/${jt2}/${st2}/preview?first_name=Max&event_date=morgen&_t=${Date.now()}`;
    }
  };

  // Preview-URL je nach Typ
  const previewSrc = isReadonly
    ? `${API}/email-preview/${activeStepMeta.previewKey}?first_name=Max&trainer=Julian&class_name=BURN+Full+Body&_t=${activeStep}`
    : (() => { const [jt0, st0] = activeStep.split("_"); return `${API}/email-blocks/${jt0}/${st0}/preview?first_name=Max&event_date=morgen`; })();

  // Sidebar-Gruppen
  const sysSteps = BLOCK_STEPS.filter(s => s.readonly);
  const f1Steps  = BLOCK_STEPS.filter(s => !s.readonly && s.key.startsWith("fall1"));
  const f2Steps  = BLOCK_STEPS.filter(s => !s.readonly && s.key.startsWith("fall2"));

  const StepItem = ({ s }) => (
    <div onClick={() => setActiveStep(s.key)}
      style={{padding:"8px 10px", borderRadius:8, marginBottom:3, cursor:"pointer",
        background: activeStep === s.key ? "#f3e8ff" : "#fff",
        border: activeStep === s.key ? "1.5px solid #9900cc" : "1px solid #e5e7eb",
        opacity: s.readonly ? 0.85 : 1}}>
      <div style={{fontSize:12, fontWeight:600, color:"#111"}}>{s.label}</div>
      <div style={{fontSize:11, color:"#6b7280", marginTop:1}}>{s.sublabel}</div>
    </div>
  );

  return (
    <div style={{display:"flex", height:"calc(100vh - 60px)", overflow:"hidden", fontFamily:"system-ui,sans-serif"}}>

      {/* Linke Sidebar — Step-Auswahl */}
      <div style={{width:220, borderRight:"1px solid #e5e7eb", background:"#f9fafb",
        flexShrink:0, overflowY:"auto", padding:"12px 10px"}}>

        <div style={{fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase",
          letterSpacing:"0.06em", marginBottom:6, padding:"0 2px"}}>Automatisch versendet</div>
        {sysSteps.map(s => <StepItem key={s.key} s={s} />)}

        <div style={{fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase",
          letterSpacing:"0.06em", margin:"14px 0 6px", padding:"0 2px"}}>Fall 1 — First-Timer</div>
        {f1Steps.map(s => <StepItem key={s.key} s={s} />)}

        <div style={{fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase",
          letterSpacing:"0.06em", margin:"14px 0 6px", padding:"0 2px"}}>Fall 2 — Direktregistrierung</div>
        {f2Steps.map(s => <StepItem key={s.key} s={s} />)}

        {!isReadonly && (
          <div style={{marginTop:16, padding:"10px 12px", background:"#eff6ff",
            borderRadius:8, fontSize:11, color:"#1d4ed8", lineHeight:1.5}}>
            <strong>Platzhalter:</strong><br/>
            <code style={{background:"#dbeafe",padding:"1px 4px",borderRadius:3}}>{"{first_name}"}</code> — Name<br/>
            <code style={{background:"#dbeafe",padding:"1px 4px",borderRadius:3}}>{"{event_date}"}</code> — Datum
          </div>
        )}
      </div>

      {/* Mitte — Info oder Formularfelder */}
      <div style={{width:300, borderRight:"1px solid #e5e7eb", overflowY:"auto",
        background:"#fff", flexShrink:0, display:"flex", flexDirection:"column"}}>

        {isReadonly ? (
          /* ── Read-only Info-Panel ── */
          <div style={{padding:20, flex:1}}>
            <div style={{fontSize:14, fontWeight:700, color:"#111", marginBottom:8}}>
              {activeStepMeta.label}
            </div>
            <div style={{fontSize:12, color:"#6b7280", marginBottom:20}}>{activeStepMeta.sublabel}</div>
            <div style={{background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10,
              padding:"14px 16px", fontSize:12, color:"#166534", lineHeight:1.7, marginBottom:16}}>
              <strong>✅ Wird automatisch versendet</strong><br/>
              Diese E-Mail geht raus sobald das entsprechende Ereignis eintritt — kein manuelles Auslösen nötig.
            </div>
            <div style={{background:"#fafafa", border:"1px solid #e5e7eb", borderRadius:10,
              padding:"14px 16px", fontSize:12, color:"#374151", lineHeight:1.7}}>
              <strong>ℹ️ Warum nicht editierbar?</strong><br/>
              {activeStepMeta.key.startsWith("email") ? (
                <>Diese Mail enthält <strong>Live-Daten</strong> aus Mindbody (Trainer-Name, Klassen-Name, Datum/Uhrzeit) — diese werden zur Sendezeit dynamisch eingefügt und können nicht vorher festgelegt werden.</>
              ) : (
                <>Diese Mail enthält den <strong>Vornahmen</strong> und eine personalisierte Nachricht je nach Milestone — der Text wird automatisch generiert.</>
              )}
            </div>
            <div style={{marginTop:20, background:"#fffbeb", border:"1px solid #fde68a",
              borderRadius:10, padding:"12px 16px", fontSize:11, color:"#92400e", lineHeight:1.6}}>
              <strong>Vorschau:</strong> Mit Beispieldaten<br/>
              {activeStepMeta.key.startsWith("email") ? "Trainer: Julian • Klasse: BURN Full Body" : ""}
            </div>
          </div>
        ) : !schema ? (
          <div style={{flex:1, display:"flex", alignItems:"center", justifyContent:"center",
            color:"#9ca3af", fontSize:13}}>Laden…</div>
        ) : (
          <>
            <div style={{padding:"14px 16px", borderBottom:"1px solid #e5e7eb"}}>
              <div style={{fontSize:14, fontWeight:700, color:"#111"}}>{schema.title}</div>
              <div style={{fontSize:11, color:"#6b7280", marginTop:2}}>
                {customKeys.size > 0
                  ? <span style={{color:"#9900cc", fontWeight:600}}>✏️ {customKeys.size} Feld(er) angepasst</span>
                  : "Standard-Texte aktiv"}
              </div>
            </div>
            <div style={{padding:"12px 16px", flex:1, overflowY:"auto"}}>
              {schema.fields.map(field => (
                <div key={field.name} style={{marginBottom:16}}>
                  <label style={{fontSize:11, fontWeight:600, color:"#374151", display:"block",
                    marginBottom:4}}>
                    {field.label}
                    {customKeys.has(field.name) &&
                      <span style={{marginLeft:6, fontSize:10, background:"#f3e8ff",
                        color:"#7e22ce", padding:"1px 6px", borderRadius:4}}>angepasst</span>}
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={blocks[field.name] || ""}
                      onChange={e => handleChange(field.name, e.target.value)}
                      rows={3}
                      style={{width:"100%", padding:"7px 10px", borderRadius:7, fontSize:13,
                        border:"1px solid #d1d5db", resize:"vertical", boxSizing:"border-box",
                        fontFamily:"system-ui,sans-serif", lineHeight:1.5}} />
                  ) : (
                    <input
                      type="text"
                      value={blocks[field.name] || ""}
                      onChange={e => handleChange(field.name, e.target.value)}
                      style={{width:"100%", padding:"7px 10px", borderRadius:7, fontSize:13,
                        border:"1px solid #d1d5db", boxSizing:"border-box"}} />
                  )}
                </div>
              ))}
            </div>
            <div style={{padding:"12px 16px", borderTop:"1px solid #e5e7eb",
              display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
              {saveMsg && (
                <span style={{fontSize:11, color: saveMsg.startsWith("✓") ? "#16a34a" : saveMsg.startsWith("✗") ? "#dc2626" : "#6b7280",
                  flex:"1 1 100%"}}>
                  {saveMsg}
                </span>
              )}
              {customKeys.size > 0 && (
                <button onClick={handleReset}
                  style={{padding:"7px 12px", borderRadius:7, border:"1px solid #fca5a5",
                    background:"#fff", fontSize:12, cursor:"pointer", color:"#dc2626"}}>
                  Zurücksetzen
                </button>
              )}
              <button onClick={handleSave} disabled={saving || !Object.keys(dirty).length}
                style={{flex:1, padding:"8px 0", borderRadius:7, border:"none", fontSize:13,
                  fontWeight:600, cursor: (!saving && Object.keys(dirty).length) ? "pointer" : "default",
                  background: Object.keys(dirty).length ? "#9900cc" : "#e5e7eb",
                  color: Object.keys(dirty).length ? "#fff" : "#9ca3af"}}>
                {saving ? "Speichert…" : "Speichern"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Rechts — Live Preview */}
      <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>
        <div style={{padding:"10px 16px", borderBottom:"1px solid #e5e7eb", background:"#fff",
          display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <span style={{fontSize:13, fontWeight:600, color:"#374151"}}>👁 Vorschau</span>
          <span style={{fontSize:11, color:"#9ca3af"}}>
            {isReadonly ? "Beispieldaten: Max • Julian • BURN Full Body" : "Vorschau-Name: Max • Datum: morgen"}
          </span>
        </div>
        <iframe ref={iframeRef} src={previewSrc}
          style={{flex:1, border:"none"}} title="E-Mail Vorschau" />
      </div>
    </div>
  );
};

const JourneysView = ({ notify }) => {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");

  useEffect(() => {
    fetch(`${API_URL}/journeys`)
      .then(r => r.json())
      .then(data => { setJourneys(data.journeys || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const stopJourney = (clientId, journey) => {
    if (!confirm("Journey wirklich stoppen? Die Person bekommt keine weiteren Mails.")) return;
    fetch(`${API_URL}/journeys/stop`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({ client_id: clientId, journey })
    }).then(r => {
      if (!r.ok) throw new Error("API Fehler");
      setJourneys(p => p.map(j => j.client_id === clientId && j.journey === journey ? {...j, status: "stopped", journey_active: false} : j));
      notify("✅ Journey gestoppt!");
    }).catch(() => {
      notify("❌ Fehler beim Stoppen — bitte erneut versuchen");
    });
  };

  const filtered = journeys.filter(j => filter === "all" ? true : j.status === filter);

  const stageColor = (stage) => {
    const map = { first_timer:"#9900cc", returning:"#2563eb", member:"#059669", lead:"#6b7280", inactive:"#9ca3af" };
    return map[stage] || "#6b7280";
  };

  const statusBadge = (status) => {
    const map = { active:["#dcfce7","#15803d"], completed:["#e0e7ff","#4338ca"], stopped:["#fee2e2","#dc2626"] };
    const [bg, color] = map[status] || ["#f3f4f6","#6b7280"];
    return <span style={{ background:bg, color, fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:99 }}>{status}</span>;
  };

  const stepLabel = (journey, step) => {
    const labels = {
      fall2: { 1:"Willkommen", 2:"Vorbereitung", 4:"ROOTS Angebot", 5:"20% Rabatt", 6:"Einwand (Nico)", 7:"10+2 Bundle", 8:"Social Proof", 10:"Nach Kauf" },
      fall1: { 1:"Event Bestätigung", 2:"Event Reminder", 3:"Nach Event", 4:"Code läuft ab", 5:"Einwand (Nico)", 6:"20% Rabatt", 7:"10+2 Bundle", 9:"Nach Rückkehr" }
    };
    return labels[journey]?.[step] || `Step ${step}`;
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      <div style={{ padding:"16px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", gap:12, alignItems:"center" }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#111827" }}>🗺️ Journeys</div>
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          {["active","completed","stopped","all"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding:"5px 14px", borderRadius:99, fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                background: filter===f ? "#9900cc" : "#f3f4f6",
                color: filter===f ? "#fff" : "#374151" }}>
              {f === "all" ? "Alle" : f === "active" ? "Aktiv" : f === "completed" ? "Abgeschlossen" : "Gestoppt"}
              {" "}({journeys.filter(j => f === "all" ? true : j.status === f).length})
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:20 }}>
        {loading ? (
          <div style={{ textAlign:"center", color:"#9ca3af", padding:40 }}>Lade Journeys...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:"center", color:"#9ca3af", padding:40 }}>Keine Journeys gefunden</div>
        ) : filtered.map((j, i) => (
          <div key={i} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:16, marginBottom:12, display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ width:40, height:40, borderRadius:"50%", background:"#9900cc", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:14, flexShrink:0 }}>
              {(j.first_name || j.email || "?")[0].toUpperCase()}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontWeight:700, fontSize:14, color:"#111827" }}>{j.first_name || j.email?.split("@")[0] || "Unbekannt"}</span>
                {statusBadge(j.status)}
                <span style={{ fontSize:11, background:"#f3f4f6", color:"#374151", padding:"2px 8px", borderRadius:99 }}>
                  {j.journey === "fall1" ? "Fall 1 — Event" : "Fall 2 — Mindbody"}
                </span>
                {j.pipeline_stage && (
                  <span style={{ fontSize:11, padding:"2px 8px", borderRadius:99, background: stageColor(j.pipeline_stage) + "20", color: stageColor(j.pipeline_stage), fontWeight:600 }}>
                    {j.pipeline_stage}
                  </span>
                )}
              </div>
              <div style={{ fontSize:12, color:"#6b7280" }}>{j.email} {j.phone && <span style={{color:"#374151"}}>· {j.phone}</span>}</div>
              <div style={{ display:"flex", gap:16, marginTop:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, color:"#374151" }}>
                  Aktueller Step: <strong>{j.current_step} — {stepLabel(j.journey, parseInt(j.current_step))}</strong>
                </span>
                {j.next_send_date && j.status === "active" && (
                  <span style={{ fontSize:12, color:"#374151" }}>
                    Nächste Mail: <strong>{j.next_send_date}</strong>
                  </span>
                )}
                {j.last_step_sent && (
                  <span style={{ fontSize:12, color:"#6b7280" }}>Letzter Step: {j.last_step_sent}</span>
                )}
                {j.sms_sent_at && (
                  <span style={{ fontSize:11, background:"#dcfce7", color:"#15803d", padding:"2px 8px", borderRadius:99 }}>
                    📱 SMS gesendet {j.sms_sent_at?.slice(0,10)}
                  </span>
                )}
                {j.sms_delivered && (
                  <span style={{ fontSize:11, background:"#e0e7ff", color:"#4338ca", padding:"2px 8px", borderRadius:99 }}>
                    ✅ SMS zugestellt
                  </span>
                )}
                {j.sms_failed && (
                  <span style={{ fontSize:11, background:"#fee2e2", color:"#dc2626", padding:"2px 8px", borderRadius:99 }}>
                    ❌ SMS Fehler
                  </span>
                )}
              </div>
            </div>
            {j.status === "active" && (
              <button onClick={() => stopJourney(j.client_id, j.journey)}
                style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:600, border:"1px solid #fca5a5", background:"#fef2f2", color:"#dc2626", cursor:"pointer" }}>
                Stop
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SegmentsView = ({ customers }) => {
  const allSources = [...new Set(customers.map(c=>c.source))].filter(Boolean);
  const allStages  = [...new Set(customers.map(c=>c.stage))].filter(Boolean);
  const isValidMobile = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
    return /^(\+49|0049|0)1[5-9]\d{7,10}$/.test(cleaned);
  };
  const now = new Date();
  const daysSince = (dateStr) => {
    if (!dateStr) return 999;
    return Math.floor((now - new Date(dateStr)) / (1000*60*60*24));
  };

  const segments = [
    { name:"First Timers", color:"#7c3aed", customers:customers.filter(c=>c.stage==="first_timer") },
    { name:"Members", color:"#059669", customers:customers.filter(c=>c.stage==="member") },
    { name:"Wiederkommer", color:"#0891b2", customers:customers.filter(c=>c.stage==="returning") },
    { name:"VIP Kunden (25+ Besuche)", color:"#d97706", customers:customers.filter(c=>c.stage==="vip") },
    { name:"Inaktive (MB Status)", color:"#dc2626", customers:customers.filter(c=>c.mbStatus==="Inactive"||c.mbStatus==="Expired") },
    { name:"Inaktive (60+ Tage kein Besuch)", color:"#f97316", customers:customers.filter(c=>c.visits>0&&daysSince(c.lastVisit)>=60) },
    { name:"SMS-fähige Inaktive", color:"#dc2626", customers:customers.filter(c=>(c.mbStatus==="Inactive"||c.mbStatus==="Expired")&&isValidMobile(c.phone)) },
    { name:"ClassPass Kunden", color:"#0891b2", customers:customers.filter(c=>c.source==="CLASSPASS") },
    { name:"EGYM / Wellpass Kunden", color:"#059669", customers:customers.filter(c=>c.source==="EGYM"||c.source==="WELLPASS") },
    { name:"Wellhub Kunden", color:"#7c3aed", customers:customers.filter(c=>c.source==="WELLHUB") },
    { name:"Hoher Lead Score (80+)", color:"#9900cc", customers:customers.filter(c=>c.leadScore>=80) },
    { name:"Kunden mit Telefon", color:"#374151", customers:customers.filter(c=>isValidMobile(c.phone)) },
  ];

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>Segmente</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Automatische Kundensegmentierung</div>
          <div style={{ fontSize:10, color:"#d1d5db", marginTop:4 }}>Quellen: {allSources.join(", ")} · Stages: {allStages.join(", ")}</div>
        </div>
        <div style={{ fontSize:11, background:"#f0fdf4", color:"#15803d", padding:"5px 12px", borderRadius:99 }}>
          🔄 Auto-aktualisiert aus Kundendaten
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
        {segments.map(s=>(
          <div key={s.name} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:s.color }} />
              <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{s.name}</span>
              <span style={{ marginLeft:"auto", background:`${s.color}15`, color:s.color, fontSize:13, fontWeight:700, padding:"2px 10px", borderRadius:20 }}>{s.customers.length}</span>
            </div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:12 }}>
              {s.customers.slice(0,5).map(c=>(
                <div key={c.id} title={c.name}>
                  <Avatar initials={c.avatar} size={28} color={s.color}/>
                </div>
              ))}
              {s.customers.length>5&&<div style={{ width:28, height:28, borderRadius:"50%", background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#6b7280", fontWeight:600 }}>+{s.customers.length-5}</div>}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Btn variant="outline" size="sm" color={s.color} onClick={() => {
                const emails = s.customers.map(c=>c.email).filter(Boolean).join("\n");
                const ta = document.createElement("textarea");
                ta.value = emails;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                alert(`✅ ${s.customers.length} E-Mail Adressen kopiert!`);
              }}>📋 E-Mails kopieren</Btn>
              {s.customers.some(c=>c.smsCapable&&c.phone)&&(
                <Btn variant="ghost" size="sm" onClick={() => {
                  window.location.hash = "sms";
                }}>💬 SMS Tab</Btn>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
const autoStage = (c) => {
  const visits = c.visits || 0;
  const mb = (c.mbStatus || "").toLowerCase();
  const src = (c.source || "").toLowerCase();
  if (mb === "active") return "member";
  if (mb === "inactive" || mb === "expired") return "inactive";
  if (src === "classpass" || src === "apple id" || src === "egym" || src === "wellhub" || src === "wellpass") {
    if (visits === 0) return "lead";
    return "first_timer";
  }
  if (visits === 0) return "lead";
  if (visits === 1) return "first_timer";
  if (visits >= 2 && visits < 5) return "returning";
  if (visits >= 25) return "vip";
  if (visits >= 5) return "returning"; // ohne Active-Status = kein Member
  return "lead";
};
const USERS = [
  { id:"julian", name:"Julian", photo:"https://cdn.prod.website-files.com/6507f8c501706168d0387816/6995ae1f8a0e3a02c407facd_01b29f0a-8b77-4a60-ab2a-0ac8a9d0c9fd.JPG", role:"Founder", type:"admin", color:"#7c3aed" },
  { id:"sertac", name:"Sertac", photo:"https://cdn.prod.website-files.com/6507f8c501706168d0387816/683dad566b78dd2315833a0a_germany-flag-48866.png", role:"Tech", type:"admin", color:"#0891b2" },
  { id:"nico",   name:"Nico",   photo:"https://cdn.prod.website-files.com/6507f8c501706168d0387816/6995ae1f32a2cb24319d2aea_a5611d2a-d4d9-4cc9-b854-662c1d3c0d75.JPG", role:"Operations", type:"admin", color:"#059669" },
];

const normalizeTrainer = name => name ? name.replace(/\s*\(engl\.\)/i, "").trim() : name;

const CDN = "https://cdn.prod.website-files.com/6507f8c501706168d0387816/";
const BURN_FALLBACK = CDN + "650c3d06688d8062fd53aad4_webclip_burn.png";
const BURN_LOGO_WHITE = CDN + "650ad1c9c2de89314d2b20f8_brand_wh.png";
const STAFF_USERS = [
  { id:"ayman",   name:"Ayman",   role:"Trainer",  type:"staff", color:"#7c3aed", photo: CDN+"67137abbba06c113f55547a5_16.png" },
  { id:"darya",   name:"Darya",   role:"Trainer",  type:"staff", color:"#0891b2", photo: CDN+"6995aed472919d61953e1308_4a5cf962-aeeb-4894-b960-c6f11ac1f7fa.JPG" },
  { id:"idoia",   name:"Idoia",   role:"Trainer",  type:"staff", color:"#059669", photo: CDN+"6995ae1f8a6b79dd3bef77a8_c8ce3284-6276-4ac6-8304-9c5427ccdb7b.JPG" },
  { id:"jeffrey", name:"Jeffrey", role:"Trainer",  type:"staff", color:"#d97706", photo: CDN+"6995ae1f4e33878d4331dc1d_1d8c23dd-9d56-4915-8021-ec1ca0df9c4f%202.jpg" },
  { id:"nahomi",  name:"Nahomi",  role:"Trainer",  type:"staff", color:"#dc2626", photo: CDN+"6995ae1f6edba8fea0367272_19502c3c-7056-48e6-a139-23c166cbc41b.JPG", scheduleEditor:true, adminAccess:true },
  { id:"sheila",  name:"Sheila",  role:"Trainer",  type:"staff", color:"#7c3aed", photo: CDN+"6995aeb9f2baead8f5edd299_4680beab-4efc-463c-96e9-04a1fa3ac2fb.jpeg" },
  { id:"stephen", name:"Stephen", role:"Trainer",  type:"staff", color:"#0891b2", photo: "https://clients-content.mindbodyonline.com/studios/burngermany/staff/100000041_xlarge.jpg?osv=639091724330000000" },
  { id:"thommy",  name:"Thommy",  role:"Trainer",  type:"staff", color:"#059669", photo: CDN+"6995ae1f0f9b1fda962640a9_527953e6-2cd1-4e9a-8ce1-2d6c7518e8d8.JPG" },
  { id:"inga",    name:"Inga",    role:"Personal", type:"staff", color:"#6366f1", photo: CDN+"6995ae1fca1703b2a775a12a_5debb352-bd10-4c11-97ca-1fa2ef0069dd.JPG" },
  { id:"yasmin",  name:"Yasmin",  role:"Personal", type:"staff", color:"#d97706", photo: CDN+"6995ae1fc5edcc0b6d82fdca_e006286f-e2a6-4328-9927-d2f6b1612c8b.JPG" },
  { id:"nashwa",  name:"Nashwa",  role:"Personal", type:"staff", color:"#6366f1", photo: CDN+"6995ae1ff0e783b2502beffd_e0992960-c7ad-412f-9665-2ac3a206286a.JPG" },
  { id:"wiki",    name:"Wiki",    role:"Personal", type:"staff", color:"#059669", photo: CDN+"6995ae1f3cd823aa20eb57dc_d5839e93-52a2-446e-be85-1f64121512d0.JPG" },
  { id:"johnson", name:"Johnson", role:"Personal", type:"staff", color:"#0891b2", photo: CDN+"6995ae1fff08d0783630834a_5e4a7bd4-eab2-4eb8-a88a-81d429284e96.JPG" },
  { id:"julian",  name:"Julian",  roles:["Trainer","Personal"], type:"staff", color:"#f97316", photo: CDN+"6995ae1f8a0e3a02c407facd_01b29f0a-8b77-4a60-ab2a-0ac8a9d0c9fd.JPG" },
  { id:"ariadna", name:"Ariadna", role:"Personal", type:"staff", color:"#ec4899", photo: "" },
  { id:"kadir",   name:"Kadir",   role:"Trainer",  type:"staff", color:"#7c3aed", photo: "", comingSoon: true },
  { id:"anna",    name:"Anna",    role:"Trainer",  type:"staff", color:"#7c3aed", photo: "", comingSoon: true },
];

/* ─── Mobile Detection Hook ───────────────────────────────────────────── */
function useMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isMobile;
}

const BirthdayActionModal = ({ customer, initialType, onClose, notify, onViewProfile }) => {
  const [type, setType] = useState(initialType || "email");
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState(`🎂 Happy Birthday, ${customer.name.split(" ")[0]}!`);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const firstName = customer.name.split(" ")[0];

  const defaultSMS = `Hey ${firstName}! 🎂 Alles Gute zum Geburtstag von uns allen bei BURN BXNG! Als Geburtstagsgeschenk laden wir dich zu einer kostenlosen Class ein. Komm vorbei! — Julian`;
  const defaultEmail = `<p>Hey ${firstName},</p><p>alles Gute zum Geburtstag! 🎂</p><p>Als kleines Geschenk von uns laden wir dich zu einer <strong>kostenlosen Class</strong> ein — einfach antworten und wir regeln das.</p><p>Wir sehen uns auf dem Floor! 💪</p>`;

  useEffect(() => {
    setMsg(type === "sms" ? defaultSMS : defaultEmail);
  }, [type]);

  const send = async () => {
    setSending(true);
    try {
      if (type === "sms") {
        const r = await fetch(`${API_URL}/sms/send`, {
          method: "POST",
          headers: {"Content-Type":"application/json"},
          body: JSON.stringify({ to: customer.phone, message: msg, client_id: customer.id })
        });
        if (r.ok) { setSent(true); notify(`🎂 SMS an ${firstName} gesendet!`); }
      } else {
        const r = await fetch(`${API_URL}/debug/test-email`, {
          method: "POST",
          headers: {"Content-Type":"application/json"},
          body: JSON.stringify({ to: customer.email, subject, body: msg })
        });
        if (r.ok) { setSent(true); notify(`🎂 Mail an ${firstName} gesendet!`); }
      }
    } catch {}
    setSending(false);
  };

  const bd = new Date(customer.birthDate);
  const age = new Date().getFullYear() - bd.getFullYear();

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000060", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:16, width:520, maxWidth:"calc(100vw - 32px)", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px #00000030" }}>
        {}
        <div style={{ background:"linear-gradient(135deg,#7c3aed,#9900cc)", padding:"20px 24px", color:"#fff" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:24 }}>🎂</div>
              <div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>{customer.name}</div>
              <div style={{ fontSize:12, opacity:0.8 }}>{age} Jahre alt heute</div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:16 }}>✕</button>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={() => setType("email")}
              style={{ padding:"5px 14px", borderRadius:99, fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                background: type === "email" ? "#fff" : "rgba(255,255,255,0.2)",
                color: type === "email" ? "#7c3aed" : "#fff" }}>📧 E-Mail</button>
            {customer.phone && (
              <button onClick={() => setType("sms")}
                style={{ padding:"5px 14px", borderRadius:99, fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                  background: type === "sms" ? "#fff" : "rgba(255,255,255,0.2)",
                  color: type === "sms" ? "#7c3aed" : "#fff" }}>💬 SMS</button>
            )}
            <button onClick={onViewProfile}
              style={{ padding:"5px 14px", borderRadius:99, fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                background: "rgba(255,255,255,0.1)", color: "#fff" }}>👤 Profil öffnen</button>
          </div>
        </div>

        {}
        <div style={{ flex:1, overflowY:"auto", padding:24 }}>
          <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>An: <strong>{type === "sms" ? customer.phone : customer.email}</strong></div>

          {type === "email" && (
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Betreff</label>
              <input value={subject} onChange={e => setSubject(e.target.value)}
                style={{ width:"100%", padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
            </div>
          )}

          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:4 }}>Nachricht</label>
            <textarea value={msg} onChange={e => setMsg(e.target.value)}
              style={{ width:"100%", height:160, padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13,
                resize:"none", fontFamily:"inherit", boxSizing:"border-box", lineHeight:1.6 }} />
          </div>

          {sent && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:12, fontSize:13, color:"#166534", fontWeight:500 }}>
              ✅ Erfolgreich gesendet!
            </div>
          )}
        </div>

        {}
        <div style={{ padding:"14px 24px", borderTop:"1px solid #e5e7eb", display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose}
            style={{ padding:"8px 16px", background:"#f3f4f6", border:"none", borderRadius:8, fontSize:13, cursor:"pointer" }}>
            Schließen
          </button>
          <button onClick={send} disabled={sending || sent}
            style={{ padding:"8px 20px", background: sent ? "#059669" : "#9900cc", color:"#fff", border:"none",
              borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
            {sending ? "Sende..." : sent ? "✅ Gesendet!" : `🎂 ${type === "sms" ? "SMS" : "E-Mail"} senden`}
          </button>
        </div>
      </div>
    </div>
  );
};
function LoginScreen({ onLogin, onBack }) {
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = async () => {
    const u = USERS.find(u => u.id === selected);
    if (!u || !password) return;
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({ username: u.id, password, type: "admin" }),
      });
      if (!r.ok) throw new Error("login failed");
      const d = await r.json();
      const merged = { ...u, ...d.user, type: "admin" };
      localStorage.setItem("burn_token", d.token);
      localStorage.setItem("burn_user", JSON.stringify(merged));
      fetch(`${API_URL}/notify/login`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          name: merged.name,
          role: merged.role,
          time: new Date().toLocaleString("de-DE", {timeZone: "Europe/Berlin"}),
        })
      }).catch(() => {});
      onLogin(merged);
    } catch {
      setError("Falsches Passwort!");
      setTimeout(() => setError(""), 2000);
    }
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";

  return (
    <div style={{ display:"flex", height:"100vh", background:"#030712", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',system-ui,sans-serif", padding:"0 16px" }}>
      <div style={{ width:"100%", maxWidth:420, textAlign:"center" }}>
        {}
        <img src={BURN_LOGO_WHITE}
          style={{ height:40, marginBottom:40, filter:"brightness(1)" }} alt="BURN" />

        <div style={{ fontSize:14, color:"#6b7280", marginBottom:32 }}>Wähle dein Profil</div>

        {}
        <div style={{ display:"flex", gap:16, justifyContent:"center", marginBottom:32 }}>
          {USERS.map(u => (
            <div key={u.id} onClick={() => { setSelected(u.id); setPassword(""); setError(""); }}
              style={{ cursor:"pointer", textAlign:"center", opacity: selected && selected !== u.id ? 0.4 : 1, transition:"all 0.2s" }}>
              <div style={{ width:72, height:72, borderRadius:"50%", overflow:"hidden", border:`3px solid ${selected===u.id?"#7c3aed":"#1f2937"}`, margin:"0 auto 8px", transition:"all 0.2s" }}>
                <img src={u.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} />
              </div>
              <div style={{ fontSize:13, fontWeight:600, color: selected===u.id?"#fff":"#6b7280" }}>{u.name}</div>
              <div style={{ fontSize:11, color:"#4b5563" }}>{u.role}</div>
            </div>
          ))}
        </div>

        {}
        {selected && (
          <div style={{ animation:"fadeIn 0.2s" }}>
            <div style={{ fontSize:15, color:"#fff", marginBottom:16 }}>
              {greeting}, <strong>{USERS.find(u=>u.id===selected)?.name}</strong>! 👋
            </div>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()}
              placeholder="Passwort eingeben..."
              style={{ width:"100%", background:"#111827", border:`1px solid ${error?"#dc2626":"#1f2937"}`, borderRadius:10, padding:"12px 16px", fontSize:14, color:"#fff", outline:"none", boxSizing:"border-box", marginBottom:12, textAlign:"center" }} />
            {error && <div style={{ color:"#dc2626", fontSize:13, marginBottom:12 }}>{error}</div>}
            <button onClick={handleLogin} style={{ width:"100%", background:"#7c3aed", border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:700, color:"#fff", cursor:"pointer" }}>
              Einloggen →
            </button>
          </div>
        )}
        {onBack && (
          <div style={{ marginTop:24 }}>
            <button onClick={onBack} style={{ background:"transparent", border:"none", color:"#4b5563", fontSize:13, cursor:"pointer" }}>← Zurück</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EntryScreen({ onAdmin, onStaff }) {
  return (
    <div style={{ display:"flex", height:"100vh", background:"#030712", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',system-ui,sans-serif", padding:"0 16px" }}>
      <div style={{ textAlign:"center", width:"100%", maxWidth:360 }}>
        <img src={BURN_LOGO_WHITE}
          style={{ height:40, marginBottom:48 }} alt="BURN" />
        <div style={{ fontSize:15, color:"#6b7280", marginBottom:48 }}>Wähle deinen Zugang</div>
        <div style={{ display:"flex", gap:20, justifyContent:"center" }}>
          <button onClick={onStaff}
            style={{ padding:"18px 40px", background:"#1f2937", border:"1px solid #374151", borderRadius:14, color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", transition:"all 0.2s" }}
            onMouseEnter={e=>e.currentTarget.style.background="#374151"}
            onMouseLeave={e=>e.currentTarget.style.background="#1f2937"}>
            👥 Staff
          </button>
          <button onClick={onAdmin}
            style={{ padding:"18px 40px", background:"#7c3aed", border:"1px solid #7c3aed", borderRadius:14, color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", transition:"all 0.2s" }}
            onMouseEnter={e=>e.currentTarget.style.background="#6d28d9"}
            onMouseLeave={e=>e.currentTarget.style.background="#7c3aed"}>
            🔐 Admin
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffLoginScreen({ onLogin, onBack }) {
  const [selected, setSelected] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const doLogin = async () => {
    if (!selected || !pw) return;
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({ username: selected.id, password: pw, type: "staff" }),
      });
      if (!r.ok) throw new Error("login failed");
      const d = await r.json();
      localStorage.setItem("burn_token", d.token);
      const merged = { ...selected, ...d.user, type: "staff" };
      onLogin(merged);
    } catch {
      setErr("Falsches Passwort");
      setTimeout(() => setErr(""), 2500);
    }
  };
  // Benutzerliste vom Server — neu angelegte Mitarbeiter erscheinen automatisch.
  // Fallback auf die eingebaute Liste, falls die API nicht erreichbar ist.
  const [remoteUsers, setRemoteUsers] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/auth/users/public`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.users) && d.users.length) setRemoteUsers(d.users); })
      .catch(() => {});
  }, []);
  const allStaff = remoteUsers || STAFF_USERS;
  const trainers        = allStaff.filter(u => (u.role || "").includes("Trainer") && !u.comingSoon);
  const trainersComingSoon = allStaff.filter(u => (u.role || "").includes("Trainer") && u.comingSoon);
  const personal        = allStaff.filter(u => !(u.role || "").includes("Trainer") && !u.comingSoon);

  const ActiveCard = ({ u }) => (
    <div key={u.id} onClick={() => { setSelected(u); setPw(""); }}
      style={{ cursor:"pointer", textAlign:"center", transition:"transform 0.15s" }}
      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"}
      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
      <div style={{ width:72, height:72, borderRadius:"50%", border:"3px solid #90c", margin:"0 auto 8px", overflow:"hidden", background:"#9900cc22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, fontWeight:800, color:"#90c", boxShadow:"0 0 0 2px #030712, 0 0 0 4px #9900cc40" }}>
        {u.photo
          ? <img src={u.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} alt={u.name} />
          : <img src={BURN_FALLBACK} className="burn-fallback-spin" style={{ width:"70%", height:"70%", objectFit:"contain", opacity:0.8 }} alt="BURN"/>
        }
      </div>
      <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>{u.name}</div>
      {u.scheduleEditor && <div style={{ fontSize:10, color:u.color, fontWeight:600 }}>Plan-Editor</div>}
      {u.adminAccess && !u.scheduleEditor && <div style={{ fontSize:10, color:u.color, fontWeight:600 }}>Admin</div>}
    </div>
  );

  const ComingSoonCard = ({ u }) => (
    <div key={u.id} style={{ cursor:"default", textAlign:"center", opacity:0.45, position:"relative" }}>
      <div style={{ width:72, height:72, borderRadius:"50%", border:"3px solid #374151", margin:"0 auto 8px", overflow:"hidden", background:"#1f2937", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 0 2px #030712, 0 0 0 4px #37415140", filter:"grayscale(100%)" }}>
        <img src={BURN_FALLBACK} style={{ width:"60%", height:"60%", objectFit:"contain", opacity:0.4 }} alt="BURN"/>
      </div>
      <div style={{ fontSize:13, fontWeight:600, color:"#6b7280" }}>{u.name}</div>
      <div style={{ fontSize:10, color:"#4b5563", fontWeight:700, letterSpacing:"0.05em", marginTop:2 }}>COMING SOON</div>
    </div>
  );

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:"#030712", alignItems:"flex-start", justifyContent:"center", fontFamily:"'Inter',system-ui,sans-serif", overflowY:"auto", padding:"24px 12px" }}>
      {selected && (
        <div onClick={() => setSelected(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#111827", border:"1px solid #374151", borderRadius:16, padding:"28px 24px", width:"100%", maxWidth:320, textAlign:"center" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", margin:"0 auto 12px", overflow:"hidden", border:`3px solid ${selected.color||"#9900cc"}` }}>
              {selected.photo
                ? <img src={selected.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} alt={selected.name}/>
                : <div style={{ width:"100%", height:"100%", background:"#1f2937", display:"flex", alignItems:"center", justifyContent:"center", color:"#9900cc", fontWeight:800, fontSize:24 }}>{selected.name[0]}</div>}
            </div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:16, marginBottom:16 }}>{selected.name}</div>
            <input type="password" autoFocus value={pw} onChange={e => setPw(e.target.value)}
              onKeyDown={e => e.key === "Enter" && doLogin()}
              placeholder="Passwort"
              style={{ width:"100%", padding:"12px 14px", borderRadius:10, border:"1px solid #374151", background:"#030712", color:"#fff", fontSize:15, outline:"none", textAlign:"center", boxSizing:"border-box" }}/>
            {err && <div style={{ color:"#f87171", fontSize:12, marginTop:8 }}>{err}</div>}
            <button onClick={doLogin} style={{ width:"100%", marginTop:14, padding:"12px", borderRadius:10, border:"none", background:"#9900cc", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>Anmelden</button>
            <div style={{ color:"#6b7280", fontSize:11, marginTop:12 }}>Passwort vergessen? → Admin kann es zurücksetzen</div>
          </div>
        </div>
      )}
      <div style={{ width:"100%", maxWidth:560, textAlign:"center", padding:"16px 0 32px" }}>
        <img src={BURN_LOGO_WHITE} style={{ height:36, marginBottom:32 }} alt="BURN" />
        <div style={{ fontSize:13, color:"#6b7280", marginBottom:28 }}>Wähle dein Profil</div>

        {/* Trainer aktiv */}
        <div style={{ fontSize:11, color:"#4b5563", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>Trainer</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:16, justifyContent:"center", marginBottom: trainersComingSoon.length ? 16 : 32 }}>
          {trainers.map(u => <ActiveCard key={u.id} u={u} />)}
        </div>

        {/* Trainer Coming Soon */}
        {trainersComingSoon.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:16, justifyContent:"center", marginBottom:32 }}>
            {trainersComingSoon.map(u => <ComingSoonCard key={u.id} u={u} />)}
          </div>
        )}

        {/* Personal */}
        {personal.length > 0 && (
          <>
            <div style={{ fontSize:11, color:"#4b5563", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>Personal</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:16, justifyContent:"center", marginBottom:32 }}>
              {personal.map(u => <ActiveCard key={u.id} u={u} />)}
            </div>
          </>
        )}

        <button onClick={onBack} style={{ background:"transparent", border:"none", color:"#4b5563", fontSize:13, cursor:"pointer" }}>← Zurück</button>
      </div>
    </div>
  );
}

function AnfragenView({ user, isNahomi, pendingSwaps, onDecision, reloadPending }) {
  const [swapBoard, setSwapBoard] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectNote, setRejectNote] = useState({}); // swap reject reason per id
  const [vacRejectNote, setVacRejectNote] = useState({}); // vacation reject reason per id

  const reload = () => {
    setLoading(true);
    // Nahomi sieht ALLE Swaps, Trainer nur eigene
    const swapUrl = isNahomi
      ? `${API_URL}/staff/shift-swap`
      : `${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`;
    const swapP = fetch(swapUrl).then(r=>r.json()).then(d=>setSwapBoard(d.swaps||[])).catch(()=>{});
    // Nahomi sieht alle Urlaubsanträge, Trainer nur eigene
    const vacUrl = isNahomi
      ? `${API_URL}/staff/vacation`
      : `${API_URL}/staff/vacation?name=${encodeURIComponent(user.name)}`;
    const vacP = fetch(vacUrl).then(r=>r.json()).then(d=>setVacations(d.requests||[])).catch(()=>{});
    Promise.all([swapP, vacP]).finally(()=>setLoading(false));
    if (reloadPending) reloadPending();
  };
  useEffect(() => { reload(); }, [user.name]);

  const takeSwap = async (swapId) => {
    const res = await fetch(`${API_URL}/staff/shift-swap/${swapId}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ status:"pending_approval", taken_by:user.name })
    }).catch(()=>({ok:false}));
    if (res && res.status === 409) { alert("Dieser Kurs wurde gerade von jemand anderem angenommen."); }
    reload();
  };

  const cancelSwap = async (swapId) => {
    const res = await fetch(`${API_URL}/staff/shift-swap/${swapId}`, { method:"DELETE" }).catch(()=>({ok:false}));
    if (res && res.status === 409) { alert("Zurückziehen nicht möglich — jemand hat bereits angenommen und wartet auf Nahomis Genehmigung."); return; }
    reload();
  };

  const approveVacation = async (vacId) => {
    await fetch(`${API_URL}/staff/vacation/${vacId}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ status:"approved", decided_by:user.name })
    });
    reload();
  };

  const rejectVacation = async (vacId) => {
    const note = vacRejectNote[vacId] || "";
    await fetch(`${API_URL}/staff/vacation/${vacId}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ status:"rejected", admin_note:note, decided_by:user.name })
    });
    reload();
  };

  const cancelVacation = async (vacId) => {
    await fetch(`${API_URL}/staff/vacation/${vacId}`, { method:"DELETE" });
    reload();
  };

  const today = new Date().toISOString().slice(0,10);
  const needApproval    = (pendingSwaps||[]).filter(s=>s.status==="pending_approval"||s.status==="taken");
  const openSwaps       = swapBoard.filter(s=>(s.status==="open"||s.status==="rejected_open")&&s.requester!==user.name&&(s.shift_date||"")>=today);
  const pendingOther    = swapBoard.filter(s=>(s.status==="pending_approval"||s.status==="taken")&&s.requester!==user.name&&s.taken_by!==user.name&&(s.shift_date||"")>=today);
  const myOffered       = swapBoard.filter(s=>s.requester===user.name);
  const myTaken         = swapBoard.filter(s=>s.taken_by===user.name&&s.requester!==user.name&&(s.shift_date||"")>=today);
  const rejectedForMe   = swapBoard.filter(s=>s.last_rejected_taker===user.name&&s.status==="rejected_open"&&(s.shift_date||"")>=today);

  // Vacation splits
  const pendingVacations  = vacations.filter(v=>v.status==="pending");
  const myVacations       = vacations.filter(v=>v.name===user.name);

  // Overlap check for Nahomi: flag if two pending vacations overlap
  const overlapWarning = (vac) => {
    if (!isNahomi) return false;
    return pendingVacations.some(other =>
      other.id !== vac.id &&
      other.name !== vac.name &&
      other.from_date <= vac.to_date &&
      other.to_date >= vac.from_date
    );
  };

  const statusBg  = s => s.status==="open"||s.status==="rejected_open"?"#fef3c7":s.status==="pending_approval"||s.status==="taken"?"#eff6ff":s.status==="approved"?"#d1fae5":"#f3f4f6";
  const statusCol = s => s.status==="open"||s.status==="rejected_open"?"#92400e":s.status==="pending_approval"||s.status==="taken"?"#2563eb":s.status==="approved"?"#059669":"#9ca3af";
  const statusLabel = s => s.status==="open"?"📢 Offen":s.status==="rejected_open"?"🔄 Wieder offen":s.status==="pending_approval"||s.status==="taken"?"⏳ Wartet auf Nahomi":s.status==="approved"?"✅ Genehmigt":"Storniert";

  if (loading) return <BurnLoader text="Lade Anfragen..." />;

  const totalPending = needApproval.length + pendingVacations.length;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ maxWidth:700, margin:"0 auto" }}>
        <div style={{ fontSize:18, fontWeight:700, color:"#111827", marginBottom:20 }}>📋 Anfragen</div>

        {/* ── NAHOMI: Schichttausch genehmigen ── */}
        {isNahomi && needApproval.length > 0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#92400e", marginBottom:10 }}>🔄 Schichttausch — wartet auf Genehmigung ({needApproval.length})</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {needApproval.map(s=>(
                <div key={s.id} style={{ background:"#fff8f0", border:"2px solid #f59e0b", borderRadius:12, padding:"14px 16px" }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{s.shift_name}</div>
                  <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>{s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""}</div>
                  <div style={{ fontSize:13, color:"#374151", marginTop:6 }}><b>{s.requester}</b> gibt ab → <b>{s.taken_by}</b> übernimmt</div>
                  {s.note && <div style={{ fontSize:12, color:"#9ca3af", marginTop:4 }}>"{s.note}"</div>}
                  <div style={{ fontSize:12, fontWeight:600, color:"#f59e0b", marginTop:6 }}>⚠️ Bei Zustimmung bitte auch in Mindbody anpassen!</div>
                  <div style={{ display:"flex", gap:10, marginTop:12 }}>
                    <button onClick={()=>{ onDecision(s.id,"approve"); setTimeout(reload,500); }}
                      style={{ flex:1, padding:"10px", background:"#059669", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      ✓ Genehmigen
                    </button>
                    <button onClick={()=>{ onDecision(s.id,"reject"); setTimeout(reload,500); }}
                      style={{ flex:1, padding:"10px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      ✗ Ablehnen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── NAHOMI: Urlaubsanträge genehmigen ── */}
        {isNahomi && pendingVacations.length > 0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#1d4ed8", marginBottom:10 }}>✈️ Urlaubsanträge — wartet auf Genehmigung ({pendingVacations.length})</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {pendingVacations.map(v=>(
                <div key={v.id} style={{ background:"#eff6ff", border:"2px solid #3b82f6", borderRadius:12, padding:"14px 16px" }}>
                  {overlapWarning(v) && (
                    <div style={{ background:"#fef3c7", border:"1px solid #f59e0b", borderRadius:8, padding:"6px 10px", fontSize:12, fontWeight:600, color:"#92400e", marginBottom:10 }}>
                      ⚠️ Überschneidung mit einem anderen Urlaubsantrag im selben Zeitraum!
                    </div>
                  )}
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{v.name}</div>
                  <div style={{ fontSize:13, color:"#374151", marginTop:4 }}>✈️ {v.from_date} — {v.to_date}</div>
                  {v.note && <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>"{v.note}"</div>}
                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Eingereicht: {v.created_at?.slice(0,10)}</div>
                  <textarea
                    placeholder="Ablehnungsgrund (optional)..."
                    value={vacRejectNote[v.id]||""}
                    onChange={e=>setVacRejectNote(prev=>({...prev,[v.id]:e.target.value}))}
                    rows={2}
                    style={{ width:"100%", marginTop:10, padding:"6px 10px", border:"1px solid #bfdbfe", borderRadius:8, fontSize:12, resize:"vertical", boxSizing:"border-box" }}
                  />
                  <div style={{ display:"flex", gap:10, marginTop:10 }}>
                    <button onClick={()=>approveVacation(v.id)}
                      style={{ flex:1, padding:"10px", background:"#059669", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      ✓ Genehmigen
                    </button>
                    <button onClick={()=>rejectVacation(v.id)}
                      style={{ flex:1, padding:"10px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      ✗ Ablehnen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Offene Tauschangebote (für alle Trainer) ── */}
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:10 }}>🔄 Offene Tauschangebote</div>
          {openSwaps.length === 0 && pendingOther.length === 0 ? (
            <div style={{ color:"#9ca3af", fontSize:13, padding:"12px 0" }}>Keine offenen Tauschangebote</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {openSwaps.map(s=>(
                <div key={s.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{s.shift_name}</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""} · von <b>{s.requester}</b></div>
                    {s.note && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>"{s.note}"</div>}
                  </div>
                  <button onClick={()=>takeSwap(s.id)} style={{ padding:"8px 16px", background:"#059669", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                    Übernehmen
                  </button>
                </div>
              ))}
              {pendingOther.map(s=>(
                <div key={s.id} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12, opacity:0.6 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#374151" }}>{s.shift_name}</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""} · von <b>{s.requester}</b></div>
                  </div>
                  <span style={{ fontSize:11, padding:"4px 10px", background:"#e0e7ff", color:"#4338ca", borderRadius:99, fontWeight:700, flexShrink:0 }}>⏳ Vergeben</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Meine angebotenen Kurse ── */}
        {myOffered.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:10 }}>📢 Meine Tauschangebote</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {myOffered.map(s=>(
                <div key={s.id} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#374151" }}>{s.shift_name} — {s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""}</div>
                    {s.taken_by && (s.status==="pending_approval"||s.status==="taken") && <div style={{ fontSize:12, color:"#2563eb", marginTop:2 }}>⏳ {s.taken_by} möchte übernehmen — wartet auf Nahomi</div>}
                    {s.taken_by && s.status==="approved" && <div style={{ fontSize:12, color:"#059669", marginTop:2 }}>✅ Genehmigt — {s.taken_by} übernimmt</div>}
                    {s.note && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>"{s.note}"</div>}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                    <span style={{ fontSize:11, padding:"3px 8px", borderRadius:99, background:statusBg(s), color:statusCol(s), fontWeight:700 }}>{statusLabel(s)}</span>
                    {(s.status==="open"||s.status==="rejected_open") && (
                      <button onClick={()=>cancelSwap(s.id)} style={{ padding:"4px 10px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                        Zurückziehen
                      </button>
                    )}
                    {(s.status==="pending_approval"||s.status==="taken") && <span style={{ fontSize:11, color:"#9ca3af" }}>Gesperrt</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Von mir angenommene Kurse ── */}
        {myTaken.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:10 }}>🙋 Von mir angenommen</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {myTaken.map(s=>(
                <div key={s.id} style={{ background:s.status==="approved"?"#f0fdf4":"#eff6ff", border:`1px solid ${s.status==="approved"?"#bbf7d0":"#bfdbfe"}`, borderRadius:10, padding:"12px 16px" }}>
                  <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{s.shift_name} — {s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""}</div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Kurs von <b>{s.requester}</b></div>
                  <div style={{ marginTop:6 }}>
                    {(s.status==="pending_approval"||s.status==="taken") && <span style={{ fontSize:12, fontWeight:700, color:"#2563eb" }}>⏳ Wartet auf Nahomis Genehmigung</span>}
                    {s.status==="approved" && <span style={{ fontSize:12, fontWeight:700, color:"#059669" }}>✅ Genehmigt! Kurs ist deiner. Bitte in Mindbody prüfen.</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Abgelehnte Übernahmen ── */}
        {rejectedForMe.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#dc2626", marginBottom:10 }}>❌ Abgelehnte Tausche</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {rejectedForMe.map(s=>(
                <div key={s.id} style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:10, padding:"12px 16px" }}>
                  <div style={{ fontSize:13, fontWeight:600, color:"#92400e" }}>{s.shift_name} — {s.shift_date}{s.shift_time?` · ${s.shift_time} Uhr`:""}</div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Kurs von <b>{s.requester}</b></div>
                  <div style={{ fontSize:12, fontWeight:700, color:"#dc2626", marginTop:6 }}>❌ Nahomi hat abgelehnt — Kurs ist wieder offen für alle</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Meine Urlaubsanträge (eigene Sicht) ── */}
        {myVacations.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:10 }}>✈️ Meine Urlaubsanträge</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {myVacations.map(v=>(
                <div key={v.id} style={{ background: v.status==="approved"?"#f0fdf4":v.status==="rejected"?"#fef2f2":"#eff6ff", border:`1px solid ${v.status==="approved"?"#bbf7d0":v.status==="rejected"?"#fca5a5":"#bfdbfe"}`, borderRadius:10, padding:"12px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>✈️ {v.from_date} — {v.to_date}</div>
                      {v.note && <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>"{v.note}"</div>}
                      <div style={{ fontSize:12, marginTop:6 }}>
                        {v.status==="pending" && <span style={{ fontWeight:700, color:"#2563eb" }}>⏳ Wartet auf Genehmigung</span>}
                        {v.status==="approved" && <span style={{ fontWeight:700, color:"#059669" }}>✅ Genehmigt — Tage im Kalender gesperrt</span>}
                        {v.status==="rejected" && <span style={{ fontWeight:700, color:"#dc2626" }}>❌ Abgelehnt{v.admin_note?` — "${v.admin_note}"`:""}</span>}
                      </div>
                    </div>
                    {v.status==="pending" && (
                      <button onClick={()=>cancelVacation(v.id)} style={{ padding:"6px 12px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                        Zurückziehen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── NAHOMI: Bearbeitete Einträge (Archiv mit Lösch-Button) ── */}
        {isNahomi && (() => {
          const doneSwaps = swapBoard.filter(s=>s.status==="approved"||s.status==="rejected"||s.status==="cancelled");
          const doneVacs  = vacations.filter(v=>v.status==="approved"||v.status==="rejected");
          if (doneSwaps.length === 0 && doneVacs.length === 0) return null;
          return (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#6b7280", marginBottom:10 }}>🗂 Bearbeitet</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {doneSwaps.map(s=>(
                  <div key={s.id} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#374151" }}>🔄 {s.shift_name} — {s.shift_date}</div>
                      <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{s.requester} → {s.taken_by||"—"}</div>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:99, fontWeight:700,
                        background: s.status==="approved"?"#d1fae5":"#fee2e2",
                        color: s.status==="approved"?"#059669":"#dc2626" }}>
                        {s.status==="approved"?"✅ Genehmigt":"❌ Abgelehnt"}
                      </span>
                    </div>
                    <button onClick={async()=>{ await fetch(`${API_URL}/staff/shift-swap/${s.id}`,{method:"DELETE"}); reload(); }}
                      style={{ padding:"5px 12px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                      Löschen
                    </button>
                  </div>
                ))}
                {doneVacs.map(v=>(
                  <div key={v.id} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#374151" }}>✈️ {v.name} — {v.from_date} bis {v.to_date}</div>
                      {v.admin_note && <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Grund: "{v.admin_note}"</div>}
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:99, fontWeight:700,
                        background: v.status==="approved"?"#d1fae5":"#fee2e2",
                        color: v.status==="approved"?"#059669":"#dc2626" }}>
                        {v.status==="approved"?"✅ Genehmigt":"❌ Abgelehnt"}
                      </span>
                    </div>
                    <button onClick={async()=>{ await fetch(`${API_URL}/staff/vacation/${v.id}`,{method:"DELETE"}); reload(); }}
                      style={{ padding:"5px 12px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                      Löschen
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {myOffered.length===0 && myTaken.length===0 && openSwaps.length===0 && pendingOther.length===0 && rejectedForMe.length===0 && myVacations.length===0 && !isNahomi && (
          <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>Keine aktiven Anfragen</div>
            <div style={{ fontSize:13 }}>Urlaub beantragen kannst du im Tab "Verfügbarkeit".</div>
          </div>
        )}
      </div>
    </div>
  );
}

function StaffScheduleView({ user }) {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [exported, setExported] = useState(false);
  // Schicht tauschen
  const [swapModal, setSwapModal] = useState(false);
  const [swapShift, setSwapShift] = useState(null);
  const [swapNote, setSwapNote]   = useState("");
  const [swapSending, setSwapSending] = useState(false);
  const [swapBoard, setSwapBoard] = useState([]);
  // Krankmeldung
  const [sickModal, setSickModal] = useState(false);
  const [sickFrom, setSickFrom]   = useState(new Date().toISOString().slice(0,10));
  const [sickTo, setSickTo]       = useState(new Date().toISOString().slice(0,10));
  const [sickNote, setSickNote]   = useState("");
  const [sickSending, setSickSending] = useState(false);
  const [sickSent, setSickSent]   = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/staff/schedule?name=${encodeURIComponent(user.name)}`)
      .then(r => r.json())
      .then(d => { setSchedule(d.schedule || []); setLoading(false); })
      .catch(() => { setLoading(false); });
    fetch(`${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`)
      .then(r=>r.json()).then(d=>setSwapBoard(d.swaps||[])).catch(()=>{});
  }, [user.name]);

  const exportICal = () => {
    const lines = [
      "BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//BURN BXNG//Schedule//DE","CALSCALE:GREGORIAN",
    ];
    schedule.forEach((s,i) => {
      const dateStr = (s.date||"").replace(/-/g,"");
      if(!dateStr) return;
      const uid = `burn-${user.id}-${i}@burnbxng.com`;
      const start = dateStr + (s.time ? "T"+s.time.replace(":","")+"00" : "");
      lines.push("BEGIN:VEVENT");
      lines.push(`UID:${uid}`);
      lines.push(`DTSTART;TZID=Europe/Berlin:${start}`);
      lines.push(`SUMMARY:${s.class_name||"Kurs"} — BURN BXNG`);
      lines.push(`DESCRIPTION:Trainer: ${user.name}`);
      lines.push(`LOCATION:BURN BXNG Studio`);
      lines.push("END:VEVENT");
    });
    lines.push("END:VCALENDAR");
    const blob = new Blob([lines.join("\r\n")], {type:"text/calendar"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `burn-stundenplan-${user.name.toLowerCase()}.ics`; a.click();
    setExported(true); setTimeout(()=>setExported(false), 2500);
  };

  const submitSickReport = async () => {
    setSickSending(true);
    try {
      await fetch(`${API_URL}/staff/sick-report`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:user.name, from_date:sickFrom, to_date:sickTo, note:sickNote })
      });
      setSickSent(true); setSickModal(false);
      setTimeout(()=>setSickSent(false), 4000);
    } catch {}
    setSickSending(false);
  };

  const submitSwap = async () => {
    if (!swapShift) return;
    setSwapSending(true);
    try {
      await fetch(`${API_URL}/staff/shift-swap`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          requester: user.name,
          shift_date: swapShift.date||"",
          shift_name: swapShift.class_name||"Kurs",
          shift_time: swapShift.time||"",
          note: swapNote
        })
      });
      const d = await fetch(`${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`).then(r=>r.json());
      setSwapBoard(d.swaps||[]);
      setSwapModal(false); setSwapNote("");
    } catch {}
    setSwapSending(false);
  };

  const takeSwap = async (swapId) => {
    const res = await fetch(`${API_URL}/staff/shift-swap/${swapId}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ status:"pending_approval", taken_by:user.name })
    }).catch(()=>({ok:false}));
    if (res && res.status === 409) {
      alert("Dieser Kurs wurde gerade von jemand anderem angenommen.");
      const d = await fetch(`${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`).then(r=>r.json()).catch(()=>({swaps:[]}));
      setSwapBoard(d.swaps||[]);
      return;
    }
    const d = await fetch(`${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`).then(r=>r.json()).catch(()=>({swaps:[]}));
    setSwapBoard(d.swaps||[]);
  };

  const cancelSwap = async (swapId) => {
    const res = await fetch(`${API_URL}/staff/shift-swap/${swapId}`, { method:"DELETE" }).catch(()=>({ok:false}));
    if (res.status === 409) {
      alert("Zurückziehen nicht möglich — jemand hat bereits angenommen und wartet auf Nahomis Genehmigung.");
      return;
    }
    const d = await fetch(`${API_URL}/staff/shift-swap?name=${encodeURIComponent(user.name)}`).then(r=>r.json()).catch(()=>({swaps:[]}));
    setSwapBoard(d.swaps||[]);
  };

  if (loading) return <BurnLoader text="Lade Stundenplan..." />;

  const today = new Date().toISOString().slice(0,10);
  const openSwaps          = swapBoard.filter(s=>(s.status==="open"||s.status==="rejected_open")&&s.requester!==user.name&&(s.shift_date||"")>=today);
  const pendingSwaps_other = swapBoard.filter(s=>(s.status==="pending_approval"||s.status==="taken")&&s.requester!==user.name&&s.taken_by!==user.name&&(s.shift_date||"")>=today);
  const mySwaps            = swapBoard.filter(s=>s.requester===user.name); // KEIN Datumsfilter — eigene Angebote immer sichtbar
  const myTakenSwaps       = swapBoard.filter(s=>s.taken_by===user.name&&s.requester!==user.name&&(s.shift_date||"")>=today);
  const offeredShiftKeys   = new Set(mySwaps.map(s=>`${s.shift_name}__${s.shift_date}`));
  const approvedTaken      = myTakenSwaps.filter(s=>s.status==="approved");
  const approvedGiven      = mySwaps.filter(s=>s.status==="approved");
  const rejectedForMe      = swapBoard.filter(s=>s.last_rejected_taker===user.name&&s.status==="rejected_open"&&(s.shift_date||"")>=today);

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ maxWidth:700, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", marginBottom:20, gap:12, flexWrap:"wrap" }}>
          <div style={{ fontSize:18, fontWeight:700, color:"#111827", flex:1 }}>📅 Dein Stundenplan</div>
          {schedule.length > 0 && (<>
            <button onClick={exportICal}
              style={{ padding:"8px 14px", background:exported?"#059669":"#7c3aed", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600 }}>
              {exported ? "✅ Exportiert!" : "📥 Download .ics"}
            </button>
            <a href={`webcal://${API_URL.replace(/^https?:\/\//,"")}/staff/ical/${encodeURIComponent(user.name)}`}
              style={{ padding:"8px 14px", background:"#0ea5e9", color:"#fff", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, textDecoration:"none", display:"inline-block" }}>
              📱 iPhone abonnieren
            </a>
            <button onClick={()=>{ navigator.clipboard?.writeText(`${API_URL}/staff/ical/${encodeURIComponent(user.name)}`); alert("URL kopiert! In Google Calendar → Einstellungen → Anderen Kalender hinzufügen → Per URL einfügen."); }}
              style={{ padding:"8px 14px", background:"#f3f4f6", color:"#374151", border:"1px solid #d1d5db", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600 }}>
              🔗 URL kopieren
            </button>
          </>)}
          <button onClick={()=>setSickModal(true)} style={{ padding:"8px 16px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:700 }}>
            🤒 Krank melden
          </button>
        </div>

        {sickSent && (
          <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 14px", fontSize:13, color:"#059669", marginBottom:16 }}>
            ✅ Krankmeldung eingereicht — Nahomi wird informiert.
          </div>
        )}

        {schedule.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📅</div>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>Kein Plan verfügbar</div>
            <div style={{ fontSize:13 }}>Dein Stundenplan wird hier angezeigt sobald er hochgeladen wurde.</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {schedule.map((s, i) => {
              const isOffered = offeredShiftKeys.has(`${s.class_name}__${s.date}`);
              const givenSwap = approvedGiven.find(m=>m.shift_name===s.class_name&&m.shift_date===s.date);
              return (
              <div key={i} style={{ background:givenSwap?"#f0fdf4":isOffered?"#fffbeb":"#fff", border:`1px solid ${givenSwap?"#86efac":isOffered?"#f59e0b":"#e5e7eb"}`, borderRadius:10, padding:"14px 18px", display:"flex", gap:16, alignItems:"center" }}>
                <div style={{ width:48, height:48, borderRadius:10, background:givenSwap?"#dcfce7":"#7c3aed18", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:givenSwap?"#16a34a":"#7c3aed", textTransform:"uppercase" }}>{new Date(s.date).toLocaleDateString("de-DE",{weekday:"short"})}</div>
                  <div style={{ fontSize:18, fontWeight:800, color:givenSwap?"#16a34a":"#7c3aed", lineHeight:1 }}>{new Date(s.date).getDate()}</div>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:givenSwap?"#15803d":"#111827" }}>{s.class_name}</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>{s.time}</div>
                  {givenSwap && <div style={{ fontSize:12, color:"#16a34a", fontWeight:600, marginTop:2 }}>✅ Übernommen von {givenSwap.taken_by}</div>}
                </div>
                {givenSwap ? (
                  <span style={{ padding:"6px 12px", background:"#dcfce7", border:"1px solid #86efac", borderRadius:8, fontSize:11, fontWeight:700, color:"#15803d", flexShrink:0 }}>
                    ✅ Getauscht
                  </span>
                ) : isOffered ? (
                  <span style={{ padding:"7px 14px",background:"#fef3c7",border:"2px solid #f59e0b",borderRadius:8,fontSize:12,fontWeight:800,color:"#92400e",flexShrink:0 }}>
                    📢 Zum Tausch angeboten
                  </span>
                ) : (
                  <button onClick={()=>{setSwapShift(s);setSwapModal(true);}} style={{ padding:"6px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",color:"#374151",flexShrink:0 }}>
                    🔄 Tauschen
                  </button>
                )}
              </div>
            );})}

            {/* Kurse die ich durch Tausch übernommen habe */}
            {approvedTaken.map(s=>(
              <div key={s.id} style={{ background:"#eff6ff", border:"2px solid #3b82f6", borderRadius:10, padding:"14px 18px", display:"flex", gap:16, alignItems:"center" }}>
                <div style={{ width:48, height:48, borderRadius:10, background:"#dbeafe", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#2563eb", textTransform:"uppercase" }}>{new Date(s.shift_date).toLocaleDateString("de-DE",{weekday:"short"})}</div>
                  <div style={{ fontSize:18, fontWeight:800, color:"#2563eb", lineHeight:1 }}>{new Date(s.shift_date).getDate()}</div>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1d4ed8" }}>{s.shift_name}</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>{s.shift_time||""}</div>
                  <div style={{ fontSize:12, color:"#2563eb", fontWeight:600, marginTop:2 }}>↩️ Übernommen von {s.requester}</div>
                </div>
                <span style={{ padding:"6px 12px", background:"#dbeafe", border:"1px solid #93c5fd", borderRadius:8, fontSize:11, fontWeight:700, color:"#1d4ed8", flexShrink:0 }}>
                  🔁 Getauscht
                </span>
              </div>
            ))}
          </div>
        )}
        {schedule.length > 0 && (
          <div style={{ marginTop:16, padding:"10px 14px", background:"#f0fdf4", borderRadius:8, fontSize:12, color:"#059669" }}>
            💡 Mit <strong>iCal Export</strong> kannst du deinen Plan direkt in iPhone-Kalender, Google Calendar oder Outlook importieren.
          </div>
        )}

      </div>

      {/* Krank-Modal */}
      {sickModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", borderRadius:16, padding:28, width:"min(380px,calc(100vw - 32px))", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#111827", marginBottom:20 }}>🤒 Krankmeldung</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Von</label>
                <input type="date" value={sickFrom} onChange={e=>setSickFrom(e.target.value)}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }}/>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Bis (voraussichtlich)</label>
                <input type="date" value={sickTo} onChange={e=>setSickTo(e.target.value)}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }}/>
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Anmerkung</label>
              <textarea value={sickNote} onChange={e=>setSickNote(e.target.value)} rows={2} placeholder="Optional..."
                style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }}/>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setSickModal(false)} style={{ flex:1, padding:"10px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", cursor:"pointer", fontSize:13 }}>Abbrechen</button>
              <button onClick={submitSickReport} disabled={sickSending} style={{ flex:2, padding:"10px", background:"#dc2626", border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>
                {sickSending?"Wird gesendet…":"📤 Krankmeldung einreichen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schicht-Tausch-Modal */}
      {swapModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", borderRadius:16, padding:28, width:"min(380px,calc(100vw - 32px))", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#111827", marginBottom:8 }}>🔄 Schicht zum Tausch anbieten</div>
            {swapShift && (
              <div style={{ background:"#f3f4f6", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#374151" }}>
                <strong>{swapShift.class_name||"Kurs"}</strong> — {swapShift.date} {swapShift.time}
              </div>
            )}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Anmerkung (optional)</label>
              <textarea value={swapNote} onChange={e=>setSwapNote(e.target.value)} rows={2} placeholder="z.B. Kann leider nicht, wer kann übernehmen?"
                style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }}/>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setSwapModal(false)} style={{ flex:1, padding:"10px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", cursor:"pointer", fontSize:13 }}>Abbrechen</button>
              <button onClick={submitSwap} disabled={swapSending} style={{ flex:2, padding:"10px", background:"#7c3aed", border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>
                {swapSending?"Wird gesendet…":"📤 Tauschangebot stellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Plan Editor helpers ──────────────────────────────────────────────
const CLASS_TYPES = [
  { name:"Full (upper focus)",   color:"#bfdbfe", text:"#1e40af" },
  { name:"Full (lower focus)",   color:"#99f6e4", text:"#065f46" },
  { name:"Chest, Back, Core",    color:"#fed7aa", text:"#9a3412" },
  { name:"Booty Burn",           color:"#fde68a", text:"#92400e" },
  { name:"Strength (Bootcamp)",  color:"#ddd6fe", text:"#5b21b6" },
  { name:"Knockout",             color:"#fbbf24", text:"#78350f" },
  { name:"Full Body",            color:"#c4b5fd", text:"#4c1d95" },
  { name:"Beginner Class",       color:"#fbcfe8", text:"#9d174d" },
  { name:"Technik",              color:"#f0abfc", text:"#701a75" },
];
const TRAINER_TIMES = ["07:00","12:15","17:30","18:40","19:50"];
const DEFAULT_DURATION = {"07:00":"07:50","12:15":"13:05","17:30":"18:20","18:40":"19:30","19:50":"20:40"};
const DAYS_SHORT = ["Mo","Di","Mi","Do","Fr","Sa","So"];
const SERVICE_SHIFTS = ["Früh","Spät"];

function getISOWeekNum(d) {
  const date = new Date(d); date.setHours(0,0,0,0);
  date.setDate(date.getDate()+4-(date.getDay()||7));
  const y = new Date(date.getFullYear(),0,1);
  return Math.ceil((((date-y)/86400000)+1)/7);
}
function getCurrentWeek() {
  const now = new Date();
  return `${now.getFullYear()}-W${String(getISOWeekNum(now)).padStart(2,"0")}`;
}
function getWeekDates(weekStr) {
  const [yr, wn] = weekStr.split("-W");
  const jan4 = new Date(parseInt(yr),0,4);
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate()-(jan4.getDay()||7)+1+(parseInt(wn)-1)*7);
  return DAYS_SHORT.map((day,i)=>{
    const d=new Date(mon); d.setDate(mon.getDate()+i);
    return { day, date:d, label:`${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}` };
  });
}
function shiftWeek(weekStr, delta) {
  const [yr, wn] = weekStr.split("-W");
  const jan4 = new Date(parseInt(yr),0,4);
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate()-(jan4.getDay()||7)+1+(parseInt(wn)-1+delta)*7);
  return `${mon.getFullYear()}-W${String(getISOWeekNum(mon)).padStart(2,"0")}`;
}
function icalDate(date, timeStr) {
  const [h,m] = timeStr.split(":").map(Number);
  const d = new Date(date); d.setHours(h,m,0,0);
  return d.toISOString().replace(/[-:]/g,"").slice(0,15);
}
function downloadText(content, filename, mime) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([content],{type:mime}));
  a.download = filename; a.click();
}
function makeId() { return Math.random().toString(36).slice(2,10); }
function classColor(name) { return CLASS_TYPES.find(c=>c.name===name)||{color:"#e5e7eb",text:"#374151"}; }
function trainerColor(name) { return STAFF_USERS.find(u=>u.name===name)?.color||"#7c3aed"; }

function ScheduleEditorView() {
  const [tab,          setTab]          = useState("trainer");
  const [week,         setWeek]         = useState(getCurrentWeek);
  const [trainerSlots, setTrainerSlots] = useState([]);
  const [serviceSlots, setServiceSlots] = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [dragSrc,      setDragSrc]      = useState(null);  // {type:"person"|"slot", ...}
  const [editSlot,     setEditSlot]     = useState(null);
  const [showIcalPick, setShowIcalPick] = useState(false);
  // Mobile tap-to-assign
  const [mobilePickSlot, setMobilePickSlot] = useState(null); // {day, time} or {day, shift, _type:"service"}
  const isMobilePlan = typeof window !== "undefined" && window.innerWidth < 768;

  const weekDates   = getWeekDates(week);
  const hasRole     = (u, r) => u.role === r || (u.roles||[]).includes(r);
  const trainers    = STAFF_USERS.filter(u=>hasRole(u,"Trainer"));
  const personal    = STAFF_USERS.filter(u=>hasRole(u,"Personal"));
  const [customPeople, setCustomPeople] = useState([]);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState("");
  const COLORS = ["#7c3aed","#0891b2","#059669","#d97706","#dc2626","#6366f1","#f97316","#ec4899"];
  const [newPersonColor, setNewPersonColor] = useState(COLORS[0]);

  const addCustomPerson = () => {
    const name = newPersonName.trim();
    if (!name) return;
    setCustomPeople(prev => [...prev, { id:`custom_${makeId()}`, name, color:newPersonColor, custom:true }]);
    setNewPersonName(""); setShowAddPerson(false);
  };

  // ── Load ──────────────────────────────────────────────────────
  useEffect(()=>{
    fetch(`${API_URL}/staff/plan?week=${week}`)
      .then(r=>r.json())
      .then(d=>{ setTrainerSlots(d.trainer_slots||[]); setServiceSlots(d.service_slots||[]); })
      .catch(()=>{});
  },[week]);

  // ── Save ──────────────────────────────────────────────────────
  const persist = useCallback(async (ts, ss) => {
    setSaving(true);
    await fetch(`${API_URL}/staff/plan`,{
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({week, trainer_slots:ts, service_slots:ss})
    }).catch(()=>{});
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),1500);
  },[week]);

  const updateTS = (ts) => { setTrainerSlots(ts); persist(ts, serviceSlots); };
  const updateSS = (ss) => { setServiceSlots(ss); persist(trainerSlots, ss); };

  // ── Trainer grid drag & drop ──────────────────────────────────
  const dropOnTrainerCell = (day, time) => {
    if (!dragSrc) return;
    if (dragSrc.type === "person") {
      const existing = trainerSlots.find(s=>s.day===day&&s.time===time);
      if (existing) return; // cell occupied
      const ns = {id:makeId(), day, time, time_end:DEFAULT_DURATION[time]||"", trainer:dragSrc.name, class_type:""};
      const updated = [...trainerSlots, ns];
      setTrainerSlots(updated); persist(updated, serviceSlots);
      setEditSlot(ns);
    } else if (dragSrc.type === "slot") {
      const updated = trainerSlots.map(s=>s.id===dragSrc.slot.id?{...s,day,time,time_end:DEFAULT_DURATION[time]||s.time_end}:s);
      updateTS(updated);
    }
    setDragSrc(null);
  };

  // ── Service grid drag & drop ──────────────────────────────────
  const dropOnServiceCell = (day, shift) => {
    if (!dragSrc) return;
    if (dragSrc.type === "person") {
      const ns = {id:makeId(), day, shift, person:dragSrc.name, time_start:"", time_end:""};
      const updated = [...serviceSlots, ns];
      setServiceSlots(updated); persist(trainerSlots, updated);
      setEditSlot({...ns, _type:"service"});
    } else if (dragSrc.type === "service_slot") {
      const updated = serviceSlots.map(s=>s.id===dragSrc.slot.id?{...s,day,shift}:s);
      updateSS(updated);
    }
    setDragSrc(null);
  };

  // Mobile: tap empty slot → pick trainer + course
  const mobileTapTrainerSlot = (day, time) => {
    if (!isMobilePlan) return false;
    const existing = trainerSlots.find(s=>s.day===day&&s.time===time);
    if (existing) { setEditSlot({...existing}); return true; }
    setMobilePickSlot({day, time});
    return true;
  };
  const mobileTapServiceSlot = (day, shift) => {
    if (!isMobilePlan) return false;
    setMobilePickSlot({day, shift, _type:"service"});
    return true;
  };
  const mobileAssignTrainer = (trainerName) => {
    if (!mobilePickSlot) return;
    const {day, time} = mobilePickSlot;
    const ns = {id:makeId(), day, time, time_end:DEFAULT_DURATION[time]||"", trainer:trainerName, class_type:""};
    const updated = [...trainerSlots, ns];
    setTrainerSlots(updated); persist(updated, serviceSlots);
    setEditSlot(ns);
    setMobilePickSlot(null);
  };
  const mobileAssignService = (personName) => {
    if (!mobilePickSlot) return;
    const {day, shift} = mobilePickSlot;
    const ns = {id:makeId(), day, shift, person:personName, time_start:"", time_end:""};
    const updated = [...serviceSlots, ns];
    setServiceSlots(updated); persist(trainerSlots, updated);
    setEditSlot({...ns, _type:"service"});
    setMobilePickSlot(null);
  };

  // ── Exports ───────────────────────────────────────────────────
  const exportPDF = () => window.print();

  const exportCSV = () => {
    const rows = [["Datum","Tag","Trainer","Kurs","Zeit"]];
    weekDates.forEach(({day,label})=>{
      TRAINER_TIMES.forEach(time=>{
        const s = trainerSlots.find(x=>x.day===day&&x.time===time);
        if(s) rows.push([label,day,s.trainer,s.class_type,`${s.time} - ${s.time_end}`]);
      });
    });
    downloadText(rows.map(r=>r.join(";")).join("\n"), `trainer_plan_${week}.csv`, "text/csv;charset=utf-8");
  };

  const exportServiceCSV = () => {
    const rows = [["Datum","Tag","Schicht","Person","Zeit"]];
    weekDates.forEach(({day,label})=>{
      SERVICE_SHIFTS.forEach(shift=>{
        serviceSlots.filter(s=>s.day===day&&s.shift===shift).forEach(s=>{
          rows.push([label,day,shift,s.person,`${s.time_start||""} - ${s.time_end||""}`]);
        });
      });
    });
    downloadText(rows.map(r=>r.join(";")).join("\n"), `service_plan_${week}.csv`, "text/csv;charset=utf-8");
  };

  const exportICal = (trainerName) => {
    const slots = trainerName ? trainerSlots.filter(s=>s.trainer===trainerName) : trainerSlots;
    let ical = "BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//BURN BXNG//Schedule//DE\r\nCALSCALE:GREGORIAN\r\n";
    slots.forEach(s=>{
      const wd = weekDates.find(d=>d.day===s.day);
      if(!wd) return;
      ical += `BEGIN:VEVENT\r\nUID:${s.id}@burnbxng\r\n`;
      ical += `DTSTART:${icalDate(wd.date,s.time)}\r\n`;
      ical += `DTEND:${icalDate(wd.date,s.time_end||s.time)}\r\n`;
      ical += `SUMMARY:${s.class_type||"Kurs"} — BURN BXNG\r\n`;
      ical += `DESCRIPTION:Trainer: ${s.trainer}\r\n`;
      ical += `LOCATION:BURN BXNG Frankfurt\r\nEND:VEVENT\r\n`;
    });
    ical += "END:VCALENDAR";
    downloadText(ical, `${trainerName||"alle"}_${week}.ics`, "text/calendar");
    setShowIcalPick(false);
  };

  // ── Save modal state (editSlot) ───────────────────────────────
  const saveEditSlot = () => {
    if (!editSlot) return;
    if (editSlot._type === "service") {
      const updated = serviceSlots.map(s=>s.id===editSlot.id?editSlot:s);
      // If it was new and not yet in list:
      const exists = serviceSlots.find(s=>s.id===editSlot.id);
      updateSS(exists ? updated : [...serviceSlots, editSlot]);
    } else {
      const exists = trainerSlots.find(s=>s.id===editSlot.id);
      const updated = trainerSlots.map(s=>s.id===editSlot.id?editSlot:s);
      updateTS(exists ? updated : [...trainerSlots, editSlot]);
    }
    setEditSlot(null);
  };

  const deleteSlot = (id, type) => {
    if (type==="service") updateSS(serviceSlots.filter(s=>s.id!==id));
    else updateTS(trainerSlots.filter(s=>s.id!==id));
    setEditSlot(null);
  };

  const weekLabel = `KW ${week.split("-W")[1]} · ${weekDates[0]?.label} – ${weekDates[6]?.label}.${weekDates[0]?.date.getFullYear()}`;

  const printStyle = `@media print{body>*:not(#plan-print-area){display:none!important}#plan-print-area{display:block!important;position:fixed;top:0;left:0;width:100%;z-index:9999;background:#fff;padding:16px}}@media screen{#plan-print-area{display:none}}`;

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",fontFamily:"'Inter',system-ui,sans-serif"}}>
      <style>{printStyle}</style>

      {/* ── Toolbar ── */}
      <div style={{padding:"12px 20px",borderBottom:"1px solid #e5e7eb",background:"#fff",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:0,background:"#f3f4f6",borderRadius:8,padding:2}}>
          {[["trainer","🥊 Trainer-Plan"],["service","🏢 Service-Plan"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)}
              style={{padding:"6px 14px",borderRadius:6,border:"none",background:tab===id?"#fff":"transparent",
                      color:tab===id?"#7c3aed":"#6b7280",fontWeight:tab===id?700:400,fontSize:13,cursor:"pointer",
                      boxShadow:tab===id?"0 1px 3px #00000015":"none"}}>
              {label}
            </button>
          ))}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:8}}>
          <button onClick={()=>setWeek(w=>shiftWeek(w,-1))} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:14}}>‹</button>
          <div style={{fontSize:13,fontWeight:600,color:"#374151",minWidth:220,textAlign:"center"}}>{weekLabel}</div>
          <button onClick={()=>setWeek(w=>shiftWeek(w,1))} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:14}}>›</button>
        </div>

        <div style={{flex:1}}/>
        <span style={{fontSize:11,color:saved?"#059669":"#9ca3af"}}>{saving?"Speichern…":saved?"✓ Gespeichert":""}</span>

        <button onClick={exportPDF} style={{padding:"6px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:500}}>📄 PDF</button>
        <button onClick={tab==="trainer"?exportCSV:exportServiceCSV} style={{padding:"6px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:500}}>📊 CSV</button>
        <div style={{position:"relative"}}>
          <button onClick={()=>setShowIcalPick(p=>!p)} style={{padding:"6px 12px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:600}}>📅 Kalender (.ics)</button>
          {showIcalPick&&(
            <div style={{position:"absolute",right:0,top:36,background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,boxShadow:"0 4px 20px #00000018",zIndex:100,minWidth:180,overflow:"hidden"}}>
              <div style={{padding:"8px 14px",fontSize:11,color:"#9ca3af",fontWeight:700,borderBottom:"1px solid #f3f4f6"}}>Für wen exportieren?</div>
              <button onClick={()=>exportICal(null)} style={{display:"block",width:"100%",padding:"8px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:13,textAlign:"left",color:"#111827"}}>👥 Alle Trainer</button>
              {trainers.map(t=>(
                <button key={t.id} onClick={()=>exportICal(t.name)}
                  style={{display:"block",width:"100%",padding:"7px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:13,textAlign:"left",color:"#111827"}}>
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        {/* ── Sidebar ── */}
        <div style={{width:148,borderRight:"1px solid #e5e7eb",background:"#fafafa",padding:12,display:"flex",flexDirection:"column",gap:6,overflowY:"auto",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1}}>
              {tab==="trainer"?"Trainer":"Personal"}
            </div>
            <button onClick={()=>setShowAddPerson(p=>!p)} title="Person hinzufügen"
              style={{width:20,height:20,borderRadius:"50%",background:"#7c3aed",color:"#fff",border:"none",cursor:"pointer",fontSize:14,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>+</button>
          </div>

          {/* Add person form */}
          {showAddPerson&&(
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:8,display:"flex",flexDirection:"column",gap:6}}>
              <input value={newPersonName} onChange={e=>setNewPersonName(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&addCustomPerson()}
                placeholder="Name…" autoFocus
                style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,outline:"none",width:"100%",boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {COLORS.map(c=>(
                  <div key={c} onClick={()=>setNewPersonColor(c)}
                    style={{width:16,height:16,borderRadius:"50%",background:c,cursor:"pointer",border:`2px solid ${newPersonColor===c?"#111":"transparent"}`}}/>
                ))}
              </div>
              <div style={{display:"flex",gap:4}}>
                <button onClick={addCustomPerson} style={{flex:1,padding:"4px 0",background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>✓</button>
                <button onClick={()=>setShowAddPerson(false)} style={{flex:1,padding:"4px 0",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,cursor:"pointer",color:"#6b7280"}}>✕</button>
              </div>
            </div>
          )}

          {[...(tab==="trainer"?trainers:personal), ...customPeople].map(u=>(
            <div key={u.id} draggable
              onDragStart={()=>setDragSrc({type:"person",name:u.name})}
              onDragEnd={()=>setDragSrc(null)}
              style={{padding:"7px 10px",borderRadius:8,background:`${u.color}18`,border:`1.5px solid ${u.color}40`,
                      color:u.color,fontSize:12,fontWeight:700,cursor:"grab",userSelect:"none",textAlign:"center",
                      display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
              <span style={{flex:1}}>{u.name}</span>
              {u.custom&&<span onClick={e=>{e.stopPropagation();setCustomPeople(p=>p.filter(x=>x.id!==u.id));}}
                style={{cursor:"pointer",opacity:.5,fontSize:14,lineHeight:1,fontWeight:400}}>×</span>}
            </div>
          ))}

          {tab==="trainer"&&(
            <>
              <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1,marginTop:8,marginBottom:2}}>Kurstypen</div>
              {CLASS_TYPES.map(ct=>(
                <div key={ct.name} style={{padding:"4px 8px",borderRadius:6,background:ct.color,color:ct.text,fontSize:10,fontWeight:600,textAlign:"center"}}>
                  {ct.name}
                </div>
              ))}
            </>
          )}
        </div>

        {/* ── Grid area ── */}
        <div style={{flex:1,overflowX:"auto",overflowY:"auto",padding:16}}>
          {tab==="trainer"?(
            /* Trainer Plan Grid */
            <table style={{borderCollapse:"separate",borderSpacing:3,minWidth:800}}>
              <thead>
                <tr>
                  <th style={{width:64,fontSize:11,color:"#9ca3af",fontWeight:600,textAlign:"left",paddingBottom:6,paddingLeft:4}}>Zeit</th>
                  {weekDates.map(({day,label,date})=>(
                    <th key={day} style={{minWidth:110,fontSize:12,fontWeight:700,color:"#374151",textAlign:"center",paddingBottom:6}}>
                      <div>{day}</div>
                      <div style={{fontSize:10,color:"#9ca3af",fontWeight:400}}>{label}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TRAINER_TIMES.map(time=>(
                  <tr key={time}>
                    <td style={{fontSize:11,color:"#6b7280",fontWeight:600,paddingRight:8,paddingTop:4,verticalAlign:"top",whiteSpace:"nowrap"}}>{time}</td>
                    {weekDates.map(({day})=>{
                      const slot = trainerSlots.find(s=>s.day===day&&s.time===time);
                      const ct = slot ? classColor(slot.class_type) : null;
                      const isOver = dragSrc && !slot;
                      return (
                        <td key={day}
                          data-drop-trainer={JSON.stringify({day, time})}
                          onDragOver={e=>{e.preventDefault();}}
                          onDrop={()=>dropOnTrainerCell(day,time)}
                          onClick={()=>{ if(isMobilePlan && !slot) mobileTapTrainerSlot(day,time); }}
                          style={{minHeight:72,verticalAlign:"top"}}>
                          {slot?(
                            <div onClick={()=>setEditSlot({...slot})}
                              draggable onDragStart={()=>setDragSrc({type:"slot",slot})} onDragEnd={()=>setDragSrc(null)}
                              style={{padding:"7px 9px",borderRadius:8,background:ct?.color||"#f3f4f6",color:ct?.text||"#374151",
                                      cursor:"pointer",fontSize:11,minHeight:68,border:`1.5px solid ${ct?.color||"#e5e7eb"}`,
                                      display:"flex",flexDirection:"column",gap:3}}>
                              <div style={{fontWeight:700,fontSize:12,color:trainerColor(slot.trainer)}}>{slot.trainer}</div>
                              <div style={{fontWeight:600,fontSize:10,lineHeight:1.3}}>{slot.class_type||<span style={{opacity:.5}}>Kurs wählen…</span>}</div>
                              <div style={{fontSize:10,opacity:.7,marginTop:"auto"}}>{slot.time}–{slot.time_end}</div>
                            </div>
                          ):(
                            <div style={{minHeight:68,borderRadius:8,border:`1.5px dashed ${isMobilePlan?"#c4b5fd":"#e5e7eb"}`,background:isOver||mobilePickSlot?"#f5f3ff":"transparent",
                                         display:"flex",alignItems:"center",justifyContent:"center",color:isMobilePlan?"#c4b5fd":"#d1d5db",fontSize:isMobilePlan?22:18}}>
                              {isMobilePlan?"＋":isOver?"＋":""}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ):(
            /* Service Plan Grid */
            <table style={{borderCollapse:"separate",borderSpacing:3,minWidth:600}}>
              <thead>
                <tr>
                  <th style={{width:80,fontSize:11,color:"#9ca3af",fontWeight:600,textAlign:"left",paddingBottom:6,paddingLeft:4}}>Tag</th>
                  {SERVICE_SHIFTS.map(sh=>(
                    <th key={sh} style={{minWidth:180,fontSize:12,fontWeight:700,color:"#374151",textAlign:"center",paddingBottom:6}}>
                      {sh==="Früh"?"🌅 Früh-Schicht":"🌆 Spät-Schicht"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekDates.map(({day,label})=>(
                  <tr key={day}>
                    <td style={{fontSize:12,fontWeight:700,color:"#374151",paddingRight:8,paddingTop:4,verticalAlign:"top"}}>
                      <div>{day}</div><div style={{fontSize:10,color:"#9ca3af",fontWeight:400}}>{label}</div>
                    </td>
                    {SERVICE_SHIFTS.map(shift=>{
                      const slots = serviceSlots.filter(s=>s.day===day&&s.shift===shift);
                      return (
                        <td key={shift}
                          data-drop-service={JSON.stringify({day, shift})}
                          onDragOver={e=>e.preventDefault()}
                          onDrop={()=>dropOnServiceCell(day,shift)}
                          onClick={()=>{ if(isMobilePlan) mobileTapServiceSlot(day,shift); }}
                          style={{verticalAlign:"top",minHeight:64}}>
                          <div style={{minHeight:64,borderRadius:8,border:`1.5px dashed ${isMobilePlan?"#c4b5fd":"#e5e7eb"}`,padding:4,display:"flex",flexDirection:"column",gap:3,background:dragSrc&&slots.length===0?"#f5f3ff":"transparent"}}>
                            {slots.map(s=>{
                              const col = trainerColor(s.person);
                              return (
                                <div key={s.id} onClick={e=>{e.stopPropagation();setEditSlot({...s,_type:"service"});}}
                                  draggable onDragStart={()=>setDragSrc({type:"service_slot",slot:s})} onDragEnd={()=>setDragSrc(null)}
                                  style={{padding:"6px 9px",borderRadius:7,background:`${col}18`,border:`1.5px solid ${col}40`,
                                          color:col,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                                  {s.person}
                                  {(s.time_start||s.time_end)&&<div style={{fontSize:10,fontWeight:400,color:"#6b7280",marginTop:2}}>{s.time_start}–{s.time_end}</div>}
                                </div>
                              );
                            })}
                            {isMobilePlan&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#c4b5fd",fontSize:20}}>＋</div>}
                            {!isMobilePlan&&dragSrc&&slots.length===0&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#c4b5fd",fontSize:20}}>＋</div>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editSlot&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>{if(e.target===e.currentTarget)setEditSlot(null);}}>
          <div style={{background:"#fff",borderRadius:16,padding:24,width:340,maxWidth:"calc(100vw - 32px)",boxShadow:"0 20px 60px #00000040",display:"flex",flexDirection:"column",gap:14}}>
            <div style={{fontWeight:700,fontSize:16,color:"#111827"}}>
              {editSlot._type==="service"?"🏢 Schicht bearbeiten":"🥊 Kurs bearbeiten"}
            </div>

            {editSlot._type!=="service"&&(
              <>
                <div>
                  <div style={{fontSize:12,color:"#6b7280",marginBottom:6,fontWeight:600}}>Trainer</div>
                  <select value={editSlot.trainer||""} onChange={e=>setEditSlot(s=>({...s,trainer:e.target.value}))}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,outline:"none"}}>
                    {[...trainers,...customPeople].map(t=><option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:12,color:"#6b7280",marginBottom:6,fontWeight:600}}>Kurstyp</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {CLASS_TYPES.map(ct=>(
                      <button key={ct.name} onClick={()=>setEditSlot(s=>({...s,class_type:ct.name}))}
                        style={{padding:"4px 10px",borderRadius:20,border:`2px solid ${editSlot.class_type===ct.name?ct.text:"transparent"}`,
                                background:ct.color,color:ct.text,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {ct.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {[["Von","time"],["Bis","time_end"]].map(([label,field])=>(
                    <div key={field} style={{flex:1}}>
                      <div style={{fontSize:12,color:"#6b7280",marginBottom:4,fontWeight:600}}>{label}</div>
                      <input type="time" value={editSlot[field]||""} onChange={e=>setEditSlot(s=>({...s,[field]:e.target.value}))}
                        style={{width:"100%",padding:"7px 8px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,outline:"none"}}/>
                    </div>
                  ))}
                </div>
              </>
            )}

            {editSlot._type==="service"&&(
              <>
                <div>
                  <div style={{fontSize:12,color:"#6b7280",marginBottom:6,fontWeight:600}}>Person</div>
                  <select value={editSlot.person||""} onChange={e=>setEditSlot(s=>({...s,person:e.target.value}))}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,outline:"none"}}>
                    {[...personal,...trainers,...customPeople].map(u=><option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {[["Schicht-Beginn","time_start"],["Schicht-Ende","time_end"]].map(([label,field])=>(
                    <div key={field} style={{flex:1}}>
                      <div style={{fontSize:12,color:"#6b7280",marginBottom:4,fontWeight:600}}>{label}</div>
                      <input type="time" value={editSlot[field]||""} onChange={e=>setEditSlot(s=>({...s,[field]:e.target.value}))}
                        style={{width:"100%",padding:"7px 8px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,outline:"none"}}/>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={()=>deleteSlot(editSlot.id, editSlot._type==="service"?"service":"trainer")}
                style={{padding:"8px 14px",background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:8,color:"#dc2626",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                🗑️ Löschen
              </button>
              <button onClick={()=>setEditSlot(null)}
                style={{flex:1,padding:"8px 14px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:8,color:"#374151",fontSize:13,cursor:"pointer"}}>
                Abbrechen
              </button>
              <button onClick={saveEditSlot}
                style={{flex:1,padding:"8px 14px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                ✓ Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile Slot Picker ── */}
      {mobilePickSlot&&(
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}
          onClick={()=>setMobilePickSlot(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",boxShadow:"0 -8px 40px #00000030",maxHeight:"80vh",overflowY:"auto"}}>
            {/* Handle bar */}
            <div style={{width:40,height:4,borderRadius:2,background:"#e5e7eb",margin:"0 auto 16px"}}/>

            {mobilePickSlot._type==="service"?(
              <>
                <div style={{fontWeight:700,fontSize:16,color:"#111827",marginBottom:4}}>
                  Person zuweisen
                </div>
                <div style={{fontSize:13,color:"#6b7280",marginBottom:16}}>
                  {mobilePickSlot.day} · {mobilePickSlot.shift === "Früh" ? "🌅 Früh-Schicht" : "🌆 Spät-Schicht"}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[...personal,...trainers,...customPeople].map(u=>(
                    <button key={u.id} onClick={()=>mobileAssignService(u.name)}
                      style={{padding:"12px 16px",borderRadius:10,background:`${u.color}18`,border:`1.5px solid ${u.color}40`,
                              color:u.color,fontSize:14,fontWeight:700,cursor:"pointer",textAlign:"left"}}>
                      {u.name}
                    </button>
                  ))}
                </div>
              </>
            ):(
              <>
                <div style={{fontWeight:700,fontSize:16,color:"#111827",marginBottom:4}}>
                  Trainer zuweisen
                </div>
                <div style={{fontSize:13,color:"#6b7280",marginBottom:16}}>
                  {mobilePickSlot.day} · {mobilePickSlot.time} Uhr
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[...trainers,...customPeople].map(u=>(
                    <button key={u.id} onClick={()=>mobileAssignTrainer(u.name)}
                      style={{padding:"12px 16px",borderRadius:10,background:`${u.color}18`,border:`1.5px solid ${u.color}40`,
                              color:u.color,fontSize:14,fontWeight:700,cursor:"pointer",textAlign:"left"}}>
                      {u.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            <button onClick={()=>setMobilePickSlot(null)}
              style={{marginTop:16,width:"100%",padding:"12px",background:"#f3f4f6",border:"none",borderRadius:10,fontSize:14,color:"#6b7280",cursor:"pointer"}}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Print area */}
      <div id="plan-print-area">
        <div style={{fontWeight:800,fontSize:18,marginBottom:12}}>BURN BXNG — {tab==="trainer"?"Trainer-Plan":"Service-Plan"} — {weekLabel}</div>
        {tab==="trainer"?(
          <table style={{borderCollapse:"collapse",width:"100%",fontSize:11}}>
            <thead><tr><th style={{border:"1px solid #ccc",padding:4}}>Zeit</th>{weekDates.map(d=><th key={d.day} style={{border:"1px solid #ccc",padding:4}}>{d.day} {d.label}</th>)}</tr></thead>
            <tbody>{TRAINER_TIMES.map(t=><tr key={t}><td style={{border:"1px solid #ccc",padding:4,fontWeight:700}}>{t}</td>{weekDates.map(({day})=>{const s=trainerSlots.find(x=>x.day===day&&x.time===t);return <td key={day} style={{border:"1px solid #ccc",padding:4,background:s?classColor(s.class_type).color:""}}>{s?`${s.trainer}\n${s.class_type}`:""}</td>;})}</tr>)}</tbody>
          </table>
        ):(
          <table style={{borderCollapse:"collapse",width:"100%",fontSize:11}}>
            <thead><tr><th style={{border:"1px solid #ccc",padding:4}}>Tag</th><th style={{border:"1px solid #ccc",padding:4}}>Früh</th><th style={{border:"1px solid #ccc",padding:4}}>Spät</th></tr></thead>
            <tbody>{weekDates.map(({day,label})=><tr key={day}><td style={{border:"1px solid #ccc",padding:4,fontWeight:700}}>{day} {label}</td>{SERVICE_SHIFTS.map(sh=><td key={sh} style={{border:"1px solid #ccc",padding:4}}>{serviceSlots.filter(s=>s.day===day&&s.shift===sh).map(s=>`${s.person} ${s.time_start||""}–${s.time_end||""}`).join(", ")}</td>)}</tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ─── Class Visits Modal ─────────────────────────────────────────── */
function ClassVisitsModal({ classEntry, onClose }) {
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!classEntry?.class_id) { setLoading(false); return; }
    fetch(`${API_URL}/staff/class-visits/${classEntry.class_id}`)
      .then(r => r.json())
      .then(d => { setParticipants(d.participants || []); setLoading(false); })
      .catch(() => { setParticipants([]); setLoading(false); });
  }, [classEntry?.class_id]);

  const regular = (participants || []).filter(p => !p.is_staff);
  const excluded = (participants || []).filter(p => p.is_staff);

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000060", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:"#fff", borderRadius:16, width:420, maxWidth:"calc(100vw - 24px)", maxHeight:"80vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px #00000030" }}>
        {/* Header */}
        <div style={{ padding:"18px 20px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"flex-start", gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#111827" }}>{classEntry?.class_name}</div>
            <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{classEntry?.class_date}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#9ca3af", padding:0, lineHeight:1 }}>×</button>
        </div>

        {/* Stats */}
        {!loading && participants && (
          <div style={{ padding:"10px 20px", display:"flex", gap:16, borderBottom:"1px solid #f3f4f6", fontSize:12 }}>
            <span style={{ color:"#059669", fontWeight:600 }}>✓ {regular.length} zahlend</span>
            {excluded.length > 0 && <span style={{ color:"#dc2626", fontWeight:600 }}>− {excluded.length} Staff</span>}
          </div>
        )}

        {/* List */}
        <div style={{ flex:1, overflowY:"auto", padding:"12px 20px" }}>
          {loading && <div style={{ textAlign:"center", padding:32, color:"#9ca3af" }}>Lade Teilnehmer...</div>}
          {!loading && !participants?.length && <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Keine Teilnehmerdaten gefunden</div>}
          {!loading && participants?.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {/* Regular participants first */}
              {regular.map((p, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #f9fafb" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"#059669", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>
                    {(p.name||"?")[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, fontSize:13, color:"#111827" }}>{p.name}</div>
                </div>
              ))}
              {/* Staff (excluded) with red styling */}
              {excluded.map((p, i) => (
                <div key={`x${i}`} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #f9fafb", opacity:0.7 }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"#fee2e2", color:"#dc2626", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>
                    {(p.name||"?")[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, fontSize:13, color:"#dc2626" }}>
                    <span style={{ fontWeight:600 }}>−</span> {p.name}
                    <span style={{ fontSize:11, marginLeft:6, color:"#9ca3af" }}>Staff</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Staff Payroll View ─────────────────────────────────────────── */
function StaffPayrollView({ user }) {
  const [selMonth, setSelMonth] = useState(new Date().toISOString().slice(0,7));
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitsModal, setVisitsModal] = useState(null);

  // Personal-staff payroll (from plan service_slots)
  const [personalEntries, setPersonalEntries] = useState([]);
  const [personalSummary, setPersonalSummary] = useState({ total_hours:0, total_pay:0, hourly_rate:15 });
  const [personalLoading, setPersonalLoading] = useState(true);
  const [showYearly, setShowYearly] = useState(false);
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const selYear = selMonth.slice(0,4);

  const isPersonal = user.role === "Personal" || String(user.roles||"").includes("Personal");
  const isAdmin = user && (user.isAdmin || user.adminAccess);

  // Pay rule for max potential calculation
  const [myPayRule, setMyPayRule] = useState(null);
  useEffect(() => {
    if (isPersonal) return;
    fetch(`${API_URL}/staff/pay-rules`)
      .then(r => r.json())
      .then(d => {
        const myName = (user.name || "").toLowerCase().trim();
        const rule = (d.rules || []).find(r =>
          (r.name || "").toLowerCase().trim() === myName ||
          (r.trainer || "").toLowerCase().trim() === myName
        );
        setMyPayRule(rule || null);
      })
      .catch(() => {});
  }, [user.name, isPersonal]);

  // ── PT Sessions (Personal Training calendar) ──────────────────────────────
  const [ptSessions, setPtSessions]   = useState([]);
  const [ptLoading,  setPtLoading]    = useState(false);
  const [newPT, setNewPT] = useState({
    client_name: '', session_date: new Date().toISOString().slice(0,10),
    time_start: '09:00', time_end: '10:00', notes: ''
  });
  // ISO week helper: returns "YYYY-Www"
  const todayISOWeek = () => {
    const d = new Date(); d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay()||7) + 1 - 3);
    const w1 = new Date(d.getFullYear(), 0, 4);
    return d.getFullYear() + '-W' + String(1 + Math.round(((d-w1)/86400000 - 3 + (w1.getDay()||7))/7)).padStart(2,'0');
  };
  const [ptWeek, setPtWeek] = useState(todayISOWeek);
  const fetchPT = () => {
    fetch(`${API_URL}/staff/pt-sessions?week=${ptWeek}`)
      .then(r => r.json())
      .then(d => setPtSessions(d.sessions || []))
      .catch(() => {});
  };
  useEffect(() => { fetchPT(); }, [ptWeek]); // eslint-disable-line

  // Trainer payroll
  useEffect(() => {
    if (isPersonal) return;
    const _cacheKey = `trainer_payroll_${user.name}_${selMonth}`;
    const _cached = lsGetStale(_cacheKey);
    if (_cached) { setPayroll(_cached); setLoading(false); } else { setLoading(true); }
    fetch(`${API_URL}/staff/payroll?month=${selMonth}`)
      .then(r => r.json())
      .then(d => {
        const myName = (user.name || "").toLowerCase().trim();
        const entries = (d.entries || []).filter(e =>
          normalizeTrainer(e.name || "").toLowerCase() === myName ||
          normalizeTrainer(e.trainer_raw || "").toLowerCase() === myName
        );
        setPayroll(entries);
        setLoading(false);
        lsSet(_cacheKey, entries);
      })
      .catch(() => setLoading(false));
  }, [selMonth, user.name, isPersonal]);

  // Personal-staff payroll from plan_editor service_slots
  useEffect(() => {
    if (!isPersonal) return;
    const _pCacheKey = `personal_payroll_${user.name}_${selMonth}`;
    const _pCached = lsGetStale(_pCacheKey);
    if (_pCached) { setPersonalEntries(_pCached.entries||[]); setPersonalSummary(_pCached.summary||{}); setPersonalLoading(false); } else { setPersonalLoading(true); }
    fetch(`${API_URL}/staff/payroll/personal?name=${encodeURIComponent(user.name)}&month=${selMonth}`)
      .then(r => r.json())
      .then(d => {
        setPersonalEntries(d.entries || []);
        setPersonalSummary({ total_hours: d.total_hours||0, total_pay: d.total_pay||0, hourly_rate: d.hourly_rate||15 });
        setPersonalLoading(false);
        lsSet(_pCacheKey, { entries: d.entries||[], summary: { total_hours: d.total_hours||0, total_pay: d.total_pay||0, hourly_rate: d.hourly_rate||15 } });
      })
      .catch(() => setPersonalLoading(false));
  }, [selMonth, user.name, isPersonal]);

  const fmt = n => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR"}).format(n||0);

  /* ── Personal-staff view ─────────────────────────────────────── */
  if (isPersonal) {
    if (personalLoading) return <BurnLoader text="Lade Abrechnung..." />;

    const downloadCSV = () => {
      const header = ["Datum","Tag","Schicht","Von","Bis","Stunden","Verdienst"];
      const rows = personalEntries.map(e=>[
        e.date, e.day, e.shift||"—", e.time_start||"—", e.time_end||"—",
        e.hours.toFixed(2), fmt(e.pay)
      ]);
      const csv = [header,...rows].map(r=>r.join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
      a.download = `abrechnung_${user.name.toLowerCase().replace(/\s+/g,"_")}_${selMonth}.csv`;
      a.click();
    };

    const loadYearly = async () => {
      setYearlyLoading(true);
      const d = await fetch(`${API_URL}/staff/payroll/personal/yearly?name=${encodeURIComponent(user.name)}&year=${selYear}`).then(r=>r.json()).catch(()=>null);
      setYearlyData(d);
      setYearlyLoading(false);
      setShowYearly(true);
    };

    const printPDF = () => {
      const win = window.open("","_blank");
      const monthLabel = new Date(selMonth+"-01").toLocaleDateString("de-DE",{month:"long",year:"numeric"});
      const rows = personalEntries.map(e=>`
        <tr>
          <td>${new Date(e.date+"T00:00").toLocaleDateString("de-DE",{weekday:"short",day:"2-digit",month:"2-digit",year:"numeric"})}</td>
          <td>${e.shift||"—"}</td>
          <td>${e.time_start||"—"}</td>
          <td>${e.time_end||"—"}</td>
          <td style="text-align:right">${e.hours.toFixed(1)} h</td>
          <td style="text-align:right">${fmt(e.pay)}</td>
        </tr>`).join("");
      win.document.write(`<!DOCTYPE html><html><head><title>Abrechnung ${user.name} ${selMonth}</title>
        <style>body{font-family:Arial,sans-serif;padding:32px;max-width:800px;margin:auto}
        h1{font-size:22px}h2{font-size:15px;color:#666;font-weight:normal;margin-top:4px}
        table{width:100%;border-collapse:collapse;margin-top:24px;font-size:13px}
        th{background:#f3f4f6;padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb}
        td{padding:8px 12px;border-bottom:1px solid #f3f4f6}
        tfoot td{font-weight:bold;background:#f0fdf4;border-top:2px solid #e5e7eb}
        .footer{margin-top:48px;border-top:1px solid #e5e7eb;padding-top:24px;display:flex;justify-content:space-between}
        </style></head><body>
        <h1>Abrechnung — ${user.name}</h1>
        <h2>${monthLabel}</h2>
        <table><thead><tr>
          <th>Datum</th><th>Schicht</th><th>Von</th><th>Bis</th><th style="text-align:right">Stunden</th><th style="text-align:right">Verdienst</th>
        </tr></thead><tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="4">Gesamt — ${personalSummary.total_hours.toFixed(1)} Stunden</td>
          <td style="text-align:right">${personalSummary.total_hours.toFixed(1)} h</td>
          <td style="text-align:right">${fmt(personalSummary.total_pay)}</td>
        </tr></tfoot></table>
        <div class="footer">
          <div>Datum: ____________</div>
          <div>Unterschrift Mitarbeiter: ____________________________</div>
          <div>Unterschrift Leitung: ____________________________</div>
        </div>
        </body></html>`);
      win.document.close();
      win.print();
    };

    const MONTHS_SHORT = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];

    return (
      <div style={{ flex:1, overflowY:"auto", padding:24 }}>
        <div style={{ maxWidth:820, margin:"0 auto" }}>
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, flexWrap:"wrap" }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#111827", flex:1 }}>Meine Abrechnung</div>
            <input type="month" value={selMonth} onChange={e=>setSelMonth(e.target.value)}
              style={{ padding:"6px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13 }} />
            {personalEntries.length > 0 && (
              <button onClick={downloadCSV} style={{ padding:"7px 14px", background:"#059669", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                ⬇ CSV
              </button>
            )}
            {personalEntries.length > 0 && (
              <button onClick={printPDF} style={{ padding:"7px 14px", background:"#374151", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                🖨️ PDF
              </button>
            )}
            <button onClick={showYearly ? ()=>setShowYearly(false) : loadYearly}
              style={{ padding:"7px 14px", background:showYearly?"#7c3aed":"#f3f4f6", color:showYearly?"#fff":"#374151", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              📊 {showYearly?"Monat":"Jahresübersicht"}
            </button>
          </div>

          {/* Jahresübersicht Chart */}
          {showYearly && (
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:"20px 16px", marginBottom:24 }}>
              <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:16 }}>📈 Jahresübersicht {selYear}</div>
              {yearlyLoading ? <BurnLoader text="Lade Jahresübersicht..." /> : yearlyData ? (() => {
                const maxPay = Math.max(...(yearlyData.months||[]).map(m=>m.pay), 1);
                return (
                  <div style={{ overflowX:"auto" }}>
                    <div style={{ display:"flex", gap:6, alignItems:"flex-end", minWidth:480, height:180, padding:"0 8px" }}>
                      {(yearlyData.months||[]).map((m,i)=>{
                        const barH = Math.max(4, (m.pay/maxPay)*140);
                        const hasData = m.pay > 0;
                        return (
                          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                            {hasData && <div style={{ fontSize:10, color:"#059669", fontWeight:700 }}>{fmt(m.pay).replace("€","").trim()}</div>}
                            <div title={`${MONTHS_SHORT[i]}: ${fmt(m.pay)}, ${m.hours.toFixed(1)}h, ${m.shifts} Schichten`}
                              style={{ width:"100%", height:barH, background:hasData?"#7c3aed":"#e5e7eb", borderRadius:"4px 4px 0 0", transition:"all 0.3s" }}/>
                            <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600 }}>{MONTHS_SHORT[i]}</div>
                            {hasData && <div style={{ fontSize:9, color:"#6b7280" }}>{m.hours.toFixed(0)}h</div>}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop:12, display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                      {[
                        { label:"Jahresverdienst", val:fmt((yearlyData.months||[]).reduce((s,m)=>s+m.pay,0)), color:"#059669" },
                        { label:"Jahresstunden",   val:`${(yearlyData.months||[]).reduce((s,m)=>s+m.hours,0).toFixed(1)} h`, color:"#0891b2" },
                        { label:"Schichten gesamt", val:(yearlyData.months||[]).reduce((s,m)=>s+m.shifts,0), color:"#7c3aed" },
                      ].map(s=>(
                        <div key={s.label} style={{ background:"#f9fafb", borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                          <div style={{ fontSize:11, color:"#6b7280", marginBottom:2 }}>{s.label}</div>
                          <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{s.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })() : <div style={{ color:"#9ca3af", textAlign:"center", padding:"20px 0" }}>Keine Daten</div>}
            </div>
          )}

          {/* Stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Verdienst gesamt</div>
              <div style={{ fontSize:24, fontWeight:800, color:"#059669" }}>{fmt(personalSummary.total_pay)}</div>
            </div>
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Stunden gesamt</div>
              <div style={{ fontSize:24, fontWeight:800, color:"#111827" }}>{personalSummary.total_hours.toFixed(1)} h</div>
            </div>
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Schichten</div>
              <div style={{ fontSize:24, fontWeight:800, color:"#7c3aed" }}>{personalEntries.length}</div>
            </div>
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Monat</div>
              <div style={{ fontSize:18, fontWeight:800, color:"#374151" }}>
                {new Date(selMonth+"-01").toLocaleDateString("de-DE",{month:"long",year:"numeric"})}
              </div>
            </div>
          </div>

          {/* Table */}
          {personalEntries.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🗓️</div>
              <div style={{ fontSize:15, fontWeight:600, color:"#374151", marginBottom:6 }}>Keine Schichten für {selMonth}</div>
              <div style={{ fontSize:13 }}>Schichten werden von Nahomi im Wochenplan eingetragen.</div>
            </div>
          ) : (
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:480 }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid #e5e7eb", background:"#f9fafb" }}>
                    <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#374151" }}>Datum</th>
                    <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#374151" }}>Schicht</th>
                    <th style={{ padding:"10px 16px", textAlign:"center", fontWeight:600, color:"#374151" }}>Von</th>
                    <th style={{ padding:"10px 16px", textAlign:"center", fontWeight:600, color:"#374151" }}>Bis</th>
                    <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#0891b2" }}>Stunden</th>
                    <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#059669" }}>Verdienst</th>
                  </tr>
                </thead>
                <tbody>
                  {personalEntries.map((e,i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #f3f4f6", background:i%2===0?"#fff":"#fafafa" }}>
                      <td style={{ padding:"10px 16px", color:"#374151", whiteSpace:"nowrap", fontWeight:600 }}>
                        {new Date(e.date+"T00:00:00").toLocaleDateString("de-DE",{weekday:"short",day:"2-digit",month:"2-digit"})}
                      </td>
                      <td style={{ padding:"10px 16px" }}>
                        <span style={{ fontSize:11, padding:"3px 10px", borderRadius:99,
                          background: e.shift==="Morgen"?"#fef3c7":e.shift==="Mittag"?"#dbeafe":"#f3e8ff",
                          color: e.shift==="Morgen"?"#92400e":e.shift==="Mittag"?"#1e40af":"#6b21a8",
                          fontWeight:600 }}>
                          {e.shift||"—"}
                        </span>
                      </td>
                      <td style={{ padding:"10px 16px", textAlign:"center", color:"#374151", fontFamily:"monospace" }}>{e.time_start||"—"}</td>
                      <td style={{ padding:"10px 16px", textAlign:"center", color:"#374151", fontFamily:"monospace" }}>{e.time_end||"—"}</td>
                      <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:700, color:"#0891b2" }}>
                        {e.hours > 0 ? `${e.hours.toFixed(1)} h` : "—"}
                      </td>
                      <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:700, color:"#059669" }}>{fmt(e.pay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:"#f0fdf4", borderTop:"2px solid #e5e7eb" }}>
                    <td colSpan={4} style={{ padding:"10px 16px", fontWeight:700, color:"#374151" }}>
                      Gesamt — {personalSummary.total_hours.toFixed(1)} Stunden
                    </td>
                    <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:800, color:"#0891b2" }}>
                      {personalSummary.total_hours.toFixed(1)} h
                    </td>
                    <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:800, color:"#059669" }}>{fmt(personalSummary.total_pay)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Info box */}
          <div style={{ marginTop:16, background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:10, padding:"12px 16px", fontSize:12, color:"#0369a1" }}>
            💡 Deine Schichten werden von Nahomi im Wochenplan eingetragen. Bei Fragen zur Abrechnung wende dich an die Studioleitung.
          </div>
        </div>
      </div>
    );
  }

  /* ── Trainer view ────────────────────────────────────────────── */
  const today = new Date().toISOString().slice(0,10);
  const isCurrentMonth = selMonth === new Date().toISOString().slice(0,7);
  const pastEntries   = payroll.filter(e => (e.class_date||"") <= today);
  const futureEntries = payroll.filter(e => (e.class_date||"") >  today);
  // Für vergangene Monate: alle Einträge gelten als "gehalten"
  const doneEntries   = isCurrentMonth ? pastEntries : payroll;
  const pendingEntries = isCurrentMonth ? futureEntries : [];

  const totalPay      = doneEntries.reduce((s,e)=>s+(e.total_pay||0),0);
  const totalClasses  = doneEntries.length;
  const totalBillable = doneEntries.reduce((s,e)=>s+(e.billable_participants||0),0);
  const totalExcluded = doneEntries.reduce((s,e)=>s+(e.staff_excluded||0),0);
  const pendingPay    = pendingEntries.reduce((s,e)=>s+(e.total_pay||0),0);
  const maxPotential = (() => {
    if (!myPayRule || totalClasses === 0) return null;
    const base   = parseFloat(myPayRule.base_fee || myPayRule.base_pay || 35);
    const pp     = parseFloat(myPayRule.per_participant || 0);
    const minTN  = parseInt(myPayRule.min_participants || 6);
    const model  = myPayRule.model || "base_only";
    const MAX_TN = 20;
    let perClass = base;
    if (model === "base_plus_retroactive" && pp > 0 && MAX_TN >= minTN)
      perClass = base + pp * MAX_TN;
    else if (model === "base_plus_overhang" && pp > 0 && MAX_TN > minTN)
      perClass = base + pp * (MAX_TN - minTN);
    else if (model === "per_participant" && pp > 0)
      perClass = base + pp * MAX_TN;
    return perClass * totalClasses;
  })();

  const downloadCSV = () => {
    const header = ["Datum","Kurs","Brutto TN","Abzug Staff","Netto TN","Verdienst"];
    const rows = payroll.map(e=>[
      e.class_date, `"${e.class_name}"`,
      (e.billable_participants||0) + (e.staff_excluded||0),
      e.staff_excluded||0,
      e.billable_participants||0,
      fmt(e.total_pay)
    ]);
    const csv = [header,...rows].map(r=>r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download = `abrechnung_${user.name.toLowerCase()}_${selMonth}.csv`;
    a.click();
  };

  if (loading) return <BurnLoader text="Lade Abrechnung..." />;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      {visitsModal && <ClassVisitsModal classEntry={visitsModal} onClose={()=>setVisitsModal(null)} />}
      <div style={{ maxWidth:820, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24, flexWrap:"wrap" }}>
          <div style={{ fontSize:18, fontWeight:700, color:"#111827", flex:1 }}>Meine Abrechnung</div>
          <input type="month" value={selMonth} onChange={e=>setSelMonth(e.target.value)}
            style={{ padding:"6px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13 }} />
          {payroll.length > 0 && (
            <button onClick={downloadCSV} style={{ padding:"7px 14px", background:"#7c3aed", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
              ⬇ CSV
            </button>
          )}
        </div>

        {/* Stats cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>
              {isCurrentMonth ? "Verdienst bisher" : "Gesamt im Monat"}
            </div>
            <div style={{ fontSize:24, fontWeight:800, color:"#9900cc" }}>{fmt(totalPay)}</div>
            {isCurrentMonth && pendingEntries.length > 0 && (
              <div style={{ fontSize:11, color:"#6b7280", marginTop:3 }}>
                + {fmt(pendingPay)} geplant
              </div>
            )}
          </div>
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>
              {isCurrentMonth ? "Kurse gehalten" : "Kurse gesamt"}
            </div>
            <div style={{ fontSize:24, fontWeight:800, color:"#111827" }}>{totalClasses}</div>
            {isCurrentMonth && pendingEntries.length > 0 && (
              <div style={{ fontSize:11, color:"#6b7280", marginTop:3 }}>
                + {pendingEntries.length} noch offen
              </div>
            )}
          </div>
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Netto Teilnehmer</div>
            <div style={{ fontSize:24, fontWeight:800, color:"#059669" }}>{totalBillable}</div>
            {totalExcluded > 0 && <div style={{ fontSize:11, color:"#dc2626", marginTop:2 }}>−{totalExcluded} Staff</div>}
          </div>
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:8, right:10, fontSize:18, opacity:0.15 }}>🚀</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Max. Potenzial</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#d97706" }}>
              {maxPotential !== null ? fmt(maxPotential) : "—"}
            </div>
            <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>bei 20 TN / Kurs</div>
          </div>
        </div>


        {/* ── Eigene PT-Sessions Zusammenfassung ─────────────────────── */}
        {(() => {
          const ownPTs = ptSessions.filter(s => s.session_date && s.session_date.startsWith(selMonth) && (s.trainer_name||'').toLowerCase() === (user.name||'').toLowerCase());
          if (ownPTs.length === 0) return null;
          return (
            <div style={{background:'#fff8f0',border:'1px solid #fed7aa',borderRadius:10,padding:'12px 16px',marginTop:12,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:20}}>🥊</span>
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:'#92400e'}}>Personal Trainings — {selMonth}</div>
                  <div style={{fontSize:12,color:'#b45309'}}>{ownPTs.length} PT{ownPTs.length!==1?'s':''} eingetragen → PT-Kalender-Tab für Details</div>
                </div>
              </div>
              <div style={{fontWeight:800,fontSize:16,color:'#dc2626'}}>−{ownPTs.length*10} €</div>
            </div>
          );
        })()}


        {payroll.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>💸</div>
            <div style={{ fontSize:14 }}>Keine Einträge für {selMonth}</div>
          </div>
        ) : (
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden", overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:480 }}>
              <thead>
                <tr style={{ borderBottom:"1px solid #e5e7eb", background:"#f9fafb" }}>
                  <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#374151" }}>Datum</th>
                  <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#374151" }}>Kurs</th>
                  <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#374151" }}>Angemeldet / Eingecheckt</th>
                  <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#dc2626" }}>Abzug</th>
                  <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#059669" }}>Netto TN</th>
                  <th style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color:"#374151" }}>Verdienst</th>
                </tr>
              </thead>
              <tbody>
                {payroll.map((e,i) => {
                  const gross     = (e.billable_participants||0) + (e.staff_excluded||0);
                  const deduction = e.staff_excluded||0;
                  const net       = e.billable_participants||0;
                  const isPending = isCurrentMonth && (e.class_date||"") > today;
                  return (
                    <tr key={i} style={{ borderBottom:"1px solid #f3f4f6", opacity: isPending ? 0.55 : 1, background: isPending ? "#fafafa" : "transparent" }}>
                      <td style={{ padding:"10px 16px", color: isPending ? "#9ca3af" : "#374151", whiteSpace:"nowrap" }}>
                        {e.class_date}
                        {isPending && <span style={{ marginLeft:6, fontSize:10, background:"#e5e7eb", color:"#6b7280", borderRadius:4, padding:"1px 5px", fontWeight:600 }}>GEPLANT</span>}
                      </td>
                      <td style={{ padding:"10px 16px" }}>
                        <button
                          onClick={() => setVisitsModal(e)}
                          style={{ background:"none", border:"none", color: isPending ? "#9ca3af" : "#7c3aed", cursor:"pointer", fontSize:13, fontWeight:600, textDecoration:"underline", padding:0, textAlign:"left" }}>
                          {e.class_name}
                        </button>
                      </td>
                      <td style={{ padding:"10px 16px", textAlign:"right", color:"#374151" }}>
                        {e.total_booked > 0 ? e.total_booked : gross}
                        <span style={{ color:"#6b7280" }}> / {gross}</span>
                        {e.no_shows > 0 && (
                          <span style={{ marginLeft:4, background:"#fee2e2", color:"#dc2626", borderRadius:4, padding:"1px 5px", fontSize:10, fontWeight:600 }}>
                            {e.no_shows} NS
                          </span>
                        )}
                      </td>
                      <td style={{ padding:"10px 16px", textAlign:"right", color: deduction > 0 ? "#dc2626" : "#9ca3af", fontWeight: deduction > 0 ? 600 : 400 }}>
                        {deduction > 0 ? `−${deduction}` : "—"}
                      </td>
                      <td style={{ padding:"10px 16px", textAlign:"right", color: isPending ? "#9ca3af" : "#059669", fontWeight:600 }}>{net}</td>
                      <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:700, color: isPending ? "#9ca3af" : "#059669" }}>{fmt(e.total_pay)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:"#f9fafb", borderTop:"2px solid #e5e7eb" }}>
                  <td colSpan={5} style={{ padding:"10px 16px", fontWeight:700, color:"#374151" }}>
                    Kursgehalt {isCurrentMonth && pendingEntries.length > 0 ? "(gehalten)" : ""}
                  </td>
                  <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:800, color:"#7c3aed" }}>{fmt(totalPay)}</td>
                </tr>
                {isCurrentMonth && pendingEntries.length > 0 && (
                  <tr style={{ background:"#f5f5f5", borderTop:"1px solid #e5e7eb" }}>
                    <td colSpan={5} style={{ padding:"8px 16px", fontWeight:600, color:"#9ca3af", fontSize:12 }}>
                      + {pendingEntries.length} geplante Kurse (Buchungsstand)
                    </td>
                    <td style={{ padding:"8px 16px", textAlign:"right", fontWeight:700, color:"#9ca3af", fontSize:12 }}>{fmt(pendingPay)}</td>
                  </tr>
                )}
                {(() => {
                  const ptMonth = ptSessions.filter(s => s.session_date && s.session_date.startsWith(selMonth) && (s.trainer_name||'').toLowerCase() === (user.name||'').toLowerCase());
                  const ptDed = ptMonth.length * 10;
                  return ptDed > 0 ? (
                    <>
                      <tr style={{ background:"#fff9f9", borderTop:"1px solid #fee2e2" }}>
                        <td colSpan={5} style={{ padding:"8px 16px", fontWeight:600, color:"#dc2626", fontSize:12 }}>PT-Abzug ({ptMonth.length}×)</td>
                        <td style={{ padding:"8px 16px", textAlign:"right", fontWeight:700, color:"#dc2626", fontSize:12 }}>−{fmt(ptDed)}</td>
                      </tr>
                      <tr style={{ background:"#f0fdf4", borderTop:"2px solid #d1fae5" }}>
                        <td colSpan={5} style={{ padding:"10px 16px", fontWeight:800, color:"#059669" }}>Auszahlung</td>
                        <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:900, color:"#059669" }}>{fmt(totalPay - ptDed)}</td>
                      </tr>
                    </>
                  ) : null;
                })()}
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── PT Kalender View ──────────────────────────────────────────────────── */
function PTKalenderView({ user }) {
  const [ptSessions, setPtSessions] = useState([]);
  const [ptLoading,  setPtLoading]  = useState(false);
  const [newPT, setNewPT] = useState({
    client_name: '', session_date: new Date().toISOString().slice(0,10),
    time_start: '09:00', time_end: '10:00'
  });

  const todayISOWeek = () => {
    const d = new Date(); d.setHours(0,0,0,0);
    const thu = new Date(d); thu.setDate(d.getDate() + 3 - ((d.getDay()+6)%7));
    const w1  = new Date(thu.getFullYear(), 0, 4);
    return thu.getFullYear() + "-W" + String(1 + Math.round(((thu - w1) / 86400000 - 3 + (w1.getDay()+6)%7) / 7)).padStart(2,"0");
  };

  const [ptWeek, setPtWeek] = useState(todayISOWeek);

  const fetchPT = () => {
    fetch(`${API_URL}/staff/pt-sessions?week=${ptWeek}`)
      .then(r => r.json())
      .then(d => setPtSessions(d.sessions || []))
      .catch(() => {});
  };
  useEffect(() => { fetchPT(); }, [ptWeek]); // eslint-disable-line

  const shiftWeek = (delta) => {
    const [yr, wn] = ptWeek.split("-W");
    const jan4 = new Date(parseInt(yr), 0, 4);
    const mon  = new Date(jan4.getTime() - ((jan4.getDay()+6)%7)*86400000 + (parseInt(wn)-1+delta)*7*86400000);
    const thu  = new Date(mon); thu.setDate(mon.getDate() + 3);
    const w1   = new Date(thu.getFullYear(), 0, 4);
    const newW = 1 + Math.round(((thu - w1) / 86400000 - 3 + (w1.getDay()+6)%7) / 7);
    setPtWeek(`${thu.getFullYear()}-W${String(newW).padStart(2,"0")}`);
  };

  // Build week days for current ptWeek
  const weekDays = (() => {
    const [yr, wn] = ptWeek.split("-W");
    const jan4 = new Date(parseInt(yr), 0, 4);
    const mon  = new Date(jan4.getTime() - ((jan4.getDay()+6)%7)*86400000 + (parseInt(wn)-1)*7*86400000);
    return Array.from({length:7}, (_,i) => {
      const d = new Date(mon); d.setDate(mon.getDate() + i);
      return d.toISOString().slice(0,10);
    });
  })();

  const DAY_NAMES = ["Mo","Di","Mi","Do","Fr","Sa","So"];

  const sessionsByDate = {};
  ptSessions.forEach(s => {
    if (!sessionsByDate[s.session_date]) sessionsByDate[s.session_date] = [];
    sessionsByDate[s.session_date].push(s);
  });

  const isOwn = (s) => (s.trainer_name||"").toLowerCase() === (user.name||"").toLowerCase();

  return (
    <div style={{padding:"0 0 40px"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:18,fontWeight:800,color:"#111"}}>🥊 PT-Kalender</div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <button onClick={()=>shiftWeek(-1)} style={{padding:"6px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,cursor:"pointer",fontSize:14}}>‹</button>
          <span style={{fontSize:13,fontWeight:600,color:"#374151",minWidth:90,textAlign:"center"}}>{ptWeek}</span>
          <button onClick={()=>shiftWeek(1)}  style={{padding:"6px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,cursor:"pointer",fontSize:14}}>›</button>
          <button onClick={fetchPT} style={{padding:"6px 10px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:6,cursor:"pointer",fontSize:13}}>↺</button>
        </div>
      </div>

      {/* Add new PT */}
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"16px",marginBottom:16}}>
        <div style={{fontWeight:600,fontSize:13,color:"#374151",marginBottom:12}}>➕ PT eintragen (wird dir mit −10€ abgezogen)</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{display:"flex",flexDirection:"column",gap:3,flex:"1 1 140px"}}>
            <label style={{fontSize:11,color:"#6b7280",fontWeight:600}}>Kundenname</label>
            <input value={newPT.client_name} onChange={e=>setNewPT(p=>({...p,client_name:e.target.value}))}
              placeholder="z.B. Max Müller"
              style={{padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:6,fontSize:13}} />
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:11,color:"#6b7280",fontWeight:600}}>Datum</label>
            <input type="date" value={newPT.session_date} onChange={e=>setNewPT(p=>({...p,session_date:e.target.value}))}
              style={{padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:6,fontSize:13}} />
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:11,color:"#6b7280",fontWeight:600}}>Von</label>
            <input type="time" value={newPT.time_start} onChange={e=>setNewPT(p=>({...p,time_start:e.target.value}))}
              style={{padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:6,fontSize:13,width:95}} />
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:11,color:"#6b7280",fontWeight:600}}>Bis</label>
            <input type="time" value={newPT.time_end} onChange={e=>setNewPT(p=>({...p,time_end:e.target.value}))}
              style={{padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:6,fontSize:13,width:95}} />
          </div>
          <button disabled={ptLoading||!newPT.client_name.trim()} onClick={()=>{
            if(!newPT.client_name.trim()) return;
            setPtLoading(true);
            fetch(`${API_URL}/staff/pt-sessions`,{method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify({trainer_name:user.name,...newPT})})
              .then(r=>r.json()).then(()=>{setNewPT(p=>({...p,client_name:""}));fetchPT();})
              .catch(()=>{}).finally(()=>setPtLoading(false));
          }} style={{padding:"8px 18px",background:"#9900cc",color:"#fff",border:"none",borderRadius:6,
            fontSize:13,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap",
            opacity:ptLoading||!newPT.client_name.trim()?0.5:1}}>
            {ptLoading?"...":"+ Eintragen"}
          </button>
        </div>
      </div>

      {/* Weekly calendar grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6}}>
        {weekDays.map((date, idx) => {
          const sessions = sessionsByDate[date] || [];
          const isToday = date === new Date().toISOString().slice(0,10);
          return (
            <div key={date} style={{background:"#fff",border:`1px solid ${isToday?"#9900cc":"#e5e7eb"}`,
              borderRadius:10,padding:"10px 8px",minHeight:100}}>
              <div style={{fontSize:11,fontWeight:700,color:isToday?"#9900cc":"#6b7280",marginBottom:6,textAlign:"center"}}>
                {DAY_NAMES[idx]}<br/>
                <span style={{fontSize:13,color:isToday?"#9900cc":"#111"}}>{date.slice(8)}</span>
              </div>
              {sessions.length === 0 ? (
                <div style={{fontSize:10,color:"#d1d5db",textAlign:"center",marginTop:8}}>frei</div>
              ) : sessions.map(s => (
                <div key={s.id} style={{background:isOwn(s)?"#f5d0fe":"#f3f4f6",borderRadius:6,
                  padding:"5px 7px",marginBottom:4,fontSize:11,border:`1px solid ${isOwn(s)?"#d946ef":"#e5e7eb"}`}}>
                  <div style={{fontWeight:700,color:isOwn(s)?"#7e22ce":"#374151",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {s.trainer_name}
                  </div>
                  <div style={{color:"#6b7280"}}>{s.time_start}–{s.time_end}</div>
                  <div style={{color:"#9ca3af",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.client_name}</div>
                  {isOwn(s) && (
                    <button onClick={()=>{
                      fetch(`${API_URL}/staff/pt-sessions/${s.id}?trainer_name=${encodeURIComponent(user.name)}`,{method:"DELETE"})
                        .then(()=>fetchPT()).catch(()=>{});
                    }} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",fontSize:12,padding:"2px 0",width:"100%",textAlign:"right"}}>✕ löschen</button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {ptSessions.length > 0 && (
        <div style={{marginTop:12,fontSize:12,color:"#6b7280",textAlign:"right"}}>
          {ptSessions.length} PT{ptSessions.length!==1?"s":""} diese Woche
        </div>
      )}
    </div>
  );
}


/* ─── Availability Calendar ─────────────────────────────────────────── */
const AVAIL_STATUS = { available:"#059669", unavailable:"#dc2626", preferred:"#d97706" };
const AVAIL_LABEL  = { available:"✓", unavailable:"✕", preferred:"★" };

function AvailabilityView({ user }) {
  const today = new Date();
  const [avTab, setAvTab] = useState("kalender"); // kalender | dauer | urlaub
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [days,  setDays]  = useState({});
  const [saving, setSaving] = useState(null);
  // Dauerverfügbarkeit
  const WEEKDAYS = ["Mo","Di","Mi","Do","Fr","Sa","So"];
  const [weekDays, setWeekDays] = useState({});
  const [weekSaving, setWeekSaving] = useState(false);
  // Urlaub
  const [vacations, setVacations] = useState([]);
  const [vacFrom, setVacFrom] = useState("");
  const [vacTo, setVacTo]   = useState("");
  const [vacNote, setVacNote] = useState("");
  const [vacSending, setVacSending] = useState(false);

  const monthKey = `${year}-${String(month+1).padStart(2,"0")}`;

  useEffect(() => {
    fetch(`${API_URL}/staff/availability?name=${encodeURIComponent(user.name)}&month=${monthKey}`)
      .then(r=>r.json()).then(d=>{
        const map={};
        (d.entries||[]).forEach(e=>{ map[e.date]=e.status; });
        setDays(map);
      }).catch(()=>{});
  }, [monthKey, user.name]);

  useEffect(() => {
    fetch(`${API_URL}/staff/availability/weekly?name=${encodeURIComponent(user.name)}`)
      .then(r=>r.json()).then(d=>setWeekDays(d.days||{})).catch(()=>{});
  }, [user.name]);

  useEffect(() => {
    fetch(`${API_URL}/staff/vacation?name=${encodeURIComponent(user.name)}`)
      .then(r=>r.json()).then(d=>setVacations(d.requests||[])).catch(()=>{});
  }, [user.name]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const pad = (firstDay+6)%7;

  const toggle = async (dateStr) => {
    const cur = days[dateStr];
    const next = !cur ? "available" : cur==="available" ? "unavailable" : cur==="unavailable" ? "preferred" : null;
    const newDays = {...days};
    if (next) newDays[dateStr]=next; else delete newDays[dateStr];
    setDays(newDays);
    setSaving(dateStr);
    try {
      await fetch(`${API_URL}/staff/availability`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:user.name, date:dateStr, status:next||null })
      });
    } catch {}
    setSaving(null);
  };

  const toggleWeekDay = (day) => {
    const cur = weekDays[day];
    const next = !cur ? "available" : cur==="available" ? "unavailable" : null;
    const newWD = {...weekDays};
    if (next) newWD[day]=next; else delete newWD[day];
    setWeekDays(newWD);
  };

  const saveWeekDays = async () => {
    setWeekSaving(true);
    try {
      await fetch(`${API_URL}/staff/availability/weekly`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:user.name, days:weekDays })
      });
    } catch {}
    setWeekSaving(false);
  };

  const submitVacation = async () => {
    if (!vacFrom||!vacTo) return;
    setVacSending(true);
    try {
      await fetch(`${API_URL}/staff/vacation`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:user.name, from_date:vacFrom, to_date:vacTo, note:vacNote })
      });
      const d = await fetch(`${API_URL}/staff/vacation?name=${encodeURIComponent(user.name)}`).then(r=>r.json());
      setVacations(d.requests||[]);
      setVacFrom(""); setVacTo(""); setVacNote("");
    } catch {}
    setVacSending(false);
  };

  const cancelVacation = async (id) => {
    await fetch(`${API_URL}/staff/vacation/${id}`, {method:"DELETE"}).catch(()=>{});
    setVacations(prev=>prev.filter(v=>v.id!==id));
  };

  const monthNames = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const STATUS_COLOR = {"available":"#059669","unavailable":"#dc2626"};
  const STATUS_LABEL = {"available":"✓ Verfügbar","unavailable":"✕ Nicht verfügbar"};
  const VAC_STATUS_COLOR = {"pending":"#d97706","approved":"#059669","rejected":"#dc2626"};
  const VAC_STATUS_LABEL = {"pending":"⏳ Offen","approved":"✅ Genehmigt","rejected":"❌ Abgelehnt"};

  const TABS = [
    { id:"kalender", label:"Kalender" },
    { id:"dauer",    label:"Dauerverfügbarkeit" },
    { id:"urlaub",   label:"Urlaub" },
  ];

  return (
    <div style={{flex:1,overflowY:"auto",padding:24}}>
      <div style={{maxWidth:680,margin:"0 auto"}}>
        {/* Tab nav */}
        <div style={{display:"flex",gap:4,marginBottom:20,background:"#f3f4f6",borderRadius:12,padding:4}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setAvTab(t.id)} style={{
              flex:1,padding:"8px 4px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:avTab===t.id?700:400,
              background:avTab===t.id?"#fff":"transparent",color:avTab===t.id?"#7c3aed":"#6b7280",
              boxShadow:avTab===t.id?"0 1px 3px rgba(0,0,0,0.1)":"none",transition:"all 0.15s"
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Kalender ── */}
        {avTab==="kalender" && (<>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <button onClick={()=>{const d=new Date(year,month-1);setYear(d.getFullYear());setMonth(d.getMonth());}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:16}}>‹</button>
            <div style={{flex:1,textAlign:"center",fontSize:17,fontWeight:700,color:"#111827"}}>{monthNames[month]} {year}</div>
            <button onClick={()=>{const d=new Date(year,month+1);setYear(d.getFullYear());setMonth(d.getMonth());}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:16}}>›</button>
          </div>
          <div style={{display:"flex",gap:12,marginBottom:16,fontSize:12,color:"#6b7280"}}>
            {Object.entries(AVAIL_STATUS).map(([s,c])=>(
              <span key={s} style={{display:"flex",alignItems:"center",gap:4}}>
                <span style={{width:14,height:14,borderRadius:4,background:c,display:"inline-block"}}/>
                {s==="available"?"Ich kann":s==="unavailable"?"Nicht möglich":"Bevorzugt"}
              </span>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:8}}>
            {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=>(
              <div key={d} style={{textAlign:"center",fontSize:11,color:"#9ca3af",fontWeight:600,padding:"4px 0"}}>{d}</div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
            {Array(pad).fill(null).map((_,i)=><div key={"p"+i}/>)}
            {Array(daysInMonth).fill(null).map((_,i)=>{
              const d = i+1;
              const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
              const status = days[dateStr];
              const isToday = d===today.getDate()&&month===today.getMonth()&&year===today.getFullYear();
              return (
                <div key={d} onClick={()=>toggle(dateStr)}
                  style={{aspectRatio:"1",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:10,cursor:"pointer",
                    background: status ? AVAIL_STATUS[status]+"22" : isToday?"#f5f3ff":"#f9fafb",
                    border:`2px solid ${status ? AVAIL_STATUS[status] : isToday?"#7c3aed":"#e5e7eb"}`,
                    position:"relative",transition:"all 0.15s"}}>
                  <span style={{fontSize:13,fontWeight:isToday?700:400,color: status?AVAIL_STATUS[status]:isToday?"#7c3aed":"#374151"}}>{d}</span>
                  {status && <span style={{position:"absolute",bottom:2,right:4,fontSize:10,color:AVAIL_STATUS[status],fontWeight:700}}>{AVAIL_LABEL[status]}</span>}
                  {saving===dateStr && <span style={{position:"absolute",top:1,right:2,fontSize:8,color:"#9ca3af"}}>…</span>}
                </div>
              );
            })}
          </div>
          <div style={{marginTop:20,fontSize:12,color:"#9ca3af",textAlign:"center"}}>Tippe auf einen Tag um deinen Status zu setzen. Nahomi sieht alle Einträge.</div>
        </>)}

        {/* ── Dauerverfügbarkeit ── */}
        {avTab==="dauer" && (<>
          <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,padding:"12px 16px",fontSize:13,color:"#0369a1",marginBottom:20}}>
            💡 Setze deine grundsätzliche Verfügbarkeit pro Wochentag. Diese gilt als Standard für Nahomi beim Planen.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10,marginBottom:24}}>
            {WEEKDAYS.map(day=>{
              const status = weekDays[day];
              const col = STATUS_COLOR[status]||"#e5e7eb";
              const bg = status ? col+"18" : "#f9fafb";
              return (
                <div key={day} onClick={()=>toggleWeekDay(day)} style={{
                  padding:"16px 12px",borderRadius:12,border:`2px solid ${col}`,
                  background:bg,cursor:"pointer",textAlign:"center",transition:"all 0.15s"
                }}>
                  <div style={{fontSize:16,fontWeight:800,color:status?col:"#374151",marginBottom:4}}>{day}</div>
                  <div style={{fontSize:11,color:status?col:"#9ca3af",fontWeight:600}}>
                    {STATUS_LABEL[status]||"Nicht gesetzt"}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={saveWeekDays} disabled={weekSaving} style={{
            width:"100%",padding:"12px",background:"#7c3aed",border:"none",borderRadius:10,
            color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer"
          }}>
            {weekSaving?"Speichert…":"💾 Dauerverfügbarkeit speichern"}
          </button>
          <div style={{marginTop:12,fontSize:12,color:"#9ca3af",textAlign:"center"}}>
            Klicke auf einen Tag zum Umschalten: Verfügbar → Nicht verfügbar → Nicht gesetzt
          </div>
        </>)}

        {/* ── Urlaub ── */}
        {avTab==="urlaub" && (<>
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
            <div style={{fontSize:15,fontWeight:700,color:"#111827",marginBottom:16}}>✈️ Urlaub beantragen</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Von</label>
                <input type="date" value={vacFrom} onChange={e=>setVacFrom(e.target.value)}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Bis</label>
                <input type="date" value={vacTo} onChange={e=>setVacTo(e.target.value)}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Anmerkung (optional)</label>
              <textarea value={vacNote} onChange={e=>setVacNote(e.target.value)} rows={2} placeholder="z.B. Urlaub in Spanien..."
                style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box",resize:"vertical"}}/>
            </div>
            <button onClick={submitVacation} disabled={vacSending||!vacFrom||!vacTo} style={{
              width:"100%",padding:"10px",background:vacFrom&&vacTo?"#7c3aed":"#d1d5db",border:"none",
              borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:vacFrom&&vacTo?"pointer":"not-allowed"
            }}>
              {vacSending?"Wird gesendet…":"📤 Urlaubsantrag einreichen"}
            </button>
          </div>

          {/* Meine Anträge */}
          <div style={{fontSize:14,fontWeight:700,color:"#111827",marginBottom:12}}>Meine Anträge</div>
          {vacations.length===0 ? (
            <div style={{textAlign:"center",padding:"40px 0",color:"#9ca3af",fontSize:13}}>Noch keine Urlaubsanträge</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {vacations.map(v=>(
                <div key={v.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>
                      {new Date(v.from_date+"T00:00").toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"numeric"})}
                      {" – "}
                      {new Date(v.to_date+"T00:00").toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"numeric"})}
                    </div>
                    {v.note && <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{v.note}</div>}
                    {v.admin_note && <div style={{fontSize:12,color:"#7c3aed",marginTop:2}}>Notiz: {v.admin_note}</div>}
                  </div>
                  <span style={{fontSize:11,padding:"4px 10px",borderRadius:99,fontWeight:700,
                    background:(VAC_STATUS_COLOR[v.status]||"#9ca3af")+"18",
                    color:VAC_STATUS_COLOR[v.status]||"#9ca3af"}}>
                    {VAC_STATUS_LABEL[v.status]||v.status}
                  </span>
                  {v.status==="pending" && (
                    <button onClick={()=>cancelVacation(v.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:18,padding:"0 4px"}}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

function AllAvailabilityView() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [data,  setData]  = useState([]);
  const [clearing, setClearing] = useState(false);

  const monthKey = `${year}-${String(month+1).padStart(2,"0")}`;
  const monthNames = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDay = (new Date(year, month, 1).getDay()+6)%7;
  const isPast = new Date(year, month+1, 0) < today;

  const load = () => {
    fetch(`${API_URL}/staff/availability?month=${monthKey}`)
      .then(r=>r.json()).then(d=>setData(d.entries||[])).catch(()=>{});
  };

  useEffect(()=>{ load(); },[monthKey]);

  const clearMonth = async () => {
    if (!window.confirm(`Alle Verfügbarkeiten für ${monthNames[month]} ${year} löschen?`)) return;
    setClearing(true);
    await fetch(`${API_URL}/staff/availability/month?month=${monthKey}`, { method:"DELETE" }).catch(()=>{});
    setData([]);
    setClearing(false);
  };

  const byDate = {};
  data.forEach(e=>{
    if(!byDate[e.date]) byDate[e.date]=[];
    byDate[e.date].push(e);
  });

  const allNames = [...new Set(STAFF_USERS.map(u=>u.name))];

  return (
    <div style={{flex:1,overflowY:"auto",padding:24}}>
      <div style={{maxWidth:860,margin:"0 auto",width:"100%"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:20,flexWrap:"wrap"}}>
          <button onClick={()=>{const d=new Date(year,month-1);setYear(d.getFullYear());setMonth(d.getMonth());}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:16}}>‹</button>
          <div style={{flex:1,textAlign:"center",fontSize:15,fontWeight:700,color:"#111827",minWidth:120}}>Alle Verfügbarkeiten — {monthNames[month]} {year}</div>
          <button onClick={()=>{const d=new Date(year,month+1);setYear(d.getFullYear());setMonth(d.getMonth());}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"4px 12px",cursor:"pointer",fontSize:16}}>›</button>
          {data.length > 0 && (
            <button onClick={clearMonth} disabled={clearing}
              style={{padding:"5px 12px",background:isPast?"#fee2e2":"#f3f4f6",border:`1px solid ${isPast?"#fca5a5":"#e5e7eb"}`,borderRadius:8,color:isPast?"#dc2626":"#6b7280",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>
              {clearing?"…":"🗑️ Monat löschen"}
            </button>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:6}}>
          {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=>(
            <div key={d} style={{textAlign:"center",fontSize:11,color:"#9ca3af",fontWeight:600,padding:"3px 0"}}>{d}</div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
          {Array(firstDay).fill(null).map((_,i)=><div key={"p"+i}/>)}
          {Array(daysInMonth).fill(null).map((_,i)=>{
            const d = i+1;
            const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const entries = byDate[dateStr]||[];
            const avail    = entries.filter(e=>e.status==="available");
            const unavail  = entries.filter(e=>e.status==="unavailable");
            const preferred= entries.filter(e=>e.status==="preferred");
            const isToday  = d===today.getDate()&&month===today.getMonth()&&year===today.getFullYear();
            return (
              <div key={d} style={{minHeight:"clamp(52px,10vw,80px)",padding:"2px 3px",borderRadius:6,background:isToday?"#f5f3ff":"#fff",border:`1px solid ${isToday?"#7c3aed":"#e5e7eb"}`}}>
                <div style={{fontSize:12,fontWeight:700,color:isToday?"#7c3aed":"#374151",marginBottom:3}}>{d}</div>
                {avail.map(e=><div key={e.name} style={{fontSize:11,background:"#dcfce7",color:"#059669",borderRadius:3,padding:"1px 4px",marginBottom:1,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>✓ {e.name}</div>)}
                {preferred.map(e=><div key={e.name} style={{fontSize:11,background:"#fef3c7",color:"#d97706",borderRadius:3,padding:"1px 4px",marginBottom:1,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>★ {e.name}</div>)}
                {unavail.map(e=><div key={e.name} style={{fontSize:11,background:"#fee2e2",color:"#dc2626",borderRadius:3,padding:"1px 4px",marginBottom:1,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>✕ {e.name}</div>)}
              </div>
            );
          })}
        </div>
        <div style={{marginTop:16,display:"flex",gap:8,flexWrap:"wrap"}}>
          {allNames.map(n=>{
            const u = STAFF_USERS.find(s=>s.name===n);
            return <span key={n} style={{fontSize:11,padding:"2px 8px",borderRadius:99,background:`${u?.color||"#6b7280"}18`,color:u?.color||"#6b7280",border:`1px solid ${u?.color||"#6b7280"}40`,fontWeight:600}}>{n}</span>;
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Permissions View ──────────────────────────────────────────────── */
const DASHBOARD_SECTIONS = [
  { id:"customers",   label:"Kundendaten",      icon:"👥" },
  { id:"pipeline",    label:"Pipeline",         icon:"🔀" },
  { id:"emails",      label:"E-Mails",          icon:"📧" },
  { id:"sms",         label:"SMS",              icon:"💬" },
  { id:"automations", label:"Automationen",     icon:"⚡" },
  { id:"analytics",   label:"Analytics",        icon:"📊" },
  { id:"cash",        label:"Cash",             icon:"💰" },
  { id:"profitability", label:"Profitabilität",   icon:"📈" },
  { id:"modules",      label:"Module",           icon:"🧩" },
  { id:"weekly",      label:"Weekly",           icon:"📈" },
  { id:"staff",       label:"Staff",            icon:"👥" },
  { id:"bestand",     label:"Bestand",          icon:"🔄" },
  { id:"segments",    label:"Segmente",         icon:"🎯" },
  { id:"leads",       label:"Leads",            icon:"◈"  },
  { id:"duplicates",  label:"Duplikate",        icon:"🔍" },
  { id:"journeys",      label:"Journeys",         icon:"🗺️" },
  { id:"journeyEditor",    label:"E-Mail Editor",  icon:"✏️" },
  { id:"emailBlockEditor", label:"Templates CMS",  icon:"📝" },
  { id:"templates",   label:"Templates",        icon:"📧" },
  { id:"messages",    label:"Nachrichten",      icon:"💬" },
  { id:"leaderboard",    label:"Rangliste",       icon:"🏆" },
  { id:"kommunikation",  label:"Kommunikation",   icon:"📬" },
];

// Staff users who can have individual section permissions
const PERMISSIONABLE_STAFF = STAFF_USERS.filter(u => u.type === "staff");

function UserAccountsPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ name:"", role:"Trainer", type:"staff" });
  const [freshPw, setFreshPw] = useState(null);   // { name, password } — wird genau einmal angezeigt
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetch(`${API_URL}/auth/users`).then(r => r.json())
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const createUser = async () => {
    if (!newUser.name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/auth/users`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(newUser),
      });
      const d = await r.json();
      if (r.ok) { setFreshPw({ name:newUser.name, password:d.password }); setNewUser({ name:"", role:"Trainer", type:"staff" }); load(); }
      else alert(d.detail || "Fehler beim Anlegen");
    } catch { alert("Fehler beim Anlegen"); }
    setBusy(false);
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Neues Passwort für ${u.name} generieren? Das alte wird ungültig.`)) return;
    try {
      const r = await fetch(`${API_URL}/auth/users/${u.username}/reset-password`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
      const d = await r.json();
      if (r.ok) setFreshPw({ name:u.name, password:d.password });
      else alert(d.detail || "Fehler");
    } catch { alert("Fehler"); }
  };

  const toggleActive = async (u) => {
    await fetch(`${API_URL}/auth/users/${u.username}`, {
      method:"PUT", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ active: !u.active }),
    }).catch(() => {});
    load();
  };

  const removeUser = async (u) => {
    if (!window.confirm(`${u.name} (${u.username}) endgültig löschen? Login + alle Sitzungen werden entfernt.`)) return;
    const r = await fetch(`${API_URL}/auth/users/${u.username}`, { method:"DELETE" }).catch(() => null);
    if (r && !r.ok) { const d = await r.json().catch(()=>({})); alert(d.detail || "Fehler beim Löschen"); }
    load();
  };

  return (
    <div style={{ marginBottom:32 }}>
      <div style={{ fontSize:18, fontWeight:700, color:"#111827", marginBottom:6 }}>👤 Benutzer & Passwörter</div>
      <div style={{ fontSize:13, color:"#6b7280", marginBottom:16 }}>
        Accounts anlegen, deaktivieren, löschen und Passwörter zurücksetzen — Passwörter werden sicher generiert und genau einmal angezeigt.
      </div>

      {freshPw && (
        <div style={{ background:"#ecfdf5", border:"1px solid #10b981", borderRadius:10, padding:"14px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div style={{ fontSize:13, color:"#065f46" }}>
            Neues Passwort für <b>{freshPw.name}</b>: <code style={{ background:"#d1fae5", padding:"3px 8px", borderRadius:6, fontSize:14, fontWeight:700 }}>{freshPw.password}</code>
          </div>
          <button onClick={() => { navigator.clipboard?.writeText(freshPw.password); }} style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #10b981", background:"#fff", color:"#065f46", fontSize:12, fontWeight:600, cursor:"pointer" }}>📋 Kopieren</button>
          <button onClick={() => setFreshPw(null)} style={{ padding:"6px 12px", borderRadius:8, border:"none", background:"transparent", color:"#065f46", fontSize:12, cursor:"pointer" }}>✕ Gesehen & notiert</button>
          <div style={{ fontSize:11, color:"#047857", width:"100%" }}>⚠️ Jetzt notieren/weitergeben — es wird nirgendwo noch einmal angezeigt.</div>
        </div>
      )}

      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <input value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name:e.target.value }))}
          placeholder="Name (z.B. Maria)" onKeyDown={e => e.key === "Enter" && createUser()}
          style={{ padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db", fontSize:13, width:180 }}/>
        <select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role:e.target.value }))}
          style={{ padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db", fontSize:13, background:"#fff" }}>
          <option>Trainer</option><option>Personal</option><option>Studioleitung</option>
        </select>
        <select value={newUser.type} onChange={e => setNewUser(p => ({ ...p, type:e.target.value }))}
          style={{ padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db", fontSize:13, background:"#fff" }}>
          <option value="staff">Staff-Zugang</option><option value="admin">Admin-Zugang</option>
        </select>
        <button onClick={createUser} disabled={busy || !newUser.name.trim()}
          style={{ padding:"9px 16px", borderRadius:8, border:"none", background:"#7c3aed", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", opacity:(busy || !newUser.name.trim()) ? 0.5 : 1 }}>
          + Mitarbeiter anlegen
        </button>
      </div>

      {loading ? <div style={{ color:"#6b7280", fontSize:13 }}>Lade Benutzer…</div> : (
        <div style={{ border:"1px solid #e5e7eb", borderRadius:10, overflow:"hidden" }}>
          {users.map((u, i) => (
            <div key={`${u.username}-${u.type}`} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderTop: i ? "1px solid #f3f4f6" : "none", background: u.active ? "#fff" : "#f9fafb", opacity: u.active ? 1 : 0.55 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", overflow:"hidden", background:`${u.color || "#7c3aed"}22`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, color:u.color || "#7c3aed", fontSize:13, flexShrink:0 }}>
                {u.photo ? <img src={u.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} alt=""/> : (u.name?.[0] || "?")}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{u.name}</span>
                <span style={{ fontSize:11, color:"#9ca3af", marginLeft:8 }}>{u.username} · {u.role || "—"}</span>
              </div>
              <span style={{ fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:99, background: u.type === "admin" ? "#ede9fe" : "#f0f9ff", color: u.type === "admin" ? "#7c3aed" : "#0369a1" }}>{u.type === "admin" ? "ADMIN" : "STAFF"}</span>
              <button onClick={() => resetPw(u)} title="Neues Passwort generieren" style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #d1d5db", background:"#fff", fontSize:11, cursor:"pointer", color:"#374151" }}>🔑 Passwort</button>
              <button onClick={() => toggleActive(u)} title={u.active ? "Deaktivieren (Login gesperrt)" : "Aktivieren"} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #d1d5db", background:"#fff", fontSize:11, cursor:"pointer", color: u.active ? "#b45309" : "#059669" }}>{u.active ? "⏸ Sperren" : "▶ Aktivieren"}</button>
              <button onClick={() => removeUser(u)} title="Endgültig löschen" style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #fecaca", background:"#fff", fontSize:11, cursor:"pointer", color:"#dc2626" }}>🗑</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PermissionsView() {
  const [permissions, setPermissions] = useState({});  // user_id → string[]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/staff/permissions`)
      .then(r => r.json())
      .then(d => { setPermissions(d.permissions || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = async (userId, sectionId) => {
    const current = permissions[userId] || [];
    const next = current.includes(sectionId)
      ? current.filter(s => s !== sectionId)
      : [...current, sectionId];
    const updated = { ...permissions, [userId]: next };
    setPermissions(updated);
    setSaving(userId);
    try {
      await fetch(`${API_URL}/staff/permissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, allowed_sections: next }),
      });
      setSaved(userId);
      setTimeout(() => setSaved(null), 1500);
    } catch {}
    setSaving(null);
  };

  const grantAll = async (userId) => {
    const all = DASHBOARD_SECTIONS.map(s => s.id);
    const updated = { ...permissions, [userId]: all };
    setPermissions(updated);
    setSaving(userId);
    try {
      await fetch(`${API_URL}/staff/permissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, allowed_sections: all }),
      });
      setSaved(userId);
      setTimeout(() => setSaved(null), 1500);
    } catch {}
    setSaving(null);
  };

  const revokeAll = async (userId) => {
    const updated = { ...permissions, [userId]: [] };
    setPermissions(updated);
    setSaving(userId);
    try {
      await fetch(`${API_URL}/staff/permissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, allowed_sections: [] }),
      });
    } catch {}
    setSaving(null);
  };

  if (loading) return <BurnLoader text="Lade Berechtigungen..." />;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24 }}>
      <div style={{ maxWidth:900, margin:"0 auto" }}>
        <UserAccountsPanel />
        <div style={{ fontSize:18, fontWeight:700, color:"#111827", marginBottom:6 }}>🔑 Berechtigungen</div>
        <div style={{ fontSize:13, color:"#6b7280", marginBottom:24 }}>
          Lege fest, welche Dashboard-Bereiche jeder Mitarbeiter sehen darf. Ohne Berechtigung sieht der Staff-User nur das Standard-Staff-Dashboard.
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {PERMISSIONABLE_STAFF.map(u => {
            const userPerms = permissions[u.id] || [];
            const isSaving = saving === u.id;
            const isSaved  = saved  === u.id;
            return (
              <div key={u.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, overflow:"hidden" }}>
                {/* User Header */}
                <div style={{ padding:"14px 18px", display:"flex", alignItems:"center", gap:12, borderBottom:"1px solid #f3f4f6", background:"#fafafa" }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:u.color||"#7c3aed", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700 }}>
                    {u.name[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#111827" }}>{u.name}</div>
                    <div style={{ fontSize:11, color:"#9ca3af" }}>{userPerms.length} Bereiche freigegeben</div>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => revokeAll(u.id)} style={{ padding:"5px 10px", background:"#fee2e2", border:"1px solid #fca5a5", borderRadius:6, color:"#dc2626", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                      Alle entziehen
                    </button>
                    <button onClick={() => grantAll(u.id)} style={{ padding:"5px 10px", background:"#dcfce7", border:"1px solid #86efac", borderRadius:6, color:"#059669", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                      Alle freigeben
                    </button>
                    {isSaving && <span style={{ fontSize:11, color:"#9ca3af", alignSelf:"center" }}>Speichern...</span>}
                    {isSaved  && <span style={{ fontSize:11, color:"#059669", alignSelf:"center" }}>✓ Gespeichert</span>}
                  </div>
                </div>
                {/* Section grid */}
                <div style={{ padding:"12px 18px", display:"flex", flexWrap:"wrap", gap:8 }}>
                  {DASHBOARD_SECTIONS.map(sec => {
                    const active = userPerms.includes(sec.id);
                    return (
                      <button key={sec.id} onClick={() => toggle(u.id, sec.id)}
                        style={{ padding:"6px 12px", borderRadius:20, border:`1px solid ${active ? "#7c3aed" : "#d1d5db"}`,
                                 background: active ? "#7c3aed18" : "#f9fafb",
                                 color: active ? "#7c3aed" : "#6b7280",
                                 fontSize:12, fontWeight: active ? 700 : 400, cursor:"pointer" }}>
                        {sec.icon} {sec.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Messages ──────────────────────────────────────────────────────── */
const ALL_USERS = [...STAFF_USERS, ...USERS];

function getAvatarProps(u) {
  if (u.photo) return { type:"img", src:u.photo };
  return { type:"fallback", color:u.color||"#7c3aed" };
}

function UserAvatar({ u, size=32 }) {
  const av = getAvatarProps(u);
  if (av.type==="img") return (
    <div style={{width:size,height:size,borderRadius:"50%",overflow:"hidden",flexShrink:0}}>
      <img src={av.src} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} alt={u.name}/>
    </div>
  );
  return (
    <div style={{width:size,height:size,borderRadius:"50%",background:`${av.color}22`,border:`2px solid ${av.color}44`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
      <img src={BURN_FALLBACK} className="burn-fallback-spin" style={{width:"70%",height:"70%",objectFit:"contain",opacity:0.7}} alt="BURN"/>
    </div>
  );
}

/* ─── Jitsi Meeting Banner ────────────────────────────────────────── */
function JitsiPanel({ room, displayName, channelLabel, onClose, onJoin }) {
  const meetUrl = `https://meet.jit.si/${room}`;
  const [copied, setCopied] = useState(false);

  const join = () => {
    window.open(meetUrl, "_blank", "noopener");
    onJoin?.();
  };

  const copy = () => {
    navigator.clipboard.writeText(meetUrl).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };

  return (
    <div style={{flexShrink:0,borderBottom:"2px solid #059669",background:"linear-gradient(135deg,#064e3b,#065f46)",padding:"16px 24px",display:"flex",alignItems:"center",gap:16}}>
      {/* Pulse indicator */}
      <div style={{position:"relative",flexShrink:0}}>
        <div style={{width:14,height:14,borderRadius:"50%",background:"#34d399"}}/>
        <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"#34d399",animation:"pulse 1.5s infinite",opacity:.6}}/>
      </div>
      <style>{`@keyframes pulse{0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(2);opacity:0}}`}</style>

      <div style={{flex:1}}>
        <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>📹 Meeting läuft — {channelLabel}</div>
        <div style={{fontSize:11,color:"#6ee7b7",marginTop:2}}>Öffnet im Browser-Tab · kein Install · unbegrenzte Zeit</div>
      </div>

      <button onClick={copy}
        style={{padding:"7px 14px",background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",
                borderRadius:8,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>
        {copied?"✓ Kopiert!":"🔗 Link kopieren"}
      </button>

      <button onClick={join}
        style={{padding:"8px 20px",background:"#34d399",border:"none",borderRadius:8,
                color:"#064e3b",fontSize:13,fontWeight:800,cursor:"pointer",flexShrink:0}}>
        Jetzt beitreten →
      </button>

      <button onClick={onClose}
        style={{padding:"7px 12px",background:"rgba(220,38,38,0.3)",border:"1px solid rgba(220,38,38,0.5)",
                borderRadius:8,color:"#fca5a5",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
        📵 Beenden
      </button>
    </div>
  );
}

const EMOJI_OPTS = ["👍","❤️","😂","😮","🔥","✅"];
const BASE_CHANNELS = [
  { id:"alle",     label:"👥 Alle" },
  { id:"trainer",  label:"🥊 Trainer" },
  { id:"staff",    label:"🏢 Staff" },
  { id:"announce", label:"📢 Ankündigungen" },
];

function MessagesView({ user, onUnreadChange }) {
  const isAdmin = user.type === "admin";
  const [channel, setChannel]         = useState("alle");
  const [messages, setMessages]       = useState([]);
  const [pinnedMsgs, setPinnedMsgs]   = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [dmChannels, setDmChannels]   = useState([]);
  const [showDmPicker, setShowDmPicker] = useState(false);
  const [text, setText]               = useState("");
  const [sending, setSending]         = useState(false);
  const [replyTo, setReplyTo]         = useState(null);
  const [forwardMsg, setForwardMsg]   = useState(null);
  const [hoveredMsg, setHoveredMsg]   = useState(null);
  const [tappedMsg,  setTappedMsg]    = useState(null); // mobile long-press action sheet
  const isMobileMsg = useMobile();
  // Polls
  const [polls, setPolls]             = useState([]);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions]  = useState(["",""]);
  const [pollSending, setPollSending]  = useState(false);
  const [mentionSearch, setMentionSearch] = useState(null);
  const [recording, setRecording]     = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const audioChunksRef  = useRef([]);
  const bottomRef       = useRef(null);
  const fileRef         = useRef(null);
  const textRef         = useRef(null);
  const prevMsgCount    = useRef(0);
  // BURN AI
  const [aiHistory, setAiHistory]     = useState([]);
  const [aiInput,   setAiInput]       = useState("");
  const [aiLoading, setAiLoading]     = useState(false);
  const aiBottomRef = useRef(null);

  const canAnnounce = isAdmin || user.adminAccess || user.scheduleEditor;
  const canPost = channel !== "announce" || canAnnounce;

  // ── Data loaders ─────────────────────────────────────────────
  const loadDmChannels = useCallback(() => {
    fetch(`${API_URL}/staff/messages/dm-channels?user_id=${encodeURIComponent(user.id)}`)
      .then(r=>r.json())
      .then(d=>{
        const chs = (d.channels||[]).map(c=>({
          id: c.id,
          otherUser: ALL_USERS.find(u=>u.id===c.other_user_id) || { name: c.other_user_id, color:"#7c3aed" }
        }));
        setDmChannels(chs);
      }).catch(()=>{});
  },[user.id]);

  const loadUnread = useCallback((dms) => {
    const chs = [...BASE_CHANNELS.map(c=>c.id), ...(dms||dmChannels).map(d=>d.id)].join(",");
    fetch(`${API_URL}/staff/messages/unread?user_id=${encodeURIComponent(user.id)}&channels=${encodeURIComponent(chs)}`)
      .then(r=>r.json())
      .then(d=>{
        setUnreadCounts(d.counts||{});
        const total = Object.values(d.counts||{}).reduce((a,b)=>a+b,0);
        onUnreadChange?.(total);
      }).catch(()=>{});
  },[user.id, dmChannels, onUnreadChange]);

  const load = useCallback(() => {
    fetch(`${API_URL}/staff/messages?channel=${encodeURIComponent(channel)}`)
      .then(r=>r.json())
      .then(d=>{
        const msgs = d.messages||[];
        setMessages(msgs);
        // mark channel as read
        fetch(`${API_URL}/staff/messages/mark-channel-read`,{
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ user_id:user.id, channel })
        }).catch(()=>{});
      }).catch(()=>{});
    fetch(`${API_URL}/staff/messages/pinned?channel=${encodeURIComponent(channel)}`)
      .then(r=>r.json()).then(d=>setPinnedMsgs(d.messages||[])).catch(()=>{});
    fetch(`${API_URL}/staff/polls?channel=${encodeURIComponent(channel)}`)
      .then(r=>r.json()).then(d=>setPolls(d.polls||[])).catch(()=>{});
  },[channel, user.id]);

  useEffect(()=>{ loadDmChannels(); },[]);
  useEffect(()=>{ load(); },[channel]);
  useEffect(()=>{
    const iv = setInterval(()=>{
      if (document.visibilityState !== "visible") return;  // Hintergrund-Tabs pollen nicht
      load(); loadUnread(); loadDmChannels();
    },30000);
    return ()=>clearInterval(iv);
  },[load, loadUnread]);
  useEffect(()=>{ loadUnread(); },[channel]);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messages]);

  // Push notifications setup
  useEffect(()=>{
    if("Notification" in window && Notification.permission==="default") Notification.requestPermission();
  },[]);

  // Notify on new messages
  useEffect(()=>{
    if(messages.length > prevMsgCount.current){
      messages.slice(prevMsgCount.current).forEach(m=>{
        if(m.sender_id!==user.id && typeof Notification!=="undefined" && Notification.permission==="granted" && document.hidden){
          try{ new Notification(`${m.sender_name}`,{ body: m.text||"📎 Datei" }); }catch{}
        }
      });
    }
    prevMsgCount.current = messages.length;
  },[messages]);

  // ── Actions ──────────────────────────────────────────────────
  const send = async () => {
    if(!text.trim()||!canPost) return;
    const trimmed = text.trim();

    // ── Slash command intercept ──────────────────────────────────
    if(trimmed.startsWith("/")) {
      setText("");
      const parts = trimmed.slice(1).split(/\s+/);
      const cmd = parts[0].toLowerCase();
      const args = parts.slice(1).join(" ");
      // Show user's command as local message in AI history
      setAiHistory(h => [...h, { role:"user", content: trimmed }]);
      if(channel !== "burn_ai") setChannel("burn_ai");
      if(isMobileMsg) setShowChat(true);
      setAiLoading(true);
      try {
        const res = await fetch(`${API_URL}/staff/ai/slash`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ command: cmd, args, user_name: user.name })
        });
        const d = await res.json();
        setAiHistory(h => [...h, { role:"assistant", content: d.reply || "⚠️ Keine Antwort" }]);
      } catch {
        setAiHistory(h => [...h, { role:"assistant", content: "⚠️ Verbindungsfehler" }]);
      }
      setAiLoading(false);
      return;
    }

    setSending(true);
    const mentions=[];
    let match; const re=/@(\w+)/g;
    while((match=re.exec(trimmed))!==null){
      const found=ALL_USERS.find(u=>u.name.toLowerCase()===match[1].toLowerCase());
      if(found) mentions.push(found.id);
    }
    try {
      await fetch(`${API_URL}/staff/messages`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ channel, sender_id:user.id, sender_name:user.name,
          sender_color:user.color||"#7c3aed", sender_photo:user.photo||null,
          text:trimmed, reply_to:replyTo?.id||null, mentions })
      });
      setText(""); setReplyTo(null); load();
    } catch{}
    setSending(false);
  };

  const sendFile = async (e) => {
    const file=e.target.files[0]; if(!file) return;
    const fd=new FormData();
    fd.append("file",file); fd.append("channel",channel);
    fd.append("sender_id",user.id); fd.append("sender_name",user.name);
    fd.append("sender_color",user.color||"#7c3aed");
    try{ await fetch(`${API_URL}/staff/messages/file`,{method:"POST",body:fd}); load(); }catch{}
    e.target.value="";
  };

  const react = async (msgId,emoji) => {
    await fetch(`${API_URL}/staff/messages/${msgId}/react`,{
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ user_id:user.id, emoji })
    }).catch(()=>{});
    load();
  };

  const togglePin = async (msg) => {
    await fetch(`${API_URL}/staff/messages/${msg.id}/pin`,{
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ pinned:!msg.pinned })
    }).catch(()=>{});
    load();
  };

  const forward = async (msg, targetCh) => {
    await fetch(`${API_URL}/staff/messages`,{
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ channel:targetCh, sender_id:user.id, sender_name:user.name,
        sender_color:user.color||"#7c3aed", text:msg.text||"📎 Datei",
        reply_to:null, mentions:[], forwarded_from:msg.channel })
    }).catch(()=>{});
    setForwardMsg(null);
    if(targetCh===channel) load();
  };

  const startDm = (otherUser) => {
    const dmId = "dm_"+[user.id,otherUser.id].sort().join("_");
    setDmChannels(prev => prev.find(d=>d.id===dmId) ? prev : [...prev,{id:dmId,otherUser}]);
    setChannel(dmId); setShowDmPicker(false);
  };

  // Voice recording — iOS Safari compatible, toggle mode
  const startRecording = async () => {
    if(typeof MediaRecorder === "undefined"){
      alert("Sprachaufnahme wird von diesem Browser nicht unterstützt");
      return;
    }
    try{
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      audioChunksRef.current=[];
      // iOS Safari supports audio/mp4, desktop browsers support audio/webm
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                     : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                     : MediaRecorder.isTypeSupported("audio/mp4")  ? "audio/mp4"
                     : "";
      const rec = mimeType ? new MediaRecorder(stream, {mimeType}) : new MediaRecorder(stream);
      const actualMime = rec.mimeType || mimeType || "audio/webm";
      const ext = actualMime.includes("mp4") ? "mp4" : "webm";
      rec.ondataavailable = ev => audioChunksRef.current.push(ev.data);
      rec.onstop = async () => {
        const blob=new Blob(audioChunksRef.current,{type:actualMime});
        stream.getTracks().forEach(t=>t.stop());
        const fd=new FormData();
        fd.append("file",blob,`voice.${ext}`); fd.append("channel",channel);
        fd.append("sender_id",user.id); fd.append("sender_name",user.name);
        fd.append("sender_color",user.color||"#7c3aed");
        await fetch(`${API_URL}/staff/messages/file`,{method:"POST",body:fd}).catch(()=>{});
        load();
      };
      rec.start(); setMediaRecorder(rec); setRecording(true);
    }catch(err){
      if(err.name==="NotAllowedError"||err.name==="PermissionDeniedError"){
        alert("Bitte Mikrofonzugriff in den Browser-Einstellungen erlauben");
      } else {
        alert("Mikrofon nicht verfügbar: "+err.message);
      }
    }
  };
  const stopRecording = () => {
    mediaRecorder?.stop(); setRecording(false); setMediaRecorder(null);
  };

  // Polls
  const createPoll = async () => {
    const opts = pollOptions.filter(o=>o.trim());
    if (!pollQuestion.trim() || opts.length < 2) return;
    setPollSending(true);
    try {
      await fetch(`${API_URL}/staff/polls`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ channel, creator_id:user.id, creator_name:user.name, question:pollQuestion, options:opts })
      });
      setShowPollCreator(false); setPollQuestion(""); setPollOptions(["",""]);
      load();
    } catch {}
    setPollSending(false);
  };

  const votePoll = async (pollId, optionId) => {
    await fetch(`${API_URL}/staff/polls/${pollId}/vote`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ user_id:user.id, option_id:optionId })
    }).catch(()=>{});
    load();
  };

  const closePoll = async (pollId) => {
    await fetch(`${API_URL}/staff/polls/${pollId}/close`, {method:"PATCH"}).catch(()=>{});
    load();
  };

  const deletePoll = async (pollId) => {
    await fetch(`${API_URL}/staff/polls/${pollId}`, {method:"DELETE"}).catch(()=>{});
    load();
  };

  // @mention autocomplete
  const handleTextChange = (e) => {
    const val=e.target.value; setText(val);
    const before=val.slice(0,e.target.selectionStart);
    const m=before.match(/@(\w*)$/);
    setMentionSearch(m ? m[1].toLowerCase() : null);
  };
  const insertMention = (name) => {
    const cur=textRef.current?.selectionStart||text.length;
    const newTxt=text.slice(0,cur).replace(/@\w*$/,`@${name} `)+text.slice(cur);
    setText(newTxt); setMentionSearch(null); textRef.current?.focus();
  };
  const mentionSuggestions = mentionSearch!==null
    ? ALL_USERS.filter(u=>u.name.toLowerCase().startsWith(mentionSearch)&&u.id!==user.id).slice(0,5)
    : [];

  const fmtTime = iso => {
    if(!iso) return "";
    const d=new Date(iso), today=new Date();
    return d.toDateString()===today.toDateString()
      ? d.toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})
      : d.toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit"})+" "+d.toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"});
  };

  const renderText = (txt,mine) => {
    if(!txt) return null;
    // Split on @mentions AND URLs
    return txt.split(/(https?:\/\/[^\s]+|@\w+)/g).map((p,i)=>{
      if(p.startsWith("@")){
        const found=ALL_USERS.find(u=>u.name.toLowerCase()===p.slice(1).toLowerCase());
        const isMe=found?.id===user.id;
        return <span key={i} style={{fontWeight:700,color:isMe?"#fbbf24":mine?"#c4b5fd":"#7c3aed"}}>{p}</span>;
      }
      if(p.startsWith("http")){
        // Jitsi meeting links → masked button
        if(p.includes("meet.jit.si")){
          return (
            <a key={i} href={p} target="_blank" rel="noopener noreferrer"
              style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 14px",
                borderRadius:20,background:"#059669",color:"#fff",fontWeight:700,fontSize:12,
                textDecoration:"none",cursor:"pointer",verticalAlign:"middle",margin:"0 2px"}}>
              📹 Hier beitreten →
            </a>
          );
        }
        // All other URLs → normal clickable link
        return <a key={i} href={p} target="_blank" rel="noopener noreferrer"
          style={{color:mine?"#c4b5fd":"#7c3aed",textDecoration:"underline",wordBreak:"break-all"}}>{p}</a>;
      }
      return p;
    });
  };

  const isMobile = useMobile();
  const [showChat, setShowChat] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const isDm = channel.startsWith("dm_");
  const dmOther = isDm ? (dmChannels.find(d=>d.id===channel)?.otherUser) : null;
  const channelLabel = isDm ? `💬 ${dmOther?.name||"DM"}` : (BASE_CHANNELS.find(c=>c.id===channel)?.label||channel);
  const allForwardTargets = [...BASE_CHANNELS, ...dmChannels.map(d=>({id:d.id,label:`💬 ${d.otherUser?.name||d.id}`}))].filter(c=>c.id!==channel);
  const MEETING_ROOMS = { staff:"burnbxng-staff", trainer:"burnbxng-trainer", alle:"burnbxng-alle" };
  const meetingRoom = MEETING_ROOMS[channel] || "burnbxng-alle";

  const pickChannel = (id) => { setChannel(id); if(isMobile) setShowChat(true); };

  /* ── Channel list (sidebar on desktop, full screen on mobile) ── */
  const channelList = (
    <div style={{
      width: isMobile ? "100%" : 196,
      borderRight: isMobile ? "none" : "1px solid #e5e7eb",
      background:"#fafafa", flexDirection:"column",
      flexShrink:0, overflowY:"auto", WebkitOverflowScrolling:"touch", display:"flex"
    }}>
      {isMobile && (
        <div style={{padding:"16px 16px 8px", fontSize:18, fontWeight:800, color:"#111827"}}>💬 Nachrichten</div>
      )}
      <div style={{padding:"14px 14px 6px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1}}>Kanäle</div>
      {BASE_CHANNELS.map(c=>(
        <button key={c.id} onClick={()=>pickChannel(c.id)}
          style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            padding: isMobile ? "14px 16px" : "7px 14px",
            border:"none", background:channel===c.id?"#9900cc14":"transparent",
            color:channel===c.id?"#9900cc":"#374151",
            fontSize: isMobile ? 15 : 13, fontWeight:channel===c.id?700:400,
            cursor:"pointer",textAlign:"left",width:"100%",gap:6}}>
          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.label}</span>
          {(unreadCounts[c.id]||0)>0 && <span style={{background:"#dc2626",color:"#fff",borderRadius:99,fontSize:11,fontWeight:700,padding:"2px 8px",flexShrink:0}}>{unreadCounts[c.id]}</span>}
          {isMobile && <span style={{color:"#d1d5db",fontSize:16}}>›</span>}
        </button>
      ))}

      <div style={{padding:"12px 14px 4px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span>Direktnachrichten</span>
        <button onClick={()=>setShowDmPicker(p=>!p)} style={{background:"none",border:"none",cursor:"pointer",color:"#9900cc",fontSize:22,lineHeight:1,padding:"0 4px"}}>+</button>
      </div>

      {/* BURN AI — always pinned at top of DMs */}
      <button onClick={()=>{ setChannel("burn_ai"); if(isMobile) setShowChat(true); }}
        style={{display:"flex",alignItems:"center",gap:8,
          padding: isMobile ? "14px 16px" : "7px 14px",
          border:"none", background:channel==="burn_ai"?"#9900cc14":"transparent",
          color:channel==="burn_ai"?"#9900cc":"#374151",
          fontSize: isMobile?15:13, fontWeight:channel==="burn_ai"?700:400,
          cursor:"pointer",textAlign:"left",width:"100%"}}>
        <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#9900cc,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>🤖</div>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>BURN AI</span>
        <span style={{fontSize:10,background:"#9900cc",color:"#fff",padding:"2px 6px",borderRadius:99,flexShrink:0}}>AI</span>
      </button>

      {showDmPicker && (
        <div style={{margin:"2px 8px 8px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden"}}>
          {STAFF_USERS.filter(u=>u.id!==user.id).map(u=>(
            <button key={u.id} onClick={()=>startDm(u)}
              style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:13,color:"#374151",textAlign:"left"}}>
              <div style={{width:32,height:32,borderRadius:"50%",overflow:"hidden",flexShrink:0}}>
                {u.photo ? <img src={u.photo} style={{width:"100%",height:"100%",objectFit:"cover"}} alt={u.name}/> : <div style={{width:"100%",height:"100%",background:u.color||"#7c3aed",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700}}>{u.name[0]}</div>}
              </div>
              {u.name}
            </button>
          ))}
        </div>
      )}

      {dmChannels.map(dm=>(
        <button key={dm.id} onClick={()=>pickChannel(dm.id)}
          style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            padding: isMobile ? "14px 16px" : "7px 14px",
            border:"none", background:channel===dm.id?"#9900cc14":"transparent",
            color:channel===dm.id?"#9900cc":"#374151",
            fontSize: isMobile ? 15 : 13, fontWeight:channel===dm.id?700:400,
            cursor:"pointer",textAlign:"left",width:"100%",gap:6}}>
          <span style={{display:"flex",alignItems:"center",gap:8,overflow:"hidden"}}>
            <div style={{width:32,height:32,borderRadius:"50%",overflow:"hidden",flexShrink:0}}>
              {dm.otherUser?.photo ? <img src={dm.otherUser.photo} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",background:dm.otherUser?.color||"#7c3aed",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700}}>{(dm.otherUser?.name||"?")[0]}</div>}
            </div>
            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{dm.otherUser?.name||dm.id}</span>
          </span>
          {(unreadCounts[dm.id]||0)>0 && <span style={{background:"#dc2626",color:"#fff",borderRadius:99,fontSize:11,fontWeight:700,padding:"2px 8px",flexShrink:0}}>{unreadCounts[dm.id]}</span>}
          {isMobile && <span style={{color:"#d1d5db",fontSize:16}}>›</span>}
        </button>
      ))}
    </div>
  );

  /* ── BURN AI send handler ── */
  const sendAiMessage = async () => {
    const msg = aiInput.trim();
    if (!msg || aiLoading) return;
    const newHistory = [...aiHistory, { role:"user", content: msg }];
    setAiHistory(newHistory);
    setAiInput("");
    setAiLoading(true);
    setTimeout(() => aiBottomRef.current?.scrollIntoView({ behavior:"smooth" }), 50);
    try {
      const res = await fetch(`${API_URL}/staff/ai/chat`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ user_id: user.id, user_name: user.name, message: msg, history: aiHistory })
      });
      const d = await res.json();
      setAiHistory(h => [...h, { role:"assistant", content: d.reply || "⚠️ Keine Antwort" }]);
    } catch {
      setAiHistory(h => [...h, { role:"assistant", content: "⚠️ Verbindungsfehler" }]);
    }
    setAiLoading(false);
    setTimeout(() => aiBottomRef.current?.scrollIntoView({ behavior:"smooth" }), 100);
  };

  /* ── BURN AI chat pane ── */
  const aiChatPane = (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:"relative"}}>
      {/* Header */}
      <div style={{padding: isMobile?"10px 14px":"10px 20px",borderBottom:"1px solid #e5e7eb",background:"#fff",flexShrink:0,display:"flex",alignItems:"center",gap:10}}>
        {isMobile && <button onClick={()=>setShowChat(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#9900cc",padding:"0 4px",flexShrink:0}}>‹</button>}
        <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#9900cc,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>🤖</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14,color:"#111827"}}>BURN AI</div>
          <div style={{fontSize:11,color:"#9ca3af"}}>Kennt Betrieb, Q&A, Protokolle & alle Kanal-Nachrichten</div>
        </div>
        <button onClick={()=>setAiHistory([])} style={{fontSize:11,color:"#9ca3af",background:"#f3f4f6",border:"none",borderRadius:8,padding:"4px 10px",cursor:"pointer"}}>Zurücksetzen</button>
      </div>
      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding: isMobile?"12px":"16px 24px",display:"flex",flexDirection:"column",gap:12,minHeight:0}}>
        {aiHistory.length === 0 && (
          <div style={{textAlign:"center",padding:"48px 24px"}}>
            <div style={{fontSize:40,marginBottom:12}}>🤖</div>
            <div style={{fontSize:16,fontWeight:700,color:"#111827",marginBottom:8}}>BURN AI</div>
            <div style={{fontSize:13,color:"#6b7280",maxWidth:320,margin:"0 auto",lineHeight:1.6}}>
              Ich kenne Betrieb, Q&A, Protokolle und alle Chat-Nachrichten. Frag mich einfach!
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",marginTop:20}}>
              {["Was steht im Kassenabschluss?","Wie läuft die Payroll?","Was sind die Öffnungszeiten?","Wer ist heute für was zuständig?"].map(q=>(
                <button key={q} onClick={()=>{ setAiInput(q); }}
                  style={{padding:"6px 12px",background:"#f5f3ff",border:"1px solid #ddd6fe",borderRadius:20,fontSize:12,color:"#7c3aed",cursor:"pointer",whiteSpace:"nowrap"}}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {aiHistory.map((h, i) => (
          <div key={i} style={{display:"flex",flexDirection:h.role==="user"?"row-reverse":"row",gap:8,alignItems:"flex-end"}}>
            {h.role==="assistant" && (
              <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#9900cc,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>🤖</div>
            )}
            <div style={{
              maxWidth:"75%",padding:"10px 14px",borderRadius: h.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",
              background: h.role==="user"?"#9900cc":"#f3f4f6",
              color: h.role==="user"?"#fff":"#111827",
              fontSize:14,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"
            }}>{h.content}</div>
          </div>
        ))}
        {aiLoading && (
          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#9900cc,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>🤖</div>
            <div style={{padding:"10px 16px",background:"#f3f4f6",borderRadius:"16px 16px 16px 4px",display:"flex",gap:4,alignItems:"center"}}>
              {[0,1,2].map(j=><span key={j} style={{width:7,height:7,borderRadius:"50%",background:"#9ca3af",display:"inline-block",animation:`pulse ${0.6+j*0.2}s infinite`}}/>)}
            </div>
          </div>
        )}
        <div ref={aiBottomRef}/>
      </div>
      {/* Input */}
      <div style={{padding: isMobile?"8px 10px":"10px 16px",borderTop:"1px solid #e5e7eb",background:"#fff",display:"flex",gap: isMobile?4:8,alignItems:"flex-end",flexShrink:0}}>
        <textarea value={aiInput} onChange={e=>setAiInput(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendAiMessage();}}}
          placeholder="Frag BURN AI…"
          rows={1} style={{flex:1,minWidth:0,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:12,fontSize: isMobile?14:13,resize:"none",fontFamily:"inherit",outline:"none",lineHeight:1.4}}/>
        <button onClick={sendAiMessage} disabled={aiLoading||!aiInput.trim()}
          style={{width: isMobile?36:undefined,height: isMobile?36:undefined,padding: isMobile?"0":"8px 20px",background:aiInput.trim()&&!aiLoading?"#9900cc":"#e5e7eb",color:aiInput.trim()&&!aiLoading?"#fff":"#9ca3af",border:"none",borderRadius:12,fontSize: isMobile?18:13,fontWeight:700,cursor:aiInput.trim()&&!aiLoading?"pointer":"default",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
          {isMobile ? "➤" : (aiLoading?"…":"Senden")}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
      {/* Channel list: hidden on mobile when chat is open */}
      {(!isMobile || !showChat) && channelList}

      {/* ── BURN AI pane ── */}
      {(!isMobile || showChat) && channel === "burn_ai" && aiChatPane}

      {/* ── Chat area: hidden on mobile when list is shown ── */}
      {(!isMobile || showChat) && channel !== "burn_ai" && <div style={{
        flex:1, display:"flex", flexDirection:"column", overflow:"hidden", position:"relative"
      }}>
        {/* Header */}
        <div style={{padding: isMobile?"10px 14px":"10px 20px",borderBottom:"1px solid #e5e7eb",background:"#fff",flexShrink:0,display:"flex",alignItems:"center",gap:10}}>
          {isMobile && (
            <button onClick={()=>setShowChat(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#9900cc",padding:"0 4px",flexShrink:0}}>‹</button>
          )}
          <div style={{fontWeight:700,fontSize:14,color:"#111827",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{channelLabel}</div>
          {channel==="announce"&&!canAnnounce&&<span style={{fontSize:11,color:"#9ca3af",background:"#f3f4f6",padding:"2px 8px",borderRadius:99,flexShrink:0}}>Lesezugriff</span>}
          {canAnnounce && (
            <button onClick={()=>setShowPollCreator(p=>!p)}
              style={{display:"flex",alignItems:"center",gap:4,padding:isMobile?"8px 10px":"6px 12px",borderRadius:8,border:"none",
                background:showPollCreator?"#7c3aed":"#f3f4f6",color:showPollCreator?"#fff":"#374151",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
              📊{!isMobile&&" Umfrage"}
            </button>
          )}
          <button onClick={async()=>{
              if(!meetingOpen){
                const meetUrl=`https://meet.jit.si/${meetingRoom}`;
                await fetch(`${API_URL}/staff/messages`,{
                  method:"POST",headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({channel,sender_id:"system",sender_name:"📹 Meeting",
                    sender_color:"#059669",text:`${user.name} hat ein Meeting gestartet! Jetzt beitreten: ${meetUrl}`,
                    reply_to:null,mentions:[]})
                }).catch(()=>{});
                load();
              }
              setMeetingOpen(p=>!p);
            }}
            style={{display:"flex",alignItems:"center",gap:4,padding: isMobile?"8px 10px":"6px 14px",borderRadius:8,border:"none",
              background:meetingOpen?"#dc2626":"#059669",color:"#fff",fontSize: isMobile?12:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
            {meetingOpen?"📵":"📹"}{!isMobile&&(meetingOpen?" beenden":" starten")}
          </button>
        </div>

        {/* Poll Creator */}
        {showPollCreator && canAnnounce && (
          <div style={{background:"#f5f3ff",borderBottom:"1px solid #ddd6fe",padding:"14px 20px",flexShrink:0}}>
            <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:10}}>📊 Umfrage erstellen</div>
            <input value={pollQuestion} onChange={e=>setPollQuestion(e.target.value)} placeholder="Frage eingeben..."
              style={{width:"100%",padding:"8px 12px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13,marginBottom:8,boxSizing:"border-box"}}/>
            {pollOptions.map((opt,i)=>(
              <div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
                <input value={opt} onChange={e=>{const a=[...pollOptions];a[i]=e.target.value;setPollOptions(a);}} placeholder={`Option ${i+1}`}
                  style={{flex:1,padding:"6px 10px",border:"1px solid #ddd6fe",borderRadius:8,fontSize:13}}/>
                {pollOptions.length>2&&<button onClick={()=>setPollOptions(p=>p.filter((_,j)=>j!==i))} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:18}}>×</button>}
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={()=>setPollOptions(p=>[...p,""])} style={{flex:1,padding:"6px",background:"#ede9fe",border:"1px solid #c4b5fd",borderRadius:8,fontSize:12,color:"#7c3aed",cursor:"pointer",fontWeight:600}}>+ Option</button>
              <button onClick={createPoll} disabled={pollSending} style={{flex:2,padding:"6px 14px",background:"#7c3aed",border:"none",borderRadius:8,fontSize:12,color:"#fff",fontWeight:700,cursor:"pointer"}}>
                {pollSending?"Erstellt…":"📊 Umfrage starten"}
              </button>
            </div>
          </div>
        )}

        {/* Active Polls */}
        {polls.filter(p=>!p.closed).length > 0 && (
          <div style={{background:"#fafafa",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
            {polls.filter(p=>!p.closed).map(poll=>{
              const totalVotes = poll.options.reduce((s,o)=>s+Object.keys(o.votes||{}).length,0);
              const myVote = poll.options.find(o=>(o.votes||{})[user.id])?.id;
              return (
                <div key={poll.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:13,fontWeight:700,color:"#111827",flex:1}}>📊 {poll.question}</span>
                    <span style={{fontSize:11,color:"#9ca3af"}}>{poll.creator_name} · {totalVotes} Stimmen</span>
                    {(isAdmin||poll.creator_id===user.id)&&<button onClick={()=>closePoll(poll.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"#9ca3af"}}>Beenden</button>}
                    {(isAdmin)&&<button onClick={()=>deletePoll(poll.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#f3f4f6",lineHeight:1}}>×</button>}
                  </div>
                  {poll.options.map(opt=>{
                    const vCount = Object.keys(opt.votes||{}).length;
                    const pct = totalVotes>0 ? Math.round(vCount/totalVotes*100) : 0;
                    const voted = opt.id===myVote;
                    return (
                      <button key={opt.id} onClick={()=>votePoll(poll.id,opt.id)} style={{width:"100%",marginBottom:6,padding:"8px 12px",border:`2px solid ${voted?"#7c3aed":"#e5e7eb"}`,borderRadius:8,background:voted?"#f5f3ff":"#f9fafb",cursor:"pointer",textAlign:"left",position:"relative",overflow:"hidden"}}>
                        <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${pct}%`,background:voted?"#7c3aed20":"#e5e7eb50",transition:"width 0.4s"}}/>
                        <div style={{position:"relative",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:13,color:voted?"#7c3aed":"#374151",fontWeight:voted?700:400}}>{opt.text}</span>
                          <span style={{fontSize:12,color:"#6b7280",fontWeight:600}}>{pct}%</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Jitsi Meeting Banner */}
        {meetingOpen&&<JitsiPanel
          room={meetingRoom}
          displayName={user.name}
          channelLabel={channelLabel}
          onClose={()=>setMeetingOpen(false)}
          onJoin={()=>{}}
        />}

        {/* Pinned banner */}
        {pinnedMsgs.length>0&&(
          <div style={{background:"#fffbeb",borderBottom:"1px solid #fde68a",padding:"7px 20px",flexShrink:0,display:"flex",alignItems:"center",gap:8}}>
            <span>📌</span>
            <span style={{fontSize:12,color:"#92400e",fontWeight:600,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {pinnedMsgs[pinnedMsgs.length-1]?.sender_name}: {pinnedMsgs[pinnedMsgs.length-1]?.text||"📎 Datei"}
            </span>
            {pinnedMsgs.length>1&&<span style={{fontSize:11,color:"#92400e",flexShrink:0}}>+{pinnedMsgs.length-1} weitere</span>}
          </div>
        )}

        {/* Messages list */}
        <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding: isMobile ? "12px 12px" : "16px 20px",display:"flex",flexDirection:"column",gap:0,minHeight:0}}>
          {messages.length===0&&(
            <div style={{textAlign:"center",color:"#9ca3af",padding:"60px 0",fontSize:14}}>Noch keine Nachrichten. Schreib die erste! 👋</div>
          )}
          {messages.map((m,i)=>{
            const mine=m.sender_id===user.id;
            const senderUser=ALL_USERS.find(u=>u.id===m.sender_id)||{name:m.sender_name,color:m.sender_color||"#7c3aed",photo:m.sender_photo};
            const prev=i>0?messages[i-1]:null;
            const grouped=prev&&prev.sender_id===m.sender_id&&(new Date(m.created_at)-new Date(prev.created_at))<300000;
            const hasReactions=m.reactions&&Object.keys(m.reactions).length>0;
            const readCount=(Array.isArray(m.read_by)?m.read_by:[]).filter(id=>id!==m.sender_id).length;
            const repliedMsg=m.reply_to?messages.find(x=>x.id===m.reply_to):null;
            const longPressTimer = {ref: null};
            return (
              <div key={m.id||i}
                onMouseEnter={()=>{ if(!isMobileMsg) setHoveredMsg(m.id); }}
                onMouseLeave={()=>{ if(!isMobileMsg) setHoveredMsg(null); }}
                onTouchStart={()=>{ longPressTimer.ref = setTimeout(()=>setTappedMsg(m), 450); }}
                onTouchEnd={()=>{ clearTimeout(longPressTimer.ref); }}
                onTouchMove={()=>{ clearTimeout(longPressTimer.ref); }}
                style={{display:"flex",gap:8,flexDirection:mine?"row-reverse":"row",alignItems:"flex-start",marginTop:grouped?2:12}}>
                <div style={{width:28,flexShrink:0,marginTop:2,opacity:grouped?0:1}}>
                  <UserAvatar u={senderUser} size={28}/>
                </div>
                <div style={{maxWidth: isMobile?"80%":"68%"}}>
                  {!grouped&&!mine&&<div style={{fontSize:11,color:"#9ca3af",marginBottom:3,marginLeft:4}}>{m.sender_name}</div>}
                  {repliedMsg&&(
                    <div style={{fontSize:11,color:"#9ca3af",marginBottom:4,paddingLeft:8,borderLeft:"2px solid #d1d5db",marginLeft:4,marginRight:mine?4:0,textAlign:mine?"right":"left"}}>
                      ↩ <strong>{repliedMsg.sender_name}</strong>: {repliedMsg.text?.slice(0,50)||"📎"}
                    </div>
                  )}
                  {m.forwarded_from&&<div style={{fontSize:10,color:"#9ca3af",marginBottom:2,marginLeft:4}}>↗ weitergeleitet aus #{m.forwarded_from}</div>}
                  {m.pinned&&<div style={{fontSize:10,color:"#d97706",marginBottom:2,marginLeft:4}}>📌 Angepinnt</div>}
                  {m.text&&(
                    <div style={{padding:"9px 13px",borderRadius:mine?"14px 14px 4px 14px":"14px 14px 14px 4px",
                      background:mine?"#7c3aed":"#fff",color:mine?"#fff":"#111827",fontSize:13,
                      border:mine?"none":"1px solid #e5e7eb",boxShadow:"0 1px 3px #00000008",lineHeight:1.45,wordBreak:"break-word"}}>
                      {renderText(m.text,mine)}
                    </div>
                  )}
                  {m.file_url&&m.file_type==="audio"&&<audio controls src={m.file_url} style={{marginTop:4,height:36,maxWidth:240,display:"block"}}/>}
                  {m.file_url&&m.file_type==="image"&&<img src={m.file_url} alt={m.file_name} style={{marginTop:4,maxWidth:220,maxHeight:220,borderRadius:8,border:"1px solid #e5e7eb",display:"block"}}/>}
                  {m.file_url&&m.file_type==="file"&&(
                    <a href={m.file_url} download={m.file_name} target="_blank" rel="noreferrer"
                      style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:4,padding:"7px 12px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,color:"#374151",textDecoration:"none"}}>
                      📎 {m.file_name||"Datei"}
                    </a>
                  )}
                  {hasReactions&&(
                    <div style={{display:"flex",gap:4,marginTop:5,flexWrap:"wrap",justifyContent:mine?"flex-end":"flex-start"}}>
                      {Object.entries(m.reactions).map(([emoji,usersRaw])=>{
                        const usrs = Array.isArray(usersRaw) ? usersRaw : [];
                        const reacted = usrs.includes(user.id);
                        return (
                          <button key={emoji} onClick={()=>react(m.id,emoji)}
                            style={{padding:"2px 8px",borderRadius:99,border:`1px solid ${reacted?"#7c3aed":"#e5e7eb"}`,
                              background:reacted?"#7c3aed14":"#fff",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:3}}>
                            {emoji}<span style={{fontSize:11,color:"#6b7280"}}>{usrs.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{fontSize:10,color:"#9ca3af",marginTop:2,textAlign:mine?"right":"left",marginRight:mine?4:0,marginLeft:mine?0:4,display:"flex",gap:4,justifyContent:mine?"flex-end":"flex-start",alignItems:"center"}}>
                    {fmtTime(m.created_at)}
                    {mine&&<span style={{color:readCount>0?"#7c3aed":"#d1d5db"}}>{readCount>0?"✓✓":"✓"}</span>}
                  </div>
                </div>
                {/* Hover action bar — desktop only */}
                {!isMobileMsg&&hoveredMsg===m.id&&(
                  <div style={{display:"flex",gap:1,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:"3px 5px",boxShadow:"0 2px 8px #00000012",alignSelf:"flex-start",flexShrink:0,marginTop:2}}>
                    {EMOJI_OPTS.map(e=>(
                      <button key={e} onClick={()=>react(m.id,e)} title={e}
                        style={{background:"none",border:"none",cursor:"pointer",fontSize:13,padding:"2px 3px",borderRadius:4}}>{e}</button>
                    ))}
                    <button onClick={()=>setReplyTo(m)} title="Antworten"
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:12,padding:"2px 5px",borderRadius:4,color:"#6b7280",borderLeft:"1px solid #f3f4f6"}}>↩</button>
                    <button onClick={()=>setForwardMsg(m)} title="Weiterleiten"
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:12,padding:"2px 5px",borderRadius:4,color:"#6b7280"}}>↗</button>
                    {isAdmin&&<button onClick={()=>togglePin(m)} title={m.pinned?"Entpinnen":"Anpinnen"}
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:12,padding:"2px 5px",borderRadius:4,color:m.pinned?"#d97706":"#6b7280"}}>📌</button>}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>

        {/* Reply preview */}
        {replyTo&&(
          <div style={{padding:"6px 20px",background:"#f3f4f6",borderTop:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            <span style={{fontSize:12,color:"#374151"}}>↩ Antwort auf <strong>{replyTo.sender_name}</strong>: {replyTo.text?.slice(0,60)||"📎"}</span>
            <button onClick={()=>setReplyTo(null)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:18,lineHeight:1}}>×</button>
          </div>
        )}

        {/* @mention autocomplete */}
        {mentionSuggestions.length>0&&(
          <div style={{position:"absolute",bottom:canPost?68:20,left: isMobile?8:210,right: isMobile?8:"auto",background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,boxShadow:"0 4px 16px #00000018",overflow:"hidden",zIndex:50,minWidth:160}}>
            {mentionSuggestions.map(u=>(
              <button key={u.id} onClick={()=>insertMention(u.name)}
                style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:13,color:"#111827",textAlign:"left"}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:u.color||"#7c3aed",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{u.name[0]}</div>
                {u.name}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        {canPost?(
          <div style={{padding: isMobile ? "8px 10px" : "10px 14px",borderTop:"1px solid #e5e7eb",background:"#fff",display:"flex",gap: isMobile ? 4 : 6,alignItems:"flex-end",flexShrink:0}}>
            <input type="file" ref={fileRef} onChange={sendFile} style={{display:"none"}}/>
            <button onClick={()=>fileRef.current?.click()} title="Datei" style={{width: isMobile?34:undefined,height: isMobile?34:undefined,padding: isMobile?"0":"8px 10px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:8,cursor:"pointer",fontSize: isMobile?16:15,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>📎</button>
            <button onClick={recording ? stopRecording : startRecording}
              title={recording ? "Aufnahme stoppen & senden" : "Sprachnachricht aufnehmen"}
              style={{width: isMobile?(recording?44:34):undefined,height: isMobile?34:undefined,padding: isMobile?"0":"8px 10px",background:recording?"#fee2e2":"#f3f4f6",border:`1px solid ${recording?"#fca5a5":"#e5e7eb"}`,borderRadius:8,cursor:"pointer",fontSize: isMobile?16:15,flexShrink:0,color:recording?"#dc2626":"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4,minWidth: isMobile?undefined:(recording?52:36),transition:"all 0.2s"}}>
              {recording ? <><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:"#dc2626",animation:"pulse 1s infinite"}}/>🔴</> : "🎤"}
            </button>
            <textarea ref={textRef} value={text} onChange={handleTextChange}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder={isMobile ? "Nachricht…" : "Schreiben… (@ für Erwähnungen · Enter = Senden)"}
              rows={1} style={{flex:1,minWidth:0,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize: isMobile?14:13,resize:"none",fontFamily:"inherit",outline:"none"}}/>
            <button onClick={send} disabled={sending||!text.trim()}
              style={{padding: isMobile?"0":"8px 16px",width: isMobile?34:undefined,height: isMobile?34:undefined,background:text.trim()?"#7c3aed":"#e5e7eb",color:text.trim()?"#fff":"#9ca3af",border:"none",borderRadius:8,fontSize: isMobile?18:13,fontWeight:700,cursor:text.trim()?"pointer":"default",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {isMobile ? (sending?"…":"➤") : (sending?"…":"Senden")}
            </button>
          </div>
        ):(
          <div style={{padding:"14px 20px",borderTop:"1px solid #e5e7eb",background:"#f9fafb",flexShrink:0,textAlign:"center",color:"#9ca3af",fontSize:13}}>
            📢 Nur Admins können in diesem Kanal schreiben
          </div>
        )}
      </div>}

      {/* ── Mobile message action sheet (WhatsApp-style) ── */}
      {isMobileMsg&&tappedMsg&&(
        <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}
          onClick={()=>setTappedMsg(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"16px 16px 32px",boxShadow:"0 -8px 40px #00000030"}}>
            {/* Handle */}
            <div style={{width:40,height:4,borderRadius:2,background:"#e5e7eb",margin:"0 auto 14px"}}/>
            {/* Message preview */}
            <div style={{fontSize:12,color:"#6b7280",marginBottom:12,padding:"8px 12px",background:"#f9fafb",borderRadius:10,lineHeight:1.4,maxLines:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              <strong>{tappedMsg.sender_name}:</strong> {tappedMsg.text?.slice(0,60)||"📎 Datei"}
            </div>
            {/* Emoji row */}
            <div style={{display:"flex",justifyContent:"space-around",marginBottom:16,padding:"4px 0"}}>
              {EMOJI_OPTS.map(e=>(
                <button key={e} onClick={()=>{ react(tappedMsg.id,e); setTappedMsg(null); }}
                  style={{background:"none",border:"none",cursor:"pointer",fontSize:26,padding:"6px",borderRadius:12,lineHeight:1}}>
                  {e}
                </button>
              ))}
            </div>
            {/* Actions */}
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <button onClick={()=>{ setReplyTo(tappedMsg); setTappedMsg(null); }}
                style={{padding:"13px 16px",background:"none",border:"none",cursor:"pointer",fontSize:14,textAlign:"left",color:"#111827",borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
                ↩ <span>Antworten</span>
              </button>
              <button onClick={()=>{ setForwardMsg(tappedMsg); setTappedMsg(null); }}
                style={{padding:"13px 16px",background:"none",border:"none",cursor:"pointer",fontSize:14,textAlign:"left",color:"#111827",borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
                ↗ <span>Weiterleiten</span>
              </button>
              {isAdmin&&(
                <button onClick={()=>{ togglePin(tappedMsg); setTappedMsg(null); }}
                  style={{padding:"13px 16px",background:"none",border:"none",cursor:"pointer",fontSize:14,textAlign:"left",color:tappedMsg.pinned?"#d97706":"#111827",borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
                  📌 <span>{tappedMsg.pinned?"Entpinnen":"Anpinnen"}</span>
                </button>
              )}
            </div>
            <button onClick={()=>setTappedMsg(null)}
              style={{marginTop:8,width:"100%",padding:"12px",background:"#f3f4f6",border:"none",borderRadius:10,fontSize:14,color:"#6b7280",cursor:"pointer"}}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Forward modal — position:fixed, works outside conditional */}
      {forwardMsg&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}} onClick={()=>setForwardMsg(null)}>
          <div style={{background:"#fff",borderRadius:16,padding:24,width:"min(300px,calc(100vw - 32px))",boxShadow:"0 20px 60px #00000040"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>↗ Nachricht weiterleiten</div>
            <div style={{fontSize:12,color:"#6b7280",marginBottom:14,padding:"8px 10px",background:"#f3f4f6",borderRadius:8,wordBreak:"break-word"}}>"{forwardMsg.text?.slice(0,80)||"📎 Datei"}"</div>
            <div style={{fontSize:11,color:"#9ca3af",marginBottom:8,textTransform:"uppercase",fontWeight:700}}>Ziel-Kanal:</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {allForwardTargets.map(c=>(
                <button key={c.id} onClick={()=>forward(forwardMsg,c.id)}
                  style={{padding:"8px 14px",border:"1px solid #e5e7eb",borderRadius:8,background:"#f9fafb",cursor:"pointer",fontSize:13,color:"#374151",textAlign:"left"}}>
                  {c.label}
                </button>
              ))}
            </div>
            <button onClick={()=>setForwardMsg(null)} style={{marginTop:14,width:"100%",padding:"8px",border:"1px solid #e5e7eb",borderRadius:8,background:"transparent",cursor:"pointer",fontSize:12,color:"#6b7280"}}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Leaderboard / Gamification ────────────────────────────────────── */
function LeaderboardView({ user }) {
  const [trainers,   setTrainers]   = useState([]);
  const [googleRev,  setGoogleRev]  = useState(null);
  const [platforms,  setPlatforms]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [editPlat,   setEditPlat]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [streaks,    setStreaks]     = useState([]);
  const [lbTab,      setLbTab]      = useState("gesamt"); // gesamt | monat | woche
  const isAdmin = user && (user.isAdmin || user.adminAccess);

  const loadReviews = (period) => {
    const curPeriod = period || lbTab || "gesamt";
    // Show stale cache instantly (only for gesamt)
    if (curPeriod === "gesamt") {
      const _rCached = lsGetStale('rangliste');
      if (_rCached && _rCached._hydrated) {
        if (_rCached.perf) setTrainers(_rCached.perf);
        if (_rCached.streaks) setStreaks(_rCached.streaks);
        if (_rCached.google !== undefined) setGoogleRev(_rCached.google);
        if (_rCached.platforms !== undefined) setPlatforms(_rCached.platforms);
        setLoading(false);
      }
    }
    return Promise.all([
    fetch(`${API_URL}/attendance/summary?period=${curPeriod}`).then(r=>r.json()).catch(()=>({trainer_perf:[]})),
    fetch(`${API_URL}/reviews/platforms`).then(r=>r.json()).catch(()=>null),
    fetch(`${API_URL}/staff/streaks`).then(r=>r.json()).catch(()=>({streaks:[]})),
  ]).then(([att, plat, streakData])=>{
    const perf = (att.trainer_perf||[]).map(t=>({
      name: normalizeTrainer(t.trainer),
      classes: t.total_classes||t.classes||0,
      participants: t.total_participants||t.participants||0,
      avgFill: t.avg_fill_rate||0,
    })).sort((a,b)=>b.participants-a.participants);
    setTrainers(perf);
    lsSet('rangliste', { perf, streaks: streakData.streaks||[], platforms: plat, _hydrated: true });
    setPlatforms(plat);
    setStreaks(streakData.streaks||[]);
    setLoading(false);
  });
  };

  useEffect(()=>{ loadReviews("gesamt"); },[]);
  useEffect(()=>{ if(lbTab) loadReviews(lbTab); },[lbTab]);

  const savePlatform = async () => {
    if(!editPlat) return;
    setSaving(true);
    const {key, ...vals} = editPlat;
    await fetch(`${API_URL}/reviews/platforms/${key}`, {
      method:"PUT",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ rating: vals.rating ? parseFloat(vals.rating) : null, total: vals.total ? parseInt(vals.total) : null, url: vals.url||"" }),
    }).catch(()=>{});
    setSaving(false);
    setEditPlat(null);
    loadReviews();
  };


  if(loading) return <BurnLoader text="Lade Rangliste..." />;

  const medals = ["🥇","🥈","🥉"];
  const badges = (t) => {
    const b=[];
    if(t.classes>=100) b.push({label:"100 Kurse",color:"#7c3aed"});
    else if(t.classes>=50) b.push({label:"50 Kurse",color:"#6366f1"});
    if(t.participants>=500) b.push({label:"500 Teilnehmer",color:"#d97706"});
    else if(t.participants>=200) b.push({label:"200 Teilnehmer",color:"#f59e0b"});
    if(t.avgFill>=0.8) b.push({label:"Top Auslastung",color:"#059669"});
    if(t.rating>=4.8) b.push({label:"Top Bewertung ⭐",color:"#dc2626"});
    return b;
  };

  const StarBar = ({rating}) => {
    if(!rating) return <span style={{color:"#9ca3af",fontSize:12}}>Noch nicht eingetragen</span>;
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return (
      <span style={{fontSize:18,letterSpacing:1}}>
        {"★".repeat(full)}{half?"½":""}{"☆".repeat(Math.max(0,5-full-(half?1:0)))}
      </span>
    );
  };

  // EINE Review-Quelle: /reviews/platforms (manuell pflegbar via Admin-UI).
  // Vorher: totes /reviews/google (404) + Google-Filter -> Google fehlte bzw.
  // erschien historisch doppelt.
  const allPlatforms = Object.entries(platforms||{}).map(([k,v])=>({key:k,...v}));

  return (
    <div style={{flex:1,overflowY:"auto",padding:24}}>
      <div style={{maxWidth:800,margin:"0 auto"}}>

        {/* ── Plattform-Bewertungen ────────────────────── */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <span style={{fontSize:16,fontWeight:700,color:"#111827"}}>⭐ Studio-Bewertungen</span>
            <span style={{fontSize:12,color:"#9ca3af"}}>alle Plattformen</span>
            {isAdmin && (
              <button onClick={()=>fetch(`${API_URL}/reviews/refresh`).then(()=>loadReviews(lbTab))}
                style={{marginLeft:"auto",fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid #e5e7eb",background:"#f9fafb",cursor:"pointer",color:"#374151",fontWeight:600}}>
                🔄 Auto-Update
              </button>
            )}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
            {allPlatforms.map(p=>{
              const logoUrl = p.logo_url || null;
              const totalDisplay = p.total >= 5000 ? "5.000+" : p.total ? p.total.toLocaleString("de-DE") : null;
              return (
                <div key={p.key} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:"16px 18px",position:"relative"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    {/* Real Logo */}
                    <div style={{width:40,height:40,borderRadius:10,background:p.color+"12",border:`1.5px solid ${p.color}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                      {p.key==="google" ? (
                        /* Google G SVG */
                        <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      ) : logoUrl ? (
                        <img src={logoUrl} alt={p.name}
                          style={{width:26,height:26,objectFit:"contain"}}
                          onError={e=>{ e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
                        />
                      ) : (
                        <span style={{fontSize:18,fontWeight:800,color:p.color}}>{p.name[0]}</span>
                      )}
                      {/* Fallback text if img fails */}
                      {p.key!=="google" && logoUrl && (
                        <span style={{display:"none",fontSize:14,fontWeight:800,color:p.color}}>{p.name[0]}</span>
                      )}
                    </div>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{p.name}</div>
                      {totalDisplay && <div style={{fontSize:11,color:"#9ca3af"}}>{totalDisplay} Bewertungen</div>}
                      {p.auto && <div style={{fontSize:10,color:"#059669"}}>🤖 automatisch</div>}
                    </div>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer"
                        style={{marginLeft:"auto",fontSize:11,color:"#9ca3af",textDecoration:"none",flexShrink:0}} title="Zur Plattform">↗</a>
                    )}
                  </div>
                  <div style={{marginBottom:6}}>
                    {p.rating
                      ? <span style={{fontSize:28,fontWeight:800,color:p.color}}>{Number(p.rating).toFixed(1)}<span style={{fontSize:14,fontWeight:400,color:"#9ca3af"}}> / 5</span></span>
                      : <span style={{fontSize:13,color:"#d1d5db",fontStyle:"italic"}}>Noch nicht eingetragen</span>
                    }
                  </div>
                  <StarBar rating={p.rating} />
                  {/* Google: show recent reviews */}
                  {p.key==="google" && (p.recent||[]).slice(0,1).map((r,i)=>(
                    <div key={i} style={{marginTop:10,padding:"8px 10px",background:"#f9fafb",borderRadius:8,fontSize:11,color:"#374151",lineHeight:1.5}}>
                      <span style={{fontWeight:600}}>{r.author}</span> · {"⭐".repeat(r.rating)}<br/>
                      {r.text?.slice(0,120)}{r.text?.length>120?"…":""}
                    </div>
                  ))}
                  {/* Non-Google: last updated + edit */}
                  {p.key!=="google" && (
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10}}>
                      <span style={{fontSize:10,color:"#d1d5db"}}>
                        {p.updated ? `${p.updated.slice(0,10)}` : "—"}
                      </span>
                      {isAdmin && !p.auto && (
                        <button onClick={()=>setEditPlat({key:p.key,name:p.name,color:p.color,rating:p.rating||"",total:p.total||"",url:p.url||""})}
                          style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid #e5e7eb",background:"#f9fafb",cursor:"pointer",color:"#374151",fontWeight:600}}>
                          ✏️ Bearbeiten
                        </button>
                      )}
                    </div>
                  )}
                  {p.key==="google" && !googleRev?.rating && (
                    <div style={{marginTop:8,fontSize:11,color:"#f59e0b"}}>⚠️ GOOGLE_PLACES_API_KEY fehlt in Railway</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Edit-Modal */}
        {editPlat && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
            <div style={{background:"#fff",borderRadius:16,padding:28,width:"min(360px,calc(100vw - 32px))",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                <div style={{width:32,height:32,borderRadius:8,background:editPlat.color+"18",border:`1px solid ${editPlat.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:editPlat.color}}>{editPlat.name[0]}</div>
                <div style={{fontWeight:700,fontSize:16,color:"#111827"}}>{editPlat.name} Bewertung</div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Durchschnittsbewertung (z.B. 4.7)</label>
                <input type="number" min="1" max="5" step="0.1"
                  value={editPlat.rating}
                  onChange={e=>setEditPlat(p=>({...p,rating:e.target.value}))}
                  style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}
                  placeholder="z.B. 4.8"
                />
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Anzahl Bewertungen</label>
                <input type="number" min="0"
                  value={editPlat.total}
                  onChange={e=>setEditPlat(p=>({...p,total:e.target.value}))}
                  style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}
                  placeholder="z.B. 127"
                />
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Profil-URL (optional)</label>
                <input type="url"
                  value={editPlat.url}
                  onChange={e=>setEditPlat(p=>({...p,url:e.target.value}))}
                  style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}
                  placeholder="https://..."
                />
              </div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setEditPlat(null)}
                  style={{flex:1,padding:"10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:13,color:"#374151"}}>
                  Abbrechen
                </button>
                <button onClick={savePlatform} disabled={saving}
                  style={{flex:2,padding:"10px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>
                  {saving?"Speichert…":"💾 Speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rangliste Tab-Nav */}
        <div style={{display:"flex",gap:4,background:"#f3f4f6",borderRadius:12,padding:4,marginBottom:20}}>
          {[{id:"gesamt",label:"🏆 Gesamt"},{id:"monat",label:"📅 Monat"},{id:"woche",label:"📆 Woche"}].map(t=>(
            <button key={t.id} onClick={()=>setLbTab(t.id)} style={{
              flex:1,padding:"8px 4px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:lbTab===t.id?700:400,
              background:lbTab===t.id?"#fff":"transparent",color:lbTab===t.id?"#7c3aed":"#6b7280",
              boxShadow:lbTab===t.id?"0 1px 3px rgba(0,0,0,0.1)":"none"
            }}>{t.label}</button>
          ))}
        </div>

        {/* Streak Top-Leiste */}
        {streaks.slice(0,5).length > 0 && (
          <div style={{background:"linear-gradient(135deg,#7c3aed18,#ec489918)",border:"1px solid #ddd6fe",borderRadius:14,padding:"14px 18px",marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:10}}>🔥 Streak Top 5 — Wochen in Folge aktiv</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {streaks.slice(0,5).map((s,i)=>(
                <div key={s.name} style={{display:"flex",alignItems:"center",gap:6,background:"#fff",borderRadius:10,padding:"6px 12px",border:"1px solid #e5e7eb"}}>
                  <span style={{fontSize:16}}>{["🥇","🥈","🥉","4️⃣","5️⃣"][i]}</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#374151"}}>{s.name}</span>
                  <span style={{fontSize:12,color:"#dc2626",fontWeight:800}}>🔥 {s.streak}W</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top 3 */}
        <div style={{fontSize:16,fontWeight:700,color:"#111827",marginBottom:16}}>
          🏆 Trainer Rangliste
          <span style={{fontSize:12,color:"#9ca3af",fontWeight:400,marginLeft:8}}>
            {lbTab==="monat"?"— aktueller Monat":lbTab==="woche"?"— aktuelle Woche":"— alle Zeit"}
          </span>
        </div>
        {trainers.slice(0,3).length > 0 && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:20}}>
            {trainers.slice(0,3).map((t,i)=>{
              const su = STAFF_USERS.find(u=>u.name===t.name);
              const borderColor = ["#fbbf24","#9ca3af","#b45309"][i];
              const streak = streaks.find(s=>s.name===t.name);
              return (
                <div key={t.name} style={{background:"#fff",border:`2px solid ${borderColor}`,borderRadius:14,padding:"16px",textAlign:"center",position:"relative"}}>
                  {streak?.streak>=3 && <span style={{position:"absolute",top:8,right:10,fontSize:12,color:"#dc2626",fontWeight:800}}>🔥{streak.streak}</span>}
                  <div style={{fontSize:22,marginBottom:6}}>{medals[i]}</div>
                  {su?.photo
                    ? <img src={su.photo} style={{width:60,height:60,borderRadius:"50%",objectFit:"cover",objectPosition:"center top",border:`3px solid ${borderColor}`,margin:"0 auto 8px",display:"block"}} alt={t.name}/>
                    : <div style={{width:60,height:60,borderRadius:"50%",background:(su?.color||"#7c3aed")+"20",border:`3px solid ${borderColor}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 8px",overflow:"hidden"}}><img src={BURN_FALLBACK} className="burn-fallback-spin" style={{width:"70%",height:"70%",objectFit:"contain",opacity:0.8}} alt="BURN"/></div>
                  }
                  <div style={{fontSize:14,fontWeight:800,color:"#111827",marginBottom:2}}>{t.name}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginBottom:8}}>{t.classes} Kurse · {t.participants} Teiln.</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"center"}}>
                    {badges(t).map(b=>(
                      <span key={b.label} style={{fontSize:11,padding:"2px 6px",borderRadius:99,background:b.color+"18",color:b.color,border:`1px solid ${b.color}30`,fontWeight:700}}>{b.label}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full table */}
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden",overflowX:"auto",marginBottom:28}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:480}}>
            <thead>
              <tr style={{background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
                <th style={{padding:"10px 16px",textAlign:"left",fontWeight:600,color:"#374151"}}>#</th>
                <th style={{padding:"10px 16px",textAlign:"left",fontWeight:600,color:"#374151"}}>Trainer</th>
                <th style={{padding:"10px 16px",textAlign:"right",fontWeight:600,color:"#374151"}}>Kurse</th>
                <th style={{padding:"10px 16px",textAlign:"right",fontWeight:600,color:"#374151"}}>Teilnehmer</th>
                <th style={{padding:"10px 16px",textAlign:"right",fontWeight:600,color:"#374151"}}>Ø Auslastung</th>
                <th style={{padding:"10px 16px",textAlign:"right",fontWeight:600,color:"#374151"}}>Streak</th>
              </tr>
            </thead>
            <tbody>
              {trainers.map((t,i)=>{
                const streak = streaks.find(s=>s.name===t.name);
                return (
                  <tr key={t.name} style={{borderBottom:"1px solid #f3f4f6",background:i<3?""+["#fffbeb","#f9fafb","#fff8f3"][i]:""}}>
                    <td style={{padding:"10px 16px",color:"#6b7280"}}>{medals[i]||i+1}</td>
                    <td style={{padding:"10px 16px"}}>
                      <div style={{fontWeight:600,color:"#111827"}}>{t.name}</div>
                      <div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>
                        {badges(t).map(b=><span key={b.label} style={{fontSize:11,padding:"1px 5px",borderRadius:99,background:b.color+"15",color:b.color,fontWeight:700}}>{b.label}</span>)}
                      </div>
                    </td>
                    <td style={{padding:"10px 16px",textAlign:"right",color:"#374151"}}>{t.classes}</td>
                    <td style={{padding:"10px 16px",textAlign:"right",fontWeight:700,color:"#7c3aed"}}>{t.participants}</td>
                    <td style={{padding:"10px 16px",textAlign:"right",color: t.avgFill>=0.75?"#059669":t.avgFill>=0.5?"#d97706":"#dc2626"}}>
                      {t.avgFill ? (t.avgFill*100).toFixed(0)+"%" : "—"}
                    </td>
                    <td style={{padding:"10px 16px",textAlign:"right"}}>
                      {streak?.streak ? <span style={{color:"#dc2626",fontWeight:700}}>🔥 {streak.streak}W</span> : <span style={{color:"#d1d5db"}}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}

/* ─── Profile ───────────────────────────────────────────────────────── */
function ProfileView({ user }) {
  const lsKey = `burn_profile_v2_${user.id}`;
  const load = () => { try { return JSON.parse(localStorage.getItem(lsKey)||"{}"); } catch { return {}; } };
  const saved = load();

  const [ptab,    setPtab]    = useState("profil");
  const [photo,   setPhoto]   = useState(saved.photo||user.photo||null);
  const [bio,     setBio]     = useState(saved.bio||"");
  const [phone,   setPhone]   = useState(saved.phone||"");
  const [since,   setSince]   = useState(saved.since||"");
  const [status,  setStatus]  = useState(saved.status||"");
  const isPersonal = user.role === "Personal" || String(user.roles||"").includes("Personal");
  const [goals,   setGoals]   = useState(saved.goals||(isPersonal ? {kunden:20,sessions:40} : {kurse:10,teilnehmer:100}));
  const [notifs,  setNotifs]  = useState(saved.notifs||{messages:true,schedule:true,payroll:true,feedback:false});
  const [edited,  setEdited]  = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [stats,   setStats]   = useState(null);
  const [schedule,setSchedule]= useState([]);
  const [pw,      setPw]      = useState({old:"",neu:"",confirm:""});
  const [pwMsg,   setPwMsg]   = useState(null);

  const mark = () => setEdited(true);

  useEffect(()=>{
    // Eigene Statistiken
    fetch(`${API_URL}/attendance/summary`).then(r=>r.json()).then(d=>{
      const me = (d.trainer_perf||[]).find(t=>normalizeTrainer(t.trainer)===user.name
        || t.trainer?.trim().toLowerCase()===user.name.toLowerCase());
      setStats(me||null);
    }).catch(()=>{});
    // Nächste Schichten
    fetch(`${API_URL}/staff/schedule?name=${encodeURIComponent(user.name)}`).then(r=>r.json()).then(d=>{
      const now = new Date();
      const upcoming = (d.schedule||[])
        .filter(s=>{ try { return new Date(s.date+(s.start_time?"T"+s.start_time:""))>=now; } catch{return false;} })  /* "T"-Separator: Safari/iOS parst "YYYY-MM-DD HH:MM" nicht */
        .sort((a,b)=>new Date(a.date)-new Date(b.date))
        .slice(0,6);
      setSchedule(upcoming);
    }).catch(()=>{});
  },[user.name]);

  const saveAll = () => {
    localStorage.setItem(lsKey, JSON.stringify({photo,bio,phone,since,status,goals,notifs}));
    setEdited(false); setSavedOk(true); setTimeout(()=>setSavedOk(false),2500);
  };

  const uploadPhoto = (e) => {
    const file = e.target.files[0]; if(!file) return;
    if(file.size>2*1024*1024){ alert("Bild max. 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = ev => { setPhoto(ev.target.result); mark(); };
    reader.readAsDataURL(file);
  };

  const changePw = async () => {
    if(!pw.neu || pw.neu!==pw.confirm){ setPwMsg({ok:false,text:"Passwörter stimmen nicht überein"}); return; }
    if(pw.neu.length<6){ setPwMsg({ok:false,text:"Mindestens 6 Zeichen"}); return; }
    try {
      const r = await fetch(`${API_URL}/staff/auth/change-password`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({user_id:user.id, old_password:pw.old, new_password:pw.neu}),
      });
      if(r.ok){ setPwMsg({ok:true,text:"✅ Passwort geändert!"}); setPw({old:"",neu:"",confirm:""}); }
      else { setPwMsg({ok:false,text:"Altes Passwort falsch"}); }
    } catch { setPwMsg({ok:false,text:"Fehler — bitte erneut versuchen"}); }
    setTimeout(()=>setPwMsg(null),4000);
  };

  // Badges berechnen
  const badges = [];
  if(isPersonal){
    // Personal Trainer Badges — nicht Kurs-basiert
    const s = goals.sessions||0;
    const k = goals.kunden||0;
    if(s>=200) badges.push({icon:"💎",label:"200 PT-Sessions",color:"#7c3aed"});
    else if(s>=100) badges.push({icon:"🥈",label:"100 PT-Sessions",color:"#6366f1"});
    else if(s>=40) badges.push({icon:"🏅",label:"40 PT-Sessions",color:"#6b7280"});
    if(k>=50) badges.push({icon:"🔥",label:"50 Kunden",color:"#dc2626"});
    else if(k>=20) badges.push({icon:"⭐",label:"20 Kunden",color:"#d97706"});
    else if(k>=10) badges.push({icon:"✨",label:"10 Kunden",color:"#f59e0b"});
  } else if(stats){
    const cl = stats.total_classes||stats.classes||0;
    const pa = stats.total_participants||stats.participants||0;
    const fl = stats.avg_fill_rate||0;
    if(cl>=100) badges.push({icon:"💎",label:"100 Kurse",color:"#7c3aed"});
    else if(cl>=50) badges.push({icon:"🥈",label:"50 Kurse",color:"#6366f1"});
    else if(cl>=10) badges.push({icon:"🏅",label:"10 Kurse",color:"#6b7280"});
    if(pa>=1000) badges.push({icon:"🔥",label:"1000 Teilnehmer",color:"#dc2626"});
    else if(pa>=500) badges.push({icon:"⭐",label:"500 Teilnehmer",color:"#d97706"});
    else if(pa>=100) badges.push({icon:"✨",label:"100 Teilnehmer",color:"#f59e0b"});
    if(fl>=0.85) badges.push({icon:"🎯",label:"Top Auslastung 85%+",color:"#059669"});
    else if(fl>=0.7) badges.push({icon:"📈",label:"Auslastung 70%+",color:"#16a34a"});
  }
  if(since){
    const months = Math.floor((Date.now()-new Date(since+"-01"))/(1000*60*60*24*30));
    if(months>=24) badges.push({icon:"🎂",label:`${Math.floor(months/12)} Jahre dabei`,color:"#7c3aed"});
    else if(months>=12) badges.push({icon:"🎉",label:"1 Jahr dabei",color:"#059669"});
    else if(months>=3) badges.push({icon:"🌱",label:"3 Monate dabei",color:"#16a34a"});
  }
  if(badges.length===0) badges.push({icon:"🌟",label:"Neues Mitglied",color:"#9ca3af"});

  const inp = {width:"100%",padding:"9px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,boxSizing:"border-box",background:"#fff"};
  const c = "#9900cc";
  const PTABS = [
    {id:"profil",label:"👤 Profil"},
    ...(isPersonal ? [] : [
      {id:"statistiken",label:"📊 Statistiken"},
      {id:"badges",label:"🏆 Badges"},
    ]),
    {id:"schichten",label:"📅 Schichten"},
    {id:"einstellungen",label:"⚙️ Einstellungen"},
  ];

  return (
    <div style={{flex:1,overflowY:"auto",padding:24}}>
      <div style={{maxWidth:600,margin:"0 auto"}}>

        {/* ── Hero-Header ── */}
        <div style={{background:`linear-gradient(135deg,${c},${c}cc)`,borderRadius:20,padding:"28px 24px 20px",marginBottom:20,color:"#fff",textAlign:"center"}}>
          <div style={{position:"relative",display:"inline-block",marginBottom:12}}>
            <div style={{width:88,height:88,borderRadius:"50%",border:"3px solid rgba(255,255,255,0.7)",overflow:"hidden",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:34,fontWeight:800,margin:"0 auto"}}>
              {photo
                ? <img src={photo} style={{width:"100%",height:"100%",objectFit:"cover"}} alt={user.name}/>
                : <img src={BURN_FALLBACK} className="burn-fallback-spin" style={{width:"70%",height:"70%",objectFit:"contain",opacity:0.85}} alt="BURN"/>}
            </div>
            <label style={{position:"absolute",bottom:0,right:-4,width:28,height:28,borderRadius:"50%",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.25)"}}>
              <span style={{fontSize:13}}>📸</span>
              <input type="file" accept="image/*" onChange={uploadPhoto} style={{display:"none"}}/>
            </label>
          </div>
          <div style={{fontSize:22,fontWeight:800,marginBottom:2}}>{user.name}</div>
          <div style={{fontSize:12,opacity:0.8,marginBottom:12}}>{user.role}</div>
          <div style={{background:"rgba(255,255,255,0.18)",borderRadius:20,padding:"5px 16px",display:"inline-flex",alignItems:"center",gap:6,backdropFilter:"blur(4px)"}}>
            <span style={{fontSize:12}}>💬</span>
            <input value={status} onChange={e=>{setStatus(e.target.value);mark();}}
              placeholder="Status setzen (z.B. Im Urlaub bis 25.04.)..."
              className="burn-status-input"
              style={{background:"transparent",border:"none",color:"#fff",fontSize:12,outline:"none",width:230,fontFamily:"inherit"}}/>
          </div>
        </div>

        {/* ── Sub-Tabs ── */}
        <div style={{display:"flex",gap:2,marginBottom:20,background:"#f3f4f6",borderRadius:12,padding:4}}>
          {PTABS.map(t=>(
            <button key={t.id} onClick={()=>setPtab(t.id)}
              style={{flex:1,padding:"8px 2px",borderRadius:9,border:"none",background:ptab===t.id?"#fff":"transparent",color:ptab===t.id?c:"#6b7280",fontSize:10.5,fontWeight:ptab===t.id?700:400,cursor:"pointer",boxShadow:ptab===t.id?"0 1px 4px rgba(0,0,0,0.1)":"none",whiteSpace:"nowrap",transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Profil ── */}
        {ptab==="profil" && (
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:22,display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:5}}>Über mich</label>
              <textarea value={bio} onChange={e=>{setBio(e.target.value);mark();}} rows={3}
                placeholder="Erzähl etwas über dich…" style={{...inp,resize:"none"}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:5}}>Telefon</label>
                <input value={phone} onChange={e=>{setPhone(e.target.value);mark();}} placeholder="+49 …" style={inp}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:5}}>Bei BURN seit</label>
                <input type="month" value={since} onChange={e=>{setSince(e.target.value);mark();}} style={inp}/>
              </div>
            </div>
            {/* Ziele — nur für Trainer */}
            {!isPersonal && (
            <div style={{borderTop:"1px solid #f3f4f6",paddingTop:14}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>🎯 Monatsziele</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
                {[["Kurse / Monat","kurse","🥊"],["Teilnehmer / Monat","teilnehmer","👥"]].map(([label,key,icon])=>{
                  const actual = key==="kurse"?(stats?.total_classes||0):(stats?.total_participants||0);
                  const target = goals[key]||1;
                  const pct = Math.min(100,Math.round(actual/target*100));
                  return (
                    <div key={key} style={{background:"#f9fafb",borderRadius:10,padding:"12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                        <span style={{fontSize:14}}>{icon}</span>
                        <span style={{fontSize:11,fontWeight:600,color:"#374151"}}>{label}</span>
                      </div>
                      <input type="number" min="1" value={goals[key]} onChange={e=>{setGoals(g=>({...g,[key]:+e.target.value}));mark();}}
                        style={{width:"100%",padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,boxSizing:"border-box",marginBottom:8}}/>
                      <div style={{height:6,background:"#e5e7eb",borderRadius:3}}>
                        <div style={{width:`${pct}%`,height:"100%",background:pct>=100?"#059669":c,borderRadius:3,transition:"width 0.4s"}}/>
                      </div>
                      <div style={{fontSize:10,color:"#6b7280",marginTop:4}}>{actual} / {target} ({pct}%)</div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
            {/* Passwort ändern */}
            <div style={{borderTop:"1px solid #f3f4f6",paddingTop:14}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>🔑 Passwort ändern</div>
              {[["Aktuelles Passwort","old"],["Neues Passwort","neu"],["Wiederholen","confirm"]].map(([label,key])=>(
                <div key={key} style={{marginBottom:10}}>
                  <label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>{label}</label>
                  <input type="password" value={pw[key]} onChange={e=>setPw(p=>({...p,[key]:e.target.value}))}
                    style={{...inp,padding:"8px 12px"}} placeholder="••••••"/>
                </div>
              ))}
              {pwMsg && <div style={{fontSize:12,color:pwMsg.ok?"#059669":"#dc2626",marginBottom:8,fontWeight:600}}>{pwMsg.text}</div>}
              <button onClick={changePw}
                style={{padding:"9px 20px",background:"#374151",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                Passwort ändern
              </button>
            </div>
            {/* Save */}
            {(edited||savedOk) && (
              <button onClick={saveAll}
                style={{padding:"11px",background:savedOk?"#059669":c,border:"none",borderRadius:9,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",marginTop:4}}>
                {savedOk?"✅ Gespeichert!":"💾 Änderungen speichern"}
              </button>
            )}
          </div>
        )}

        {/* ── TAB: Statistiken ── */}
        {ptab==="statistiken" && (
          <div>
            {isPersonal ? (
              <>
                <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:24,textAlign:"center",marginBottom:12}}>
                  <div style={{fontSize:36,marginBottom:8}}>💪</div>
                  <div style={{fontSize:15,fontWeight:700,color:"#111827",marginBottom:6}}>Personal Training</div>
                  <div style={{fontSize:13,color:"#6b7280"}}>Statistiken werden manuell über Monatsziele gepflegt.</div>
                </div>
                <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:14}}>🎯 Meine Monatsziele</div>
                  {[["Kunden / Monat","kunden","👤","#90c"],["PT-Sessions / Monat","sessions","💪","#0891b2"]].map(([label,key,icon,col])=>{
                    const target = goals[key]||1;
                    return (
                      <div key={key} style={{marginBottom:14}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                          <span style={{fontSize:13,fontWeight:600}}>{icon} {label}</span>
                          <span style={{fontSize:12,color:"#6b7280"}}>Ziel: {target}</span>
                        </div>
                        <div style={{height:8,background:"#f3f4f6",borderRadius:4}}>
                          <div style={{width:"0%",height:"100%",background:col,borderRadius:4}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : !stats ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:32,marginBottom:8}}>📊</div>
                <div>Noch keine Kursdaten vorhanden</div>
              </div>
            ) : (
              <>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
                  {[
                    {label:"Kurse gesamt",value:stats.total_classes||stats.classes||0,icon:"🥊",color:"#7c3aed"},
                    {label:"Teilnehmer gesamt",value:stats.total_participants||stats.participants||0,icon:"👥",color:"#0891b2"},
                    {label:"Ø Auslastung",value:`${Math.round((stats.avg_fill_rate||0)*100)}%`,icon:"📈",color:"#059669"},
                    {label:"Ø Bewertung",value:stats.avg_rating?`${Number(stats.avg_rating).toFixed(1)} ⭐`:"—",icon:"⭐",color:"#d97706"},
                  ].map(s=>(
                    <div key={s.label} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:"20px",textAlign:"center"}}>
                      <div style={{fontSize:28,marginBottom:6}}>{s.icon}</div>
                      <div style={{fontSize:28,fontWeight:800,color:s.color}}>{s.value}</div>
                      <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginTop:12}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:14}}>🎯 Fortschritt zu deinen Zielen</div>
                  {[["Kurse",stats.total_classes||0,goals.kurse,"🥊","#7c3aed"],["Teilnehmer",stats.total_participants||0,goals.teilnehmer,"👥","#0891b2"]].map(([label,val,target,icon,col])=>{
                    const pct = Math.min(100,Math.round(val/(target||1)*100));
                    return (
                      <div key={label} style={{marginBottom:14}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                          <span style={{fontSize:13,fontWeight:600}}>{icon} {label}</span>
                          <span style={{fontSize:12,color:"#6b7280"}}>{val} / {target} ({pct}%)</span>
                        </div>
                        <div style={{height:8,background:"#f3f4f6",borderRadius:4}}>
                          <div style={{width:`${pct}%`,height:"100%",background:pct>=100?"#059669":col,borderRadius:4,transition:"width 0.5s"}}/>
                        </div>
                        {pct>=100 && <div style={{fontSize:11,color:"#059669",marginTop:3,fontWeight:600}}>✅ Ziel erreicht!</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── TAB: Badges ── */}
        {ptab==="badges" && (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
              {badges.map((b,i)=>(
                <div key={i} style={{background:"#fff",border:`2px solid ${b.color}30`,borderRadius:14,padding:"20px 12px",textAlign:"center",boxShadow:`0 0 0 1px ${b.color}15`}}>
                  <div style={{fontSize:36,marginBottom:8}}>{b.icon}</div>
                  <div style={{fontSize:12,fontWeight:700,color:b.color}}>{b.label}</div>
                </div>
              ))}
            </div>
            <div style={{background:"#f9fafb",border:"1px dashed #e5e7eb",borderRadius:14,padding:20,marginTop:16,textAlign:"center"}}>
              <div style={{fontSize:13,color:"#9ca3af",marginBottom:6}}>🔒 Noch zu verdienen</div>
              <div style={{fontSize:12,color:"#d1d5db"}}>
                {isPersonal
                  ? ((goals.sessions||0)<100?"100 PT-Sessions → 🥈 Silber-Badge · ":"") + ((goals.sessions||0)<200?"200 PT-Sessions → 💎 Diamant-Badge":"")
                  : ((!stats||(stats.total_classes||0)<50)?"50 Kurse → 🥈 Silber-Badge":"") + ((!stats||(stats.total_classes||0)<100)?" · 100 Kurse → 💎 Diamant-Badge":"")
                }
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Nächste Schichten ── */}
        {ptab==="schichten" && (
          <div>
            {schedule.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                <div style={{fontSize:32,marginBottom:8}}>📅</div>
                <div>Keine bevorstehenden Schichten gefunden</div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {schedule.map((s,i)=>(
                  <div key={i} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:14}}>
                    <div style={{width:44,height:44,borderRadius:10,background:c+"18",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <div style={{fontSize:10,fontWeight:700,color:c,textTransform:"uppercase"}}>{new Date(s.date).toLocaleDateString("de-DE",{weekday:"short"})}</div>
                      <div style={{fontSize:18,fontWeight:800,color:c,lineHeight:1}}>{new Date(s.date).getDate()}</div>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14,color:"#111827"}}>{s.class_type||s.type||"Kurs"}</div>
                      <div style={{fontSize:12,color:"#6b7280"}}>{s.start_time||""}{s.end_time?" – "+s.end_time:""} {s.location?`· ${s.location}`:""}</div>
                    </div>
                    {i===0 && <span style={{fontSize:10,padding:"3px 10px",borderRadius:99,background:c+"18",color:c,fontWeight:700}}>Nächste</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Einstellungen ── */}
        {ptab==="einstellungen" && (
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:22}}>
            <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:16}}>🔔 Benachrichtigungen</div>
            {[
              ["messages","💬 Neue Nachrichten im Chat"],
              ["schedule","📅 Änderungen im Dienstplan"],
              ["payroll","💸 Neue Abrechnung verfügbar"],
              ["feedback","⭐ Feedback-Reaktionen"],
            ].map(([key,label])=>(
              <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:"1px solid #f3f4f6"}}>
                <span style={{fontSize:13,color:"#374151"}}>{label}</span>
                <button onClick={()=>{setNotifs(n=>({...n,[key]:!n[key]}));mark();}}
                  style={{width:44,height:24,borderRadius:12,border:"none",background:notifs[key]?c:"#d1d5db",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:notifs[key]?23:3,transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                </button>
              </div>
            ))}
            {(edited||savedOk) && (
              <button onClick={saveAll}
                style={{width:"100%",padding:"11px",background:savedOk?"#059669":c,border:"none",borderRadius:9,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",marginTop:16}}>
                {savedOk?"✅ Gespeichert!":"💾 Einstellungen speichern"}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

/* ─── Betrieb Module ─────────────────────────────────────────────────── */
/* ── Aufgaben (To-Do) ── */
function AufgabenTab({ data, api, reload, user }) {
  const isAdmin = user.isAdmin || user.adminAccess;
  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState("alle");
  const aufgaben = data?.aufgaben || [];
  const PRIO = { hoch:{label:"Hoch",color:"#dc2626"}, mittel:{label:"Mittel",color:"#d97706"}, niedrig:{label:"Niedrig",color:"#059669"} };
  const STATUS_LABEL = { offen:"🔵 Offen", in_bearbeitung:"🟡 In Bearbeitung", erledigt:"✅ Erledigt" };
  const filtered = aufgaben.filter(a => filter==="alle" || a.status===filter);

  const save = async () => {
    if(!form) return;
    if(form.id) await api("PUT", `/aufgaben/${form.id}`, form);
    else await api("POST", "/aufgaben", {...form, erstellt_von:user.name, erstellt_am:new Date().toISOString()});
    setForm(null); reload();
  };
  const del = async (id) => { await api("DELETE", `/aufgaben/${id}`); reload(); };
  const setStatus = async (id, status) => { await api("PUT", `/aufgaben/${id}`, {status}); reload(); };
  const EMPTY = {titel:"",beschreibung:"",zugewiesen_an:"",faellig:"",prioritaet:"mittel",status:"offen"};

  return (
    <div style={{padding:24,maxWidth:860,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <span style={{fontSize:16,fontWeight:700,color:"#111827"}}>📝 Aufgaben</span>
        <div style={{flex:1}}/>
        {["alle","offen","in_bearbeitung","erledigt"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{padding:"5px 12px",borderRadius:20,border:"1px solid #e5e7eb",background:filter===f?"#7c3aed":"#fff",color:filter===f?"#fff":"#374151",fontSize:12,cursor:"pointer",fontWeight:filter===f?700:400}}>
            {f==="alle"?"Alle":STATUS_LABEL[f]}
          </button>
        ))}
        {isAdmin && <button onClick={()=>setForm({...EMPTY})}
          style={{padding:"8px 18px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>+ Neue Aufgabe</button>}
      </div>

      {filtered.length===0 && <div style={{textAlign:"center",color:"#9ca3af",padding:40}}>Keine Aufgaben</div>}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(a=>(
          <div key={a.id} style={{background:"#fff",border:`1px solid ${PRIO[a.prioritaet]?.color||"#e5e7eb"}30`,borderLeft:`4px solid ${PRIO[a.prioritaet]?.color||"#6b7280"}`,borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <input type="checkbox" checked={a.status==="erledigt"} onChange={e=>setStatus(a.id,e.target.checked?"erledigt":"offen")}
                style={{marginTop:3,width:16,height:16,cursor:"pointer",accentColor:"#7c3aed"}}/>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{fontWeight:700,fontSize:14,color:a.status==="erledigt"?"#9ca3af":"#111827",textDecoration:a.status==="erledigt"?"line-through":"none"}}>{a.titel}</span>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:(PRIO[a.prioritaet]?.color||"#6b7280")+"18",color:PRIO[a.prioritaet]?.color||"#6b7280",fontWeight:700}}>{PRIO[a.prioritaet]?.label||a.prioritaet}</span>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:"#f3f4f6",color:"#6b7280"}}>{STATUS_LABEL[a.status]||a.status}</span>
                </div>
                {a.beschreibung && <div style={{fontSize:13,color:"#6b7280",marginBottom:6,lineHeight:1.5}}>{a.beschreibung}</div>}
                <div style={{display:"flex",gap:16,fontSize:11,color:"#9ca3af",flexWrap:"wrap"}}>
                  {a.zugewiesen_an && <span>👤 {a.zugewiesen_an}</span>}
                  {a.faellig && <span>📅 {a.faellig}</span>}
                  {a.erstellt_von && <span>Von: {a.erstellt_von}</span>}
                </div>
              </div>
              {isAdmin && (
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>setForm({...a})} style={{padding:"5px 10px",fontSize:11,border:"1px solid #e5e7eb",borderRadius:6,background:"#f9fafb",cursor:"pointer"}}>✏️</button>
                  <button onClick={()=>del(a.id)} style={{padding:"5px 10px",fontSize:11,border:"1px solid #fee2e2",borderRadius:6,background:"#fff5f5",color:"#dc2626",cursor:"pointer"}}>✕</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:460,maxWidth:"calc(100vw - 32px)",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:20}}>📝 {form.id?"Aufgabe bearbeiten":"Neue Aufgabe"}</div>
            {[["Titel","titel","text"],["Beschreibung","beschreibung","textarea"],["Zugewiesen an","zugewiesen_an","text"],["Fällig bis","faellig","date"]].map(([label,field,type])=>(
              <div key={field} style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
                {type==="textarea"
                  ? <textarea value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                      style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,minHeight:70,resize:"vertical",boxSizing:"border-box"}}/>
                  : <input type={type} value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                      style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                }
              </div>
            ))}
            <div style={{display:"flex",gap:12,marginBottom:14}}>
              {[["Priorität","prioritaet",[["hoch","🔴 Hoch"],["mittel","🟡 Mittel"],["niedrig","🟢 Niedrig"]]],["Status","status",[["offen","🔵 Offen"],["in_bearbeitung","🟡 In Bearbeitung"],["erledigt","✅ Erledigt"]]]].map(([label,field,opts])=>(
                <div key={field} style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
                  <select value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                    style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13}}>
                    {opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setForm(null)} style={{flex:1,padding:"10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:13}}>Abbrechen</button>
              <button onClick={save} style={{flex:2,padding:"10px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>💾 Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Unfallbuch ── */
function UnfallbuchTab({ data, api, reload, user }) {
  const isAdmin = user.isAdmin || user.adminAccess;
  const [form, setForm] = useState(null);
  const unfaelle = data?.unfallbuch || [];
  const now = new Date();
  const EMPTY = {datum:now.toISOString().slice(0,10),uhrzeit:now.toTimeString().slice(0,5),verletzter:"",art:"",ort:"",beschreibung:"",ersthelfer:user.name,massnahmen:"",arzt:false,unterschrift:user.name};

  const save = async () => {
    await api("POST", "/unfallbuch", {...form, erstellt_von:user.name, erstellt_am:new Date().toISOString()});
    setForm(null); reload();
  };
  const del = async (id) => { await api("DELETE", `/unfallbuch/${id}`); reload(); };

  const print = (u) => {
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>Unfallprotokoll</title><style>body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:0 auto}h1{font-size:20px;border-bottom:2px solid #000;padding-bottom:10px}.row{display:flex;gap:20px;margin-bottom:16px}.field{flex:1}label{font-size:11px;font-weight:bold;display:block;margin-bottom:4px;text-transform:uppercase}.val{border-bottom:1px solid #999;padding:4px 0;min-height:28px;font-size:14px}.sig{margin-top:40px;border-top:1px solid #000;padding-top:10px;font-size:12px}</style></head><body>
    <h1>🏥 Unfallprotokoll — BURN BXNG</h1>
    <div class="row"><div class="field"><label>Datum</label><div class="val">${u.datum}</div></div><div class="field"><label>Uhrzeit</label><div class="val">${u.uhrzeit} Uhr</div></div></div>
    <div class="row"><div class="field"><label>Verletzte Person</label><div class="val">${u.verletzter}</div></div><div class="field"><label>Art der Verletzung</label><div class="val">${u.art}</div></div></div>
    <div class="row"><div class="field"><label>Ort</label><div class="val">${u.ort||"—"}</div></div><div class="field"><label>Arzt aufgesucht</label><div class="val">${u.arzt?"Ja":"Nein"}</div></div></div>
    <div class="row"><div class="field"><label>Beschreibung</label><div class="val" style="min-height:60px">${u.beschreibung||"—"}</div></div></div>
    <div class="row"><div class="field"><label>Maßnahmen / Erste Hilfe</label><div class="val" style="min-height:60px">${u.massnahmen||"—"}</div></div></div>
    <div class="row"><div class="field"><label>Ersthelfer</label><div class="val">${u.ersthelfer||"—"}</div></div><div class="field"><label>Protokolliert von</label><div class="val">${u.erstellt_von}</div></div></div>
    <div class="sig"><strong>Unterschrift / Bestätigung:</strong> ${u.unterschrift||"—"}</div>
    <div class="sig">Erstellt: ${new Date(u.erstellt_am).toLocaleString("de-DE")}</div>
    </body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div style={{padding:24,maxWidth:860,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
        <span style={{fontSize:16,fontWeight:700,color:"#111827"}}>🏥 Unfallbuch</span>
        <span style={{fontSize:11,color:"#9ca3af",background:"#fef3c7",padding:"2px 10px",borderRadius:99,border:"1px solid #fde68a"}}>⚖️ Gesetzlich vorgeschrieben</span>
        <div style={{flex:1}}/>
        <button onClick={()=>setForm({...EMPTY})}
          style={{padding:"8px 18px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>+ Vorfall erfassen</button>
      </div>

      {unfaelle.length===0 && (
        <div style={{textAlign:"center",color:"#9ca3af",padding:60}}>
          <div style={{fontSize:48,marginBottom:12}}>🏥</div>
          <div style={{fontSize:14}}>Noch keine Vorfälle erfasst</div>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {[...unfaelle].reverse().map(u=>(
          <div key={u.id} style={{background:"#fff",border:"1px solid #fee2e2",borderLeft:"4px solid #dc2626",borderRadius:10,padding:"16px 20px"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                  <span style={{fontWeight:700,fontSize:14,color:"#111827"}}>🩹 {u.verletzter}</span>
                  <span style={{fontSize:11,color:"#9ca3af"}}>{u.datum} · {u.uhrzeit} Uhr</span>
                  {u.arzt && <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:"#fef3c7",color:"#92400e",fontWeight:700}}>Arzt aufgesucht</span>}
                </div>
                <div style={{fontSize:13,color:"#374151",marginBottom:3}}><strong>Art:</strong> {u.art}</div>
                {u.ort && <div style={{fontSize:13,color:"#374151",marginBottom:3}}><strong>Ort:</strong> {u.ort}</div>}
                {u.beschreibung && <div style={{fontSize:13,color:"#374151",marginBottom:3,lineHeight:1.5}}><strong>Vorfall:</strong> {u.beschreibung}</div>}
                <div style={{fontSize:11,color:"#9ca3af",marginTop:6}}>Ersthelfer: {u.ersthelfer||"—"} · Protokoll: {u.erstellt_von} · {new Date(u.erstellt_am).toLocaleString("de-DE")}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,flexDirection:"column"}}>
                <button onClick={()=>print(u)} style={{padding:"6px 12px",fontSize:11,border:"1px solid #e5e7eb",borderRadius:6,background:"#f9fafb",cursor:"pointer",fontWeight:600}}>🖨️ Drucken</button>
                {isAdmin && <button onClick={()=>del(u.id)} style={{padding:"6px 10px",fontSize:11,border:"1px solid #fee2e2",borderRadius:6,background:"#fff5f5",color:"#dc2626",cursor:"pointer"}}>✕ Löschen</button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:520,maxWidth:"calc(100vw - 32px)",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontWeight:700,fontSize:16,color:"#dc2626",marginBottom:4}}>🏥 Vorfall erfassen</div>
            <div style={{fontSize:12,color:"#9ca3af",marginBottom:20}}>Alle Felder werden gespeichert und können ausgedruckt werden</div>
            <div style={{display:"flex",gap:12,marginBottom:14}}>
              {[["Datum","datum","date"],["Uhrzeit","uhrzeit","time"]].map(([label,field,type])=>(
                <div key={field} style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
                  <input type={type} value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                    style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                </div>
              ))}
            </div>
            {[["Verletzte Person *","verletzter","text"],["Art der Verletzung *","art","text"],["Ort im Studio","ort","text"],["Ersthelfer","ersthelfer","text"]].map(([label,field,type])=>(
              <div key={field} style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
                <input type={type} value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                  style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              </div>
            ))}
            {[["Beschreibung des Vorfalls","beschreibung"],["Maßnahmen / Erste Hilfe","massnahmen"]].map(([label,field])=>(
              <div key={field} style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
                <textarea value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                  style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,minHeight:70,resize:"vertical",boxSizing:"border-box"}}/>
              </div>
            ))}
            <div style={{display:"flex",gap:16,marginBottom:14,alignItems:"center"}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                <input type="checkbox" checked={form.arzt||false} onChange={e=>setForm(f=>({...f,arzt:e.target.checked}))} style={{width:16,height:16,accentColor:"#dc2626"}}/>
                Arzt / Notarzt aufgesucht
              </label>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Unterschrift (Name)</label>
              <input value={form.unterschrift||""} onChange={e=>setForm(f=>({...f,unterschrift:e.target.value}))}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setForm(null)} style={{flex:1,padding:"10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:13}}>Abbrechen</button>
              <button onClick={save} style={{flex:2,padding:"10px",border:"none",borderRadius:8,background:"#dc2626",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>🏥 Protokoll speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mitarbeiter-Feedback (anonym) ── */
function FeedbackTab({ data, api, reload, user }) {
  const isAdmin = user.isAdmin || user.adminAccess;
  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState("alle");
  const feedback = data?.feedback || [];
  const CATS = {
    positiv:      {label:"👍 Positiv",           color:"#059669"},
    verbesserung: {label:"💡 Verbesserungsidee", color:"#d97706"},
    problem:      {label:"⚠️ Problem",           color:"#dc2626"},
  };
  const filtered = feedback.filter(fb => filter==="alle" || fb.kategorie===filter);

  const save = async () => {
    await api("POST", "/feedback", {...form, erstellt_am:new Date().toISOString()});
    setForm(null); reload();
  };
  const upvote  = async (id) => { await api("PUT", `/feedback/${id}/upvote`, {}); reload(); };
  const markRead = async (id) => { await api("PUT", `/feedback/${id}`, {gelesen:true}); reload(); };
  const del     = async (id) => { await api("DELETE", `/feedback/${id}`); reload(); };

  return (
    <div style={{padding:24,maxWidth:860,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <span style={{fontSize:16,fontWeight:700,color:"#111827"}}>⭐ Mitarbeiter-Feedback</span>
        <span style={{fontSize:11,background:"#f3f4f6",color:"#6b7280",padding:"2px 10px",borderRadius:99}}>🔒 Anonym</span>
        <div style={{flex:1}}/>
        {["alle","positiv","verbesserung","problem"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{padding:"5px 12px",borderRadius:20,border:"1px solid #e5e7eb",background:filter===f?"#7c3aed":"#fff",color:filter===f?"#fff":"#374151",fontSize:12,cursor:"pointer",fontWeight:filter===f?700:400}}>
            {f==="alle"?"Alle":CATS[f].label}
          </button>
        ))}
        <button onClick={()=>setForm({titel:"",text:"",kategorie:"verbesserung"})}
          style={{padding:"8px 18px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>+ Feedback geben</button>
      </div>

      {filtered.length===0 && <div style={{textAlign:"center",color:"#9ca3af",padding:40}}>Noch kein Feedback vorhanden</div>}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {[...filtered].reverse().map(fb=>{
          const cat = CATS[fb.kategorie]||CATS.verbesserung;
          return (
            <div key={fb.id} style={{background:"#fff",border:`1px solid ${cat.color}30`,borderLeft:`4px solid ${cat.color}`,borderRadius:10,padding:"16px 20px",opacity:fb.gelesen?0.65:1}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:cat.color+"18",color:cat.color,fontWeight:700}}>{cat.label}</span>
                    <span style={{fontWeight:700,fontSize:14,color:"#111827"}}>{fb.titel}</span>
                    {fb.gelesen && <span style={{fontSize:10,color:"#9ca3af"}}>✓ Gelesen</span>}
                  </div>
                  <div style={{fontSize:13,color:"#374151",lineHeight:1.6}}>{fb.text}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:8}}>{new Date(fb.erstellt_am).toLocaleDateString("de-DE")}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                  <button onClick={()=>upvote(fb.id)}
                    style={{padding:"5px 14px",border:"1px solid #e5e7eb",borderRadius:20,background:"#f9fafb",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    👍 {fb.upvotes||0}
                  </button>
                  {isAdmin && !fb.gelesen && (
                    <button onClick={()=>markRead(fb.id)}
                      style={{padding:"4px 10px",fontSize:11,border:"1px solid #d1fae5",borderRadius:6,background:"#f0fdf4",cursor:"pointer",color:"#059669",fontWeight:600}}>✓ Gelesen</button>
                  )}
                  {isAdmin && (
                    <button onClick={()=>del(fb.id)}
                      style={{padding:"4px 10px",fontSize:11,border:"1px solid #fee2e2",borderRadius:6,background:"#fff5f5",color:"#dc2626",cursor:"pointer"}}>✕</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:440,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>⭐ Feedback geben</div>
            <div style={{fontSize:12,color:"#9ca3af",marginBottom:20}}>🔒 Dein Name wird nicht gespeichert — komplett anonym</div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:6}}>Kategorie</label>
              <div style={{display:"flex",gap:8}}>
                {Object.entries(CATS).map(([k,v])=>(
                  <button key={k} onClick={()=>setForm(f=>({...f,kategorie:k}))}
                    style={{flex:1,padding:"8px",borderRadius:8,border:`2px solid ${form.kategorie===k?v.color:"#e5e7eb"}`,background:form.kategorie===k?v.color+"15":"#fff",cursor:"pointer",fontSize:11,fontWeight:600,color:form.kategorie===k?v.color:"#374151"}}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Titel</label>
              <input value={form.titel||""} onChange={e=>setForm(f=>({...f,titel:e.target.value}))}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}} placeholder="Kurzer Betreff..."/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Nachricht</label>
              <textarea value={form.text||""} onChange={e=>setForm(f=>({...f,text:e.target.value}))}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,minHeight:100,resize:"vertical",boxSizing:"border-box"}} placeholder="Was möchtest du mitteilen?"/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setForm(null)} style={{flex:1,padding:"10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:13}}>Abbrechen</button>
              <button onClick={save} style={{flex:2,padding:"10px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>📤 Anonym senden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── #14 Kurs-Monitor ── */
const PROTO_TYPES = [
  { id:"koop_event",  label:"🤝 Koop-Event",            short:"Koop-Event" },
  { id:"waesche",     label:"🧺 Wäscheprotokoll",        short:"Wäsche" },
  { id:"kasse_tag",   label:"💶 Kassenabschluss täglich", short:"Kasse täglich" },
  { id:"kasse_monat", label:"📅 Kassenabschluss Monat",  short:"Kasse Monat" },
  { id:"inventar",    label:"📦 Inventar",               short:"Inventar" },
];
const KASSE_DENOMS = [0.01,0.02,0.05,0.10,0.20,0.50,1,2,5,10,20,50,100,200];
const INV_TYPES = [
  { id:"sales",  label:"🛒 Sales Items",      cols:["Datum","Artikel","Kategorie","Lager","Verkauft","Bar","Karte"] },
  { id:"items",  label:"📋 Items Inventory",  cols:["Datum","Artikel","Kategorie","Lager","Leer","Nachfüllen","Status"] },
  { id:"wasser", label:"💧 Water Inventory",  cols:["Datum","Flaschen","Pack","Leer","Nachfüllen","Status"] },
  { id:"team",   label:"👥 Water Team",       cols:["Datum","Flaschen","Name"] },
];

function ProtokollTab({ data, api, reload, user }) {
  const [selType, setSelType] = useState(null);
  const [selDoc,  setSelDoc]  = useState(null);  // null = list, obj = edit
  const [docData, setDocData] = useState({});
  const [title,   setTitle]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(null);
  const [kasseAI, setKasseAI] = useState(null);
  const [kasseAILoading, setKasseAILoading] = useState(false);
  const protokolle = (data?.protokolle || []);

  const filtered = selType ? protokolle.filter(p=>p.type===selType) : [];

  const openNew = () => {
    const defaults = {};
    if(selType==="kasse_tag"||selType==="kasse_monat") {
      defaults.datum=new Date().toISOString().slice(0,10);
      defaults.name="";
      defaults.unterschrift="";
      KASSE_DENOMS.forEach(d=>{ defaults[`anz_${d}`]=""; });
    }
    if(selType==="waesche") {
      defaults.woche="";
      ["Mo","Di","Mi","Do","Fr","Sa","So"].forEach(day=>{
        for(let slot=0;slot<6;slot++) {
          defaults[`${day}_${slot}_name`]="";
          defaults[`${day}_${slot}_ladung`]="";
        }
      });
    }
    if(selType==="inventar") defaults.inv_type="sales";
    setDocData(defaults);
    setTitle(selType==="kasse_tag"?"Kassenabschluss "+new Date().toLocaleDateString("de-DE"):
             selType==="kasse_monat"?"Kassenabschluss "+new Date().toLocaleDateString("de-DE",{month:"long",year:"numeric"}):
             selType==="waesche"?"Wäsche KW "+new Date().toISOString().slice(0,10):"Neu");
    setSelDoc("new");
  };

  const openEdit = (doc) => { setSelDoc(doc); setDocData({...doc.data}); setTitle(doc.title); };

  const save = async () => {
    setSaving(true);
    if(selDoc==="new") {
      const created = await api("POST","/betrieb/protokolle",{type:selType,title,data:docData,created_by:user.name});
      reload();
      setSelDoc(created);
    } else {
      await api("PUT",`/betrieb/protokolle/${selDoc.id}`,{title,data:docData});
      reload();
    }
    setSaving(false);
  };

  const del = async(id) => {
    if(!window.confirm("Protokoll löschen?")) return;
    setDeleting(id);
    await api("DELETE",`/betrieb/protokolle/${id}`);
    reload();
    setDeleting(null);
    if(selDoc?.id===id) setSelDoc(null);
  };

  const inp = (key,placeholder="",type="text",style={}) => (
    <input type={type} placeholder={placeholder}
      value={docData[key]||""}
      onChange={e=>setDocData(d=>({...d,[key]:e.target.value}))}
      style={{border:"1px solid #e5e7eb",borderRadius:5,padding:"5px 8px",fontSize:12,width:"100%",boxSizing:"border-box",...style}}/>
  );
  const ta = (key,placeholder="",rows=2) => (
    <textarea placeholder={placeholder} rows={rows}
      value={docData[key]||""}
      onChange={e=>setDocData(d=>({...d,[key]:e.target.value}))}
      style={{border:"1px solid #e5e7eb",borderRadius:5,padding:"5px 8px",fontSize:12,width:"100%",boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
  );
  const chk = (key,label) => (
    <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,cursor:"pointer"}}>
      <input type="checkbox" checked={!!docData[key]} onChange={e=>setDocData(d=>({...d,[key]:e.target.checked}))} />
      {label}
    </label>
  );
  const sel = (key,opts) => (
    <select value={docData[key]||""} onChange={e=>setDocData(d=>({...d,[key]:e.target.value}))}
      style={{border:"1px solid #e5e7eb",borderRadius:5,padding:"5px 8px",fontSize:12,width:"100%"}}>
      <option value="">—</option>
      {opts.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );

  const labelStyle = {fontSize:11,fontWeight:600,color:"#374151",marginBottom:2,display:"block"};
  const secStyle   = {background:"#f8f7ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"12px 14px",marginBottom:12};
  const secH       = {fontSize:12,fontWeight:800,color:"#7c3aed",marginBottom:10,marginTop:0};
  const row2       = {display:"grid",gridTemplateColumns:"1fr 1fr",gap:8};

  /* ─ FORM RENDERERS ─ */
  const renderKoopEvent = () => (
    <div>
      <div style={secStyle}>
        <p style={secH}>A. Eventdaten & Basisinformationen</p>
        {[["eventtitel","Eventtitel"],["art","Art des Events"],["ort","Ort"],["ap_partner","Ansprechpartner (Partner)"],
          ["kontakt_partner","Kontaktdaten (Partner)"],["ap_intern","Ansprechpartner (intern)"],["kontakt_intern","Kontaktdaten (intern)"]].map(([k,l])=>(
          <div key={k} style={{marginBottom:8}}>
            <span style={labelStyle}>{l}</span>{inp(k,l)}
          </div>
        ))}
        <div style={{marginBottom:8}}><span style={labelStyle}>Datum & Uhrzeit</span>{inp("datum_uhrzeit","",  "datetime-local")}</div>
      </div>
      <div style={secStyle}>
        <p style={secH}>B. Kooperationsrahmen</p>
        {[["partnerbeitrag","Partnerbeitrag"],["unser_beitrag","Unser Beitrag"],["kostenaufteilung","Kostenaufteilung"],
          ["std_posting","Save the Date Posting durch wen?"]].map(([k,l])=>(
          <div key={k} style={{marginBottom:8}}><span style={labelStyle}>{l}</span>{inp(k,l)}</div>
        ))}
        <div style={row2}>
          {chk("logos","Verwendung von Logos")}
          {chk("reposting","Reposting verpflichtend")}
        </div>
        <div style={{marginBottom:8,marginTop:8}}><span style={labelStyle}>Frist Save the Date</span>{inp("frist_std","","date")}</div>
        <div style={{marginBottom:8}}><span style={labelStyle}>Orga-Freeze ab</span>{inp("freeze","","date")}</div>
      </div>
      <div style={secStyle}>
        <p style={secH}>C. Logistik & Ausstattung</p>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead><tr style={{background:"#ede9fe"}}>
              {["Punkt","Ja/Nein","Wer?","Frist"].map(h=><th key={h} style={{padding:"4px 6px",textAlign:"left",fontWeight:700}}>{h}</th>)}
            </tr></thead>
            <tbody>{["Equipment (Matten, Boxen)","Musik/Mikro/Lautsprecher","Technik getestet",
              "Essen & Getränke","Goodie Bags/Gutscheine","Aufsteller/Deko/Branding",
              "Aufbau (Datum+Uhrzeit)","Abbau (Datum+Uhrzeit)","Vorablieferung"].map((pt,i)=>{
              const k=`log_${i}`;
              return <tr key={k} style={{borderBottom:"1px solid #e5e7eb"}}>
                <td style={{padding:"3px 6px",fontSize:11,color:"#374151"}}>{pt}</td>
                <td style={{padding:"3px 6px"}}>{sel(`${k}_ja`,["Ja","Nein"])}</td>
                <td style={{padding:"3px 6px"}}>{inp(`${k}_wer`,"Wer?","text",{minWidth:80})}</td>
                <td style={{padding:"3px 6px"}}>{inp(`${k}_frist`,"","date",{minWidth:100})}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      </div>
      <div style={secStyle}>
        <p style={secH}>D. Kommunikation & Marketing</p>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead><tr style={{background:"#ede9fe"}}>{["Thema","Zuständigkeit","Deadline"].map(h=><th key={h} style={{padding:"4px 6px",textAlign:"left",fontWeight:700}}>{h}</th>)}</tr></thead>
            <tbody>{["Save the Date","Einladung/Eventlink","Reposting-Reminder","Eventfotografie","Nachbericht"].map((pt,i)=>{
              const k=`kom_${i}`;
              return <tr key={k} style={{borderBottom:"1px solid #e5e7eb"}}>
                <td style={{padding:"3px 6px",fontSize:11}}>{pt}</td>
                <td style={{padding:"3px 6px"}}>{inp(`${k}_wer`,"Wer?")}</td>
                <td style={{padding:"3px 6px"}}>{inp(`${k}_dl`,"","date")}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      </div>
      <div style={secStyle}>
        <p style={secH}>E. Rollen vor Ort</p>
        {[["Host/Moderator:in","Begrüßung"],["Trainer:in/Coach","Durchführung"],["AP vor Ort","Koordination"],
          ["Check-In/Gästeliste","Empfang"],["Foto/Video","Dokumentation"]].map(([rolle,verantwortl],i)=>(
          <div key={i} style={{display:"grid",gridTemplateColumns:"1.5fr 2fr",gap:8,marginBottom:6,alignItems:"center"}}>
            <span style={{fontSize:11,fontWeight:600}}>{rolle}<br/><span style={{fontWeight:400,color:"#6b7280"}}>{verantwortl}</span></span>
            {inp(`rolle_${i}_person`,"Person")}
          </div>
        ))}
      </div>
      <div style={secStyle}>
        <p style={secH}>F. Nachbereitung & Erfolgskontrolle</p>
        <div style={row2}>
          <div><span style={labelStyle}>Teilnehmerzahl</span>{inp("nb_tn","Anzahl","number")}</div>
          <div><span style={labelStyle}>Leads / Upsells</span>{inp("nb_leads","Anzahl","number")}</div>
          <div><span style={labelStyle}>Social Media Reichweite</span>{inp("nb_reichweite","")}</div>
          <div><span style={labelStyle}>Feedback (Ø)</span>{inp("nb_feedback","1-5","number")}</div>
        </div>
        <div style={{marginTop:8}}><span style={labelStyle}>Was hat gefehlt?</span>{ta("nb_fehlt","Was hat gefehlt?")}</div>
        <div style={{marginTop:8}}><span style={labelStyle}>Learnings für nächstes Mal</span>{ta("nb_learnings","Learnings")}</div>
        <div style={{marginTop:8,display:"flex",gap:16}}>
          {chk("erfolgreich","Event war erfolgreich")}
          {chk("nochmal","Nochmal machen")}
        </div>
      </div>
      <div style={secStyle}>
        <p style={secH}>G. Fristen & Cut-Offs</p>
        {[["Save the Date Post","g_std"],["Anmeldung geöffnet bis","g_anmeld"],["Orga-Freeze","g_freeze"],
          ["Aufbauzeit geblockt","g_aufbau"],["Nachbereitung abgeschlossen","g_nachb"]].map(([l,k])=>(
          <div key={k} style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:6,alignItems:"center"}}>
            <span style={{fontSize:11,fontWeight:600}}>{l}</span>
            {inp(k,"","date")}
          </div>
        ))}
      </div>
      <div style={secStyle}>
        <p style={secH}>Ideen & Notizen</p>
        <div style={{marginBottom:8}}><span style={labelStyle}>Ideen für Ablauf/Deko</span>{ta("ideen_ablauf","Ideen...",3)}</div>
        <div style={{marginBottom:8}}><span style={labelStyle}>Content-Ideen/Moodboard</span>{ta("ideen_content","Content...",2)}</div>
        <div><span style={labelStyle}>Sonstige Hinweise</span>{ta("sonstige","Hinweise...",2)}</div>
      </div>
    </div>
  );

  const renderWaesche = () => {
    const days = ["Mo","Di","Mi","Do","Fr","Sa","So"];
    const slots = [0,1,2,3,4,5];
    return (
      <div>
        <div style={{marginBottom:12}}>
          <span style={labelStyle}>Woche (z.B. 01.09.–07.09.2025)</span>
          {inp("woche","KW / Datum")}
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead><tr style={{background:"#ede9fe"}}>
              <th style={{padding:"4px 6px",textAlign:"left",fontWeight:700,minWidth:50}}>Slot</th>
              {days.map(d=><th key={d} style={{padding:"4px 6px",fontWeight:700,minWidth:110}}>{d}</th>)}
            </tr></thead>
            <tbody>{slots.map(slot=>(
              <tr key={slot} style={{borderBottom:"1px solid #e5e7eb"}}>
                <td style={{padding:"4px 6px",fontWeight:600,color:"#7c3aed"}}>#{slot+1}</td>
                {days.map(day=>(
                  <td key={day} style={{padding:"3px 4px",verticalAlign:"top"}}>
                    <input placeholder="Name" value={docData[`${day}_${slot}_name`]||""} onChange={e=>setDocData(d=>({...d,[`${day}_${slot}_name`]:e.target.value}))}
                      style={{border:"1px solid #e5e7eb",borderRadius:4,padding:"3px 5px",fontSize:11,width:"100%",marginBottom:2,boxSizing:"border-box"}}/>
                    <input placeholder="Ladung" value={docData[`${day}_${slot}_ladung`]||""} onChange={e=>setDocData(d=>({...d,[`${day}_${slot}_ladung`]:e.target.value}))}
                      style={{border:"1px solid #e5e7eb",borderRadius:4,padding:"3px 5px",fontSize:11,width:"100%",boxSizing:"border-box"}}/>
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderKasse = (monat=false) => {
    const denoms = KASSE_DENOMS;
    let summe = 0;
    denoms.forEach(d=>{
      const anz = parseFloat(docData[`anz_${d}`]||0);
      summe += anz * d;
    });
    summe = Math.round(summe*100)/100;
    const vortag = parseFloat(docData.vortag||0);
    const diff   = Math.round((summe - vortag)*100)/100;
    return (
      <div>
        <div style={row2}>
          <div><span style={labelStyle}>Datum</span>{inp("datum","","date")}</div>
          <div><span style={labelStyle}>Name</span>{inp("name","Dein Name")}</div>
        </div>
        {monat && <div style={{marginTop:8,padding:"8px 10px",background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,fontSize:11,color:"#92400e",marginBottom:8}}>
          Am Ende eines Monats ist ein Gesamtabschluss durchzuführen.
        </div>}
        <div style={{overflowX:"auto",marginTop:10}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:"#ede9fe"}}>
              {["€","Anzahl","Betrag (€)"].map(h=><th key={h} style={{padding:"6px 8px",textAlign:"left",fontWeight:700}}>{h}</th>)}
            </tr></thead>
            <tbody>{denoms.map(d=>{
              const anz = parseFloat(docData[`anz_${d}`]||0);
              const betrag = Math.round(anz*d*100)/100;
              return <tr key={d} style={{borderBottom:"1px solid #f3f4f6"}}>
                <td style={{padding:"4px 8px",fontWeight:600,color:"#374151"}}>{d < 1 ? `${Math.round(d*100)} Ct` : `${d} €`}</td>
                <td style={{padding:"4px 8px"}}>
                  <input type="number" min="0" placeholder="0" value={docData[`anz_${d}`]||""}
                    onChange={e=>setDocData(dd=>({...dd,[`anz_${d}`]:e.target.value}))}
                    style={{border:"1px solid #e5e7eb",borderRadius:4,padding:"3px 6px",width:70,fontSize:12}}/>
                </td>
                <td style={{padding:"4px 8px",color: betrag>0?"#059669":"#9ca3af"}}>{betrag > 0 ? `${betrag.toFixed(2)} €` : "—"}</td>
              </tr>;
            })}</tbody>
            <tfoot>
              <tr style={{background:"#f0fdf4",fontWeight:700}}>
                <td style={{padding:"6px 8px"}} colSpan={2}>Summe</td>
                <td style={{padding:"6px 8px",color:"#059669",fontSize:14}}>{summe.toFixed(2)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:10}}>
          <div><span style={labelStyle}>Betrag Vortag (€)</span>
            <input type="number" step="0.01" placeholder="0.00" value={docData.vortag||""}
              onChange={e=>setDocData(d=>({...d,vortag:e.target.value}))}
              style={{border:"1px solid #e5e7eb",borderRadius:5,padding:"5px 8px",fontSize:12,width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div><span style={labelStyle}>Differenz</span>
            <div style={{padding:"6px 8px",border:"1px solid #e5e7eb",borderRadius:5,fontSize:12,
              color: diff===0?"#374151":diff>0?"#059669":"#dc2626",fontWeight:700,background:"#f9fafb"}}>
              {vortag ? `${diff >= 0 ? "+" : ""}${diff.toFixed(2)} €` : "—"}
            </div>
          </div>
        </div>
        <div style={{marginTop:10}}><span style={labelStyle}>Unterschrift</span>{inp("unterschrift","Unterschrift")}</div>
      </div>
    );
  };

  const renderInventar = () => {
    const invType = docData.inv_type || "sales";
    const typeDef = INV_TYPES.find(t=>t.id===invType) || INV_TYPES[0];
    const rowCount = 15;
    const rowKeys = Array.from({length:rowCount},(_,i)=>i);
    return (
      <div>
        <div style={{marginBottom:12}}>
          <span style={labelStyle}>Inventar-Typ</span>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {INV_TYPES.map(t=>(
              <button key={t.id} onClick={()=>setDocData(d=>({...d,inv_type:t.id}))}
                style={{padding:"4px 10px",fontSize:11,fontWeight:600,border:"none",borderRadius:4,cursor:"pointer",
                  background: invType===t.id?"#9900cc":"#f3f4f6",color: invType===t.id?"#fff":"#374151"}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{marginBottom:8}}><span style={labelStyle}>Datum</span>{inp("inv_datum","","date",{maxWidth:160})}</div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead><tr style={{background:"#ede9fe"}}>
              <th style={{padding:"4px 4px",fontWeight:700,textAlign:"left",minWidth:28}}>#</th>
              {typeDef.cols.map(c=><th key={c} style={{padding:"4px 6px",fontWeight:700,textAlign:"left",minWidth:80}}>{c}</th>)}
            </tr></thead>
            <tbody>{rowKeys.map(i=>(
              <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}>
                <td style={{padding:"3px 4px",color:"#9ca3af",fontSize:10}}>{i+1}</td>
                {typeDef.cols.map(c=>(
                  <td key={c} style={{padding:"2px 3px"}}>
                    <input placeholder="—" value={docData[`${invType}_${i}_${c}`]||""}
                      onChange={e=>setDocData(d=>({...d,[`${invType}_${i}_${c}`]:e.target.value}))}
                      style={{border:"1px solid #e5e7eb",borderRadius:3,padding:"3px 5px",fontSize:11,width:"100%",boxSizing:"border-box"}}/>
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  };

  /* ─ LIST VIEW ─ */
  if(!selType) return (
    <div style={{padding:20}}>
      <h3 style={{margin:"0 0 16px",fontSize:15,fontWeight:700,color:"#111"}}>📄 Protokolle & Formulare</h3>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
        {PROTO_TYPES.map(t=>{
          const count = protokolle.filter(p=>p.type===t.id).length;
          return (
            <div key={t.id} onClick={()=>{setSelType(t.id);setSelDoc(null);}}
              style={{background:"#fff",border:"1px solid #e9d5ff",borderRadius:10,padding:"14px 16px",
                cursor:"pointer",transition:"all .15s"}}
              onMouseEnter={e=>e.currentTarget.style.background="#faf5ff"}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              <div style={{fontSize:20,marginBottom:4}}>{t.label.split(" ")[0]}</div>
              <div style={{fontWeight:700,fontSize:13,color:"#111"}}>{t.label.substring(3)}</div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>{count} Einträge</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  /* ─ FORM TYPE SELECTED — list of saved entries ─ */
  if(selDoc===null) return (
    <div style={{padding:20}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <button onClick={()=>setSelType(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#6b7280"}}>←</button>
        <h3 style={{margin:0,fontSize:15,fontWeight:700}}>{PROTO_TYPES.find(t=>t.id===selType)?.label}</h3>
        <button onClick={openNew}
          style={{marginLeft:"auto",background:"#9900cc",color:"#fff",border:"none",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          + Neu
        </button>
      </div>
      {filtered.length===0 ? (
        <div style={{textAlign:"center",padding:"40px 20px",color:"#9ca3af",fontSize:13}}>
          Noch keine Einträge.<br/>
          <button onClick={openNew} style={{marginTop:12,background:"#9900cc",color:"#fff",border:"none",borderRadius:6,padding:"8px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}>
            Ersten Eintrag erstellen
          </button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(doc=>(
            <div key={doc.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,cursor:"pointer"}} onClick={()=>openEdit(doc)}>
                <div style={{fontWeight:600,fontSize:13,color:"#111"}}>{doc.title}</div>
                <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                  {doc.created_by} · {new Date(doc.updated_at||doc.created_at).toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                </div>
              </div>
              <button onClick={()=>openEdit(doc)} style={{background:"#f3f4f6",border:"none",borderRadius:5,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:600}}>Öffnen</button>
              <button onClick={()=>del(doc.id)} disabled={deleting===doc.id}
                style={{background:"#fee2e2",border:"none",borderRadius:5,padding:"4px 8px",fontSize:11,cursor:"pointer",color:"#dc2626"}}>
                {deleting===doc.id?"...":"🗑"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ─ EDIT / NEW ─ */
  const isNew = selDoc==="new";
  return (
    <div style={{padding:20,maxWidth:780,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <button onClick={()=>setSelDoc(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#6b7280"}}>←</button>
        <input value={title} onChange={e=>setTitle(e.target.value)}
          style={{flex:1,border:"1px solid #d1d5db",borderRadius:6,padding:"6px 10px",fontSize:14,fontWeight:600,minWidth:120}}
          placeholder="Titel..."/>
        <button onClick={save} disabled={saving}
          style={{background:"#9900cc",color:"#fff",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:saving?0.6:1}}>
          {saving?"Speichern...":"💾 Speichern"}
        </button>
        {(selType==="kasse_tag"||selType==="kasse_monat") && (
          <button onClick={async () => {
            setKasseAILoading(true); setKasseAI(null);
            try {
              const r = await fetch(`${API_URL}/staff/ai/kasse-analyse`, {
                method:"POST", headers:{"Content-Type":"application/json"},
                body: JSON.stringify({ protokoll_data: docData, type: selType, date: docData.datum || new Date().toISOString().slice(0,10) })
              });
              const d = await r.json();
              setKasseAI(d);
            } catch(e) { setKasseAI({ analysis: "Fehler: " + e.message, ok: false }); }
            setKasseAILoading(false);
          }} disabled={kasseAILoading}
            style={{background:kasseAILoading?"#f3f4f6":"#faf5ff",border:"1px solid #d8b4fe",borderRadius:6,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",color:"#7c3aed"}}>
            {kasseAILoading ? "⏳..." : "🤖 AI Analyse"}
          </button>
        )}
      </div>
      {kasseAI && (
        <div style={{background: kasseAI.ok?"#f0fdf4":"#fef3c7", border:`1px solid ${kasseAI.ok?"#bbf7d0":"#fcd34d"}`, borderRadius:10, padding:"12px 16px", marginBottom:16}}>
          <div style={{fontWeight:700, fontSize:12, color: kasseAI.ok?"#059669":"#92400e", marginBottom:6}}>🤖 AI Kassenanalyse</div>
          <div style={{fontSize:12, color:"#374151", whiteSpace:"pre-wrap", lineHeight:1.6}}>{kasseAI.analysis}</div>
        </div>
      )}
      {selType==="koop_event"  && renderKoopEvent()}
      {selType==="waesche"     && renderWaesche()}
      {selType==="kasse_tag"   && renderKasse(false)}
      {selType==="kasse_monat" && renderKasse(true)}
      {selType==="inventar"    && renderInventar()}
    </div>
  );
}

/* ── AI Knowledge Tab ── */
function AIKnowledgeTab({ user }) {
  const isAdmin = user && (user.isAdmin || user.adminAccess || user.type === "admin");
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]     = useState({ title:"", content:"", category:"" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const CATEGORIES = ["Allgemein","Abläufe","Payroll","Regeln","Buchungen","Trainer","Studio"];

  const load = () => {
    fetch(`${API_URL}/staff/ai/knowledge`)
      .then(r=>r.json()).then(d=>{ setItems(d.items||[]); setLoading(false); }).catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    if (editing) {
      await fetch(`${API_URL}/staff/ai/knowledge/${editing}`, {
        method:"PUT", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({...form, created_by: user.name })
      }).catch(()=>{});
    } else {
      await fetch(`${API_URL}/staff/ai/knowledge`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({...form, created_by: user.name })
      }).catch(()=>{});
    }
    setForm({ title:"", content:"", category:"" });
    setEditing(null);
    setSaving(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await fetch(`${API_URL}/staff/ai/knowledge/${id}`, { method:"DELETE" }).catch(()=>{});
    load();
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ title: item.title, content: item.content, category: item.category||"" });
  };

  const isMobile = useMobile();

  return (
    <div style={{padding: isMobile?12:24, maxWidth:800, margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <div style={{width:40,height:40,borderRadius:"50%",background:"linear-gradient(135deg,#9900cc,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>🤖</div>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:"#111827"}}>BURN AI — Wissensdatenbank</div>
          <div style={{fontSize:12,color:"#6b7280"}}>Diese Einträge kennt die KI immer. Regeln, SOPs, Infos pflegen.</div>
        </div>
      </div>

      {isAdmin && (
        <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:12,padding:16,marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:12}}>{editing ? "✏️ Eintrag bearbeiten" : "➕ Neuer Wissenseintrag"}</div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Titel *"
              style={{flex:2,minWidth:160,padding:"8px 12px",border:"1px solid #ddd6fe",borderRadius:8,fontSize:13,outline:"none"}}/>
            <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}
              style={{flex:1,minWidth:120,padding:"8px 12px",border:"1px solid #ddd6fe",borderRadius:8,fontSize:13,background:"#fff",outline:"none"}}>
              <option value="">Kategorie</option>
              {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <textarea value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))} placeholder="Inhalt — was soll die KI über dieses Thema wissen? *"
            rows={4} style={{width:"100%",padding:"8px 12px",border:"1px solid #ddd6fe",borderRadius:8,fontSize:13,resize:"vertical",fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={save} disabled={saving||!form.title.trim()||!form.content.trim()}
              style={{padding:"8px 20px",background:form.title.trim()&&form.content.trim()?"#9900cc":"#e5e7eb",color:form.title.trim()&&form.content.trim()?"#fff":"#9ca3af",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {saving?"Speichert…": editing ? "Aktualisieren" : "Hinzufügen"}
            </button>
            {editing && <button onClick={()=>{setEditing(null);setForm({title:"",content:"",category:""});}}
              style={{padding:"8px 16px",background:"#f3f4f6",border:"none",borderRadius:8,fontSize:13,cursor:"pointer",color:"#374151"}}>Abbrechen</button>}
          </div>
        </div>
      )}

      {loading ? <BurnLoader text="Lade Wissen…"/> : (
        items.length === 0 ? (
          <div style={{textAlign:"center",padding:"40px 0",color:"#9ca3af",fontSize:14}}>
            <div style={{fontSize:32,marginBottom:8}}>📭</div>
            Noch keine Einträge. {isAdmin?"Füge oben den ersten Eintrag hinzu!":"Admins können hier Wissen hinterlegen."}
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {items.map(item=>(
              <div key={item.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"14px 16px"}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,fontSize:14,color:"#111827"}}>{item.title}</span>
                      {item.category && <span style={{fontSize:11,background:"#f5f3ff",color:"#7c3aed",padding:"2px 8px",borderRadius:99,fontWeight:600}}>{item.category}</span>}
                    </div>
                    <div style={{fontSize:13,color:"#374151",lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{item.content}</div>
                    <div style={{fontSize:11,color:"#9ca3af",marginTop:6}}>
                      {item.created_by && `von ${item.created_by} · `}{item.created_at?.slice(0,10)}
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={()=>startEdit(item)} style={{padding:"5px 10px",background:"#f3f4f6",border:"none",borderRadius:6,fontSize:12,cursor:"pointer",color:"#374151"}}>✏️</button>
                      <button onClick={()=>del(item.id)} style={{padding:"5px 10px",background:"#fee2e2",border:"none",borderRadius:6,fontSize:12,cursor:"pointer",color:"#dc2626"}}>🗑</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function BetriebView({ user }) {
  const [tab, setTab] = useState("wiki");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await fetch(`${API_URL}/staff/betrieb`); setData(await r.json()); } catch(e){}
  }, []);
  useEffect(() => { load(); }, [load]);

  const api = useCallback(async (method, path, body) => {
    const r = await fetch(`${API_URL}/staff/betrieb${path}`, {
      method, headers: body ? {"Content-Type":"application/json"} : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    return r.json();
  }, []);

  const BTABS = [
    { id:"wiki",     label:"📚 Wiki" },
    { id:"qa",       label:"❓ Q&A" },
    { id:"inv",      label:"📦 Inventar" },
    { id:"forms",    label:"📋 Formulare" },
    { id:"check",    label:"✅ Checklisten" },
    { id:"aufgaben", label:"📝 Aufgaben" },
    { id:"unfall",   label:"🏥 Unfallbuch" },
    { id:"feedback",   label:"⭐ Feedback" },
    { id:"ki",         label:"🤖 KI Wissen" },
  ];

  const isMobile = useMobile();

  if (!data) return <BurnLoader text="Lade Betrieb..." />;

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"0 8px",display:"flex",gap:0,alignItems:"center",flexShrink:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {BTABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding: isMobile?"10px 12px":"12px 16px",border:"none",borderBottom:`2px solid ${tab===t.id?"#9900cc":"transparent"}`,background:"transparent",cursor:"pointer",fontSize: isMobile?12:13,fontWeight:tab===t.id?700:400,color:tab===t.id?"#9900cc":"#6b7280",whiteSpace:"nowrap",flexShrink:0}}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{flex:1,overflow:"auto"}}>
        {tab==="wiki"     && <WikiTab      data={data} api={api} reload={load} user={user}/>}
        {tab==="qa"       && <QATab        data={data} api={api} reload={load} user={user}/>}
        {tab==="inv"      && <InventarTab  data={data} api={api} reload={load} user={user}/>}
        {tab==="forms"    && <><FormulareTab data={data} api={api} reload={load} user={user}/><ProtokollTab data={data} api={api} reload={load} user={user}/></>}
        {tab==="check"    && <ChecklistTab data={data} api={api} reload={load} user={user}/>}
        {tab==="aufgaben" && <AufgabenTab  data={data} api={api} reload={load} user={user}/>}
        {tab==="unfall"   && <UnfallbuchTab data={data} api={api} reload={load} user={user}/>}
        {tab==="feedback"   && <FeedbackTab   data={data} api={api} reload={load} user={user}/>}
        {tab==="ki"         && <AIKnowledgeTab user={user}/>}
      </div>
    </div>
  );
}

/* ── Wiki ── */
function WikiTab({ data, api, reload, user }) {
  const isMobile = useMobile();
  const [selCat, setSelCat] = useState(null);
  const [selArt, setSelArt] = useState(null);
  const [editArt, setEditArt] = useState(null);
  const [newCat, setNewCat] = useState(false);
  const [catForm, setCatForm] = useState({name:"",icon:"📄"});
  const [artForm, setArtForm] = useState({title:"",content:"",links:[""]});
  const isAdmin = user.isAdmin || user.adminAccess;
  const cats = data?.wiki?.categories || [];
  const articles = (data?.wiki?.articles||[]).filter(a=>a.category_id===selCat);

  const saveCategory = async () => {
    await api("POST","/wiki/categories",catForm);
    setCatForm({name:"",icon:"📄"}); setNewCat(false); reload();
  };
  const deleteCategory = async id => {
    if(!confirm("Kategorie und alle Artikel löschen?")) return;
    await api("DELETE",`/wiki/categories/${id}`);
    if(selCat===id) setSelCat(null); reload();
  };
  const saveArticle = async () => {
    const links = artForm.links.filter(l=>l.trim());
    if(editArt==="new") await api("POST","/wiki/articles",{...artForm,links,category_id:selCat,author:user.name});
    else await api("PUT",`/wiki/articles/${editArt.id}`,{title:artForm.title,content:artForm.content,links});
    setEditArt(null); setSelArt(null); reload();
  };
  const printArticle = a => {
    const w=window.open("","_blank");
    w.document.write(`<html><body style="font-family:sans-serif;max-width:700px;margin:40px auto"><h1>${a.title}</h1><pre style="white-space:pre-wrap;font-family:inherit;line-height:1.7">${a.content}</pre>${a.links?.filter(l=>l).length?`<h3>Links</h3><ul>${a.links.filter(l=>l).map(l=>`<li><a href="${l}">${l}</a></li>`).join("")}</ul>`:""}</body></html>`);
    w.print();
  };
  const exportWiki = async () => {
    const r = await fetch(`${API_URL}/staff/betrieb/export/ai`);
    const {text} = await r.json();
    const blob = new Blob([text],{type:"text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="burn-wissensdatenbank.txt"; a.click();
  };

  if(editArt!==null) return (
    <div style={{padding:24,maxWidth:800,margin:"0 auto"}}>
      <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
        <button onClick={()=>setEditArt(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>{editArt==="new"?"Neuer Artikel":"Artikel bearbeiten"}</h2>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <input placeholder="Titel" value={artForm.title} onChange={e=>setArtForm(f=>({...f,title:e.target.value}))}
          style={{padding:"10px 14px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:15,fontWeight:600,outline:"none"}}/>
        <textarea placeholder="Inhalt — Schritte, Regeln, Vorgehensweise..." value={artForm.content}
          onChange={e=>setArtForm(f=>({...f,content:e.target.value}))}
          style={{padding:"12px 14px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,resize:"vertical",minHeight:320,outline:"none",fontFamily:"inherit",lineHeight:1.7}}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:6}}>🔗 Links (optional)</div>
          {artForm.links.map((l,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
              <input value={l} onChange={e=>setArtForm(f=>({...f,links:f.links.map((x,j)=>j===i?e.target.value:x)}))}
                placeholder="https://..." style={{flex:1,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none"}}/>
              <button onClick={()=>setArtForm(f=>({...f,links:f.links.filter((_,j)=>j!==i)}))}
                style={{padding:"8px",border:"none",background:"#fee2e2",color:"#dc2626",borderRadius:6,cursor:"pointer"}}>✕</button>
            </div>
          ))}
          <button onClick={()=>setArtForm(f=>({...f,links:[...f.links,""]}))}
            style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",padding:0}}>+ Link hinzufügen</button>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={saveArticle} style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",cursor:"pointer",fontWeight:600,fontSize:14}}>💾 Speichern</button>
          <button onClick={()=>setEditArt(null)} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"10px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    </div>
  );

  // Mobile: show categories OR content, not both
  if (isMobile && selCat && selArt === null) {
    // Article list — full screen
    const articles = (data?.wiki?.articles||[]).filter(a=>a.category_id===selCat);
    const cat = (data?.wiki?.categories||[]).find(c=>c.id===selCat);
    return (
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid #e5e7eb",background:"#fff",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <button onClick={()=>setSelCat(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#9900cc",padding:"0 4px"}}>‹</button>
          <div style={{fontWeight:700,fontSize:15,flex:1}}>{cat?.icon} {cat?.name}</div>
          {isAdmin&&<button onClick={()=>{setArtForm({title:"",content:"",links:[""]});setEditArt("new");}} style={{background:"#9900cc",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>+ Neu</button>}
        </div>
        <div style={{flex:1,overflowY:"auto",padding:12}}>
          {articles.length===0?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Noch keine Artikel.</div>:
          articles.map(a=>(
            <div key={a.id} onClick={()=>setSelArt(a)} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:"14px 16px",cursor:"pointer",marginBottom:10}}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>{a.title}</div>
              <div style={{fontSize:12,color:"#6b7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.content.substring(0,100)}...</div>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:4}}>{a.author&&`von ${a.author} · `}{new Date(a.updated_at||a.created_at).toLocaleDateString("de-DE")}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",height:"100%",overflow:"hidden",flexDirection: isMobile?"column":"row"}}>
      {/* Sidebar / Mobile Category List */}
      <div style={{
        width: isMobile ? "100%" : 220,
        borderRight: isMobile ? "none" : "1px solid #e5e7eb",
        borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
        background:"#fff", display: isMobile && selCat ? "none" : "flex",
        flexDirection:"column", flexShrink:0,
        maxHeight: isMobile ? "none" : "100%"
      }}>
        <div style={{padding:"16px 16px 8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase",letterSpacing:0.5}}>Bereiche</span>
          <div style={{display:"flex",gap:6}}>
            <button onClick={exportWiki} title="Wiki exportieren (TXT/KI)" style={{fontSize:14,color:"#6b7280",background:"none",border:"none",cursor:"pointer"}}>📥</button>
            {isAdmin&&<button onClick={()=>setNewCat(true)} style={{fontSize:20,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",lineHeight:1}}>+</button>}
          </div>
        </div>
        {newCat&&(
          <div style={{padding:"8px 12px",borderBottom:"1px solid #f3f4f6"}}>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <input value={catForm.icon} onChange={e=>setCatForm(f=>({...f,icon:e.target.value}))} style={{width:36,textAlign:"center",border:"1px solid #e5e7eb",borderRadius:6,fontSize:16,padding:4}}/>
              <input value={catForm.name} onChange={e=>setCatForm(f=>({...f,name:e.target.value}))} placeholder="Bereichsname" style={{flex:1,border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 8px",fontSize:12,outline:"none"}} onKeyDown={e=>e.key==="Enter"&&saveCategory()}/>
            </div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={saveCategory} style={{flex:1,background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,padding:6,cursor:"pointer",fontSize:12}}>✓</button>
              <button onClick={()=>setNewCat(false)} style={{flex:1,background:"#f3f4f6",border:"none",borderRadius:6,padding:6,cursor:"pointer",fontSize:12}}>✕</button>
            </div>
          </div>
        )}
        <div style={{overflowY:"auto",flex:1,padding:"4px 8px"}}>
          {cats.map(cat=>{
            const cnt=(data?.wiki?.articles||[]).filter(a=>a.category_id===cat.id).length;
            return(
              <div key={cat.id} onClick={()=>{setSelCat(cat.id);setSelArt(null);}}
                style={{display:"flex",alignItems:"center",gap:8,padding: isMobile?"14px 16px":"9px 10px",borderRadius:8,cursor:"pointer",background:selCat===cat.id?"#f5f3ff":"transparent",marginBottom:2}}>
                <span style={{fontSize:16}}>{cat.icon}</span>
                <span style={{flex:1,fontSize:13,fontWeight:selCat===cat.id?600:400,color:selCat===cat.id?"#7c3aed":"#374151"}}>{cat.name}</span>
                {cnt>0&&<span style={{fontSize:10,color:"#9ca3af"}}>{cnt}</span>}
                {isAdmin&&<button onClick={e=>{e.stopPropagation();deleteCategory(cat.id);}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",opacity:.6}}>✕</button>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {!selCat?(
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:"#9ca3af"}}>
          <div style={{fontSize:32}}>📚</div>
          <div>Bereich wählen</div>
        </div>
      ):!selArt?(
        <div style={{flex:1,padding:24,overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h2 style={{margin:0,fontSize:18,color:"#111827"}}>{cats.find(c=>c.id===selCat)?.icon} {cats.find(c=>c.id===selCat)?.name}</h2>
            {isAdmin&&<button onClick={()=>{setArtForm({title:"",content:"",links:[""]});setEditArt("new");}}
              style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>
              + Neuer Artikel
            </button>}
          </div>
          {articles.length===0?(
            <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
              Noch keine Artikel.{isAdmin&&<div style={{marginTop:8}}><button onClick={()=>{setArtForm({title:"",content:"",links:[""]});setEditArt("new");}} style={{color:"#7c3aed",background:"none",border:"none",cursor:"pointer"}}>Ersten Artikel erstellen →</button></div>}
            </div>
          ):articles.map(a=>(
            <div key={a.id} onClick={()=>setSelArt(a)}
              style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"14px 16px",cursor:"pointer",marginBottom:8,transition:"box-shadow .15s"}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 8px #0000000f"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>{a.title}</div>
              <div style={{fontSize:11,color:"#9ca3af",marginBottom:6}}>
                {a.author&&`von ${a.author} · `}{new Date(a.updated_at||a.created_at).toLocaleDateString("de-DE")}
                {a.links?.filter(l=>l).length>0&&` · ${a.links.filter(l=>l).length} Link(s)`}
              </div>
              <div style={{fontSize:12,color:"#6b7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.content.substring(0,130)}{a.content.length>130?"...":""}</div>
            </div>
          ))}
        </div>
      ):(
        <div style={{flex:1,padding:24,overflowY:"auto"}}>
          <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
            <button onClick={()=>setSelArt(null)} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:13}}>← Zurück</button>
            <button onClick={()=>printArticle(selArt)} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:13}}>🖨️ Drucken / PDF</button>
            {isAdmin&&<>
              <button onClick={()=>{setArtForm({title:selArt.title,content:selArt.content,links:selArt.links?.length?selArt.links:[""]});setEditArt(selArt);}}
                style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:13}}>✏️ Bearbeiten</button>
              <button onClick={async()=>{if(!confirm("Artikel löschen?"))return;await api("DELETE",`/wiki/articles/${selArt.id}`);setSelArt(null);reload();}}
                style={{background:"#fee2e2",color:"#dc2626",border:"none",borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:13}}>🗑️ Löschen</button>
            </>}
          </div>
          <h1 style={{fontSize:22,fontWeight:700,color:"#111827",marginBottom:8}}>{selArt.title}</h1>
          <div style={{fontSize:12,color:"#9ca3af",marginBottom:20}}>
            {selArt.author&&`von ${selArt.author} · `}
            {selArt.updated_at?`Aktualisiert: ${new Date(selArt.updated_at).toLocaleDateString("de-DE")}`:new Date(selArt.created_at).toLocaleDateString("de-DE")}
          </div>
          <div style={{fontSize:14,lineHeight:1.8,color:"#374151",whiteSpace:"pre-wrap",background:"#fff",padding:"20px 24px",borderRadius:10,border:"1px solid #e5e7eb"}}>{selArt.content}</div>
          {selArt.links?.filter(l=>l).length>0&&(
            <div style={{marginTop:16,background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"16px 20px"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:8}}>🔗 Links</div>
              {selArt.links.filter(l=>l).map((l,i)=><div key={i}><a href={l} target="_blank" rel="noopener noreferrer" style={{color:"#7c3aed",fontSize:13}}>{l}</a></div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Q&A ── */
function QATab({ data, api, reload, user }) {
  const [filter, setFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("");
  const [newQ, setNewQ] = useState(false);
  const [qForm, setQForm] = useState({question:"",category:""});
  const [answerFor, setAnswerFor] = useState(null);
  const [ansText, setAnsText] = useState("");
  const isAdmin = user.isAdmin || user.adminAccess;
  const cats = data?.wiki?.categories || [];
  const questions = (data?.qa||[])
    .filter(q=>filter==="all"||(filter==="solved"?q.solved:!q.solved))
    .filter(q=>!catFilter||q.category===catFilter)
    .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));

  const submitQ = async()=>{
    if(!qForm.question.trim()) return;
    await api("POST","/qa",{...qForm,asker:user.name});
    setQForm({question:"",category:""}); setNewQ(false); reload();
  };
  const submitAnswer = async id=>{
    if(!ansText.trim()) return;
    await api("POST",`/qa/${id}/answer`,{text:ansText,author:user.name});
    setAnsText(""); setAnswerFor(null); reload();
  };
  const exportQA = ()=>{
    const solved=(data?.qa||[]).filter(q=>q.solved&&q.answers?.length);
    const text=solved.map(q=>`F: ${q.question}\n${q.answers.map(a=>`A (${a.author}): ${a.text}`).join("\n")}`).join("\n\n---\n\n");
    const blob=new Blob([text],{type:"text/plain"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download="burn-qa-loesungen.txt"; a.click();
  };

  return (
    <div style={{padding:24,maxWidth:800,margin:"0 auto"}}>
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
        <button onClick={()=>setNewQ(true)} style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",cursor:"pointer",fontWeight:600,fontSize:14}}>❓ Frage stellen</button>
        <div style={{display:"flex",gap:4}}>
          {[["all","Alle"],["open","Offen"],["solved","Gelöst"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)} style={{padding:"8px 14px",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:filter===v?700:400,background:filter===v?"#f5f3ff":"#f9fafb",color:filter===v?"#7c3aed":"#6b7280"}}>{l}</button>
          ))}
        </div>
        <select value={catFilter} onChange={e=>setCatFilter(e.target.value)} style={{padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,outline:"none"}}>
          <option value="">Alle Bereiche</option>
          {cats.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        <button onClick={exportQA} style={{marginLeft:"auto",background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:12}}>📥 Lösungen exportieren</button>
      </div>

      {newQ&&(
        <div style={{background:"#fff",border:"1px solid #7c3aed30",borderRadius:10,padding:16,marginBottom:16}}>
          <textarea placeholder="Deine Frage..." value={qForm.question} onChange={e=>setQForm(f=>({...f,question:e.target.value}))}
            style={{width:"100%",border:"1px solid #e5e7eb",borderRadius:8,padding:"10px 12px",fontSize:13,resize:"vertical",minHeight:80,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <select value={qForm.category} onChange={e=>setQForm(f=>({...f,category:e.target.value}))}
              style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none"}}>
              <option value="">Bereich wählen (optional)</option>
              {cats.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
            <button onClick={submitQ} style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>Absenden</button>
            <button onClick={()=>setNewQ(false)} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:13}}>Abbrechen</button>
          </div>
        </div>
      )}

      {questions.length===0?(
        <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Keine Fragen gefunden.</div>
      ):questions.map(q=>{
        const cat=cats.find(c=>c.id===q.category);
        return(
          <div key={q.id} style={{background:"#fff",border:`1.5px solid ${q.solved?"#d1fae5":"#e5e7eb"}`,borderRadius:10,padding:16,marginBottom:12}}>
            <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>{q.question}</div>
                <div style={{fontSize:11,color:"#9ca3af"}}>von {q.asker} · {new Date(q.created_at).toLocaleDateString("de-DE")}{cat&&` · ${cat.icon} ${cat.name}`}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                {q.solved?<span style={{fontSize:11,background:"#d1fae5",color:"#059669",padding:"3px 8px",borderRadius:99,fontWeight:600}}>✅ Gelöst</span>
                  :<span style={{fontSize:11,background:"#fef3c7",color:"#d97706",padding:"3px 8px",borderRadius:99}}>⏳ Offen</span>}
                {(isAdmin||q.asker===user.name)&&<button onClick={async()=>{await api("PATCH",`/qa/${q.id}/solve`);reload();}}
                  style={{fontSize:11,padding:"3px 8px",borderRadius:99,border:"1px solid #e5e7eb",background:"none",cursor:"pointer",color:"#6b7280"}}>{q.solved?"Reaktivieren":"Als gelöst"}</button>}
                {(isAdmin||q.asker===user.name)&&<button onClick={async()=>{if(!confirm("Löschen?"))return;await api("DELETE",`/qa/${q.id}`);reload();}}
                  style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer"}}>✕</button>}
              </div>
            </div>
            {q.answers?.map(a=>(
              <div key={a.id} style={{background:"#f9fafb",borderRadius:8,padding:"10px 12px",marginBottom:6,marginLeft:16}}>
                <div style={{fontSize:13,color:"#374151",lineHeight:1.6}}>{a.text}</div>
                <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>— {a.author} · {new Date(a.created_at).toLocaleDateString("de-DE")}</div>
              </div>
            ))}
            {answerFor===q.id?(
              <div style={{marginLeft:16,marginTop:8}}>
                <textarea value={ansText} onChange={e=>setAnsText(e.target.value)} placeholder="Antwort..."
                  style={{width:"100%",border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical",minHeight:60,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
                <div style={{display:"flex",gap:8,marginTop:6}}>
                  <button onClick={()=>submitAnswer(q.id)} style={{background:"#059669",color:"#fff",border:"none",borderRadius:6,padding:"7px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>Antworten</button>
                  <button onClick={()=>{setAnswerFor(null);setAnsText("");}} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"7px 12px",cursor:"pointer",fontSize:13}}>Abbrechen</button>
                </div>
              </div>
            ):<button onClick={()=>setAnswerFor(q.id)} style={{marginLeft:16,marginTop:6,fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer"}}>💬 Antworten</button>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Inventar ── */
function InventarTab({ data, api, reload, user }) {
  const [catFilter, setCatFilter] = useState("Alle");
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({name:"",category:"Ausrüstung",quantity:0,min_quantity:0,unit:"Stück",supplier:"",notes:"",last_check:""});
  const isAdmin = user.isAdmin || user.adminAccess;
  const isMobile = useMobile();
  const items = data?.inventory || [];
  const INV_CATS = ["Alle","Ausrüstung","Reinigung","Büro","Getränke","Sonstiges"];
  const filtered = catFilter==="Alle"?items:items.filter(i=>i.category===catFilter);
  const lowStock = items.filter(i=>i.min_quantity>0&&i.quantity<=i.min_quantity);

  const saveItem = async()=>{
    if(!form.name.trim()) return;
    if(editItem==="new") await api("POST","/inventory",form);
    else await api("PUT",`/inventory/${editItem.id}`,form);
    setEditItem(null); reload();
  };
  const exportCSV = ()=>{
    const rows=[["Name","Kategorie","Bestand","Mindestbestand","Einheit","Lieferant","Notizen","Letzter Check"]];
    items.forEach(i=>rows.push([i.name,i.category,i.quantity,i.min_quantity,i.unit,i.supplier||"",i.notes||"",i.last_check||""]));
    const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download="burn-inventar.csv"; a.click();
  };
  const printInventar = ()=>{
    const w=window.open("","_blank");
    w.document.write(`<html><body style="font-family:sans-serif;padding:30px"><h1>BURN Inventar — ${new Date().toLocaleDateString("de-DE")}</h1><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%"><tr><th>Name</th><th>Kategorie</th><th>Bestand</th><th>Mindest</th><th>Einheit</th><th>Lieferant</th><th>Notizen</th></tr>${items.map(i=>`<tr style="${i.min_quantity>0&&i.quantity<=i.min_quantity?"background:#fee2e2":""}"><td>${i.name}</td><td>${i.category}</td><td><b>${i.quantity}</b></td><td>${i.min_quantity||"—"}</td><td>${i.unit}</td><td>${i.supplier||"—"}</td><td>${i.notes||"—"}</td></tr>`).join("")}</table></body></html>`);
    w.print();
  };

  if(editItem!==null) return (
    <div style={{padding:isMobile?16:24,maxWidth:580,margin:"0 auto"}}>
      <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
        <button onClick={()=>setEditItem(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>{editItem==="new"?"Neuer Inventarartikel":"Artikel bearbeiten"}</h2>
      </div>
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:isMobile?16:24,display:"flex",flexDirection:"column",gap:12}}>
        {[["name","Bezeichnung","text"],["category","Kategorie","text"],["quantity","Bestand","number"],["min_quantity","Mindestbestand (Alert wenn unter)","number"],["unit","Einheit (Stück, Paar, Liter...)","text"],["supplier","Lieferant (optional)","text"],["last_check","Letzter Check (Datum)","date"]].map(([k,label,type])=>(
          <div key={k}>
            <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
            {k==="category"?(
              <select value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:14,outline:"none"}}>
                {["Ausrüstung","Reinigung","Büro","Getränke","Sonstiges"].map(c=><option key={c}>{c}</option>)}
              </select>
            ):<input type={type} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:type==="number"?Number(e.target.value):e.target.value}))}
              style={{width:"100%",padding:"10px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:14,outline:"none",boxSizing:"border-box"}}/>}
          </div>
        ))}
        <div>
          <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>Notizen</label>
          <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
            style={{width:"100%",border:"1px solid #e5e7eb",borderRadius:6,padding:"10px 12px",fontSize:14,resize:"vertical",outline:"none",fontFamily:"inherit",minHeight:70,boxSizing:"border-box"}}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={saveItem} style={{flex:1,background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"12px 24px",cursor:"pointer",fontWeight:600,fontSize:14}}>💾 Speichern</button>
          <button onClick={()=>setEditItem(null)} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"12px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{padding:isMobile?"12px 12px 80px":"24px"}}>
      {lowStock.length>0&&(
        <div style={{background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",gap:8,alignItems:"flex-start",flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:700,color:"#92400e",whiteSpace:"nowrap"}}>⚠️ Nachbestellen:</span>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {lowStock.map(i=><span key={i.id} style={{fontSize:12,color:"#78350f",background:"#fde68a",padding:"2px 8px",borderRadius:4}}>{i.name} ({i.quantity} {i.unit})</span>)}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        {isAdmin&&<button onClick={()=>{setForm({name:"",category:"Ausrüstung",quantity:0,min_quantity:0,unit:"Stück",supplier:"",notes:""});setEditItem("new");}}
          style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"9px 16px",cursor:"pointer",fontWeight:600,fontSize:13,whiteSpace:"nowrap"}}>+ Hinzufügen</button>}
        {!isMobile&&<div style={{display:"flex",gap:8,marginLeft:"auto"}}>
          <button onClick={exportCSV} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:12}}>📊 CSV</button>
          <button onClick={printInventar} style={{background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:12}}>🖨️ Drucken</button>
        </div>}
      </div>

      {/* Kategorie-Filter — horizontal scroll auf Mobile */}
      <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:4,WebkitOverflowScrolling:"touch"}}>
        {INV_CATS.map(c=><button key={c} onClick={()=>setCatFilter(c)} style={{padding:"7px 12px",border:"none",borderRadius:20,cursor:"pointer",fontSize:12,whiteSpace:"nowrap",flexShrink:0,background:catFilter===c?"#7c3aed":"#f3f4f6",color:catFilter===c?"#fff":"#6b7280",fontWeight:catFilter===c?600:400}}>{c}</button>)}
      </div>

      {/* Mobile: Karten-Layout */}
      {isMobile ? (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map(item=>{
            const low=item.min_quantity>0&&item.quantity<=item.min_quantity;
            return(
              <div key={item.id} style={{background:"#fff",border:`1px solid ${low?"#f59e0b":"#e5e7eb"}`,borderRadius:12,padding:"14px 16px",boxShadow:low?"0 0 0 1px #fde68a inset":undefined}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:15,color:"#111827",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {low&&<span>⚠️</span>}
                      <span style={{wordBreak:"break-word"}}>{item.name}</span>
                    </div>
                    <div style={{marginTop:4,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                      <span style={{fontSize:11,background:"#f3f4f6",color:"#6b7280",padding:"2px 8px",borderRadius:12,fontWeight:500}}>{item.category}</span>
                      {item.supplier&&<span style={{fontSize:11,color:"#9ca3af"}}>📦 {item.supplier}</span>}
                    </div>
                    {item.notes&&<div style={{fontSize:12,color:"#9ca3af",marginTop:4}}>{item.notes}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:22,fontWeight:800,color:low?"#dc2626":"#059669",lineHeight:1}}>{item.quantity}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{item.unit}</div>
                    {item.min_quantity>0&&<div style={{fontSize:10,color:low?"#dc2626":"#9ca3af",marginTop:2}}>Min: {item.min_quantity}</div>}
                  </div>
                </div>
                {isAdmin&&<div style={{display:"flex",gap:8,marginTop:12,paddingTop:10,borderTop:"1px solid #f3f4f6"}}>
                  <button onClick={()=>{setForm({name:item.name,category:item.category,quantity:item.quantity,min_quantity:item.min_quantity,unit:item.unit,supplier:item.supplier||"",notes:item.notes||"",last_check:item.last_check||""});setEditItem(item);}}
                    style={{flex:1,background:"#f5f3ff",color:"#7c3aed",border:"none",borderRadius:8,padding:"9px",cursor:"pointer",fontSize:13,fontWeight:600}}>✏️ Bearbeiten</button>
                  <button onClick={async()=>{if(!confirm("Löschen?"))return;await api("DELETE",`/inventory/${item.id}`);reload();}}
                    style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:8,padding:"9px 14px",cursor:"pointer",fontSize:13}}>🗑️</button>
                </div>}
              </div>
            );
          })}
          {filtered.length===0&&<div style={{textAlign:"center",padding:40,color:"#9ca3af",fontSize:14}}>Kein Inventar vorhanden.</div>}
        </div>
      ) : (
        /* Desktop: Tabellen-Layout */
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
              {["Artikel","Kategorie","Bestand","Mindest","Einheit","Lieferant",""].map(h=><th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:"#6b7280",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map(item=>{
                const low=item.min_quantity>0&&item.quantity<=item.min_quantity;
                return(
                  <tr key={item.id} style={{borderBottom:"1px solid #f3f4f6",background:low?"#fff7ed":"#fff"}}>
                    <td style={{padding:"12px 14px",fontSize:13,fontWeight:600,color:"#111827"}}>
                      {low&&<span title="Mindestbestand!" style={{marginRight:6}}>⚠️</span>}{item.name}
                      {item.notes&&<div style={{fontSize:11,color:"#9ca3af",fontWeight:400}}>{item.notes}</div>}
                    </td>
                    <td style={{padding:"12px 14px",fontSize:12,color:"#6b7280"}}>{item.category}</td>
                    <td style={{padding:"12px 14px",fontSize:15,fontWeight:700,color:low?"#dc2626":"#059669"}}>{item.quantity}</td>
                    <td style={{padding:"12px 14px",fontSize:12,color:"#9ca3af"}}>{item.min_quantity||"—"}</td>
                    <td style={{padding:"12px 14px",fontSize:12,color:"#6b7280"}}>{item.unit}</td>
                    <td style={{padding:"12px 14px",fontSize:12,color:"#6b7280"}}>{item.supplier||"—"}</td>
                    <td style={{padding:"12px 14px"}}>
                      {isAdmin&&<div style={{display:"flex",gap:6}}>
                        <button onClick={()=>{setForm({name:item.name,category:item.category,quantity:item.quantity,min_quantity:item.min_quantity,unit:item.unit,supplier:item.supplier||"",notes:item.notes||"",last_check:item.last_check||""});setEditItem(item);}}
                          style={{fontSize:13,color:"#7c3aed",background:"none",border:"none",cursor:"pointer"}}>✏️</button>
                        <button onClick={async()=>{if(!confirm("Löschen?"))return;await api("DELETE",`/inventory/${item.id}`);reload();}}
                          style={{fontSize:13,color:"#dc2626",background:"none",border:"none",cursor:"pointer"}}>🗑️</button>
                      </div>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0&&<tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Kein Inventar vorhanden.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Formulare ── */
function FormulareTab({ data, api, reload, user }) {
  const [view, setView] = useState("list");
  const [selTpl, setSelTpl] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [photoUploading, setPhotoUploading] = useState({});
  const [expandedSub, setExpandedSub] = useState(null);
  const [tplEdit, setTplEdit] = useState(null); // null | "new" | template obj
  const [tplFields, setTplFields] = useState([]);
  const isAdmin = user.isAdmin || user.adminAccess;
  const templates = data?.forms?.templates || [];
  const submissions = data?.forms?.submissions || [];

  /* Upload photo for a form field — reuses the uploads infrastructure */
  const uploadPhoto = async (fieldId, file) => {
    setPhotoUploading(p=>({...p,[fieldId]:true}));
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const r = await fetch(`${API_URL}/staff/messages/file`, {method:"POST", body:fd});
      const d = await r.json();
      const url = d.file_url || d.url || "";
      setFormData(f=>({...f,[fieldId]:url}));
    } catch(e) { alert("Upload fehlgeschlagen: " + e.message); }
    setPhotoUploading(p=>({...p,[fieldId]:false}));
  };

  const submitForm = async()=>{
    if(!selTpl) return;
    const missing = selTpl.fields.filter(f=>f.required&&!formData[f.id]?.toString().trim());
    if(missing.length){alert(`Pflichtfeld(er) fehlen: ${missing.map(f=>f.label).join(", ")}`);return;}
    setSubmitting(true);
    await api("POST","/forms/submit",{template_id:selTpl.id,data:formData,submitted_by:user.name});
    setSubmitting(false); setView("list"); setFormData({}); reload();
    alert("✅ Formular eingereicht!");
  };

  /* Template editor helpers */
  const openNewTpl = () => {
    setTplEdit({name:"",icon:"📋",description:"",fields:[]});
    setTplFields([{id:"f1",label:"",type:"text",required:false,options:""}]);
    setView("editTpl");
  };
  const openEditTpl = (tpl) => {
    setTplEdit({...tpl});
    setTplFields((tpl.fields||[]).map(f=>({...f,options:Array.isArray(f.options)?f.options.join("\n"):f.options||""})));
    setView("editTpl");
  };
  const saveTpl = async () => {
    if(!tplEdit.name.trim()) return alert("Name fehlt");
    const fields = tplFields.filter(f=>f.label.trim()).map(f=>({
      ...f,
      options: f.type==="select" ? f.options.split("\n").map(s=>s.trim()).filter(Boolean) : undefined
    }));
    if(tplEdit.id) await api("PUT",`/forms/templates/${tplEdit.id}`,{...tplEdit,fields});
    else await api("POST","/forms/templates",{...tplEdit,fields});
    setView("list"); reload();
  };
  const printSub = (sub,tpl)=>{
    const w=window.open("","_blank");
    w.document.write(`<html><body style="font-family:sans-serif;padding:30px;max-width:700px"><h1>${tpl?.icon||""} ${tpl?.name||""}</h1><p style="color:#666">Eingereicht: ${sub.submitted_by} · ${new Date(sub.submitted_at).toLocaleString("de-DE")} · Status: ${sub.status}</p><hr/>${(tpl?.fields||[]).map(f=>`<p><strong>${f.label}:</strong><br/>${sub.data[f.id]||"—"}</p>`).join("")}</body></html>`);
    w.print();
  };
  const exportSubmCSV = ()=>{
    if(!submissions.length) return;
    const allKeys=[...new Set(submissions.flatMap(s=>Object.keys(s.data||{})))];
    const hdr=["Formular","Eingereicht von","Datum","Status",...allKeys];
    const rows=submissions.map(s=>{
      const tpl=templates.find(t=>t.id===s.template_id);
      return [tpl?.name||s.template_id,s.submitted_by,new Date(s.submitted_at).toLocaleDateString("de-DE"),s.status,...allKeys.map(k=>s.data[k]||"")];
    });
    const csv=[hdr,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download="burn-formulare.csv"; a.click();
  };

  if(view==="fill"&&selTpl) return (
    <div style={{padding:24,maxWidth:700,margin:"0 auto"}}>
      <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"flex-start"}}>
        <button onClick={()=>{setView("list");setFormData({});}} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280",marginTop:2}}>←</button>
        <div><h2 style={{margin:0,fontSize:18,color:"#111827"}}>{selTpl.icon} {selTpl.name}</h2>
          <p style={{margin:"4px 0 0",fontSize:12,color:"#9ca3af"}}>{selTpl.description}</p></div>
      </div>
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:24,display:"flex",flexDirection:"column",gap:16}}>
        {selTpl.fields.map(field=>(
          <div key={field.id}>
            <label style={{fontSize:13,fontWeight:600,color:"#374151",display:"block",marginBottom:6}}>
              {field.label}{field.required&&<span style={{color:"#dc2626"}}> *</span>}
            </label>
            {field.type==="textarea" ? (
              <textarea value={formData[field.id]||""} onChange={e=>setFormData(f=>({...f,[field.id]:e.target.value}))}
                style={{width:"100%",border:"1px solid #e5e7eb",borderRadius:6,padding:"8px 12px",fontSize:13,resize:"vertical",minHeight:80,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
            ) : field.type==="select" ? (
              <select value={formData[field.id]||""} onChange={e=>setFormData(f=>({...f,[field.id]:e.target.value}))}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none"}}>
                <option value="">Bitte wählen...</option>
                {field.options?.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            ) : (field.type==="photo" || field.type==="file") ? (
              <div>
                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"10px 14px",border:"2px dashed #d1d5db",borderRadius:8,background:"#fafafa",transition:"border-color .2s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#7c3aed"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#d1d5db"}>
                  <input type="file" accept={field.type==="photo"?"image/*":"*/*"} style={{display:"none"}}
                    onChange={e=>{ if(e.target.files[0]) uploadPhoto(field.id, e.target.files[0]); }}/>
                  <span style={{fontSize:20}}>{field.type==="photo"?"📷":"📎"}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>
                      {photoUploading[field.id] ? "Wird hochgeladen..." : formData[field.id] ? "✅ Foto hochgeladen" : field.type==="photo" ? "Foto auswählen" : "Datei auswählen"}
                    </div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>Tippe hier um {field.type==="photo"?"ein Foto aufzunehmen / auszuwählen":"eine Datei zu wählen"}</div>
                  </div>
                </label>
                {formData[field.id] && (
                  <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
                    {field.type==="photo" && <img src={formData[field.id]} style={{width:80,height:60,objectFit:"cover",borderRadius:6,border:"1px solid #e5e7eb"}} alt="Vorschau"/>}
                    <a href={formData[field.id]} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#7c3aed"}}>🔗 Anzeigen</a>
                    <button onClick={()=>setFormData(f=>({...f,[field.id]:""}))} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer"}}>✕ Entfernen</button>
                  </div>
                )}
              </div>
            ) : (
              <input type={field.type||"text"} value={formData[field.id]||""} onChange={e=>setFormData(f=>({...f,[field.id]:e.target.value}))}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
            )}
          </div>
        ))}
        <div style={{paddingTop:8,display:"flex",gap:10}}>
          <button onClick={submitForm} disabled={submitting}
            style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"11px 28px",cursor:submitting?"wait":"pointer",fontWeight:600,fontSize:14}}>
            {submitting?"Wird eingereicht...":"📤 Einreichen"}
          </button>
          <button onClick={()=>{setView("list");setFormData({});}} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"11px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    </div>
  );

  if(view==="submissions") return (
    <div style={{padding:24}}>
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center"}}>
        <button onClick={()=>setView("list")} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>Alle Einreichungen ({submissions.length})</h2>
        <button onClick={exportSubmCSV} style={{marginLeft:"auto",background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:12}}>📊 CSV Export</button>
      </div>
      {submissions.length===0?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Noch keine Formulare eingereicht.</div>
      :[...submissions].reverse().map(sub=>{
        const tpl=templates.find(t=>t.id===sub.template_id);
        const statusColors={Offen:"#fef3c7","In Bearbeitung":"#eff6ff",Gelöst:"#d1fae5"};
        const isOpen=expandedSub===sub.id;
        return(
          <div key={sub.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,marginBottom:8,overflow:"hidden"}}>
            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",padding:"14px 16px",cursor:"pointer"}} onClick={()=>setExpandedSub(isOpen?null:sub.id)}>
              <span style={{fontSize:18}}>{tpl?.icon||"📋"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{tpl?.name||sub.template_id}</div>
                <div style={{fontSize:11,color:"#9ca3af"}}>{sub.submitted_by} · {new Date(sub.submitted_at).toLocaleString("de-DE")}</div>
              </div>
              <select value={sub.status} onClick={e=>e.stopPropagation()} onChange={async e=>{await api("PATCH",`/forms/submissions/${sub.id}`,{status:e.target.value});reload();}}
                style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,outline:"none",background:statusColors[sub.status]||"#fff"}}>
                {["Offen","In Bearbeitung","Gelöst"].map(s=><option key={s}>{s}</option>)}
              </select>
              <button onClick={e=>{e.stopPropagation();printSub(sub,tpl);}} style={{fontSize:13,color:"#6b7280",background:"none",border:"none",cursor:"pointer"}}>🖨️</button>
              {isAdmin&&<button onClick={async e=>{e.stopPropagation();if(!confirm("Löschen?"))return;await api("DELETE",`/forms/submissions/${sub.id}`);reload();}}
                style={{fontSize:13,color:"#dc2626",background:"none",border:"none",cursor:"pointer"}}>✕</button>}
              <span style={{fontSize:12,color:"#9ca3af"}}>{isOpen?"▲":"▼"}</span>
            </div>
            {/* Preview (collapsed) */}
            {!isOpen && <div style={{padding:"0 16px 12px",display:"flex",gap:16,flexWrap:"wrap"}}>
              {(tpl?.fields||[]).slice(0,3).map(f=>(
                <div key={f.id} style={{fontSize:11}}><span style={{color:"#9ca3af"}}>{f.label}: </span>
                  {(f.type==="photo"||f.type==="file") && sub.data[f.id]
                    ? <a href={sub.data[f.id]} target="_blank" rel="noreferrer" style={{color:"#7c3aed"}}>📷 Foto</a>
                    : <span style={{color:"#374151",fontWeight:500}}>{String(sub.data[f.id]||"—").substring(0,60)}</span>}
                </div>
              ))}
            </div>}
            {/* Full detail (expanded) */}
            {isOpen && <div style={{borderTop:"1px solid #f3f4f6",padding:"16px"}}>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {(tpl?.fields||[]).map(f=>(
                  <div key={f.id} style={{display:"flex",flexDirection:"column",gap:4}}>
                    <span style={{fontSize:11,fontWeight:600,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.5}}>{f.label}</span>
                    {(f.type==="photo"||f.type==="file") && sub.data[f.id]
                      ? <div style={{display:"flex",alignItems:"center",gap:10}}>
                          {f.type==="photo" && <img src={sub.data[f.id]} style={{width:120,height:90,objectFit:"cover",borderRadius:8,border:"1px solid #e5e7eb"}} alt={f.label}/>}
                          <a href={sub.data[f.id]} target="_blank" rel="noreferrer" style={{color:"#7c3aed",fontSize:13}}>🔗 Öffnen</a>
                        </div>
                      : <span style={{fontSize:13,color:"#374151",whiteSpace:"pre-wrap"}}>{sub.data[f.id]||"—"}</span>}
                  </div>
                ))}
              </div>
            </div>}
          </div>
        );
      })}
    </div>
  );

  /* Template editor view */
  if(view==="editTpl") return (
    <div style={{padding:24,maxWidth:660,margin:"0 auto"}}>
      <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
        <button onClick={()=>setView("list")} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>{tplEdit?.id?"Vorlage bearbeiten":"Neue Vorlage"}</h2>
      </div>
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:24,display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",gap:10}}>
          <input value={tplEdit?.icon||""} onChange={e=>setTplEdit(t=>({...t,icon:e.target.value}))} style={{width:48,textAlign:"center",border:"1px solid #e5e7eb",borderRadius:6,fontSize:20,padding:4}}/>
          <input value={tplEdit?.name||""} onChange={e=>setTplEdit(t=>({...t,name:e.target.value}))} placeholder="Formularname *"
            style={{flex:1,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:14,fontWeight:600,outline:"none"}}/>
        </div>
        <input value={tplEdit?.description||""} onChange={e=>setTplEdit(t=>({...t,description:e.target.value}))} placeholder="Kurzbeschreibung"
          style={{width:"100%",padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:10}}>Felder</div>
          {tplFields.map((f,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:10,alignItems:"flex-start",flexWrap:"wrap"}}>
              <input value={f.label} onChange={e=>setTplFields(fs=>fs.map((x,j)=>j===i?{...x,label:e.target.value}:x))}
                placeholder="Feldname" style={{flex:2,minWidth:100,padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none"}}/>
              <select value={f.type} onChange={e=>setTplFields(fs=>fs.map((x,j)=>j===i?{...x,type:e.target.value}:x))}
                style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,outline:"none"}}>
                {[["text","Text"],["textarea","Textblock"],["number","Zahl"],["date","Datum"],["select","Auswahl"],["photo","Foto 📷"],["file","Datei 📎"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
              {f.type==="select" && <textarea value={f.options} onChange={e=>setTplFields(fs=>fs.map((x,j)=>j===i?{...x,options:e.target.value}:x))}
                placeholder="Optionen (eine pro Zeile)" style={{flex:3,minWidth:120,padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,resize:"vertical",minHeight:52}}/>}
              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
                <input type="checkbox" checked={f.required} onChange={e=>setTplFields(fs=>fs.map((x,j)=>j===i?{...x,required:e.target.checked}:x))} style={{accentColor:"#7c3aed"}}/>
                Pflicht
              </label>
              <button onClick={()=>setTplFields(fs=>fs.filter((_,j)=>j!==i))}
                style={{padding:"7px 10px",border:"none",background:"#fee2e2",color:"#dc2626",borderRadius:6,cursor:"pointer"}}>✕</button>
            </div>
          ))}
          <button onClick={()=>setTplFields(fs=>[...fs,{id:`f${Date.now()}`,label:"",type:"text",required:false,options:""}])}
            style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>+ Feld hinzufügen</button>
        </div>
        <div style={{display:"flex",gap:10,paddingTop:4}}>
          <button onClick={saveTpl} style={{flex:1,background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"11px",cursor:"pointer",fontWeight:600,fontSize:14}}>💾 Speichern</button>
          <button onClick={()=>setView("list")} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"11px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>📋 Formulare ausfüllen</h2>
        <div style={{display:"flex",gap:8}}>
          {isAdmin&&<button onClick={openNewTpl}
            style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"9px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Vorlage erstellen</button>}
          {isAdmin&&<button onClick={()=>setView("submissions")}
            style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"9px 16px",cursor:"pointer",fontSize:13}}>
            📂 Einreichungen ({submissions.length}){submissions.filter(s=>s.status==="Offen").length>0&&<span style={{marginLeft:6,background:"#f59e0b",color:"#fff",borderRadius:99,padding:"1px 6px",fontSize:11}}>{submissions.filter(s=>s.status==="Offen").length}</span>}
          </button>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:12}}>
        {templates.map(tpl=>{
          const subCnt=submissions.filter(s=>s.template_id===tpl.id).length;
          const openCnt=submissions.filter(s=>s.template_id===tpl.id&&s.status==="Offen").length;
          return(
            <div key={tpl.id} style={{background:"#fff",border:"1.5px solid #e5e7eb",borderRadius:12,padding:20,transition:"all .15s",display:"flex",flexDirection:"column"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="#7c3aed50";e.currentTarget.style.boxShadow="0 2px 10px #7c3aed12";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.boxShadow="none";}}>
              <div style={{flex:1,cursor:"pointer"}} onClick={()=>{setSelTpl(tpl);setFormData({});setView("fill");}}>
                <div style={{fontSize:28,marginBottom:8}}>{tpl.icon}</div>
                <div style={{fontSize:15,fontWeight:700,color:"#111827",marginBottom:4}}>{tpl.name}</div>
                <div style={{fontSize:12,color:"#9ca3af",marginBottom:12}}>{tpl.description}</div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:11,color:"#6b7280"}}>{subCnt} eingereicht</span>
                  {openCnt>0&&<span style={{fontSize:11,color:"#d97706",fontWeight:600}}>⚠️ {openCnt} offen</span>}
                </div>
              </div>
              {isAdmin && <div style={{display:"flex",gap:6,marginTop:12,paddingTop:10,borderTop:"1px solid #f3f4f6"}}>
                <button onClick={()=>openEditTpl(tpl)}
                  style={{flex:1,padding:"6px",fontSize:12,background:"#f5f3ff",color:"#7c3aed",border:"none",borderRadius:6,cursor:"pointer",fontWeight:600}}>✏️ Bearbeiten</button>
                <button onClick={async()=>{if(!confirm("Vorlage löschen?"))return;await api("DELETE",`/forms/templates/${tpl.id}`);reload();}}
                  style={{padding:"6px 10px",fontSize:12,background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:6,cursor:"pointer"}}>🗑️</button>
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Checklisten ── */
function ChecklistTab({ data, api, reload, user }) {
  const [view, setView] = useState("list");
  const [selTpl, setSelTpl] = useState(null);
  const [checks, setChecks] = useState({});
  const [editForm, setEditForm] = useState({name:"",icon:"✅",items:[""]});
  const [editingId, setEditingId] = useState(null);
  const [openLog, setOpenLog] = useState(null);
  const isAdmin = user.isAdmin || user.adminAccess;
  const templates = data?.checklists?.templates || [];
  const logs = data?.checklists?.logs || [];

  const submitChecklist = async()=>{
    if(!selTpl) return;
    const items=selTpl.items.map(t=>({text:t,done:!!checks[t]}));
    await api("POST","/checklists/log",{template_id:selTpl.id,completed_by:user.name,items});
    setView("list"); setChecks({}); reload(); alert("✅ Checkliste gespeichert!");
  };
  const saveTpl = async()=>{
    const body={name:editForm.name,icon:editForm.icon,items:editForm.items.filter(i=>i.trim())};
    if(editingId==="new") await api("POST","/checklists/templates",body);
    else await api("PUT",`/checklists/templates/${editingId}`,body);
    setView("list"); setEditingId(null); reload();
  };
  const exportLogsCSV = ()=>{
    const rows=[["Vorlage","Abgehakt von","Datum","Uhrzeit","Erledigt","Gesamt","Prozent"]];
    [...logs].reverse().forEach(l=>{
      const tpl=templates.find(t=>t.id===l.template_id);
      const done=(l.items||[]).filter(i=>i.done).length;
      const total=l.items?.length||0;
      rows.push([tpl?.name||l.template_id,l.completed_by,l.date,l.time,done,total,total?Math.round(done/total*100)+"%":"0%"]);
    });
    const csv=rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download="burn-checklisten-protokoll.csv"; a.click();
  };
  const printChecklist = tpl=>{
    const w=window.open("","_blank");
    w.document.write(`<html><body style="font-family:sans-serif;padding:30px;max-width:700px"><h1>${tpl.icon} ${tpl.name}</h1><p style="color:#666">Datum: __________________ | Mitarbeiter: __________________</p><hr/>${tpl.items.map((item,i)=>`<p style="margin:12px 0;display:flex;gap:10px"><span style="display:inline-block;width:20px;height:20px;border:2px solid #333;border-radius:4px;margin-right:8px"></span>${i+1}. ${item}</p>`).join("")}</body></html>`);
    w.print();
  };

  if(view==="fill"&&selTpl){
    const done=selTpl.items.filter(i=>checks[i]).length;
    const total=selTpl.items.length;
    return(
      <div style={{padding:24,maxWidth:700,margin:"0 auto"}}>
        <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
          <button onClick={()=>{setView("list");setChecks({});}} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
          <div style={{flex:1}}><h2 style={{margin:0,fontSize:18,color:"#111827"}}>{selTpl.icon} {selTpl.name}</h2></div>
          <span style={{fontSize:14,fontWeight:700,color:done===total?"#059669":"#d97706"}}>{done}/{total}</span>
        </div>
        <div style={{height:6,background:"#e5e7eb",borderRadius:3,marginBottom:20}}>
          <div style={{height:"100%",width:`${total?done/total*100:0}%`,background:done===total?"#059669":"#7c3aed",borderRadius:3,transition:"width .3s"}}/>
        </div>
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden"}}>
          {selTpl.items.map((item,i)=>(
            <div key={i} onClick={()=>setChecks(c=>({...c,[item]:!c[item]}))}
              style={{display:"flex",gap:12,padding:"14px 16px",borderBottom:i<selTpl.items.length-1?"1px solid #f3f4f6":"none",cursor:"pointer",background:checks[item]?"#f0fdf4":"#fff",transition:"background .15s"}}>
              <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${checks[item]?"#059669":"#d1d5db"}`,background:checks[item]?"#059669":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                {checks[item]&&<span style={{color:"#fff",fontSize:13,fontWeight:700}}>✓</span>}
              </div>
              <span style={{fontSize:13,color:checks[item]?"#6b7280":"#111827",textDecoration:checks[item]?"line-through":"none"}}>{item}</span>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button onClick={submitChecklist} style={{background:"#059669",color:"#fff",border:"none",borderRadius:8,padding:"11px 28px",cursor:"pointer",fontWeight:600,fontSize:14}}>✅ Abschließen & Speichern</button>
          <button onClick={()=>{setView("list");setChecks({});}} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"11px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    );
  }

  if(view==="logs") {
    return(
      <div style={{padding:24,maxWidth:760,margin:"0 auto"}}>
        <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center"}}>
          <button onClick={()=>setView("list")} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
          <h2 style={{margin:0,fontSize:18,color:"#111827"}}>📋 Protokoll</h2>
          <button onClick={exportLogsCSV} style={{marginLeft:"auto",background:"#f3f4f6",border:"none",borderRadius:6,padding:"8px 14px",cursor:"pointer",fontSize:12}}>📊 CSV Export</button>
        </div>
        {logs.length===0&&<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Noch kein Protokoll vorhanden.</div>}
        {[...logs].reverse().map(log=>{
          const tpl=templates.find(t=>t.id===log.template_id);
          const done=(log.items||[]).filter(i=>i.done).length;
          const total=log.items?.length||0;
          const pct=total?Math.round(done/total*100):0;
          const isOpen=openLog===log.id;
          return(
            <div key={log.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,marginBottom:10,overflow:"hidden"}}>
              {/* Header — klickbar zum Aufklappen */}
              <div onClick={()=>setOpenLog(isOpen?null:log.id)}
                style={{display:"flex",gap:12,padding:"14px 16px",cursor:"pointer",alignItems:"center",userSelect:"none"}}>
                <span style={{fontSize:22}}>{tpl?.icon||"✅"}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{tpl?.name||log.template_id}</div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>👤 {log.completed_by} · 📅 {log.date} {log.time}</div>
                </div>
                <div style={{textAlign:"right",marginRight:8}}>
                  <div style={{fontSize:15,fontWeight:700,color:done===total?"#059669":"#d97706"}}>{done}/{total}</div>
                  <div style={{fontSize:10,color:"#9ca3af"}}>{pct}%</div>
                </div>
                {/* Progress mini-bar */}
                <div style={{width:60,height:6,background:"#f3f4f6",borderRadius:3,flexShrink:0}}>
                  <div style={{width:`${pct}%`,height:"100%",background:done===total?"#059669":"#7c3aed",borderRadius:3}}/>
                </div>
                <span style={{fontSize:14,color:"#9ca3af",flexShrink:0}}>{isOpen?"▲":"▼"}</span>
              </div>
              {/* Detail — aufgeklappt */}
              {isOpen && (
                <div style={{borderTop:"1px solid #f3f4f6",padding:"12px 16px"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#9ca3af",marginBottom:8,textTransform:"uppercase",letterSpacing:0.5}}>Abgehakte Punkte</div>
                  {(log.items||[]).map((item,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<(log.items.length-1)?"1px solid #f9fafb":"none"}}>
                      <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${item.done?"#059669":"#d1d5db"}`,background:item.done?"#059669":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {item.done&&<span style={{color:"#fff",fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:13,color:item.done?"#374151":"#9ca3af",textDecoration:item.done?"none":"line-through"}}>{item.text}</span>
                      {!item.done&&<span style={{fontSize:10,color:"#f59e0b",marginLeft:"auto"}}>⚠️ Nicht erledigt</span>}
                    </div>
                  ))}
                  <div style={{marginTop:12,padding:"8px 12px",background:"#f9fafb",borderRadius:8,fontSize:12,color:"#6b7280"}}>
                    ✅ <strong>{done}</strong> von <strong>{total}</strong> Punkten erledigt von <strong>{log.completed_by}</strong> am {log.date} um {log.time}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if(view==="editTpl") return(
    <div style={{padding:24,maxWidth:600,margin:"0 auto"}}>
      <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
        <button onClick={()=>{setView("list");setEditingId(null);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#6b7280"}}>←</button>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>{editingId==="new"?"Neue Checkliste":"Checkliste bearbeiten"}</h2>
      </div>
      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:24,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",gap:10}}>
          <input value={editForm.icon} onChange={e=>setEditForm(f=>({...f,icon:e.target.value}))} style={{width:48,textAlign:"center",border:"1px solid #e5e7eb",borderRadius:6,fontSize:20,padding:4}}/>
          <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} placeholder="Checklistenname"
            style={{flex:1,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:14,fontWeight:600,outline:"none"}}/>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"#374151",marginBottom:8}}>Punkte</div>
          {editForm.items.map((item,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
              <span style={{color:"#9ca3af",fontSize:12,width:20,textAlign:"right",flexShrink:0}}>{i+1}.</span>
              <input value={item} onChange={e=>setEditForm(f=>({...f,items:f.items.map((x,j)=>j===i?e.target.value:x)}))}
                placeholder="Punkt..." style={{flex:1,padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,outline:"none"}}
                onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();setEditForm(f=>({...f,items:[...f.items.slice(0,i+1),"",...f.items.slice(i+1)]}))}}}/>
              <button onClick={()=>setEditForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}
                style={{padding:"8px",border:"none",background:"#fee2e2",color:"#dc2626",borderRadius:6,cursor:"pointer",flexShrink:0}}>✕</button>
            </div>
          ))}
          <button onClick={()=>setEditForm(f=>({...f,items:[...f.items,""]}))}
            style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>+ Punkt hinzufügen</button>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={saveTpl} style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",cursor:"pointer",fontWeight:600,fontSize:14}}>💾 Speichern</button>
          <button onClick={()=>{setView("list");setEditingId(null);}} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"10px 16px",cursor:"pointer",fontSize:14}}>Abbrechen</button>
        </div>
      </div>
    </div>
  );

  return(
    <div style={{padding:24}}>
      <div style={{display:"flex",gap:10,marginBottom:20,alignItems:"center",flexWrap:"wrap"}}>
        <h2 style={{margin:0,fontSize:18,color:"#111827"}}>✅ Checklisten</h2>
        {isAdmin&&<button onClick={()=>{setEditForm({name:"",icon:"✅",items:[""]});setEditingId("new");setView("editTpl");}}
          style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Neue Checkliste</button>}
        <button onClick={()=>setView("logs")} style={{marginLeft:"auto",background:"#f3f4f6",border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:13}}>📋 Protokoll ({logs.length})</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:12}}>
        {templates.map(tpl=>{
          const recentLogs=logs.filter(l=>l.template_id===tpl.id);
          const lastLog=recentLogs[recentLogs.length-1];
          return(
            <div key={tpl.id} style={{background:"#fff",border:"1.5px solid #e5e7eb",borderRadius:12,padding:20}}>
              <div style={{fontSize:28,marginBottom:8}}>{tpl.icon}</div>
              <div style={{fontSize:15,fontWeight:700,color:"#111827",marginBottom:4}}>{tpl.name}</div>
              <div style={{fontSize:12,color:"#9ca3af",marginBottom:12}}>{tpl.items.length} Punkte</div>
              {lastLog&&<div style={{fontSize:11,color:"#6b7280",marginBottom:12,background:"#f9fafb",borderRadius:6,padding:"6px 10px"}}>
                Zuletzt: {lastLog.completed_by} · {lastLog.date}
              </div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setSelTpl(tpl);setChecks({});setView("fill");}}
                  style={{flex:1,background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,padding:"9px",cursor:"pointer",fontSize:13,fontWeight:600}}>Abhaken</button>
                <button onClick={()=>printChecklist(tpl)} title="Drucken" style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:"#fff",borderRadius:8,cursor:"pointer",fontSize:13}}>🖨️</button>
                {isAdmin&&<>
                  <button onClick={()=>{setEditForm({name:tpl.name,icon:tpl.icon,items:[...tpl.items]});setEditingId(tpl.id);setView("editTpl");}}
                    style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:"#fff",borderRadius:8,cursor:"pointer",fontSize:13}}>✏️</button>
                  <button onClick={async()=>{if(!confirm("Löschen?"))return;await api("DELETE",`/checklists/templates/${tpl.id}`);reload();}}
                    style={{padding:"9px 12px",border:"1px solid #fee2e2",color:"#dc2626",background:"#fff",borderRadius:8,cursor:"pointer",fontSize:13}}>🗑️</button>
                </>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Heroicons Filled nav icons ────────────────────────────────────── */
function NavIcon({ id, color }) {
  const s = { display:"block" };
  const icons = {
    schedule: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5z" clipRule="evenodd"/></svg>,
    messages: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223z" clipRule="evenodd"/></svg>,
    payroll:  <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path d="M4.5 3.75a3 3 0 0 0-3 3v.75h21v-.75a3 3 0 0 0-3-3h-15z"/><path fillRule="evenodd" d="M22.5 9.75h-21v7.5a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-7.5zm-18 3.75a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1-.75-.75zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3z" clipRule="evenodd"/></svg>,
    availability: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5zm-12.51 3.47a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.06 0l4.5-4.5a.75.75 0 1 0-1.06-1.06l-3.97 3.97-1.72-1.72z" clipRule="evenodd"/></svg>,
    more:     <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z" clipRule="evenodd"/></svg>,
    profile:  <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0zM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695z" clipRule="evenodd"/></svg>,
    betrieb:  <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625zM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15zm.75-6.75a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25z" clipRule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963z"/></svg>,
    leaderboard: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 0 0-.584.859 6.753 6.753 0 0 0 6.138 5.6 6.73 6.73 0 0 0 2.743 1.346A6.707 6.707 0 0 1 9.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a2.25 2.25 0 0 0-2.25 2.25c0 .414.336.75.75.75h15a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-2.25-2.25h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.706 6.706 0 0 1-1.112-3.173 6.73 6.73 0 0 0 2.743-1.347 6.753 6.753 0 0 0 6.139-5.6.75.75 0 0 0-.585-.858 47.077 47.077 0 0 0-3.07-.543V2.62a.75.75 0 0 0-.658-.744 49.798 49.798 0 0 0-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 0 0-.657.744zm0 2.629c0 1.196.312 2.32.857 3.294A5.266 5.266 0 0 1 3.16 5.337a45.6 45.6 0 0 1 2.006-.343v.256zm13.5 0v-.256c.674.1 1.343.214 2.006.343a5.265 5.265 0 0 1-2.863 3.207 6.72 6.72 0 0 0 .857-3.294z" clipRule="evenodd"/></svg>,
    "pt-kalender": <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001z"/></svg>,
    editor: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4z"/><path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25z"/></svg>,
    avail_all: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0zM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0zM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003z"/></svg>,
    staff: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0zM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0zM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0zM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38z" clipRule="evenodd"/><path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047zM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z"/></svg>,
    kundendaten: <svg style={s} width="24" height="24" viewBox="0 0 24 24" fill={color}><path d="M11.25 4.533A9.707 9.707 0 0 0 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103z"/></svg>,
  };
  return icons[id] || icons.more;
}

/* ─── StaffDashboard ─────────────────────────────────────────────────── */
function StaffDashboard({ user, onLogout }) {
  const isMobile = useMobile();
  const [activeTab, setActiveTab] = useState("schedule");
  const [extraSections, setExtraSections] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [staffNotification, setStaffNotification] = useState("");
  const [pendingSwaps, setPendingSwaps] = useState([]);
  const notify = (msg) => { setStaffNotification(msg); setTimeout(() => setStaffNotification(""), 3000); };

  // Nahomi: Tausch-Anfragen laden die auf Genehmigung warten
  const isNahomi = (user.scheduleEditor || user.adminAccess) && user.name === "Nahomi";
  const loadPendingSwaps = () => {
    if (!isNahomi) return;
    const swapP = fetch(`${API_URL}/staff/shift-swap`)
      .then(r => r.json())
      .then(d => (d.swaps||[]).filter(s => s.status === "pending_approval" || s.status === "taken"))
      .catch(() => []);
    const vacP = fetch(`${API_URL}/staff/vacation`)
      .then(r => r.json())
      .then(d => (d.requests||[]).filter(v => v.status === "pending").map(v => ({...v, _type:"vacation"})))
      .catch(() => []);
    Promise.all([swapP, vacP]).then(([swaps, vacs]) => setPendingSwaps([...swaps, ...vacs]));
  };
  useEffect(() => { loadPendingSwaps(); }, []);

  const handleSwapDecision = async (swapId, action) => {
    await fetch(`${API_URL}/staff/shift-swap/${swapId}/${action}`, {
      method: "POST", headers: {"Content-Type":"application/json"},
      body: JSON.stringify({ approved_by: user.name })
    }).catch(() => {});
    loadPendingSwaps();
    notify(action === "approve" ? "✅ Tausch genehmigt" : "❌ Tausch abgelehnt");
  };

  useEffect(() => {
    fetch(`${API_URL}/staff/permissions?user_id=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setExtraSections(d.allowed_sections || []); })
      .catch(() => {});
  }, [user.id]);

  const swapBadge = pendingSwaps.filter(s=>s.status==="pending_approval"||s.status==="taken"||s.status==="pending").length;
  const baseTabs = [
    { id:"schedule",     label:"Mein Plan",      icon:"📅" },
    { id:"messages",     label:`Nachrichten${msgUnread>0?` (${msgUnread})`:``}`, icon:"💬" },
    { id:"tauschboerse", label:`Anfragen${swapBadge>0?` (${swapBadge})`:``}`, icon:"📋" },
    { id:"payroll",      label:"Abrechnung",      icon:"💸" },
    { id:"availability", label:"Verfügbarkeit",   icon:"📆" },
    { id:"pt-kalender",  label:"PT Kalender",     icon:"🥊" },
    { id:"profile",      label:"Profil",          icon:"👤" },
    { id:"betrieb",      label:"Betrieb",         icon:"📁" },
    { id:"leaderboard",  label:"Rangliste",       icon:"🏆" },
    ...(user.scheduleEditor ? [{ id:"editor", label:"Plan-Editor", icon:"✏️" }, { id:"avail_all", label:"Alle Verfügbar.", icon:"👥" }] : []),
  ];

  const baseTabIds = new Set(baseTabs.map(t => t.id));
  const extraTabs = extraSections
    .filter(s => !baseTabIds.has(s))
    .map(s => { const n = NAV.find(x => x.id === s); return n ? { id: s, label: n.label } : null; })
    .filter(Boolean);

  const tabs = [...baseTabs, ...extraTabs];

  // Mobile: first 4 tabs in bottom nav (Plan, Nachrichten, Abrechnung, Verfügbarkeit), rest in "Mehr" drawer
  const mobileMain = tabs.slice(0, 4);
  const mobileMore = tabs.slice(4);

  const tabContent = (
    <>
      {activeTab === "schedule"     && <StaffScheduleView user={user} />}
      {activeTab === "tauschboerse" && <AnfragenView user={user} isNahomi={isNahomi} pendingSwaps={pendingSwaps} onDecision={handleSwapDecision} reloadPending={loadPendingSwaps} />}
      {activeTab === "payroll"      && <StaffPayrollView user={user} />}
      {activeTab === "availability" && <AvailabilityView user={user} />}
      {activeTab === "pt-kalender"  && <PTKalenderView user={user} />}
      {activeTab === "messages"     && <MsgErrorBoundary><MessagesView user={user} onUnreadChange={setMsgUnread} /></MsgErrorBoundary>}
      {activeTab === "leaderboard"  && <LeaderboardView user={user} />}
      {activeTab === "profile"      && <ProfileView user={user} />}
      {activeTab === "editor"       && <ScheduleEditorView />}
      {activeTab === "avail_all"    && <AllAvailabilityView />}
      {activeTab === "pipeline"     && <PipelineView customers={[]} setCustomers={()=>{}} notify={()=>{}} />}
      {activeTab === "customers"    && <CustomerDB customers={[]} setCustomers={()=>{}} />}
      {activeTab === "emails"       && <EmailView customers={[]} />}
      {activeTab === "sms"          && <SMSView customers={[]} />}
      {activeTab === "automations"  && <AutomationsView customers={[]} />}
      {activeTab === "analytics"    && <AnalyticsView customers={[]} />}
      {activeTab === "segments"     && <SegmentsView customers={[]} />}
      {activeTab === "leads"        && <LeadsView customers={[]} setCustomers={()=>{}} notify={()=>{}} />}
      {activeTab === "cash"         && <CashView notify={()=>{}} />}
      {activeTab === "weekly"       && <WeeklyView customers={[]} notify={()=>{}} />}
      {activeTab === "staff"        && <StaffView notify={()=>{}} />}
      {activeTab === "bestand"      && <BestandView notify={()=>{}} />}
      {activeTab === "duplicates"   && <DuplicatesView customers={[]} setCustomers={()=>{}} notify={()=>{}} />}
      {activeTab === "journeys"     && <JourneysView notify={()=>{}} />}
      {activeTab === "journeyEditor" && <JourneyTemplateEditorView />}
      {activeTab === "emailBlockEditor" && <EmailBlockEditorView />}
      {activeTab === "templates"    && <TemplatesView />}
      {activeTab === "betrieb"      && <BetriebView user={user} />}
      {activeTab === "kommunikation"   && <KommunikationView user={user} notify={notify} />}
    </>
  );

  if (isMobile) {
    return (
      <div style={{ display:"flex", height:"100dvh", flexDirection:"column", background:"#f9fafb", fontFamily:"'Inter',system-ui,sans-serif" }}>
        {/* Mobile Top Bar */}
        <div style={{ height:52, background:"#030712", display:"flex", alignItems:"center", padding:"0 16px", gap:12, flexShrink:0 }}>
          <img src={BURN_LOGO_WHITE} style={{ height:22 }} alt="BURN" />
          <div style={{ flex:1, fontSize:14, fontWeight:700, color:"#fff" }}>
            {tabs.find(t=>t.id===activeTab)?.label}
          </div>
          <div onClick={onLogout} style={{ width:36, height:36, borderRadius:"50%", overflow:"hidden", border:`2px solid ${user.color}`, cursor:"pointer", flexShrink:0 }}>
            {user.photo
              ? <img src={user.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} alt={user.name}/>
              : <div style={{ width:"100%", height:"100%", background:`${user.color}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:user.color }}>{user.name[0]}</div>}
          </div>
        </div>

        {/* Content area — takes remaining space above bottom nav */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", minWidth:0, maxWidth:"100vw" }}>
          {isNahomi && swapBadge > 0 && activeTab !== "tauschboerse" && (
            <div onClick={()=>setActiveTab("tauschboerse")} style={{ background:"#fff8f0", borderBottom:"2px solid #f59e0b", padding:"10px 16px", flexShrink:0, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#92400e" }}>📋 {swapBadge} Anfrage{swapBadge>1?"n":""} warten auf Genehmigung</span>
              <span style={{ fontSize:12, color:"#b45309", textDecoration:"underline" }}>→ Zur Tauschbörse</span>
            </div>
          )}
          {tabContent}
        </div>

        {/* "Mehr" drawer */}
        {moreMenuOpen && (
          <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
            <div style={{ background:"rgba(0,0,0,0.5)", position:"absolute", inset:0 }} onClick={() => setMoreMenuOpen(false)} />
            <div style={{ position:"relative", background:"#fff", borderRadius:"20px 20px 0 0", padding:"8px 0 24px", maxHeight:"60vh", overflowY:"auto" }}>
              <div style={{ width:40, height:4, background:"#e5e7eb", borderRadius:2, margin:"8px auto 16px" }} />
              {mobileMore.map(t => (
                <button key={t.id} onClick={() => { setActiveTab(t.id); setMoreMenuOpen(false); }}
                  style={{ display:"flex", alignItems:"center", gap:14, width:"100%", padding:"14px 24px", border:"none", background: activeTab===t.id ? "#f5f3ff" : "transparent", color: activeTab===t.id ? "#7c3aed" : "#374151", fontSize:15, fontWeight: activeTab===t.id?700:400, cursor:"pointer", textAlign:"left" }}>
                  <NavIcon id={t.id} color={activeTab===t.id?"#7c3aed":"#9ca3af"} />
                  <span>{t.label}</span>
                </button>
              ))}
              <div style={{ height:1, background:"#f3f4f6", margin:"8px 16px" }} />
              <button onClick={onLogout} style={{ display:"flex", alignItems:"center", gap:14, width:"100%", padding:"14px 24px", border:"none", background:"transparent", color:"#dc2626", fontSize:15, cursor:"pointer", textAlign:"left" }}>
                <span style={{ fontSize:20 }}>🚪</span><span>Abmelden</span>
              </button>
            </div>
          </div>
        )}

        {/* Mobile Bottom Navigation */}
        <div style={{ height:64, background:"#fff", borderTop:"1px solid #e5e7eb", display:"flex", alignItems:"stretch", flexShrink:0, paddingBottom:"env(safe-area-inset-bottom)" }}>
          {mobileMain.map(t => {
            const active = activeTab === t.id;
            const color = active ? "#9900cc" : "#9ca3af";
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, border:"none", background:"transparent", cursor:"pointer", padding:"6px 4px", position:"relative" }}>
                <NavIcon id={t.id} color={color} />
                <span style={{ fontSize:10, fontWeight: active?700:400, lineHeight:1, color }}>{t.label.replace(/\([^)]*\)/,"").trim()}</span>
                {t.id==="messages" && msgUnread>0 && <span style={{ position:"absolute", top:6, left:"55%", background:"#dc2626", color:"#fff", fontSize:9, fontWeight:700, borderRadius:99, padding:"1px 5px", minWidth:16, textAlign:"center" }}>{msgUnread}</span>}
              </button>
            );
          })}
          {mobileMore.length > 0 && (
            <button onClick={() => setMoreMenuOpen(true)}
              style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, border:"none", background:"transparent", cursor:"pointer", padding:"6px 4px", color: mobileMore.some(t=>t.id===activeTab) ? "#9900cc" : "#9ca3af" }}>
              <NavIcon id="more" color={mobileMore.some(t=>t.id===activeTab) ? "#9900cc" : "#9ca3af"} />
              <span style={{ fontSize:10, fontWeight:400, lineHeight:1 }}>Mehr</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div style={{ display:"flex", height:"100vh", flexDirection:"column", background:"#f9fafb", fontFamily:"'Inter',system-ui,sans-serif" }}>
      <div style={{ height:56, background:"#fff", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", padding:"0 24px", gap:16, flexShrink:0 }}>
        <img src={BURN_LOGO_WHITE} style={{ height:24, filter:"brightness(0)" }} alt="BURN" />
        <div style={{ flex:1 }} />
        <div style={{ display:"flex", gap:4, flexWrap:"wrap", overflowX:"auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{ padding:"6px 14px", border:"none", borderBottom:`2px solid ${activeTab===t.id?"#7c3aed":"transparent"}`, background:"transparent", fontSize:13, fontWeight:activeTab===t.id?700:400, color:activeTab===t.id?"#7c3aed":"#6b7280", cursor:"pointer", whiteSpace:"nowrap" }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", marginLeft:16 }} onClick={onLogout}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:`${user.color}18`, border:`2px solid ${user.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:user.color, overflow:"hidden" }}>
            {user.photo ? <img src={user.photo} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} alt={user.name}/> : <img src={BURN_FALLBACK} style={{width:"70%",height:"70%",objectFit:"contain",opacity:0.7}} alt="BURN"/>}
          </div>
          <span style={{ fontSize:12, color:"#6b7280" }}>{user.name}</span>
          <span style={{ fontSize:11, color:"#9ca3af" }}>Abmelden</span>
        </div>
      </div>
      {isNahomi && swapBadge > 0 && activeTab !== "tauschboerse" && (
        <div onClick={()=>setActiveTab("tauschboerse")} style={{ background:"#fff8f0", borderBottom:"2px solid #f59e0b", padding:"10px 24px", flexShrink:0, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:"#92400e" }}>📋 {swapBadge} Anfrage{swapBadge>1?"n":""} warten auf Genehmigung</span>
          <span style={{ fontSize:12, color:"#b45309", textDecoration:"underline" }}>→ Zur Tauschbörse</span>
        </div>
      )}
      {tabContent}
    </div>
  );
}

/* ─── Restricted Admin View (for staff with specific section permissions) ── */
export default function BURNDashboard() {
  useEffect(() => {
    const id = "burn-keyframes";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
        @keyframes burnSpin {
          0%   { transform: rotate(0deg) scale(1.15); opacity:0.6; }
          60%  { transform: rotate(360deg) scale(1); opacity:1; }
          100% { transform: rotate(360deg) scale(1); opacity:1; }
        }
        .burn-fallback-spin { animation: burnSpin 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        .burn-status-input::placeholder { color: rgba(255,255,255,0.7); }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; margin: 0; padding: 0; }
        html, body, #root { height: 100%; overflow-x: hidden; }
        body { font-family: 'Inter', system-ui, sans-serif; -webkit-font-smoothing: antialiased; overscroll-behavior-y: none; }
        input, textarea, select, button { font-family: inherit; }
        /* Prevent iOS auto-zoom on inputs */
        @media (max-width: 767px) {
          input, textarea, select { font-size: 16px !important; }
        }
        /* Touch-friendly tap targets */
        button { min-height: 44px; cursor: pointer; }
        /* Scrollable tables on small screens */
        .mob-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        /* Mobile responsive rules */
        @media (max-width: 767px) {
          /* Prevent any content from breaking layout */
          .burn-modal {
            position: fixed !important;
            inset: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
            margin: 0 !important;
            overflow-y: auto !important;
          }
          /* Side panels become full screen */
          .burn-panel { width: 100% !important; max-width: 100% !important; }
          /* Tables scroll horizontally */
          table { min-width: 500px; }
          .burn-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; }
          /* Grid becomes single column */
          .burn-grid-2 { grid-template-columns: 1fr !important; }
          .burn-hide-mobile { display: none !important; }
          .burn-full-mobile { width: 100% !important; max-width: 100% !important; }
          /* Content padding for mobile */
          .burn-page-pad { padding: 12px !important; }
        }
        @media (min-width: 768px) {
          .burn-hide-desktop { display: none !important; }
          .burn-bottom-nav { display: none !important; }
        }
      `;
      document.head.appendChild(s);
    }
  }, []);
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("burn_user");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loginMode, setLoginMode] = useState(null);
  const [activeNav, setActiveNav] = useState("pipeline");
  const [modules, setModules] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [branding, setBranding] = useState({name:"BURN", primary_color:"#FF4F00"});
  const [customerTab, setCustomerTab] = useState("kunden");
  const [emailTab, setEmailTab] = useState("emails");
  const [customers, setCustomers] = useState(() => {
    try {
      const cached = localStorage.getItem("burn_customers");
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      setSourceFilter(e.detail);
      setActiveNav("customers");
    };
    window.addEventListener("filterBySource", handler);
    return () => window.removeEventListener("filterBySource", handler);
  }, []);

  // Load modules & branding — also called after toggle in ModulesAdminView
  const loadModules = () => {
    fetch(`${API_URL}/modules`)
      .then(r => r.json())
      .then(d => {
        setModules(d.modules || []);
        setEnabledModules(d.enabled || []);
        if (d.branding && d.branding.name) setBranding(d.branding);
      })
      .catch(() => {
        setEnabledModules(["crm","cash","journeys","email_editor","payroll","profitability"]);
      });
  };
  useEffect(() => { loadModules(); }, []);

  const hasModule = (m) => enabledModules.length === 0 || enabledModules.includes(m);

  const loadCustomers = (clearCache = false) => {
    // Show cached data instantly
    const cached = cacheGet("customers");
    if (cached) {
      const safe = (cached.customers || []).map(c => ({...sanitize(c), stage: autoStage(c)}));
      setCustomers(safe); setLoading(false);
    }

    const fetchFresh = () =>
      fetch(`${API_URL}/customers`)
        .then(r => r.json())
        .then(data => {
          cacheSet("customers", data);
          const safe = (data.customers || []).map(c => ({...sanitize(c), stage: autoStage(c)}));
          fetch(`${API_URL}/pipeline/positions`)
            .then(r => r.json())
            .then(positions => {
              const final = safe.map(c => ({...c, stage: positions[c.id] || c.stage}));
              setCustomers(final);
              try { localStorage.setItem("burn_customers", JSON.stringify(final)); } catch {}
            })
            .catch(() => setCustomers(safe));
          setLoading(false); setSyncing(false); setLastSync(new Date());
        })
        .catch(() => { setLoading(false); setSyncing(false); });

    if (clearCache) {
      // Delta-Sync: nur neue/geänderte Kunden holen, kein Full-Reload
      fetch(`${API_URL}/customers/sync`, { method: "POST" })
        .then(r => r.json())
        .then(d => {
          if (d.ok) {
            notify(`✅ Sync: ${d.added || 0} neu, ${d.updated || 0} aktualisiert`);
            fetchFresh();
          } else {
            // Fallback: normaler Fetch wenn kein Disk-Cache
            fetchFresh();
          }
        })
        .catch(fetchFresh);
    } else {
      fetchFresh();
    }
  };

  useEffect(() => { if (user && user.type === "admin") loadCustomers(); }, [user]);  // Staff-Geräte laden keine Kundendaten (DSGVO)

  const notify = msg => { setNotification(msg); setTimeout(() => setNotification(null), 3000); };
  const todayBirthdays = customers.filter(c => {
    if (!c.birthDate) return false;
    const today = new Date();
    const bd = new Date(c.birthDate);
    return bd.getMonth() === today.getMonth() && bd.getDate() === today.getDate();
  });

  const [birthdayQuickAction, setBirthdayQuickAction] = useState(null);
  // ⚠️ Must be before any conditional return — Rules of Hooks
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);

  const logout = () => {
    fetch(`${API_URL}/auth/logout`, { method: "POST" }).catch(() => {});
    localStorage.removeItem("burn_token");
    localStorage.removeItem("burn_user");
    setUser(null); setLoginMode(null);
  };

  if (!user) {
    if (!loginMode) return <EntryScreen onAdmin={() => setLoginMode("admin")} onStaff={() => setLoginMode("staff")} />;
    if (loginMode === "staff") return <StaffLoginScreen onLogin={u => { localStorage.setItem("burn_user", JSON.stringify(u)); setUser(u); }} onBack={() => setLoginMode(null)} />;
    return <LoginScreen onLogin={u => { setLoginMode(null); setUser(u); }} onBack={() => setLoginMode(null)} />;
  }

  // All staff users get StaffDashboard — extra granted sections load automatically inside
  if (user.type === "staff") return <StaffDashboard user={user} onLogout={logout} />;

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ display:"flex", height:"100vh", background:"#f9fafb", fontFamily:"'Inter',system-ui,sans-serif", overflow:"hidden", flexDirection: isMobile ? "column" : "row" }}>
      {notification && (
        <div style={{ position:"fixed", top:20, right:20, zIndex:300, background:"#111827", color:"#fff", padding:"10px 18px", borderRadius:8, fontSize:13, fontWeight:500, boxShadow:"0 4px 20px #00000030" }}>
          ✓ {notification}
        </div>
      )}

      {birthdayQuickAction && (
        <BirthdayActionModal
          customer={birthdayQuickAction.customer}
          initialType={birthdayQuickAction.type}
          onClose={() => setBirthdayQuickAction(null)}
          notify={notify}
          onViewProfile={() => { setActiveNav("customers"); setBirthdayQuickAction(null); }}
        />
      )}

      {/* Mobile: drawer overlay nav */}
      {isMobile && adminMenuOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex" }}>
          <div style={{ width:280, background:"#fff", display:"flex", flexDirection:"column", height:"100%", boxShadow:"4px 0 24px #00000030", overflowY:"auto" }}>
            <div style={{ padding:"18px 20px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <img src={BURN_LOGO_WHITE} style={{ height:26, filter:"brightness(0)" }} alt="BURN" />
                <div style={{ fontSize:11, color:"#9ca3af" }}>CRM &amp; Marketing</div>
              </div>
              <button onClick={() => setAdminMenuOpen(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280" }}>✕</button>
            </div>
            <nav style={{ padding:"10px", flex:1, overflowY:"auto" }}>
              {NAV.filter(n => !n.hidden && (!n.module || hasModule(n.module))).map(n => (
                <button key={n.id} onClick={() => { setActiveNav(n.id); setAdminMenuOpen(false); }}
                  style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"12px 14px", borderRadius:10, border:"none", cursor:"pointer", fontSize:14, fontWeight:activeNav===n.id?700:400, background:activeNav===n.id?"#f5f3ff":"transparent", color:activeNav===n.id?"#7c3aed":"#374151", fontFamily:"inherit", marginBottom:4, textAlign:"left" }}>
                  <CrmNavIcon id={n.id} color={activeNav===n.id?"#7c3aed":"#9ca3af"} />
                  <span>{n.label}</span>
                </button>
              ))}
            </nav>
            <div style={{ padding:"14px 16px", borderTop:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ flex:1, fontSize:12, color:"#6b7280" }}>{user.name}</div>
              <button onClick={logout} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"6px 12px", fontSize:12, color:"#6b7280", cursor:"pointer" }}>Abmelden</button>
            </div>
          </div>
          <div style={{ flex:1, background:"rgba(0,0,0,0.5)" }} onClick={() => setAdminMenuOpen(false)} />
        </div>
      )}

      {/* Desktop sidebar */}
      {!isMobile && <Sidebar active={activeNav} setActive={setActiveNav} customers={customers} loading={loading} hasModule={hasModule} />}

      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minWidth:0 }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{ height:56, background:"#fff", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", padding:"0 16px", gap:12, flexShrink:0 }}>
            <button onClick={() => setAdminMenuOpen(true)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#374151", padding:"4px 8px", borderRadius:8 }}>☰</button>
            <img src={BURN_LOGO_WHITE} style={{ height:22, filter:"brightness(0)" }} alt="BURN" />
            <div style={{ flex:1, fontSize:14, fontWeight:700, color:"#111827" }}>{NAV.find(n=>n.id===activeNav)?.label}</div>
            <button onClick={() => { setSyncing(true); notify("Sync gestartet..."); loadCustomers(true); }}
              disabled={syncing}
              style={{ background: syncing?"#e5e7eb":"#7c3aed", border:"none", color: syncing?"#9ca3af":"#fff", borderRadius:8, padding:"8px 12px", cursor: syncing?"default":"pointer", fontSize:13, fontWeight:700 }}>
              {syncing ? "⟳" : "↻"}
            </button>
            <div onClick={logout} style={{ width:34, height:34, borderRadius:"50%", overflow:"hidden", border:"2px solid #e5e7eb", cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background:"#f3f4f6" }}>
              {user.photo ? <img src={user.photo} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ fontSize:14, fontWeight:700, color:"#6b7280" }}>{user.name?.[0]}</span>}
            </div>
          </div>
        )}

        {/* Desktop top bar */}
        {!isMobile && (
          <>
            {todayBirthdays.length > 0 && (
              <div style={{ background:"linear-gradient(90deg,#7c3aed,#9900cc)", color:"#fff", padding:"7px 24px", fontSize:13, display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", flexShrink:0 }}>
                <span style={{ fontWeight:700 }}>🎂 Geburtstag heute:</span>
                {todayBirthdays.map(c => (
                  <span key={c.id} style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                    <button onClick={() => setBirthdayQuickAction({customer: c, open: true})}
                      style={{ background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.4)", color:"#fff", borderRadius:99, padding:"2px 10px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      🎂 {c.name}
                    </button>
                    <button onClick={() => setBirthdayQuickAction({customer: c, type: "sms"})}
                      style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:99, padding:"2px 8px", fontSize:11, cursor:"pointer" }}>💬</button>
                    <button onClick={() => setBirthdayQuickAction({customer: c, type: "email"})}
                      style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:99, padding:"2px 8px", fontSize:11, cursor:"pointer" }}>📧</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ height:56, background:"#fff", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", padding:"0 24px", gap:16, flexShrink:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>{NAV.find(n=>n.id===activeNav)?.label}</div>
              <div style={{ flex:1 }} />
              {loading && customers.length === 0 && <span style={{ fontSize:12, color:"#9ca3af" }}>⟳ Lädt Daten...</span>}
              {loading && customers.length > 0 && <span style={{ fontSize:12, color:"#9ca3af" }}>⟳ Aktualisiere...</span>}
              <div style={{ fontSize:12, color:"#9ca3af" }}>{customers.length} Kunden · {customers.filter(c => isValidMobile(c.phone)).length} SMS-fähig</div>
              <button onClick={() => { setSyncing(true); notify("Sync gestartet..."); loadCustomers(true); }}
                disabled={syncing}
                style={{ background: syncing?"#e5e7eb":"#7c3aed", border:"none", color: syncing?"#9ca3af":"#fff", borderRadius:6, padding:"7px 14px", cursor: syncing?"default":"pointer", fontSize:12, fontWeight:700 }}>
                {syncing ? "⟳ Syncing..." : "↻ Sync"}
              </button>
              {lastSync && !syncing && <span style={{ fontSize:11, color:"#9ca3af" }}>Letzter Sync: {Math.round((new Date()-lastSync)/60000) < 1 ? "gerade eben" : `vor ${Math.round((new Date()-lastSync)/60000)} Min`}</span>}
              <div style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }} onClick={logout}>
                {user.photo ? (
                  <div style={{ width:32, height:32, borderRadius:"50%", overflow:"hidden", border:"2px solid #e5e7eb" }}>
                    <img src={user.photo} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} />
                  </div>
                ) : (
                  <div style={{ width:32, height:32, borderRadius:"50%", background:`${user.color||"#7c3aed"}20`, border:`2px solid ${user.color||"#7c3aed"}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:user.color||"#7c3aed" }}>
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <span style={{ fontSize:12, color:"#6b7280" }}>{user.name}</span>
              </div>
            </div>
          </>
        )}

        {activeNav==="pipeline"    && <PipelineView customers={customers} setCustomers={setCustomers} notify={notify}/>}
        {activeNav==="customers" && (
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e5e7eb", background:"#fff", flexShrink:0, paddingLeft:24 }}>
              {[["kunden","👥 Kunden"],["pipeline","🔀 Pipeline"],["sms","💬 SMS"],["segments","🎯 Segmente"],["leads","◈ Leads"],["duplicates","🔍 Duplikate"]].map(([id,label]) => (
                <button key={id} onClick={() => setCustomerTab(id)} style={{ padding:"10px 18px", border:"none", borderBottom: customerTab===id ? "2px solid #7c3aed" : "2px solid transparent", background:"transparent", color: customerTab===id ? "#7c3aed" : "#6b7280", fontWeight: customerTab===id ? 700 : 400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                  {label}
                </button>
              ))}
            </div>
            {customerTab==="kunden"    && <CustomerDB customers={customers} setCustomers={setCustomers} sourceFilter={sourceFilter} onClearSourceFilter={()=>setSourceFilter(null)}/>}
            {customerTab==="pipeline"  && <PipelineView customers={customers} setCustomers={setCustomers} notify={notify}/>}
            {customerTab==="sms"      && <SMSView customers={customers}/>}
            {customerTab==="segments" && <SegmentsView customers={customers}/>}
            {customerTab==="leads"       && <LeadsView customers={customers} setCustomers={setCustomers} notify={notify}/>}
            {customerTab==="duplicates"  && <DuplicatesView customers={customers} setCustomers={setCustomers} notify={notify}/>}
          </div>
        )}
        {activeNav==="emails" && (
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e5e7eb", background:"#fff", flexShrink:0, paddingLeft:24 }}>
              {[["emails","📧 E-Mails"],["automations","⚡ Automationen"],["journeys","🗺️ Journeys"],["journeyEditor","✏️ Editor"],["emailBlockEditor","📝 Templates"],["templates","🗂️ Vorlagen"],["unsubscribe","🚫 Abmeldungen"]].map(([id,label]) => (
                <button key={id} onClick={() => setEmailTab(id)} style={{ padding:"10px 18px", border:"none", borderBottom: emailTab===id ? "2px solid #7c3aed" : "2px solid transparent", background:"transparent", color: emailTab===id ? "#7c3aed" : "#6b7280", fontWeight: emailTab===id ? 700 : 400, fontSize:13, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  {label}
                </button>
              ))}
            </div>
            {emailTab==="emails"           && <EmailView customers={customers}/>}
            {emailTab==="automations"      && <AutomationsView customers={customers}/>}
            {emailTab==="journeys"         && <JourneysView customers={customers}/>}
            {emailTab==="journeyEditor"    && <JourneyTemplateEditorView />}
            {emailTab==="emailBlockEditor" && <EmailBlockEditorView />}
            {emailTab==="templates"        && <TemplatesView />}
            {emailTab==="unsubscribe"      && <UnsubscribeListView />}
          </div>
        )}
        {activeNav==="automations" && <AutomationsView customers={customers}/>}
        {activeNav==="analytics"   && <AnalyticsView customers={customers}/>}
        {activeNav==="weekly"      && <WeeklyView customers={customers} notify={notify}/>}
        {activeNav==="bestand"     && <BestandView notify={notify}/>}
        {activeNav==="staff"       && <StaffView notify={notify}/>}
        {activeNav==="cash"        && <CashView notify={notify}/>}
          {activeNav==="profitability" && <ProfitabilityModule notify={notify}/>}
          {activeNav==="modules"       && <ModulesAdminView notify={notify} onModulesChanged={loadModules}/>}
        {activeNav==="duplicates"  && <DuplicatesView customers={customers} setCustomers={setCustomers} notify={notify}/>}
        {activeNav==="journeys"    && <JourneysView notify={notify}/>}
        {activeNav==="journeyEditor" && <JourneyTemplateEditorView />}
        {activeNav==="emailBlockEditor" && <EmailBlockEditorView />}
        {activeNav==="templates"   && <TemplatesView/>}
        {activeNav==="messages"    && <MsgErrorBoundary><MessagesView user={user}/></MsgErrorBoundary>}
        {activeNav==="leaderboard" && <LeaderboardView user={user}/>}
        {activeNav==="permissions" && <PermissionsView/>}
        {activeNav==="kommunikation" && <KommunikationView user={user} notify={notify} />}
      </div>
    </div>
  );
}
